/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const T0 = window, be = T0.ShadowRoot && (T0.ShadyCSS === void 0 || T0.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, we = Symbol(), De = /* @__PURE__ */ new WeakMap();
let n2 = class {
  constructor(e, s, r) {
    if (this._$cssResult$ = !0, r !== we)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = s;
  }
  get styleSheet() {
    let e = this.o;
    const s = this.t;
    if (be && e === void 0) {
      const r = s !== void 0 && s.length === 1;
      r && (e = De.get(s)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), r && De.set(s, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const $ = (t) => new n2(typeof t == "string" ? t : t + "", void 0, we), F2 = (t, ...e) => {
  const s = t.length === 1 ? t[0] : e.reduce((r, o, i) => r + ((a) => {
    if (a._$cssResult$ === !0)
      return a.cssText;
    if (typeof a == "number")
      return a;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + a + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(o) + t[i + 1], t[0]);
  return new n2(s, t, we);
}, j2 = (t, e) => {
  be ? t.adoptedStyleSheets = e.map((s) => s instanceof CSSStyleSheet ? s : s.styleSheet) : e.forEach((s) => {
    const r = document.createElement("style"), o = T0.litNonce;
    o !== void 0 && r.setAttribute("nonce", o), r.textContent = s.cssText, t.appendChild(r);
  });
}, Le = be ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let s = "";
  for (const r of e.cssRules)
    s += r.cssText;
  return $(s);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var te;
const I0 = window, Oe = I0.trustedTypes, R2 = Oe ? Oe.emptyScript : "", Ee = I0.reactiveElementPolyfillSupport, _0 = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? R2 : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let s = t;
  switch (e) {
    case Boolean:
      s = t !== null;
      break;
    case Number:
      s = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        s = JSON.parse(t);
      } catch {
        s = null;
      }
  }
  return s;
} }, l2 = (t, e) => e !== t && (e == e || t == t), se = { attribute: !0, type: String, converter: _0, reflect: !1, hasChanged: l2 };
let X1 = class extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this.u();
  }
  static addInitializer(e) {
    var s;
    (s = this.h) !== null && s !== void 0 || (this.h = []), this.h.push(e);
  }
  static get observedAttributes() {
    this.finalize();
    const e = [];
    return this.elementProperties.forEach((s, r) => {
      const o = this._$Ep(r, s);
      o !== void 0 && (this._$Ev.set(o, r), e.push(o));
    }), e;
  }
  static createProperty(e, s = se) {
    if (s.state && (s.attribute = !1), this.finalize(), this.elementProperties.set(e, s), !s.noAccessor && !this.prototype.hasOwnProperty(e)) {
      const r = typeof e == "symbol" ? Symbol() : "__" + e, o = this.getPropertyDescriptor(e, r, s);
      o !== void 0 && Object.defineProperty(this.prototype, e, o);
    }
  }
  static getPropertyDescriptor(e, s, r) {
    return { get() {
      return this[s];
    }, set(o) {
      const i = this[e];
      this[s] = o, this.requestUpdate(e, i, r);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) || se;
  }
  static finalize() {
    if (this.hasOwnProperty("finalized"))
      return !1;
    this.finalized = !0;
    const e = Object.getPrototypeOf(this);
    if (e.finalize(), this.elementProperties = new Map(e.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const s = this.properties, r = [...Object.getOwnPropertyNames(s), ...Object.getOwnPropertySymbols(s)];
      for (const o of r)
        this.createProperty(o, s[o]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(e) {
    const s = [];
    if (Array.isArray(e)) {
      const r = new Set(e.flat(1 / 0).reverse());
      for (const o of r)
        s.unshift(Le(o));
    } else
      e !== void 0 && s.push(Le(e));
    return s;
  }
  static _$Ep(e, s) {
    const r = s.attribute;
    return r === !1 ? void 0 : typeof r == "string" ? r : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  u() {
    var e;
    this._$E_ = new Promise((s) => this.enableUpdating = s), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (e = this.constructor.h) === null || e === void 0 || e.forEach((s) => s(this));
  }
  addController(e) {
    var s, r;
    ((s = this._$ES) !== null && s !== void 0 ? s : this._$ES = []).push(e), this.renderRoot !== void 0 && this.isConnected && ((r = e.hostConnected) === null || r === void 0 || r.call(e));
  }
  removeController(e) {
    var s;
    (s = this._$ES) === null || s === void 0 || s.splice(this._$ES.indexOf(e) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((e, s) => {
      this.hasOwnProperty(s) && (this._$Ei.set(s, this[s]), delete this[s]);
    });
  }
  createRenderRoot() {
    var e;
    const s = (e = this.shadowRoot) !== null && e !== void 0 ? e : this.attachShadow(this.constructor.shadowRootOptions);
    return j2(s, this.constructor.elementStyles), s;
  }
  connectedCallback() {
    var e;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$ES) === null || e === void 0 || e.forEach((s) => {
      var r;
      return (r = s.hostConnected) === null || r === void 0 ? void 0 : r.call(s);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$ES) === null || e === void 0 || e.forEach((s) => {
      var r;
      return (r = s.hostDisconnected) === null || r === void 0 ? void 0 : r.call(s);
    });
  }
  attributeChangedCallback(e, s, r) {
    this._$AK(e, r);
  }
  _$EO(e, s, r = se) {
    var o;
    const i = this.constructor._$Ep(e, r);
    if (i !== void 0 && r.reflect === !0) {
      const a = (((o = r.converter) === null || o === void 0 ? void 0 : o.toAttribute) !== void 0 ? r.converter : _0).toAttribute(s, r.type);
      this._$El = e, a == null ? this.removeAttribute(i) : this.setAttribute(i, a), this._$El = null;
    }
  }
  _$AK(e, s) {
    var r;
    const o = this.constructor, i = o._$Ev.get(e);
    if (i !== void 0 && this._$El !== i) {
      const a = o.getPropertyOptions(i), c = typeof a.converter == "function" ? { fromAttribute: a.converter } : ((r = a.converter) === null || r === void 0 ? void 0 : r.fromAttribute) !== void 0 ? a.converter : _0;
      this._$El = i, this[i] = c.fromAttribute(s, a.type), this._$El = null;
    }
  }
  requestUpdate(e, s, r) {
    let o = !0;
    e !== void 0 && (((r = r || this.constructor.getPropertyOptions(e)).hasChanged || l2)(this[e], s) ? (this._$AL.has(e) || this._$AL.set(e, s), r.reflect === !0 && this._$El !== e && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(e, r))) : o = !1), !this.isUpdatePending && o && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (s) {
      Promise.reject(s);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending)
      return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((o, i) => this[i] = o), this._$Ei = void 0);
    let s = !1;
    const r = this._$AL;
    try {
      s = this.shouldUpdate(r), s ? (this.willUpdate(r), (e = this._$ES) === null || e === void 0 || e.forEach((o) => {
        var i;
        return (i = o.hostUpdate) === null || i === void 0 ? void 0 : i.call(o);
      }), this.update(r)) : this._$Ek();
    } catch (o) {
      throw s = !1, this._$Ek(), o;
    }
    s && this._$AE(r);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var s;
    (s = this._$ES) === null || s === void 0 || s.forEach((r) => {
      var o;
      return (o = r.hostUpdated) === null || o === void 0 ? void 0 : o.call(r);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$EC !== void 0 && (this._$EC.forEach((s, r) => this._$EO(r, this[r], s)), this._$EC = void 0), this._$Ek();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
X1.finalized = !0, X1.elementProperties = /* @__PURE__ */ new Map(), X1.elementStyles = [], X1.shadowRootOptions = { mode: "open" }, Ee == null || Ee({ ReactiveElement: X1 }), ((te = I0.reactiveElementVersions) !== null && te !== void 0 ? te : I0.reactiveElementVersions = []).push("1.4.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var re;
const P0 = window, s0 = P0.trustedTypes, Te = s0 ? s0.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, D1 = `lit$${(Math.random() + "").slice(9)}$`, c2 = "?" + D1, N2 = `<${c2}>`, r0 = document, Z0 = (t = "") => r0.createComment(t), C0 = (t) => t === null || typeof t != "object" && typeof t != "function", h2 = Array.isArray, U2 = (t) => h2(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", m0 = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ze = /-->/g, Ie = />/g, R1 = RegExp(`>|[ 	
\f\r](?:([^\\s"'>=/]+)([ 	
\f\r]*=[ 	
\f\r]*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), Pe = /'/g, Be = /"/g, d2 = /^(?:script|style|textarea|title)$/i, W2 = (t) => (e, ...s) => ({ _$litType$: t, strings: e, values: s }), g = W2(1), X = Symbol.for("lit-noChange"), O = Symbol.for("lit-nothing"), Fe = /* @__PURE__ */ new WeakMap(), q2 = (t, e, s) => {
  var r, o;
  const i = (r = s == null ? void 0 : s.renderBefore) !== null && r !== void 0 ? r : e;
  let a = i._$litPart$;
  if (a === void 0) {
    const c = (o = s == null ? void 0 : s.renderBefore) !== null && o !== void 0 ? o : null;
    i._$litPart$ = a = new xe(e.insertBefore(Z0(), c), c, void 0, s ?? {});
  }
  return a._$AI(t), a;
}, J1 = r0.createTreeWalker(r0, 129, null, !1), K2 = (t, e) => {
  const s = t.length - 1, r = [];
  let o, i = e === 2 ? "<svg>" : "", a = m0;
  for (let h = 0; h < s; h++) {
    const d = t[h];
    let v, p, u = -1, m = 0;
    for (; m < d.length && (a.lastIndex = m, p = a.exec(d), p !== null); )
      m = a.lastIndex, a === m0 ? p[1] === "!--" ? a = ze : p[1] !== void 0 ? a = Ie : p[2] !== void 0 ? (d2.test(p[2]) && (o = RegExp("</" + p[2], "g")), a = R1) : p[3] !== void 0 && (a = R1) : a === R1 ? p[0] === ">" ? (a = o ?? m0, u = -1) : p[1] === void 0 ? u = -2 : (u = a.lastIndex - p[2].length, v = p[1], a = p[3] === void 0 ? R1 : p[3] === '"' ? Be : Pe) : a === Be || a === Pe ? a = R1 : a === ze || a === Ie ? a = m0 : (a = R1, o = void 0);
    const b = a === R1 && t[h + 1].startsWith("/>") ? " " : "";
    i += a === m0 ? d + N2 : u >= 0 ? (r.push(v), d.slice(0, u) + "$lit$" + d.slice(u) + D1 + b) : d + D1 + (u === -2 ? (r.push(void 0), h) : b);
  }
  const c = i + (t[s] || "<?>") + (e === 2 ? "</svg>" : "");
  if (!Array.isArray(t) || !t.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return [Te !== void 0 ? Te.createHTML(c) : c, r];
};
let le = class p2 {
  constructor({ strings: e, _$litType$: s }, r) {
    let o;
    this.parts = [];
    let i = 0, a = 0;
    const c = e.length - 1, h = this.parts, [d, v] = K2(e, s);
    if (this.el = p2.createElement(d, r), J1.currentNode = this.el.content, s === 2) {
      const p = this.el.content, u = p.firstChild;
      u.remove(), p.append(...u.childNodes);
    }
    for (; (o = J1.nextNode()) !== null && h.length < c; ) {
      if (o.nodeType === 1) {
        if (o.hasAttributes()) {
          const p = [];
          for (const u of o.getAttributeNames())
            if (u.endsWith("$lit$") || u.startsWith(D1)) {
              const m = v[a++];
              if (p.push(u), m !== void 0) {
                const b = o.getAttribute(m.toLowerCase() + "$lit$").split(D1), w = /([.?@])?(.*)/.exec(m);
                h.push({ type: 1, index: i, name: w[2], strings: b, ctor: w[1] === "." ? G2 : w[1] === "?" ? X2 : w[1] === "@" ? J2 : R0 });
              } else
                h.push({ type: 6, index: i });
            }
          for (const u of p)
            o.removeAttribute(u);
        }
        if (d2.test(o.tagName)) {
          const p = o.textContent.split(D1), u = p.length - 1;
          if (u > 0) {
            o.textContent = s0 ? s0.emptyScript : "";
            for (let m = 0; m < u; m++)
              o.append(p[m], Z0()), J1.nextNode(), h.push({ type: 2, index: ++i });
            o.append(p[u], Z0());
          }
        }
      } else if (o.nodeType === 8)
        if (o.data === c2)
          h.push({ type: 2, index: i });
        else {
          let p = -1;
          for (; (p = o.data.indexOf(D1, p + 1)) !== -1; )
            h.push({ type: 7, index: i }), p += D1.length - 1;
        }
      i++;
    }
  }
  static createElement(e, s) {
    const r = r0.createElement("template");
    return r.innerHTML = e, r;
  }
};
function o0(t, e, s = t, r) {
  var o, i, a, c;
  if (e === X)
    return e;
  let h = r !== void 0 ? (o = s._$Cl) === null || o === void 0 ? void 0 : o[r] : s._$Cu;
  const d = C0(e) ? void 0 : e._$litDirective$;
  return (h == null ? void 0 : h.constructor) !== d && ((i = h == null ? void 0 : h._$AO) === null || i === void 0 || i.call(h, !1), d === void 0 ? h = void 0 : (h = new d(t), h._$AT(t, s, r)), r !== void 0 ? ((a = (c = s)._$Cl) !== null && a !== void 0 ? a : c._$Cl = [])[r] = h : s._$Cu = h), h !== void 0 && (e = o0(t, h._$AS(t, e.values), h, r)), e;
}
class Y2 {
  constructor(e, s) {
    this.v = [], this._$AN = void 0, this._$AD = e, this._$AM = s;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  p(e) {
    var s;
    const { el: { content: r }, parts: o } = this._$AD, i = ((s = e == null ? void 0 : e.creationScope) !== null && s !== void 0 ? s : r0).importNode(r, !0);
    J1.currentNode = i;
    let a = J1.nextNode(), c = 0, h = 0, d = o[0];
    for (; d !== void 0; ) {
      if (c === d.index) {
        let v;
        d.type === 2 ? v = new xe(a, a.nextSibling, this, e) : d.type === 1 ? v = new d.ctor(a, d.name, d.strings, this, e) : d.type === 6 && (v = new et(a, this, e)), this.v.push(v), d = o[++h];
      }
      c !== (d == null ? void 0 : d.index) && (a = J1.nextNode(), c++);
    }
    return i;
  }
  m(e) {
    let s = 0;
    for (const r of this.v)
      r !== void 0 && (r.strings !== void 0 ? (r._$AI(e, r, s), s += r.strings.length - 2) : r._$AI(e[s])), s++;
  }
}
let xe = class u2 {
  constructor(e, s, r, o) {
    var i;
    this.type = 2, this._$AH = O, this._$AN = void 0, this._$AA = e, this._$AB = s, this._$AM = r, this.options = o, this._$C_ = (i = o == null ? void 0 : o.isConnected) === null || i === void 0 || i;
  }
  get _$AU() {
    var e, s;
    return (s = (e = this._$AM) === null || e === void 0 ? void 0 : e._$AU) !== null && s !== void 0 ? s : this._$C_;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const s = this._$AM;
    return s !== void 0 && e.nodeType === 11 && (e = s.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, s = this) {
    e = o0(this, e, s), C0(e) ? e === O || e == null || e === "" ? (this._$AH !== O && this._$AR(), this._$AH = O) : e !== this._$AH && e !== X && this.$(e) : e._$litType$ !== void 0 ? this.T(e) : e.nodeType !== void 0 ? this.k(e) : U2(e) ? this.O(e) : this.$(e);
  }
  S(e, s = this._$AB) {
    return this._$AA.parentNode.insertBefore(e, s);
  }
  k(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.S(e));
  }
  $(e) {
    this._$AH !== O && C0(this._$AH) ? this._$AA.nextSibling.data = e : this.k(r0.createTextNode(e)), this._$AH = e;
  }
  T(e) {
    var s;
    const { values: r, _$litType$: o } = e, i = typeof o == "number" ? this._$AC(e) : (o.el === void 0 && (o.el = le.createElement(o.h, this.options)), o);
    if (((s = this._$AH) === null || s === void 0 ? void 0 : s._$AD) === i)
      this._$AH.m(r);
    else {
      const a = new Y2(i, this), c = a.p(this.options);
      a.m(r), this.k(c), this._$AH = a;
    }
  }
  _$AC(e) {
    let s = Fe.get(e.strings);
    return s === void 0 && Fe.set(e.strings, s = new le(e)), s;
  }
  O(e) {
    h2(this._$AH) || (this._$AH = [], this._$AR());
    const s = this._$AH;
    let r, o = 0;
    for (const i of e)
      o === s.length ? s.push(r = new u2(this.S(Z0()), this.S(Z0()), this, this.options)) : r = s[o], r._$AI(i), o++;
    o < s.length && (this._$AR(r && r._$AB.nextSibling, o), s.length = o);
  }
  _$AR(e = this._$AA.nextSibling, s) {
    var r;
    for ((r = this._$AP) === null || r === void 0 || r.call(this, !1, !0, s); e && e !== this._$AB; ) {
      const o = e.nextSibling;
      e.remove(), e = o;
    }
  }
  setConnected(e) {
    var s;
    this._$AM === void 0 && (this._$C_ = e, (s = this._$AP) === null || s === void 0 || s.call(this, e));
  }
};
class R0 {
  constructor(e, s, r, o, i) {
    this.type = 1, this._$AH = O, this._$AN = void 0, this.element = e, this.name = s, this._$AM = o, this.options = i, r.length > 2 || r[0] !== "" || r[1] !== "" ? (this._$AH = Array(r.length - 1).fill(new String()), this.strings = r) : this._$AH = O;
  }
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e, s = this, r, o) {
    const i = this.strings;
    let a = !1;
    if (i === void 0)
      e = o0(this, e, s, 0), a = !C0(e) || e !== this._$AH && e !== X, a && (this._$AH = e);
    else {
      const c = e;
      let h, d;
      for (e = i[0], h = 0; h < i.length - 1; h++)
        d = o0(this, c[r + h], s, h), d === X && (d = this._$AH[h]), a || (a = !C0(d) || d !== this._$AH[h]), d === O ? e = O : e !== O && (e += (d ?? "") + i[h + 1]), this._$AH[h] = d;
    }
    a && !o && this.P(e);
  }
  P(e) {
    e === O ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
let G2 = class extends R0 {
  constructor() {
    super(...arguments), this.type = 3;
  }
  P(e) {
    this.element[this.name] = e === O ? void 0 : e;
  }
};
const Q2 = s0 ? s0.emptyScript : "";
let X2 = class extends R0 {
  constructor() {
    super(...arguments), this.type = 4;
  }
  P(e) {
    e && e !== O ? this.element.setAttribute(this.name, Q2) : this.element.removeAttribute(this.name);
  }
};
class J2 extends R0 {
  constructor(e, s, r, o, i) {
    super(e, s, r, o, i), this.type = 5;
  }
  _$AI(e, s = this) {
    var r;
    if ((e = (r = o0(this, e, s, 0)) !== null && r !== void 0 ? r : O) === X)
      return;
    const o = this._$AH, i = e === O && o !== O || e.capture !== o.capture || e.once !== o.once || e.passive !== o.passive, a = e !== O && (o === O || i);
    i && this.element.removeEventListener(this.name, this, o), a && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var s, r;
    typeof this._$AH == "function" ? this._$AH.call((r = (s = this.options) === null || s === void 0 ? void 0 : s.host) !== null && r !== void 0 ? r : this.element, e) : this._$AH.handleEvent(e);
  }
}
let et = class {
  constructor(e, s, r) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = s, this.options = r;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    o0(this, e);
  }
};
const je = P0.litHtmlPolyfillSupport;
je == null || je(le, xe), ((re = P0.litHtmlVersions) !== null && re !== void 0 ? re : P0.litHtmlVersions = []).push("2.3.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var oe, ie;
let e0 = class extends X1 {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e, s;
    const r = super.createRenderRoot();
    return (e = (s = this.renderOptions).renderBefore) !== null && e !== void 0 || (s.renderBefore = r.firstChild), r;
  }
  update(e) {
    const s = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = q2(s, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) === null || e === void 0 || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) === null || e === void 0 || e.setConnected(!1);
  }
  render() {
    return X;
  }
};
e0.finalized = !0, e0._$litElement$ = !0, (oe = globalThis.litElementHydrateSupport) === null || oe === void 0 || oe.call(globalThis, { LitElement: e0 });
const Re = globalThis.litElementPolyfillSupport;
Re == null || Re({ LitElement: e0 });
((ie = globalThis.litElementVersions) !== null && ie !== void 0 ? ie : globalThis.litElementVersions = []).push("3.2.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const S = (t) => (e) => typeof e == "function" ? ((s, r) => (customElements.define(s, r), r))(t, e) : ((s, r) => {
  const { kind: o, elements: i } = r;
  return { kind: o, elements: i, finisher(a) {
    customElements.define(s, a);
  } };
})(t, e);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const tt = (t, e) => e.kind === "method" && e.descriptor && !("value" in e.descriptor) ? { ...e, finisher(s) {
  s.createProperty(e.key, t);
} } : { kind: "field", key: Symbol(), placement: "own", descriptor: {}, originalKey: e.key, initializer() {
  typeof e.initializer == "function" && (this[e.key] = e.initializer.call(this));
}, finisher(s) {
  s.createProperty(e.key, t);
} };
function l(t) {
  return (e, s) => s !== void 0 ? ((r, o, i) => {
    o.constructor.createProperty(i, r);
  })(t, e, s) : tt(t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function K(t) {
  return l({ ...t, state: !0 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const st = ({ finisher: t, descriptor: e }) => (s, r) => {
  var o;
  if (r === void 0) {
    const i = (o = s.originalKey) !== null && o !== void 0 ? o : s.key, a = e != null ? { kind: "method", placement: "prototype", key: i, descriptor: e(s.key) } : { ...s, key: i };
    return t != null && (a.finisher = function(c) {
      t(c, i);
    }), a;
  }
  {
    const i = s.constructor;
    e !== void 0 && Object.defineProperty(s, r, e(r)), t == null || t(i, r);
  }
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function g1(t, e) {
  return st({ descriptor: (s) => {
    const r = { get() {
      var o, i;
      return (i = (o = this.renderRoot) === null || o === void 0 ? void 0 : o.querySelector(t)) !== null && i !== void 0 ? i : null;
    }, enumerable: !0, configurable: !0 };
    if (e) {
      const o = typeof s == "symbol" ? Symbol() : "__" + s;
      r.get = function() {
        var i, a;
        return this[o] === void 0 && (this[o] = (a = (i = this.renderRoot) === null || i === void 0 ? void 0 : i.querySelector(t)) !== null && a !== void 0 ? a : null), this[o];
      };
    }
    return r;
  } });
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var ae;
((ae = window.HTMLSlotElement) === null || ae === void 0 ? void 0 : ae.prototype.assignedElements) != null;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Z1 = { ATTRIBUTE: 1, CHILD: 2, PROPERTY: 3, BOOLEAN_ATTRIBUTE: 4, EVENT: 5, ELEMENT: 6 }, N0 = (t) => (...e) => ({ _$litDirective$: t, values: e });
let U0 = class {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, s, r) {
    this._$Ct = e, this._$AM = s, this._$Ci = r;
  }
  _$AS(e, s) {
    return this.update(e, s);
  }
  update(e, s) {
    return this.render(...s);
  }
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let ce = class extends U0 {
  constructor(e) {
    if (super(e), this.it = O, e.type !== Z1.CHILD)
      throw Error(this.constructor.directiveName + "() can only be used in child bindings");
  }
  render(e) {
    if (e === O || e == null)
      return this._t = void 0, this.it = e;
    if (e === X)
      return e;
    if (typeof e != "string")
      throw Error(this.constructor.directiveName + "() called with a non-string value");
    if (e === this.it)
      return this._t;
    this.it = e;
    const s = [e];
    return s.raw = s, this._t = { _$litType$: this.constructor.resultType, strings: s, values: [] };
  }
};
ce.directiveName = "unsafeHTML", ce.resultType = 1;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let he = class extends ce {
};
he.directiveName = "unsafeSVG", he.resultType = 2;
const rt = N0(he), ot = "design:options";
function x(t, e) {
  return (s, r) => {
    if (typeof Reflect == "object" && typeof Reflect.metadata == "function" && Reflect.metadata(ot, t)(s, r), typeof e < "u") {
      const o = r;
      if (t.includes(String)) {
        console.error(
          `You cannot use a default value with an options array containing the \`String\` constructor. %c(See property ${s.constructor.name}.${r})`,
          "font-weight: bold;"
        );
        return;
      }
      const i = s.update;
      s.update = function(a) {
        if (a.has(o)) {
          const c = this[o];
          t.includes(c) || (this[o] = e);
        }
        i.call(this, a);
      };
    }
  };
}
const it = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M4 4a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H4Zm9.02 6.864c-.263.52-.396 1.116-.396 1.788 0 .68.133 1.284.396 1.812.273.52.64.924 1.104 1.212.464.288.976.432 1.537.432.496 0 .932-.1 1.307-.3.377-.2.669-.464.877-.792V16h1.704V7.12h-1.704v3.156a2.421 2.421 0 0 0-.936-.744 2.775 2.775 0 0 0-1.236-.288 2.94 2.94 0 0 0-1.549.42 2.98 2.98 0 0 0-1.104 1.2Zm4.596.768c.16.288.24.636.24 1.044 0 .408-.08.76-.24 1.056-.16.288-.375.512-.648.672a1.767 1.767 0 0 1-.875.228c-.304 0-.592-.08-.864-.24a1.842 1.842 0 0 1-.648-.684 2.235 2.235 0 0 1-.24-1.056c0-.4.08-.744.24-1.032.168-.296.383-.52.648-.672.264-.152.552-.228.864-.228.311 0 .604.08.875.24.273.152.488.376.648.672Zm-11.35 2.772h3.337L10.155 16h1.776L8.919 7.612H6.963L3.95 16h1.764l.552-1.596Zm1.669-4.848 1.212 3.504H6.723l1.212-3.504Z" clip-rule="evenodd"/></svg>', at = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 9 16v-5a.97.97 0 0 0-.287-.713A.97.97 0 0 0 8 10a.97.97 0 0 0-.713.287A.97.97 0 0 0 7 11v5c0 .283.096.52.287.712.192.192.43.288.713.288Zm4 0a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16V8a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8v8c0 .283.096.52.288.712A.965.965 0 0 0 12 17Zm4 0c.283 0 .52-.096.712-.288A.965.965 0 0 0 17 16v-2a.968.968 0 0 0-.288-.713A.967.967 0 0 0 16 13a.967.967 0 0 0-.712.287A.968.968 0 0 0 15 14v2c0 .283.096.52.288.712A.965.965 0 0 0 16 17ZM5 21a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 3 19V5c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 3h8a.97.97 0 0 1 .713.287A.97.97 0 0 1 14 4a.97.97 0 0 1-.287.712A.968.968 0 0 1 13 5H5v14h14v-8c0-.283.096-.521.288-.713A.967.967 0 0 1 20 10a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v8a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 19 21H5ZM18 9a.965.965 0 0 1-.712-.288A.965.965 0 0 1 17 8V7h-1a.967.967 0 0 1-.712-.287A.968.968 0 0 1 15 6c0-.283.096-.521.288-.713A.967.967 0 0 1 16 5h1V4c0-.283.096-.521.288-.713A.967.967 0 0 1 18 3a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v1h1a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 20 7h-1v1c0 .283-.096.52-.288.712A.965.965 0 0 1 18 9Z"/></svg>', nt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 8a.965.965 0 0 1-.712-.288A.965.965 0 0 1 18 7V6h-1a.967.967 0 0 1-.712-.287A.968.968 0 0 1 16 5c0-.283.096-.521.288-.713A.967.967 0 0 1 17 4h1V3c0-.283.096-.521.288-.713A.967.967 0 0 1 19 2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v1h1a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 21 6h-1v1c0 .283-.096.52-.288.712A.965.965 0 0 1 19 8Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M5 21a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 3 19V5c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 3h8a.97.97 0 0 1 .713.287A.97.97 0 0 1 14 4v1a5 5 0 0 0 5 5h1a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v8a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 19 21H5Zm7.713-3.288A.968.968 0 0 1 12 18a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 17V9c0-.283.096-.521.288-.713A.967.967 0 0 1 12 8a.97.97 0 0 1 .713.287A.97.97 0 0 1 13 9v8a.97.97 0 0 1-.287.712Zm-4 0A.968.968 0 0 1 8 18a.968.968 0 0 1-.713-.288A.967.967 0 0 1 7 17v-5a.97.97 0 0 1 .287-.713A.97.97 0 0 1 8 11a.97.97 0 0 1 .713.287A.97.97 0 0 1 9 12v5a.97.97 0 0 1-.287.712ZM16 18c.283 0 .52-.096.712-.288A.965.965 0 0 0 17 17v-2a.968.968 0 0 0-.288-.713A.967.967 0 0 0 16 14a.967.967 0 0 0-.712.287A.968.968 0 0 0 15 15v2c0 .283.096.52.288.712A.965.965 0 0 0 16 18Z" clip-rule="evenodd"/></svg>', lt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16v-3h3.025a.926.926 0 0 0 .7-.288A.99.99 0 0 0 17 12a.968.968 0 0 0-.288-.713A.967.967 0 0 0 16 11h-3V7.975a.928.928 0 0 0-.287-.7A.993.993 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8v3H7.975a.928.928 0 0 0-.7.287A.993.993 0 0 0 7 12c0 .283.096.52.287.712.192.192.43.288.713.288h3v3.025c0 .283.096.517.288.7A.99.99 0 0 0 12 17Zm0 5a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Zm0-2c2.217 0 4.104-.779 5.663-2.337C19.221 16.104 20 14.217 20 12s-.779-4.104-2.337-5.663C16.104 4.779 14.217 4 12 4s-4.104.779-5.662 2.337C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.221 9.783 20 12 20Z"/></svg>', ct = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16v-3h3.025a.926.926 0 0 0 .7-.288A.99.99 0 0 0 17 12a.968.968 0 0 0-.288-.713A.967.967 0 0 0 16 11h-3V7.975a.928.928 0 0 0-.287-.7A.993.993 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8v3H7.975a.928.928 0 0 0-.7.287A.993.993 0 0 0 7 12c0 .283.096.52.287.712.192.192.43.288.713.288h3v3.025c0 .283.096.517.288.7A.99.99 0 0 0 12 17Zm0 5a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Z"/></svg>', ht = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 19a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 18v-5H6a.968.968 0 0 1-.713-.288A.967.967 0 0 1 5 12a.97.97 0 0 1 .287-.713A.97.97 0 0 1 6 11h5V6c0-.283.096-.521.288-.713A.967.967 0 0 1 12 5a.97.97 0 0 1 .713.287A.97.97 0 0 1 13 6v5h5a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.965.965 0 0 1 18 13h-5v5a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 19Z"/></svg>', dt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8.15 21.75 6.7 19.3l-2.75-.6a.943.943 0 0 1-.6-.387.928.928 0 0 1-.175-.688L3.45 14.8l-1.875-2.15a.934.934 0 0 1-.25-.65c0-.25.083-.467.25-.65L3.45 9.2l-.275-2.825a.928.928 0 0 1 .175-.688.943.943 0 0 1 .6-.387l2.75-.6 1.45-2.45a.983.983 0 0 1 .55-.438.97.97 0 0 1 .7.038l2.6 1.1 2.6-1.1a.97.97 0 0 1 .7-.038.983.983 0 0 1 .55.438L17.3 4.7l2.75.6c.25.05.45.18.6.388.15.208.208.437.175.687L20.55 9.2l1.875 2.15c.167.183.25.4.25.65s-.083.467-.25.65L20.55 14.8l.275 2.825a.928.928 0 0 1-.175.688.943.943 0 0 1-.6.387l-2.75.6-1.45 2.45a.984.984 0 0 1-.55.438.97.97 0 0 1-.7-.038l-2.6-1.1-2.6 1.1a.97.97 0 0 1-.7.038.983.983 0 0 1-.55-.438Zm1.3-1.8 2.55-1.1 2.6 1.1 1.4-2.4 2.75-.65-.25-2.8 1.85-2.1-1.85-2.15.25-2.8-2.75-.6-1.45-2.4L12 5.15l-2.6-1.1L8 6.45l-2.75.6.25 2.8L3.65 12l1.85 2.1-.25 2.85 2.75.6 1.45 2.4Z"/><path fill="inherit" stroke="none" d="M12.713 16.712A.968.968 0 0 1 12 17a.968.968 0 0 1-.713-.288A.968.968 0 0 1 11 16c0-.283.096-.52.287-.713A.968.968 0 0 1 12 15c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.712Zm0-3.999A.968.968 0 0 1 12 13a.968.968 0 0 1-.713-.287A.968.968 0 0 1 11 12V8c0-.283.096-.52.287-.713A.968.968 0 0 1 12 7c.283 0 .52.096.713.287.191.192.287.43.287.713v4c0 .283-.096.52-.287.713Z"/></svg>', pt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M8.15 21.75 6.7 19.3l-2.75-.6a.943.943 0 0 1-.6-.387.928.928 0 0 1-.175-.688L3.45 14.8l-1.875-2.15a.934.934 0 0 1-.25-.65c0-.25.083-.467.25-.65L3.45 9.2l-.275-2.825a.928.928 0 0 1 .175-.688.943.943 0 0 1 .6-.387l2.75-.6 1.45-2.45a.983.983 0 0 1 .55-.438.97.97 0 0 1 .7.038l2.6 1.1 2.6-1.1a.97.97 0 0 1 .7-.038.983.983 0 0 1 .55.438L17.3 4.7l2.75.6c.25.05.45.18.6.388.15.208.208.437.175.687L20.55 9.2l1.875 2.15c.167.183.25.4.25.65s-.083.467-.25.65L20.55 14.8l.275 2.825a.928.928 0 0 1-.175.688.943.943 0 0 1-.6.387l-2.75.6-1.45 2.45a.984.984 0 0 1-.55.438.97.97 0 0 1-.7-.038l-2.6-1.1-2.6 1.1a.97.97 0 0 1-.7.038.983.983 0 0 1-.55-.438ZM12 13c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 12V8a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 7a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 8v4c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 4a.97.97 0 0 0 .713-.288A.968.968 0 0 0 13 16a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 15a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 16c0 .283.096.52.287.712.192.192.43.288.713.288Z" clip-rule="evenodd"/></svg>', ut = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22a.968.968 0 0 1-.713-.288A.968.968 0 0 1 11 21v-4c0-.933-.142-1.625-.425-2.075A6.704 6.704 0 0 0 9.45 13.6l1.425-1.425c.2.183.392.38.575.587.183.209.367.43.55.663a7.18 7.18 0 0 1 .713-.838c.241-.241.487-.479.737-.712a11.385 11.385 0 0 0 1.725-2.025c.517-.767.792-2.108.825-4.025l-.875.875a.933.933 0 0 1-.688.275 1.02 1.02 0 0 1-.712-.275c-.2-.2-.3-.437-.3-.712 0-.275.1-.513.3-.713L16.3 2.7c.1-.1.208-.17.325-.212.117-.042.242-.063.375-.063s.258.02.375.063a.877.877 0 0 1 .325.212l2.6 2.6c.183.183.28.413.287.688a.93.93 0 0 1-.287.712.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275l-.9-.875c-.033 2.383-.4 4.08-1.1 5.087-.7 1.009-1.4 1.83-2.1 2.463a9.433 9.433 0 0 0-1.3 1.412c-.333.459-.5 1.196-.5 2.213v4a.97.97 0 0 1-.287.712A.967.967 0 0 1 12 22ZM6.2 8.175a8.251 8.251 0 0 1-.137-1.1c-.025-.4-.046-.817-.063-1.25l-.9.9A.933.933 0 0 1 4.413 7a.973.973 0 0 1-.713-.3.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l2.6-2.6c.1-.1.208-.17.325-.212.117-.042.242-.063.375-.063s.258.02.375.063A.877.877 0 0 1 7.7 2.7l2.6 2.6c.2.2.296.433.287.7a1.006 1.006 0 0 1-1.012.975c-.267 0-.5-.092-.7-.275L8 5.85c0 .35.017.68.05.988.033.308.067.595.1.862l-1.95.475Zm2.15 4.4a8.822 8.822 0 0 1-.962-1.225c-.309-.467-.58-1.042-.813-1.725L8.5 9.15c.167.45.358.833.575 1.15.217.317.45.6.7.85L8.35 12.575Z"/></svg>', gt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M15.463 8.537C16.438 9.512 17.617 10 19 10s2.563-.488 3.538-1.463C23.512 7.563 24 6.383 24 5s-.488-2.563-1.462-3.538C21.562.488 20.383 0 19 0s-2.563.487-3.537 1.462C14.488 2.438 14 3.617 14 5s.488 2.563 1.463 3.537ZM19.35 5.85a.48.48 0 0 1-.7 0 .48.48 0 0 1-.15-.35v-3c0-.133.05-.25.15-.35a.48.48 0 0 1 .7 0c.1.1.15.217.15.35v3a.48.48 0 0 1-.15.35Zm0 2a.48.48 0 0 1-.7 0 .48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7ZM12 5c0-.34.024-.673.07-.999L12 4c-.55 0-1.02.196-1.412.588A1.923 1.923 0 0 0 10 6c0 .55.196 1.02.588 1.412.391.392.862.588 1.412.588.223 0 .432-.032.629-.096A6.974 6.974 0 0 1 12 5Zm7 7a6.974 6.974 0 0 1-2.904-.629A2.017 2.017 0 0 0 16 12c0 .55.196 1.02.587 1.412.392.392.863.588 1.413.588s1.021-.196 1.413-.588a1.925 1.925 0 0 0 .586-1.483c-.326.047-.66.071-.999.071ZM6 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 16c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.021-.588 1.413A1.925 1.925 0 0 1 6 20Zm6 0c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 10 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 12 16c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.021-.587 1.413A1.928 1.928 0 0 1 12 20Zm6 0a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 16 18c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 18 16c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.021-.587 1.413A1.928 1.928 0 0 1 18 20ZM6 14c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 4 12c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 10c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.02-.588 1.412A1.923 1.923 0 0 1 6 14Zm6 0c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 10 12c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 12 10c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 12 14ZM6 8c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 4 6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 6 4c.55 0 1.02.196 1.412.588C7.804 4.979 8 5.45 8 6c0 .55-.196 1.02-.588 1.412A1.923 1.923 0 0 1 6 8Z" clip-rule="evenodd"/></svg>', vt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 16c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.021-.588 1.413A1.925 1.925 0 0 1 6 20Zm6 0c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 10 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 12 16c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.021-.587 1.413A1.928 1.928 0 0 1 12 20Zm6 0a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 16 18c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 18 16c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.021-.587 1.413A1.928 1.928 0 0 1 18 20ZM6 14c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 4 12c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 10c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.02-.588 1.412A1.923 1.923 0 0 1 6 14Zm6 0c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 10 12c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 12 10c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 12 14Zm6 0c-.55 0-1.021-.196-1.413-.588A1.925 1.925 0 0 1 16 12c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 18 10c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 18 14ZM6 8c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 4 6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 6 4c.55 0 1.02.196 1.412.588C7.804 4.979 8 5.45 8 6c0 .55-.196 1.02-.588 1.412A1.923 1.923 0 0 1 6 8Zm6 0c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 10 6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 12 4c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.927 1.927 0 0 1 12 8Zm6 0c-.55 0-1.021-.196-1.413-.588A1.925 1.925 0 0 1 16 6c0-.55.196-1.02.587-1.412A1.927 1.927 0 0 1 18 4c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.927 1.927 0 0 1 18 8Z"/></svg>', mt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V6.5c0-.25.042-.475.125-.675.083-.2.192-.392.325-.575l1.4-1.7c.133-.183.3-.32.5-.412C5.55 3.046 5.767 3 6 3h12c.233 0 .45.046.65.138.2.091.367.229.5.412l1.4 1.7c.133.183.242.375.325.575.083.2.125.425.125.675V19c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Zm.4-15h13.2l-.85-1H6.25L5.4 6ZM5 19h14V8H5v11Zm7-1.425c.133 0 .258-.02.375-.063a.877.877 0 0 0 .325-.212l2.6-2.6a.948.948 0 0 0 .275-.7.948.948 0 0 0-.275-.7.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275l-.9.9V11a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 10a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 11v3.2l-.9-.9a.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275.948.948 0 0 0-.275.7.95.95 0 0 0 .275.7l2.6 2.6c.1.1.208.17.325.212.117.042.242.063.375.063Z"/></svg>', ft = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V6.5c0-.25.042-.475.125-.675.083-.2.192-.392.325-.575l1.4-1.7c.133-.183.3-.32.5-.412C5.55 3.046 5.767 3 6 3h12c.233 0 .45.046.65.138.2.091.367.229.5.412l1.4 1.7c.133.183.242.375.325.575.083.2.125.425.125.675V19c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Zm.4-15h13.2l-.85-1H6.25L5.4 6ZM12 17.575c.133 0 .258-.02.375-.063a.877.877 0 0 0 .325-.212l2.6-2.6a.948.948 0 0 0 .275-.7.948.948 0 0 0-.275-.7.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275l-.9.9V11a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 10a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 11v3.2l-.9-.9a.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275.948.948 0 0 0-.275.7.95.95 0 0 0 .275.7l2.6 2.6c.1.1.208.17.325.212.117.042.242.063.375.063Z"/></svg>', bt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 19.575a1.1 1.1 0 0 1-.375-.063.872.872 0 0 1-.325-.212l-6.6-6.6c-.2-.2-.3-.438-.3-.713 0-.275.1-.512.3-.712.2-.2.433-.3.7-.3.267 0 .5.1.7.3l4.9 4.9v-11.2c0-.283.096-.517.288-.7A.99.99 0 0 1 12 4a.97.97 0 0 1 .713.287A.97.97 0 0 1 13 5v11.175l4.9-4.9c.2-.2.433-.3.7-.3.267 0 .5.1.7.3.2.2.3.437.3.712 0 .275-.1.513-.3.713l-6.6 6.6c-.1.1-.208.17-.325.212a1.098 1.098 0 0 1-.375.063Z"/></svg>', wt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.875 19.3-6.6-6.6a.883.883 0 0 1-.213-.325A1.115 1.115 0 0 1 4 12c0-.133.02-.258.062-.375a.883.883 0 0 1 .213-.325l6.6-6.6a.978.978 0 0 1 .687-.288.933.933 0 0 1 .713.288c.2.183.304.412.313.687a.933.933 0 0 1-.288.713L7.4 11h11.175a.97.97 0 0 1 .713.287.97.97 0 0 1 .287.713.97.97 0 0 1-.287.712.968.968 0 0 1-.713.288H7.4l4.9 4.9c.183.183.28.417.288.7a.872.872 0 0 1-.288.7c-.183.2-.417.3-.7.3a.988.988 0 0 1-.725-.3Z"/></svg>', xt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.3 19.3a.994.994 0 0 1-.288-.7.91.91 0 0 1 .263-.7l4.9-4.9H5a.968.968 0 0 1-.713-.288A.967.967 0 0 1 4 12a.97.97 0 0 1 .287-.713A.97.97 0 0 1 5 11h11.175l-4.9-4.9a.91.91 0 0 1-.263-.7.994.994 0 0 1 .288-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l6.6 6.6c.1.083.171.187.213.312.041.125.062.255.062.388s-.02.258-.062.375a.883.883 0 0 1-.213.325l-6.6 6.6a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', At = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 20a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 19V7.825L6.125 12.7c-.2.2-.438.3-.713.3a.973.973 0 0 1-.712-.3.96.96 0 0 1-.3-.7c0-.267.1-.5.3-.7l6.6-6.6a.88.88 0 0 1 .325-.213 1.212 1.212 0 0 1 .763 0 .681.681 0 0 1 .312.213l6.6 6.6c.2.2.3.433.3.7 0 .267-.1.5-.3.7-.2.2-.438.3-.713.3a.973.973 0 0 1-.712-.3L13 7.825V19a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 20Z"/></svg>', yt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5.85 17.1c.85-.65 1.8-1.163 2.85-1.538A9.737 9.737 0 0 1 12 15c1.15 0 2.25.188 3.3.563 1.05.374 2 .887 2.85 1.537a7.73 7.73 0 0 0 1.363-2.325A7.838 7.838 0 0 0 20 12c0-2.217-.78-4.104-2.337-5.662C16.104 4.779 14.217 4 12 4s-4.104.78-5.662 2.338C4.779 7.896 4 9.783 4 12c0 .983.162 1.908.487 2.775A7.73 7.73 0 0 0 5.85 17.1ZM12 13c-.983 0-1.813-.338-2.488-1.012C8.837 11.313 8.5 10.483 8.5 9.5c0-.983.338-1.813 1.012-2.487C10.188 6.338 11.017 6 12 6c.983 0 1.813.338 2.488 1.013.675.675 1.012 1.504 1.012 2.487 0 .983-.338 1.813-1.012 2.488C13.813 12.663 12.983 13 12 13Zm0 9a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm0-2a7.97 7.97 0 0 0 2.5-.387 7.53 7.53 0 0 0 2.15-1.113 7.53 7.53 0 0 0-2.15-1.113A7.933 7.933 0 0 0 12 17a7.97 7.97 0 0 0-2.5.387A7.53 7.53 0 0 0 7.35 18.5a7.53 7.53 0 0 0 2.15 1.113A7.933 7.933 0 0 0 12 20Zm0-9c.433 0 .792-.142 1.075-.425.283-.283.425-.642.425-1.075 0-.433-.142-.792-.425-1.075C12.792 8.142 12.433 8 12 8c-.433 0-.792.142-1.075.425-.283.283-.425.642-.425 1.075 0 .433.142.792.425 1.075.283.283.642.425 1.075.425Z"/></svg>', kt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5.85 17.1c.85-.65 1.8-1.163 2.85-1.538A9.737 9.737 0 0 1 12 15c1.15 0 2.25.188 3.3.563 1.05.374 2 .887 2.85 1.537a7.73 7.73 0 0 0 1.363-2.325A7.838 7.838 0 0 0 20 12c0-2.217-.78-4.104-2.337-5.662C16.104 4.779 14.217 4 12 4s-4.104.78-5.662 2.338C4.779 7.896 4 9.783 4 12c0 .983.162 1.908.487 2.775A7.73 7.73 0 0 0 5.85 17.1ZM12 13c-.983 0-1.813-.338-2.488-1.012C8.837 11.313 8.5 10.483 8.5 9.5c0-.983.338-1.813 1.012-2.487C10.188 6.338 11.017 6 12 6c.983 0 1.813.338 2.488 1.013.675.675 1.012 1.504 1.012 2.487 0 .983-.338 1.813-1.012 2.488C13.813 12.663 12.983 13 12 13Zm0 9a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Z"/></svg>', _t = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17.25 13.5a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75h-2.5a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75h2.5Zm0 3a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75h-2.5a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75h2.5ZM15 7h5c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 22H4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 20V9c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 7h5V4c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 11 2h2c.55 0 1.02.196 1.412.587C14.804 2.98 15 3.45 15 4v3Zm-4 2h2V4h-2v5Zm-2 6c.417 0 .77-.146 1.063-.438.291-.291.437-.645.437-1.062 0-.417-.146-.77-.438-1.063A1.446 1.446 0 0 0 9 12c-.417 0-.77.146-1.063.438A1.447 1.447 0 0 0 7.5 13.5c0 .417.146.77.438 1.063.291.291.645.437 1.062.437Zm-3 3h6v-.45c0-.283-.08-.546-.238-.788a1.57 1.57 0 0 0-.662-.562 5.158 5.158 0 0 0-1.012-.337A5.063 5.063 0 0 0 9 15.75a5.009 5.009 0 0 0-2.1.45 1.57 1.57 0 0 0-.663.563A1.41 1.41 0 0 0 6 17.55V18Zm3-9H4v11h16V9h-5c0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 13 11h-2c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 9 9Z"/></svg>', Zt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17.25 13.5a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75h-2.5a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75h2.5Zm0 3a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75h-2.5a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75h2.5ZM15 7h5c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 22H4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 20V9c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 7h5V4c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 11 2h2c.55 0 1.02.196 1.412.587C14.804 2.98 15 3.45 15 4v3Zm-4 2h2V4h-2v5Zm-2 6c.417 0 .77-.146 1.063-.438.291-.291.437-.645.437-1.062 0-.417-.146-.77-.438-1.063A1.446 1.446 0 0 0 9 12c-.417 0-.77.146-1.063.438A1.447 1.447 0 0 0 7.5 13.5c0 .417.146.77.438 1.063.291.291.645.437 1.062.437Zm-3 3h6v-.45c0-.283-.08-.546-.238-.788a1.57 1.57 0 0 0-.662-.562 5.158 5.158 0 0 0-1.012-.337A5.063 5.063 0 0 0 9 15.75a5.009 5.009 0 0 0-2.1.45 1.57 1.57 0 0 0-.663.563A1.41 1.41 0 0 0 6 17.55V18Z"/></svg>', Ct = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 17a.967.967 0 0 1-.713-.288A.968.968 0 0 1 5 16v-5.025c0-.283.096-.517.287-.7A.991.991 0 0 1 6 10c.283 0 .52.096.713.287.191.192.287.43.287.713v5.025a.927.927 0 0 1-.287.7A.991.991 0 0 1 6 17Zm6 0a.968.968 0 0 1-.713-.288A.968.968 0 0 1 11 16v-5.025c0-.283.096-.517.287-.7A.991.991 0 0 1 12 10c.283 0 .52.096.713.287.191.192.287.43.287.713v5.025a.927.927 0 0 1-.287.7A.991.991 0 0 1 12 17Zm-9.025 4a.927.927 0 0 1-.7-.288A.99.99 0 0 1 2 20c0-.283.096-.52.288-.712A.967.967 0 0 1 3 19h18.025c.283 0 .517.096.7.288.183.191.275.429.275.712s-.096.52-.288.712A.968.968 0 0 1 21 21H2.975ZM18 17a.968.968 0 0 1-.712-.288A.968.968 0 0 1 17 16v-5.025c0-.283.096-.517.288-.7A.991.991 0 0 1 18 10a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v5.025a.927.927 0 0 1-.288.7A.991.991 0 0 1 18 17ZM12.9 1.45l8.425 4.2c.217.117.383.28.5.487.117.209.175.438.175.688 0 .333-.12.612-.363.837a1.217 1.217 0 0 1-.862.338H3.25c-.333 0-.625-.112-.875-.338A1.081 1.081 0 0 1 2 6.825c0-.25.054-.48.163-.688a.998.998 0 0 1 .512-.462L11.1 1.45c.283-.133.583-.2.9-.2.317 0 .617.067.9.2ZM6.45 6h11.1L12 3.25 6.45 6Z"/></svg>', $t = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 17a.967.967 0 0 1-.713-.288A.968.968 0 0 1 5 16v-5.025c0-.283.096-.517.287-.7A.991.991 0 0 1 6 10c.283 0 .52.096.713.287.191.192.287.43.287.713v5.025a.927.927 0 0 1-.287.7A.991.991 0 0 1 6 17Zm6 0a.968.968 0 0 1-.713-.288A.968.968 0 0 1 11 16v-5.025c0-.283.096-.517.287-.7A.991.991 0 0 1 12 10c.283 0 .52.096.713.287.191.192.287.43.287.713v5.025a.927.927 0 0 1-.287.7A.991.991 0 0 1 12 17Zm-9.025 4a.927.927 0 0 1-.7-.288A.99.99 0 0 1 2 20c0-.283.096-.52.288-.712A.967.967 0 0 1 3 19h18.025c.283 0 .517.096.7.288.183.191.275.429.275.712s-.096.52-.288.712A.968.968 0 0 1 21 21H2.975ZM18 17a.968.968 0 0 1-.712-.288A.968.968 0 0 1 17 16v-5.025c0-.283.096-.517.288-.7A.991.991 0 0 1 18 10a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v5.025a.927.927 0 0 1-.288.7A.991.991 0 0 1 18 17ZM12.9 1.45l8.425 4.2c.217.117.383.28.5.487.117.209.175.438.175.688 0 .333-.12.612-.363.837a1.217 1.217 0 0 1-.862.338H3.25c-.333 0-.625-.112-.875-.338A1.081 1.081 0 0 1 2 6.825c0-.233.054-.458.163-.675a.986.986 0 0 1 .512-.475L11.1 1.45c.283-.133.583-.2.9-.2.317 0 .617.067.9.2Z"/></svg>', Mt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 14.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Zm0-4a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15ZM6 19a.967.967 0 0 1-.713-.288A.968.968 0 0 1 5 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 6 17a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.967.967 0 0 1 6 19Zm0-4a.967.967 0 0 1-.713-.287A.968.968 0 0 1 5 14c0-.283.096-.52.287-.713A.967.967 0 0 1 6 13c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.967.967 0 0 1 6 15Zm0-4a.967.967 0 0 1-.713-.287A.968.968 0 0 1 5 10c0-.283.096-.52.287-.713A.968.968 0 0 1 6 9c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.967.967 0 0 1 6 11Zm0-4a.968.968 0 0 1-.713-.287A.968.968 0 0 1 5 6c0-.283.096-.52.287-.713A.968.968 0 0 1 6 5c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 6 7Zm4 8.5c-.417 0-.77-.146-1.063-.438A1.447 1.447 0 0 1 8.5 14c0-.417.146-.77.438-1.063A1.446 1.446 0 0 1 10 12.5c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062 0 .417-.146.77-.438 1.063A1.446 1.446 0 0 1 10 15.5Zm0-4c-.417 0-.77-.146-1.063-.438A1.447 1.447 0 0 1 8.5 10c0-.417.146-.77.438-1.063A1.447 1.447 0 0 1 10 8.5c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062 0 .417-.146.77-.438 1.063A1.446 1.446 0 0 1 10 11.5Zm0 7.5a.967.967 0 0 1-.713-.288A.968.968 0 0 1 9 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 10 17a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 10 19Zm0-12a.968.968 0 0 1-.713-.287A.968.968 0 0 1 9 6c0-.283.096-.52.287-.713A.968.968 0 0 1 10 5c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 10 7Zm0 14.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Zm0-18a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Zm4 12c-.417 0-.77-.146-1.063-.438A1.446 1.446 0 0 1 12.5 14c0-.417.146-.77.438-1.063A1.446 1.446 0 0 1 14 12.5c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062 0 .417-.146.77-.438 1.063A1.446 1.446 0 0 1 14 15.5Zm0-4c-.417 0-.77-.146-1.063-.438A1.446 1.446 0 0 1 12.5 10c0-.417.146-.77.438-1.063A1.447 1.447 0 0 1 14 8.5c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062 0 .417-.146.77-.438 1.063A1.446 1.446 0 0 1 14 11.5Zm0 7.5a.968.968 0 0 1-.713-.288A.968.968 0 0 1 13 18a.97.97 0 0 1 .287-.712A.968.968 0 0 1 14 17a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 14 19Zm0-12a.968.968 0 0 1-.713-.287A.967.967 0 0 1 13 6c0-.283.096-.52.287-.713A.968.968 0 0 1 14 5c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 14 7Zm0 14.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Zm0-18a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15ZM18 19a.968.968 0 0 1-.712-.288A.968.968 0 0 1 17 18c0-.283.096-.52.288-.712A.968.968 0 0 1 18 17c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 18 19Zm0-4a.968.968 0 0 1-.712-.287A.968.968 0 0 1 17 14a.97.97 0 0 1 .288-.713A.968.968 0 0 1 18 13a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.713A.968.968 0 0 1 18 15Zm0-4a.968.968 0 0 1-.712-.287A.968.968 0 0 1 17 10a.97.97 0 0 1 .288-.713A.968.968 0 0 1 18 9a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.713A.968.968 0 0 1 18 11Zm0-4a.968.968 0 0 1-.712-.287A.967.967 0 0 1 17 6a.97.97 0 0 1 .288-.713A.968.968 0 0 1 18 5a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.713A.968.968 0 0 1 18 7Zm3 7.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Zm0-4a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Z"/></svg>', Vt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.55 18.2 5.175-6.2h-4l.725-5.675L7.825 13H11.3l-.75 5.2ZM9 15H5.9c-.4 0-.696-.18-.888-.537-.191-.359-.17-.705.063-1.038l7.475-10.75c.167-.233.383-.396.65-.488.267-.091.542-.087.825.013.283.1.492.275.625.525s.183.517.15.8L14 10h3.875c.433 0 .738.192.913.575.174.383.12.742-.163 1.075L10.4 21.5a1.27 1.27 0 0 1-.675.425 1.133 1.133 0 0 1-.775-.075 1.31 1.31 0 0 1-.588-.538 1.232 1.232 0 0 1-.162-.787L9 15Z"/></svg>', Ht = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 15H5.9c-.4 0-.696-.18-.888-.537-.191-.359-.17-.705.063-1.038l7.475-10.75c.167-.233.383-.396.65-.488.267-.091.542-.087.825.013.283.1.492.275.625.525s.183.517.15.8L14 10h3.875c.433 0 .738.192.913.575.174.383.12.742-.163 1.075L10.4 21.5a1.27 1.27 0 0 1-.675.425 1.133 1.133 0 0 1-.775-.075 1.31 1.31 0 0 1-.588-.538 1.232 1.232 0 0 1-.162-.787L9 15Z"/></svg>', St = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m7 17.95 5-2.15 5 2.15V5H7v12.95Zm-.6 2.45a.99.99 0 0 1-.95-.087c-.3-.192-.45-.471-.45-.838V5c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 3h10c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14.475c0 .367-.15.646-.45.837a.99.99 0 0 1-.95.088L12 18l-5.6 2.4Z"/></svg>', Dt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6.4 20.4a.99.99 0 0 1-.95-.087c-.3-.192-.45-.471-.45-.838V5c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 3h10c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14.475c0 .367-.15.646-.45.837a.99.99 0 0 1-.95.088L12 18l-5.6 2.4Z"/></svg>', Lt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V9c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 5 7h2V5c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 9 3h6c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v6h2c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v6c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21h-6v-4h-2v4H5Zm0-2h2v-2H5v2Zm0-4h2v-2H5v2Zm0-4h2V9H5v2Zm4 4h2v-2H9v2Zm0-4h2V9H9v2Zm0-4h2V5H9v2Zm4 8h2v-2h-2v2Zm0-4h2V9h-2v2Zm0-4h2V5h-2v2Zm4 12h2v-2h-2v2Zm0-4h2v-2h-2v2Z"/></svg>', Ot = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 9.2h3.5a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75H7a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75Zm6.75 8.05h3.5a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75h-3.5a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75Zm0-2.5h3.5A.728.728 0 0 0 18 14a.728.728 0 0 0-.75-.75h-3.5A.728.728 0 0 0 13 14a.728.728 0 0 0 .75.75Zm-5 3.25a.728.728 0 0 0 .75-.75V16h1.25a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75H9.5v-1.25a.728.728 0 0 0-.75-.75.728.728 0 0 0-.75.75v1.25H6.75a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75H8v1.25a.728.728 0 0 0 .75.75Zm4.825-7.575a.72.72 0 0 0 .525.225.72.72 0 0 0 .525-.225l.875-.875.9.9c.133.133.3.2.5.2a.72.72 0 0 0 .525-.225.694.694 0 0 0 .2-.513.782.782 0 0 0-.2-.537l-.875-.925.9-.9a.68.68 0 0 0 .2-.5.72.72 0 0 0-.225-.525.72.72 0 0 0-.525-.225.72.72 0 0 0-.525.225L15.5 7.4l-.9-.9a.678.678 0 0 0-.5-.2.72.72 0 0 0-.525.225.72.72 0 0 0-.225.525c0 .2.075.375.225.525l.875.875-.9.925a.766.766 0 0 0-.2.525c0 .2.075.375.225.525ZM5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h14c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Zm0-2h14V5H5v14Z"/></svg>', Et = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 9.2h3.5a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75H7a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75Zm6.75 8.05h3.5a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75h-3.5a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75Zm0-2.5h3.5A.728.728 0 0 0 18 14a.728.728 0 0 0-.75-.75h-3.5A.728.728 0 0 0 13 14a.728.728 0 0 0 .75.75Zm-5 3.25a.728.728 0 0 0 .75-.75V16h1.25a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75H9.5v-1.25a.728.728 0 0 0-.75-.75.728.728 0 0 0-.75.75v1.25H6.75a.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75H8v1.25a.728.728 0 0 0 .75.75Zm4.825-7.575a.72.72 0 0 0 .525.225.72.72 0 0 0 .525-.225l.875-.875.9.9c.133.133.3.2.5.2a.72.72 0 0 0 .525-.225.694.694 0 0 0 .2-.513.782.782 0 0 0-.2-.537l-.875-.925.9-.9a.68.68 0 0 0 .2-.5.72.72 0 0 0-.225-.525.72.72 0 0 0-.525-.225.72.72 0 0 0-.525.225L15.5 7.4l-.9-.9a.678.678 0 0 0-.5-.2.72.72 0 0 0-.525.225.72.72 0 0 0-.225.525c0 .2.075.375.225.525l.875.875-.9.925a.766.766 0 0 0-.2.525c0 .2.075.375.225.525ZM5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h14c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Z"/></svg>', Tt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14.5 18c-.7 0-1.292-.242-1.775-.725C12.242 16.792 12 16.2 12 15.5s.242-1.292.725-1.775C13.208 13.242 13.8 13 14.5 13s1.292.242 1.775.725c.483.483.725 1.075.725 1.775s-.242 1.292-.725 1.775c-.483.483-1.075.725-1.775.725ZM5 22a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 3 20V6c0-.55.196-1.02.587-1.412A1.927 1.927 0 0 1 5 4h1V3a.97.97 0 0 1 .287-.713A.97.97 0 0 1 7 2a.97.97 0 0 1 .713.287A.97.97 0 0 1 8 3v1h8V3c0-.283.096-.521.288-.713A.967.967 0 0 1 17 2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v1h1c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412v14a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 19 22H5Zm0-2h14V10H5v10Z"/></svg>', zt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7.7 16.3a.948.948 0 0 0 .7.275.948.948 0 0 0 .7-.275l2.9-2.9 2.925 2.925c.183.183.413.27.688.262a.975.975 0 0 0 .687-.287.948.948 0 0 0 .275-.7.948.948 0 0 0-.275-.7L13.4 12l2.925-2.925a.891.891 0 0 0 .262-.688.975.975 0 0 0-.287-.687.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275L12 10.6 9.075 7.675a.893.893 0 0 0-.687-.263.98.98 0 0 0-.688.288.948.948 0 0 0-.275.7.95.95 0 0 0 .275.7l2.9 2.9-2.925 2.925a.89.89 0 0 0-.262.687.976.976 0 0 0 .287.688ZM12 22a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Zm0-2c2.217 0 4.104-.779 5.663-2.337C19.221 16.104 20 14.217 20 12s-.779-4.104-2.337-5.663C16.104 4.779 14.217 4 12 4s-4.104.779-5.662 2.337C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.221 9.783 20 12 20Z"/></svg>', It = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7.7 16.3a.948.948 0 0 0 .7.275.948.948 0 0 0 .7-.275l2.9-2.9 2.925 2.925c.183.183.413.27.688.262a.975.975 0 0 0 .687-.287.948.948 0 0 0 .275-.7.948.948 0 0 0-.275-.7L13.4 12l2.925-2.925a.891.891 0 0 0 .262-.688.975.975 0 0 0-.287-.687.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275L12 10.6 9.075 7.675a.893.893 0 0 0-.687-.263.98.98 0 0 0-.688.288.948.948 0 0 0-.275.7.95.95 0 0 0 .275.7l2.9 2.9-2.925 2.925a.89.89 0 0 0-.262.687.976.976 0 0 0 .287.688ZM12 22a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Z"/></svg>', Pt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m11.3 14.3-2.6-2.6c-.317-.317-.388-.68-.213-1.087.175-.409.488-.613.938-.613h5.15c.45 0 .763.204.938.613.175.408.104.77-.213 1.087l-2.6 2.6c-.1.1-.208.175-.325.225A.942.942 0 0 1 12 14.6a.941.941 0 0 1-.375-.075 1.038 1.038 0 0 1-.325-.225Z"/></svg>', Bt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m12.3 15.3-2.6-2.6c-.1-.1-.175-.208-.225-.325A.942.942 0 0 1 9.4 12c0-.133.025-.258.075-.375.05-.117.125-.225.225-.325l2.6-2.6c.317-.317.68-.388 1.087-.213.409.175.613.488.613.938v5.15c0 .45-.204.763-.613.938-.408.175-.77.104-1.087-.213Z"/></svg>', Ft = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.7 15.3c-.317.317-.68.387-1.087.212-.409-.175-.613-.487-.613-.937v-5.15c0-.45.204-.763.613-.938.408-.175.77-.104 1.087.213l2.6 2.6c.1.1.175.208.225.325.05.117.075.242.075.375a.942.942 0 0 1-.075.375c-.05.117-.125.225-.225.325l-2.6 2.6Z"/></svg>', jt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9.425 14c-.45 0-.763-.204-.938-.613-.175-.408-.104-.77.213-1.087l2.6-2.6c.1-.1.208-.175.325-.225A.941.941 0 0 1 12 9.4c.133 0 .258.025.375.075.117.05.225.125.325.225l2.6 2.6c.317.317.387.68.212 1.087-.175.409-.487.613-.937.613h-5.15Z"/></svg>', Rt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20a.968.968 0 0 1-.712-.288A.968.968 0 0 1 3 19V7.25c.083 0 .18.012.288.037.108.025.212.08.312.163L7 10l4.375-6.15a.972.972 0 0 1 .675-.413.978.978 0 0 1 .775.213L17 7h3a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v11c0 .283-.096.52-.288.712A.968.968 0 0 1 20 20H4Zm4-3 3.4-4.675a.964.964 0 0 1 .662-.412.958.958 0 0 1 .763.212L19 16.95V9h-2.7l-3.9-3.125-4.95 6.95L5 11v3.6L8 17Z"/></svg>', Nt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m21 16-7.775-6.075a1.951 1.951 0 0 0-1.512-.413 1.894 1.894 0 0 0-1.338.813l-2.75 3.8L4.25 11.45a1.809 1.809 0 0 0-.625-.35A2.122 2.122 0 0 0 3 11V7.25c.083 0 .18.012.288.037.108.025.212.08.312.163L7 10l4.375-6.15a.97.97 0 0 1 .675-.412.977.977 0 0 1 .775.212L17 7h3a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v8ZM4 20a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 19v-5.725c.117 0 .225.017.325.05s.2.092.3.175L8 17l3.4-4.675a.966.966 0 0 1 .662-.413c.275-.041.53.03.763.213l8.175 6.4V19c0 .283-.096.52-.288.712A.965.965 0 0 1 20 20H4Z"/></svg>', Ut = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 18v-7c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 9c.55 0 1.02.196 1.412.587C7.804 9.979 8 10.45 8 11v7c0 .55-.196 1.021-.588 1.413A1.925 1.925 0 0 1 6 20Zm6 0c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 10 18V6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 12 4c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412v12a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 12 20Zm6 0a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 16 18v-3c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 18 13c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v3a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 20Z"/></svg>', Wt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20v-3h4v3H4Zm0-4v-4h4v4H4Zm0-5V4h4v7H4Zm6 9v-7h4v7h-4Zm0-8V8h4v4h-4Zm0-5V4h4v3h-4Zm6 13v-2h4v2h-4Zm0-3v-4h4v4h-4Zm0-5V4h4v8h-4Z"/></svg>', qt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2.725 17.75A1.03 1.03 0 0 1 2.4 17c0-.283.108-.533.325-.75l6.05-6.05c.2-.2.433-.3.7-.3.267 0 .5.1.7.3l3.3 3.3 6.4-7.225a.89.89 0 0 1 .713-.325c.291 0 .537.1.737.3a.95.95 0 0 1 .288.662.943.943 0 0 1-.263.688l-7.175 8.1a.928.928 0 0 1-.712.338.963.963 0 0 1-.738-.288l-3.25-3.25-5.25 5.25a1.03 1.03 0 0 1-.75.325 1.03 1.03 0 0 1-.75-.325Z"/></svg>', Kt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 18V9h4v9c0 .55-.196 1.021-.588 1.413A1.925 1.925 0 0 1 6 20ZM4 8V6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 6 4c.55 0 1.02.196 1.412.588C7.804 4.979 8 5.45 8 6v2H4Zm8 12c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 10 18v-6h4v6a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 12 20Zm-2-9V9c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 12 7c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v2h-4Zm8 9a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 16 18v-3h4v3a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 20Zm-2-6v-2c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 18 10c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v2h-4Z"/></svg>', Yt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12.025 11.675a1.52 1.52 0 0 1-.337-.088.812.812 0 0 1-.288-.187L7.625 7.625 4.575 9.85c-.333.233-.679.258-1.037.075-.359-.183-.538-.483-.538-.9 0-.167.033-.317.1-.45a.992.992 0 0 1 .3-.35L7.125 5.5a.744.744 0 0 1 .325-.162.982.982 0 0 1 .35-.013c.117.017.23.046.338.087a.825.825 0 0 1 .287.188L12.2 9.375l7.225-5.225c.333-.233.679-.258 1.037-.075.359.183.538.483.538.9a.905.905 0 0 1-.112.45c-.075.133-.18.25-.313.35L12.7 11.5a.745.745 0 0 1-.325.162.984.984 0 0 1-.35.013Zm0 9a.882.882 0 0 1-.35-.075.946.946 0 0 1-.3-.2L5.8 15l-1.225.875c-.333.233-.679.258-1.037.075-.359-.183-.538-.483-.538-.9a.98.98 0 0 1 .112-.463.875.875 0 0 1 .313-.337l1.9-1.375a.757.757 0 0 1 .313-.163.917.917 0 0 1 .337-.012c.117.017.229.046.337.087a.822.822 0 0 1 .288.188l5.525 5.375 3.9-3.125a.687.687 0 0 1 .287-.175c.109-.033.221-.05.338-.05H20a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.965.965 0 0 1 20 17h-3l-4.325 3.45a.907.907 0 0 1-.312.175c-.109.033-.221.05-.338.05Z"/></svg>', Gt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5.175 16H20V7.9a6.002 6.002 0 0 0 1.075-.338c.333-.141.642-.329.925-.562v9c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h10.1a5.085 5.085 0 0 0 0 2H4v13.175L5.175 16ZM19 6a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 16 3c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 19 0c.833 0 1.542.292 2.125.875S22 2.167 22 3s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 19 6Z"/></svg>', Qt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 6a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 16 3c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 19 0c.833 0 1.542.292 2.125.875S22 2.167 22 3s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 19 6ZM2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h10.1a5.085 5.085 0 0 0 0 2c.233 1.15.808 2.104 1.725 2.862A4.836 4.836 0 0 0 19 8a5.1 5.1 0 0 0 1.575-.25A4.674 4.674 0 0 0 22 7v9c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Z"/></svg>', Xt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Zm2-4.825L5.175 16H20V4H4v13.175Z"/></svg>', Jt = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 11h2V5h-2v6Zm1 4c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 14a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 13a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 14c0 .283.096.52.287.713.192.191.43.287.713.287ZM2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Zm2-4.825L5.175 16H20V4H4v13.175Z"/></svg>', e5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 11h2V5h-2v6Zm1 4c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 14a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 13a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 14c0 .283.096.52.287.713.192.191.43.287.713.287ZM2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Z"/></svg>', t5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Z"/></svg>', s5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.6 13.8-2.175-2.175a.89.89 0 0 0-.687-.262.976.976 0 0 0-.688.287.948.948 0 0 0-.275.7.95.95 0 0 0 .275.7L9.9 15.9a.948.948 0 0 0 .7.275.948.948 0 0 0 .7-.275l5.675-5.675a.894.894 0 0 0 .263-.688.979.979 0 0 0-.288-.687.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275L10.6 13.8ZM12 22a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Zm0-2c2.217 0 4.104-.779 5.663-2.337C19.221 16.104 20 14.217 20 12s-.779-4.104-2.337-5.663C16.104 4.779 14.217 4 12 4s-4.104.779-5.662 2.337C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.221 9.783 20 12 20Z"/></svg>', r5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.6 13.8-2.175-2.175a.918.918 0 0 0-.675-.275c-.267 0-.5.1-.7.3a.948.948 0 0 0-.275.7.95.95 0 0 0 .275.7L9.9 15.9a.948.948 0 0 0 .7.275.948.948 0 0 0 .7-.275l5.675-5.675a.918.918 0 0 0 .275-.675c0-.267-.1-.5-.3-.7a.948.948 0 0 0-.7-.275.948.948 0 0 0-.7.275L10.6 13.8ZM12 22a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Z"/></svg>', o5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9.55 17.575c-.133 0-.258-.02-.375-.063a.876.876 0 0 1-.325-.212L4.55 13c-.183-.183-.27-.42-.262-.713.008-.291.104-.529.287-.712a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275L9.55 15.15l8.475-8.475c.183-.183.42-.275.713-.275.291 0 .529.092.712.275.183.183.275.42.275.713 0 .291-.092.529-.275.712l-9.2 9.2c-.1.1-.208.17-.325.212a1.106 1.106 0 0 1-.375.063Z"/></svg>', i5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10 17-5-5 1.41-1.42L10 14.17l7.59-7.59L19 8l-9 9Z"/></svg>', a5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 11h10v2H7z"/></svg>', n5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m16.525 8.175 3.55-3.55c.2-.2.433-.296.7-.287a1.006 1.006 0 0 1 .975 1.012c0 .267-.092.5-.275.7L17.25 10.3c-.2.2-.433.3-.7.3a.96.96 0 0 1-.7-.3L13.7 8.15a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l1.425 1.425ZM3 9a.968.968 0 0 1-.712-.287A.968.968 0 0 1 2 8c0-.283.096-.52.288-.712A.968.968 0 0 1 3 7h7a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.713A.968.968 0 0 1 10 9H3Zm14 8.4-1.9 1.9a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l1.9-1.9-1.9-1.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l1.9 1.9 1.9-1.9a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.949.949 0 0 1 .275.7.948.948 0 0 1-.275.7L18.4 16l1.9 1.9a.949.949 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275L17 17.4ZM3 17a.967.967 0 0 1-.712-.288A.967.967 0 0 1 2 16a.97.97 0 0 1 .288-.713A.968.968 0 0 1 3 15h7c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.712A.968.968 0 0 1 10 17H3Z"/></svg>', l5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 14.95a1.1 1.1 0 0 1-.375-.063.873.873 0 0 1-.325-.212L6.675 10.05a.891.891 0 0 1-.262-.688.975.975 0 0 1 .287-.687.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l3.9 3.9 3.925-3.925a.894.894 0 0 1 .688-.263.978.978 0 0 1 .687.288.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-4.6 4.6c-.1.1-.208.17-.325.212a1.098 1.098 0 0 1-.375.063Z"/></svg>', c5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m13.3 17.3-4.6-4.6a.871.871 0 0 1-.212-.325A1.098 1.098 0 0 1 8.425 12a1.1 1.1 0 0 1 .063-.375.872.872 0 0 1 .212-.325l4.6-4.6a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7L10.8 12l3.9 3.9a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', h5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8.7 17.3a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l3.9-3.9-3.9-3.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l4.6 4.6c.1.1.171.208.213.325.041.117.062.242.062.375s-.02.258-.062.375a.883.883 0 0 1-.213.325l-4.6 4.6a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', d5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6.7 14.675a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l4.6-4.6a.88.88 0 0 1 .325-.213c.117-.041.242-.062.375-.062s.258.02.375.062a.883.883 0 0 1 .325.213l4.625 4.625a.918.918 0 0 1 .275.675c0 .267-.1.5-.3.7a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275l-3.9-3.9L8.075 14.7a.917.917 0 0 1-.675.275.96.96 0 0 1-.7-.3Z"/></svg>', p5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.7 18c-1.6-.083-2.95-.7-4.05-1.85C6.55 15 6 13.617 6 12c0-1.667.583-3.083 1.75-4.25C8.917 6.583 10.333 6 12 6c1.617 0 3 .55 4.15 1.65 1.15 1.1 1.767 2.45 1.85 4.05l-2.1-.625a3.944 3.944 0 0 0-1.4-2.212C13.783 8.288 12.95 8 12 8c-1.1 0-2.042.392-2.825 1.175C8.392 9.958 8 10.9 8 12c0 .95.287 1.783.863 2.5a3.944 3.944 0 0 0 2.212 1.4L11.7 18Zm.3 4a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12v.45c0 .15-.017.3-.05.45L20 12.3V12c0-2.233-.775-4.125-2.325-5.675C16.125 4.775 14.233 4 12 4c-2.233 0-4.125.775-5.675 2.325C4.775 7.875 4 9.767 4 12c0 2.233.775 4.125 2.325 5.675C7.875 19.225 9.767 20 12 20h.3l.6 1.95c-.15.033-.3.05-.45.05H12Zm8.175.15-3.925-3.925-.75 2.275c-.083.233-.242.346-.475.337-.233-.008-.392-.129-.475-.362L12.275 12.9c-.067-.183-.025-.35.125-.5.15-.15.317-.192.5-.125l7.575 2.275c.233.083.354.242.362.475.009.233-.104.392-.337.475l-2.275.75 3.925 3.925a.48.48 0 0 1 0 .7l-1.275 1.275a.48.48 0 0 1-.7 0Z"/></svg>', u5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8 17h5a.97.97 0 0 0 .713-.288A.968.968 0 0 0 14 16a.968.968 0 0 0-.287-.713A.968.968 0 0 0 13 15H8a.967.967 0 0 0-.713.287A.968.968 0 0 0 7 16c0 .283.096.52.287.712.192.192.43.288.713.288Zm0-4h8a.97.97 0 0 0 .712-.287A.968.968 0 0 0 17 12a.968.968 0 0 0-.288-.713A.968.968 0 0 0 16 11H8a.967.967 0 0 0-.713.287A.968.968 0 0 0 7 12c0 .283.096.52.287.713.192.191.43.287.713.287Zm0-4h8a.97.97 0 0 0 .712-.287A.967.967 0 0 0 17 8a.967.967 0 0 0-.288-.713A.968.968 0 0 0 16 7H8a.968.968 0 0 0-.713.287A.968.968 0 0 0 7 8c0 .283.096.52.287.713.192.191.43.287.713.287ZM5 19h14V5H5v14Zm0 2c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h4.2a3.01 3.01 0 0 1 1.088-1.45A2.857 2.857 0 0 1 12 1c.633 0 1.204.183 1.713.55.508.367.87.85 1.087 1.45H19c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Zm7-16.75a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75Z"/></svg>', g5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8 17h5a.97.97 0 0 0 .713-.288A.968.968 0 0 0 14 16a.968.968 0 0 0-.287-.713A.968.968 0 0 0 13 15H8a.967.967 0 0 0-.713.287A.968.968 0 0 0 7 16c0 .283.096.52.287.712.192.192.43.288.713.288Zm0-4h8a.97.97 0 0 0 .712-.287A.968.968 0 0 0 17 12a.968.968 0 0 0-.288-.713A.968.968 0 0 0 16 11H8a.967.967 0 0 0-.713.287A.968.968 0 0 0 7 12c0 .283.096.52.287.713.192.191.43.287.713.287Zm0-4h8a.97.97 0 0 0 .712-.287A.967.967 0 0 0 17 8a.967.967 0 0 0-.288-.713A.968.968 0 0 0 16 7H8a.968.968 0 0 0-.713.287A.968.968 0 0 0 7 8c0 .283.096.52.287.713.192.191.43.287.713.287ZM5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h4.2a3.01 3.01 0 0 1 1.088-1.45A2.857 2.857 0 0 1 12 1c.633 0 1.204.183 1.713.55.508.367.87.85 1.087 1.45H19c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Zm7-16.75a.728.728 0 0 0 .75-.75.728.728 0 0 0-.75-.75.728.728 0 0 0-.75.75.728.728 0 0 0 .75.75Z"/></svg>', v5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m13 11.6 2.5 2.5a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275l-2.8-2.8a.999.999 0 0 1-.225-.337 1.034 1.034 0 0 1-.075-.388V8c0-.283.096-.52.287-.713A.968.968 0 0 1 12 7c.283 0 .52.096.713.287.191.192.287.43.287.713v3.6ZM12 21c-1.25 0-2.42-.238-3.512-.712a9.149 9.149 0 0 1-2.85-1.925 9.147 9.147 0 0 1-1.925-2.85A8.707 8.707 0 0 1 3 12c0-1.25.237-2.42.712-3.512a9.148 9.148 0 0 1 1.925-2.85 9.148 9.148 0 0 1 2.85-1.925A8.707 8.707 0 0 1 12 3a8.93 8.93 0 0 1 3.887.875A8.732 8.732 0 0 1 19 6.35V5a.97.97 0 0 1 .288-.713A.968.968 0 0 1 20 4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v4a.97.97 0 0 1-.288.713A.968.968 0 0 1 20 10h-4a.968.968 0 0 1-.713-.287A.967.967 0 0 1 15 9c0-.283.096-.52.287-.713A.968.968 0 0 1 16 8h1.75a7.431 7.431 0 0 0-2.525-2.2A6.75 6.75 0 0 0 12 5c-1.95 0-3.604.68-4.963 2.037C5.68 8.396 5 10.05 5 12c0 1.95.68 3.604 2.037 4.962C8.396 18.322 10.05 19 12 19a6.93 6.93 0 0 0 4.163-1.35 6.572 6.572 0 0 0 2.512-3.55 1.47 1.47 0 0 1 .462-.713.826.826 0 0 1 .763-.187c.3.067.52.233.663.5.141.267.17.55.087.85-.55 1.917-1.625 3.47-3.225 4.662C15.825 20.404 14.017 21 12 21Z"/></svg>', m5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m13 11.6 2.5 2.5a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275l-2.8-2.8a.999.999 0 0 1-.225-.337 1.034 1.034 0 0 1-.075-.388V8c0-.283.096-.52.287-.713A.968.968 0 0 1 12 7c.283 0 .52.096.713.287.191.192.287.43.287.713v3.6ZM12 21c-2.017 0-3.825-.596-5.425-1.788-1.6-1.191-2.675-2.745-3.225-4.662-.083-.3-.054-.583.087-.85a.948.948 0 0 1 .663-.5.826.826 0 0 1 .763.188c.224.191.379.429.462.712a6.572 6.572 0 0 0 2.513 3.55A6.92 6.92 0 0 0 12 19c1.95 0 3.604-.68 4.962-2.038C18.322 15.604 19 13.95 19 12c0-1.95-.68-3.604-2.038-4.963C15.604 5.68 13.95 5 12 5a6.75 6.75 0 0 0-3.225.8A7.431 7.431 0 0 0 6.25 8H8c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 8 10H4a.968.968 0 0 1-.712-.287A.968.968 0 0 1 3 9V5a.97.97 0 0 1 .288-.713A.968.968 0 0 1 4 4c.283 0 .52.096.713.287.191.192.287.43.287.713v1.35a8.732 8.732 0 0 1 3.113-2.475A8.93 8.93 0 0 1 12 3c1.25 0 2.42.237 3.512.712a9.147 9.147 0 0 1 2.85 1.925 9.149 9.149 0 0 1 1.926 2.85A8.707 8.707 0 0 1 21 12c0 1.25-.238 2.42-.712 3.512a9.148 9.148 0 0 1-1.925 2.85 9.148 9.148 0 0 1-2.85 1.926A8.707 8.707 0 0 1 12 21Z"/></svg>', f5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14.625 16.025a.918.918 0 0 0 .675.275c.267 0 .5-.1.7-.3a.948.948 0 0 0 .275-.7.948.948 0 0 0-.275-.7l-3-3V7.975a.927.927 0 0 0-.287-.7A.99.99 0 0 0 12 7a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 8v3.975a.997.997 0 0 0 .3.725l3.325 3.325ZM12 22a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Z"/></svg>', b5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m12 13.4-4.9 4.9a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l4.9-4.9-4.9-4.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l4.9 4.9 4.9-4.9a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7L13.4 12l4.9 4.9a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275L12 13.4Z"/></svg>', w5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6.5 20c-1.517 0-2.813-.525-3.888-1.575C1.537 17.375 1 16.092 1 14.575c0-1.3.392-2.458 1.175-3.475S3.983 9.433 5.25 9.15c.417-1.533 1.25-2.775 2.5-3.725C9 4.475 10.417 4 12 4c1.95 0 3.604.68 4.962 2.037C18.322 7.396 19 9.05 19 11c1.15.133 2.104.63 2.863 1.488A4.407 4.407 0 0 1 23 15.5c0 1.25-.438 2.313-1.313 3.188S19.75 20 18.5 20h-12Zm0-2h12c.7 0 1.292-.242 1.775-.725.483-.483.725-1.075.725-1.775s-.242-1.292-.725-1.775C19.792 13.242 19.2 13 18.5 13H17v-2c0-1.383-.488-2.563-1.463-3.537C14.563 6.487 13.383 6 12 6s-2.563.487-3.537 1.463C7.488 8.437 7 9.617 7 11h-.5c-.967 0-1.792.342-2.475 1.025A3.372 3.372 0 0 0 3 14.5c0 .967.342 1.792 1.025 2.475A3.372 3.372 0 0 0 6.5 18Z"/></svg>', x5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6.5 20c-1.517 0-2.813-.525-3.888-1.575C1.537 17.375 1 16.092 1 14.575c0-1.3.392-2.458 1.175-3.475S3.983 9.433 5.25 9.15c.417-1.533 1.25-2.775 2.5-3.725C9 4.475 10.417 4 12 4c1.95 0 3.604.68 4.962 2.037C18.322 7.396 19 9.05 19 11c1.15.133 2.104.63 2.863 1.488A4.407 4.407 0 0 1 23 15.5c0 1.25-.438 2.313-1.313 3.188S19.75 20 18.5 20h-12Z"/></svg>', A5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10.942 8.164c-.334.514-.546 1.439-.138 2.961H13a.875.875 0 0 1 0 1.75h-1.939a4.877 4.877 0 0 1-.56 2.145 7.1 7.1 0 0 1-.353.605H14.5a1.1 1.1 0 0 0 .785-.322c.215-.204.342-.452.385-.58a.875.875 0 0 1 1.66.554 3.302 3.302 0 0 1-.84 1.295 2.85 2.85 0 0 1-1.99.803h-6a.875.875 0 0 1-.783-1.266c.148-.295.37-.601.559-.86l.067-.092a7.12 7.12 0 0 0 .6-.937c.197-.383.349-.819.367-1.345H8a.875.875 0 0 1 0-1.75h1.004c-.348-1.616-.163-2.939.47-3.914.716-1.103 1.894-1.586 3.026-1.586 1.41 0 2.287.672 2.78 1.37.28.397.505.88.584 1.365a.875.875 0 0 1-1.726.289 1.879 1.879 0 0 0-.287-.644c-.213-.302-.586-.63-1.351-.63-.647 0-1.22.267-1.558.789Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Zm-2 0a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" clip-rule="evenodd"/></svg>', y5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10ZM10.942 8.164c-.334.514-.546 1.439-.138 2.961H13a.875.875 0 0 1 0 1.75h-1.939a4.877 4.877 0 0 1-.56 2.145 7.1 7.1 0 0 1-.353.605H14.5a1.1 1.1 0 0 0 .785-.322c.215-.204.342-.452.385-.58a.875.875 0 0 1 1.66.554 3.302 3.302 0 0 1-.84 1.295 2.85 2.85 0 0 1-1.99.803h-6a.875.875 0 0 1-.783-1.266c.148-.295.37-.601.559-.86l.067-.092a7.12 7.12 0 0 0 .6-.937c.197-.383.349-.819.367-1.345H8a.875.875 0 0 1 0-1.75h1.004c-.348-1.616-.163-2.939.47-3.914.716-1.103 1.894-1.586 3.026-1.586 1.41 0 2.287.672 2.78 1.37.28.397.505.88.584 1.365a.875.875 0 0 1-1.726.289 1.879 1.879 0 0 0-.287-.644c-.213-.302-.586-.63-1.351-.63-.647 0-1.22.267-1.558.789Z" clip-rule="evenodd"/></svg>', k5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.975 19a.84.84 0 0 0 .613-.262.84.84 0 0 0 .262-.613v-.375c.833-.15 1.55-.475 2.15-.975.6-.5.9-1.242.9-2.225 0-.7-.2-1.342-.6-1.925-.4-.583-1.2-1.092-2.4-1.525-1-.333-1.692-.625-2.075-.875-.383-.25-.575-.592-.575-1.025 0-.433.154-.775.463-1.025.308-.25.754-.375 1.337-.375.333 0 .625.058.875.175.25.117.458.275.625.475.167.2.354.338.563.413.208.075.404.07.587-.013.25-.1.42-.27.513-.512a.69.69 0 0 0-.063-.663 3.79 3.79 0 0 0-.988-.975 2.546 2.546 0 0 0-1.262-.45v-.375a.84.84 0 0 0-.263-.612.84.84 0 0 0-.612-.263.84.84 0 0 0-.613.263.84.84 0 0 0-.262.612v.375c-.833.183-1.483.55-1.95 1.1-.467.55-.7 1.167-.7 1.85 0 .783.23 1.417.688 1.9.458.483 1.179.9 2.162 1.25 1.05.383 1.78.725 2.188 1.025.408.3.612.692.612 1.175 0 .55-.196.954-.588 1.212-.391.259-.862.388-1.412.388-.433 0-.825-.104-1.175-.312-.35-.209-.642-.521-.875-.938a1.03 1.03 0 0 0-.525-.475.877.877 0 0 0-.65 0 .871.871 0 0 0-.513.475.77.77 0 0 0-.012.675c.267.567.625 1.03 1.075 1.387.45.359.992.605 1.625.738v.425a.84.84 0 0 0 .263.613.84.84 0 0 0 .612.262ZM12 22a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm0-2c2.217 0 4.104-.78 5.663-2.337C19.22 16.104 20 14.217 20 12s-.78-4.104-2.337-5.662C16.104 4.779 14.217 4 12 4s-4.104.78-5.662 2.338C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.22 9.783 20 12 20Z"/></svg>', _5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.975 19a.84.84 0 0 0 .613-.262.84.84 0 0 0 .262-.613v-.375c.833-.15 1.55-.475 2.15-.975.6-.5.9-1.242.9-2.225 0-.7-.2-1.342-.6-1.925-.4-.583-1.2-1.092-2.4-1.525-1-.333-1.692-.625-2.075-.875-.383-.25-.575-.592-.575-1.025 0-.433.154-.775.463-1.025.308-.25.754-.375 1.337-.375.333 0 .625.058.875.175.25.117.458.275.625.475.167.2.354.338.563.413.208.075.404.07.587-.013.25-.1.42-.27.513-.512a.69.69 0 0 0-.063-.663 3.79 3.79 0 0 0-.988-.975 2.546 2.546 0 0 0-1.262-.45v-.375a.84.84 0 0 0-.263-.612.84.84 0 0 0-.612-.263.84.84 0 0 0-.613.263.84.84 0 0 0-.262.612v.375c-.833.183-1.483.55-1.95 1.1-.467.55-.7 1.167-.7 1.85 0 .783.23 1.417.688 1.9.458.483 1.179.9 2.162 1.25 1.05.383 1.78.725 2.188 1.025.408.3.612.692.612 1.175 0 .55-.196.954-.588 1.212-.391.259-.862.388-1.412.388-.433 0-.825-.104-1.175-.312-.35-.209-.642-.521-.875-.938a1.03 1.03 0 0 0-.525-.475.877.877 0 0 0-.65 0 .871.871 0 0 0-.513.475.77.77 0 0 0-.012.675c.267.567.625 1.03 1.075 1.387.45.359.992.605 1.625.738v.425a.84.84 0 0 0 .263.613.84.84 0 0 0 .612.262ZM12 22a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Z"/></svg>', Z5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 9.475a1.034 1.034 0 0 1-.7-.275L8.2 6.1a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275L12 7.1l2.4-2.4a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-3.1 3.1c-.1.1-.208.17-.325.213a1.106 1.106 0 0 1-.375.062ZM8.2 19.3a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l3.1-3.1c.1-.1.208-.17.325-.213a1.219 1.219 0 0 1 .762 0 .68.68 0 0 1 .313.213l3.125 3.125c.183.183.27.412.262.688a.977.977 0 0 1-.287.687.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275L12 16.9l-2.4 2.4a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', C5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 15a1 1 0 0 0 1 1h3v3a1 1 0 1 0 2 0v-5H5a1 1 0 0 0-1 1Zm4-7H5a1 1 0 0 0 0 2h5V5a1 1 0 0 0-2 0v3Zm6 11a1 1 0 1 0 2 0v-3h3a1 1 0 1 0 0-2h-5v5Zm2-11V5a1 1 0 1 0-2 0v5h5a1 1 0 1 0 0-2h-3Z"/></svg>', $5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 14h8v-2H6v2Zm0-3h12V9H6v2Zm0-3h12V6H6v2ZM2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Zm2-4.825L5.175 16H20V4H4v13.175Z"/></svg>', M5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 14h8v-2H6v2Zm0-3h12V9H6v2Zm0-3h12V6H6v2ZM2 22V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H6l-4 4Z"/></svg>', V5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 20a.968.968 0 0 1-.713-.288A.968.968 0 0 1 10 19v-5.225c0-.033.017-.117.05-.25L11.4 9.65a1.23 1.23 0 0 1 .362-.463A.919.919 0 0 1 12.35 9h7.3a.92.92 0 0 1 .588.188c.158.124.279.279.362.462l1.35 3.875.05.25V19c0 .283-.096.52-.288.712A.968.968 0 0 1 21 20a.968.968 0 0 1-.712-.288A.968.968 0 0 1 20 19v-.5h-8v.5a.97.97 0 0 1-.287.712A.968.968 0 0 1 11 20Zm1-7.5h8l-.7-2h-6.6l-.7 2Zm1 4a.97.97 0 0 0 .713-.288A.968.968 0 0 0 14 15.5a.968.968 0 0 0-.287-.713A.968.968 0 0 0 13 14.5a.968.968 0 0 0-.713.287.968.968 0 0 0-.287.713c0 .283.096.52.287.712.192.192.43.288.713.288Zm6 0c.283 0 .52-.096.712-.288A.968.968 0 0 0 20 15.5a.968.968 0 0 0-.288-.713A.968.968 0 0 0 19 14.5a.968.968 0 0 0-.712.287.968.968 0 0 0-.288.713c0 .283.096.52.288.712A.965.965 0 0 0 19 16.5ZM4 19.4c0-.1.025-.217.075-.35a.897.897 0 0 1 .2-.325L5 18a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 2 15V7c0-1.1.492-1.875 1.475-2.325C4.458 4.225 6.133 4 8.5 4c2.467 0 4.167.217 5.1.65.933.433 1.4 1.217 1.4 2.35v1h-2V7H4v6h5v7H4.6a.578.578 0 0 1-.425-.175A.579.579 0 0 1 4 19.4ZM5 16c.283 0 .52-.096.713-.287A.968.968 0 0 0 6 15a.968.968 0 0 0-.287-.713A.967.967 0 0 0 5 14a.967.967 0 0 0-.713.287A.968.968 0 0 0 4 15c0 .283.096.52.287.713.192.191.43.287.713.287Zm6.5 1h9v-3h-9v3Z"/></svg>', H5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 20a.968.968 0 0 1-.713-.288A.968.968 0 0 1 10 19v-5.225c0-.033.017-.117.05-.25L11.4 9.65a1.23 1.23 0 0 1 .362-.463A.919.919 0 0 1 12.35 9h7.3a.92.92 0 0 1 .588.188c.158.124.279.279.362.462l1.35 3.875.05.25V19c0 .283-.096.52-.288.712A.968.968 0 0 1 21 20a.968.968 0 0 1-.712-.288A.968.968 0 0 1 20 19v-.5h-8v.5a.97.97 0 0 1-.287.712A.968.968 0 0 1 11 20Zm1-7.5h8l-.7-2h-6.6l-.7 2Zm1 4a.97.97 0 0 0 .713-.288A.968.968 0 0 0 14 15.5a.968.968 0 0 0-.287-.713A.968.968 0 0 0 13 14.5a.968.968 0 0 0-.713.287.968.968 0 0 0-.287.713c0 .283.096.52.287.712.192.192.43.288.713.288Zm6 0c.283 0 .52-.096.712-.288A.968.968 0 0 0 20 15.5a.968.968 0 0 0-.288-.713A.968.968 0 0 0 19 14.5a.968.968 0 0 0-.712.287.968.968 0 0 0-.288.713c0 .283.096.52.288.712A.965.965 0 0 0 19 16.5ZM4 19.4c0-.1.025-.217.075-.35a.897.897 0 0 1 .2-.325L5 18a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 2 15V7c0-1.1.492-1.875 1.475-2.325C4.458 4.225 6.133 4 8.5 4c2.467 0 4.167.217 5.1.65.933.433 1.4 1.217 1.4 2.35v1h-2V7H4v6h5v7H4.6a.578.578 0 0 1-.425-.175A.579.579 0 0 1 4 19.4ZM5 16c.283 0 .52-.096.713-.287A.968.968 0 0 0 6 15a.968.968 0 0 0-.287-.713A.967.967 0 0 0 5 14a.967.967 0 0 0-.713.287A.968.968 0 0 0 4 15c0 .283.096.52.287.713.192.191.43.287.713.287Z"/></svg>', S5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 13a.968.968 0 0 1-.713-.287A.968.968 0 0 1 11 12c0-.283.096-.52.287-.713A.968.968 0 0 1 12 11c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 12 13Zm0 9a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm0-2c2.233 0 4.125-.775 5.675-2.325C19.225 16.125 20 14.233 20 12c0-2.233-.775-4.125-2.325-5.675C16.125 4.775 14.233 4 12 4c-2.233 0-4.125.775-5.675 2.325C4.775 7.875 4 9.767 4 12c0 2.233.775 4.125 2.325 5.675C7.875 19.225 9.767 20 12 20Zm-4.575-2.925 6.25-2.925c.1-.05.192-.117.275-.2.083-.083.15-.175.2-.275l2.925-6.25c.083-.167.063-.313-.063-.438-.125-.125-.27-.145-.437-.062l-6.25 2.925c-.1.05-.192.117-.275.2-.083.083-.15.175-.2.275l-2.925 6.25c-.083.167-.063.313.063.438.124.124.27.145.437.062Z"/></svg>', D5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 13a.968.968 0 0 1-.713-.287A.968.968 0 0 1 11 12c0-.283.096-.52.287-.713A.968.968 0 0 1 12 11c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 12 13Zm0 9a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm0-2c2.233 0 4.125-.775 5.675-2.325C19.225 16.125 20 14.233 20 12c0-2.233-.775-4.125-2.325-5.675C16.125 4.775 14.233 4 12 4c-2.233 0-4.125.775-5.675 2.325C4.775 7.875 4 9.767 4 12c0 2.233.775 4.125 2.325 5.675C7.875 19.225 9.767 20 12 20Zm0 0c-2.233 0-4.125-.775-5.675-2.325C4.775 16.125 4 14.233 4 12c0-2.233.775-4.125 2.325-5.675C7.875 4.775 9.767 4 12 4c2.233 0 4.125.775 5.675 2.325C19.225 7.875 20 9.767 20 12c0 2.233-.775 4.125-2.325 5.675C16.125 19.225 14.233 20 12 20Zm1.675-5.85c.1-.05.192-.117.275-.2.083-.083.15-.175.2-.275l2.925-6.25c.083-.167.063-.313-.063-.438-.125-.125-.27-.145-.437-.062l-6.25 2.925c-.1.05-.192.117-.275.2-.083.083-.15.175-.2.275l-2.925 6.25c-.083.167-.063.313.063.438.124.124.27.145.437.062l6.25-2.925Z"/></svg>', L5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2 17V3c0-.283.096-.52.288-.712A.968.968 0 0 1 3 2h13c.283 0 .52.096.712.288A.965.965 0 0 1 17 3v9a.97.97 0 0 1-.288.713A.968.968 0 0 1 16 13H6l-4 4Zm5 1a.967.967 0 0 1-.713-.288A.968.968 0 0 1 6 17v-2h13V6h2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v15l-4-4H7Zm8-14H4v8.175L5.175 11H15V4Z"/></svg>', O5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2 17V3c0-.283.096-.52.288-.712A.968.968 0 0 1 3 2h13c.283 0 .52.096.712.288A.965.965 0 0 1 17 3v9a.97.97 0 0 1-.288.713A.968.968 0 0 1 16 13H6l-4 4Zm5 1a.967.967 0 0 1-.713-.288A.968.968 0 0 1 6 17v-2h13V6h2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v15l-4-4H7Z"/></svg>', E5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 18c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 7 16V4c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 9 2h9c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 18H9Zm0-2h9V4H9v12Zm-4 6c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 20V7a.97.97 0 0 1 .288-.713A.968.968 0 0 1 4 6c.283 0 .52.096.713.287.191.192.287.43.287.713v13h10a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 15 22H5Z"/></svg>', T5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 18c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 7 16V4c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 9 2h9c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 18H9Zm-4 4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 20V7a.97.97 0 0 1 .288-.713A.968.968 0 0 1 4 6c.283 0 .52.096.713.287.191.192.287.43.287.713v13h10a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 15 22H5Z"/></svg>', z5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M22 6v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h16c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412ZM4 8h16V6H4v2Zm0 4v6h16v-6H4Z"/></svg>', I5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h16c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4Zm0-8h16V8H4v4Z"/></svg>', P5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 13a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 12V7.975a.928.928 0 0 0-.287-.7A.993.993 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8v4.025c0 .283.096.517.288.7A.99.99 0 0 0 12 13Zm0 4a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 15a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 16c0 .283.096.52.288.712A.965.965 0 0 0 12 17Zm0 5a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Zm0-2c2.217 0 4.104-.779 5.663-2.337C19.221 16.104 20 14.217 20 12s-.779-4.104-2.337-5.663C16.104 4.779 14.217 4 12 4s-4.104.779-5.662 2.337C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.221 9.783 20 12 20Z"/></svg>', B5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 13a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 12V7.975a.928.928 0 0 0-.287-.7A.993.993 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8v4.025c0 .283.096.517.288.7A.99.99 0 0 0 12 13Zm0 4a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 15a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 16c0 .283.096.52.288.712A.965.965 0 0 0 12 17Zm0 5a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Z"/></svg>', F5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15 13V9h-4V7h4c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v4h-2Zm1 6a.968.968 0 0 1-.713-.288A.968.968 0 0 1 15 18v-1H9c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 7 15V9H6a.968.968 0 0 1-.712-.287A.968.968 0 0 1 5 8a.97.97 0 0 1 .288-.713A.968.968 0 0 1 6 7h1V6a.97.97 0 0 1 .288-.713A.968.968 0 0 1 8 5c.283 0 .52.096.713.287.191.192.287.43.287.713v9h9a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.968.968 0 0 1 18 17h-1v1c0 .283-.096.52-.288.712A.968.968 0 0 1 16 19Zm6.9-8a.954.954 0 0 1-.662-.262 1.149 1.149 0 0 1-.363-.638c-.433-2.183-1.47-4.025-3.113-5.525-1.641-1.5-3.595-2.342-5.862-2.525l.85.85a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275l-2.7-2.7c-.3-.3-.412-.6-.337-.9.075-.3.32-.483.737-.55.333-.05.658-.087.975-.112C11.342.013 11.667 0 12 0c2.967 0 5.563.942 7.788 2.825 2.224 1.883 3.587 4.258 4.087 7.125.05.283-.02.53-.212.738-.192.208-.446.312-.763.312ZM12 24c-2.967 0-5.562-.942-7.787-2.825C1.988 19.292.625 16.917.125 14.05a.824.824 0 0 1 .213-.738C.529 13.104.783 13 1.1 13c.25 0 .47.088.663.262.191.175.312.388.362.638.433 2.183 1.47 4.025 3.113 5.525 1.641 1.5 3.595 2.342 5.862 2.525l-.85-.85a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l2.7 2.7c.3.3.412.6.338.9-.075.3-.321.483-.738.55-.333.05-.658.087-.975.112A12.39 12.39 0 0 1 12 24Z"/></svg>', j5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17 15V7H9V5h8c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v8h-2Zm1 8a.968.968 0 0 1-.712-.288A.968.968 0 0 1 17 22v-3H7c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 17V7H2a.968.968 0 0 1-.712-.287A.968.968 0 0 1 1 6a.97.97 0 0 1 .288-.713A.968.968 0 0 1 2 5h3V2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 6 1a.97.97 0 0 1 .713.288A.968.968 0 0 1 7 2v15h15c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 22 19h-3v3c0 .283-.096.52-.288.712A.968.968 0 0 1 18 23Z"/></svg>', R5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 21c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 5 19V6a.97.97 0 0 1-.713-.287A.97.97 0 0 1 4 5a.97.97 0 0 1 .287-.713A.97.97 0 0 1 5 4h4c0-.283.096-.521.288-.713A.967.967 0 0 1 10 3h4a.97.97 0 0 1 .713.287A.97.97 0 0 1 15 4h4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 19 6v13a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 17 21H7Zm2-5c0 .283.096.52.288.712A.965.965 0 0 0 10 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 11 16V9a.97.97 0 0 0-.287-.713A.97.97 0 0 0 10 8a.967.967 0 0 0-.712.287A.968.968 0 0 0 9 9v7Zm4 0c0 .283.096.52.288.712A.965.965 0 0 0 14 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 15 16V9a.97.97 0 0 0-.287-.713A.97.97 0 0 0 14 8a.967.967 0 0 0-.712.287A.968.968 0 0 0 13 9v7Z"/></svg>', N5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 21c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 5 19V6a.97.97 0 0 1-.713-.287A.97.97 0 0 1 4 5a.97.97 0 0 1 .287-.713A.97.97 0 0 1 5 4h4c0-.283.096-.521.288-.713A.967.967 0 0 1 10 3h4a.97.97 0 0 1 .713.287A.97.97 0 0 1 15 4h4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 19 6v13a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 17 21H7ZM7 6v13h10V6H7Zm2 10c0 .283.096.52.288.712A.965.965 0 0 0 10 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 11 16V9a.97.97 0 0 0-.287-.713A.97.97 0 0 0 10 8a.967.967 0 0 0-.712.287A.968.968 0 0 0 9 9v7Zm4 0c0 .283.096.52.288.712A.965.965 0 0 0 14 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 15 16V9a.97.97 0 0 0-.287-.713A.97.97 0 0 0 14 8a.967.967 0 0 0-.712.287A.968.968 0 0 0 13 9v7Z"/></svg>', U5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 22a.967.967 0 0 1-.713-.288A.968.968 0 0 1 8 21a.97.97 0 0 1 .287-.712A.967.967 0 0 1 9 20h1v-2H4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 16V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 3h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18h-6v2h1a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 15 22H9Zm-5-6h16V5H4v11Z"/></svg>', W5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 22a.967.967 0 0 1-.713-.288A.968.968 0 0 1 8 21a.97.97 0 0 1 .287-.712A.967.967 0 0 1 9 20h1v-2H4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 16V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 3h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18h-6v2h1a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 15 22H9Z"/></svg>', q5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m11.028 1.972-9.056 9.056a1.375 1.375 0 0 0 0 1.944l9.056 9.056a1.375 1.375 0 0 0 1.944 0l9.056-9.056a1.375 1.375 0 0 0 0-1.944l-9.056-9.056a1.375 1.375 0 0 0-1.944 0Z"/></svg>', K5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M13.85 16.208a.953.953 0 0 1 0-1.345l1.013-1.012a.952.952 0 0 1 1.345 0l3.943 3.943a10 10 0 1 0-2.357 2.357l-3.943-3.943Z"/></svg>', Y5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 15.575a1.1 1.1 0 0 1-.375-.063.871.871 0 0 1-.325-.212l-3.6-3.6a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7c.183-.183.42-.28.712-.288.292-.008.53.08.713.263L11 12.15V5c0-.283.096-.521.288-.713A.967.967 0 0 1 12 4a.97.97 0 0 1 .713.287A.97.97 0 0 1 13 5v7.15l1.875-1.875c.183-.183.421-.271.713-.263.291.009.529.105.712.288a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-3.6 3.6c-.1.1-.208.17-.325.212a1.1 1.1 0 0 1-.375.063ZM6 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 18v-2a.97.97 0 0 1 .287-.713A.97.97 0 0 1 5 15a.97.97 0 0 1 .713.287A.97.97 0 0 1 6 16v2h12v-2c0-.283.096-.521.288-.713A.967.967 0 0 1 19 15a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 20H6Z"/></svg>', G5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 7 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 9 16c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.021-.587 1.413A1.928 1.928 0 0 1 9 20Zm6 0c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 13 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 15 16c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.021-.587 1.413A1.928 1.928 0 0 1 15 20Zm-6-6c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 7 12c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 9 10c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 9 14Zm6 0c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 13 12c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 15 10c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 15 14ZM9 8c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 7 6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 9 4c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.927 1.927 0 0 1 9 8Zm6 0c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 13 6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 15 4c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.927 1.927 0 0 1 15 8Z"/></svg>', Q5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 14a.968.968 0 0 0 .713-.288A.967.967 0 0 0 15 13v-2h2c.283 0 .52-.096.712-.288A.965.965 0 0 0 18 10a.968.968 0 0 0-.288-.713A.967.967 0 0 0 17 9h-2V7a.97.97 0 0 0-.287-.713A.97.97 0 0 0 14 6a.967.967 0 0 0-.712.287A.968.968 0 0 0 13 7v2h-2a.967.967 0 0 0-.712.287A.968.968 0 0 0 10 10c0 .283.096.52.288.712A.965.965 0 0 0 11 11h2v2c0 .283.096.52.288.712A.965.965 0 0 0 14 14Zm-6 4c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 6 16V4c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 8 2h12c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v12a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 20 18H8Zm0-2h12V4H8v12Zm-4 6c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 2 20V7c0-.283.096-.521.288-.713A.967.967 0 0 1 3 6a.97.97 0 0 1 .713.287A.97.97 0 0 1 4 7v13h13c.283 0 .52.096.712.288A.965.965 0 0 1 18 21c0 .283-.096.52-.288.712A.965.965 0 0 1 17 22H4Z"/></svg>', X5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 14a.968.968 0 0 0 .713-.288A.967.967 0 0 0 15 13v-2h2c.283 0 .52-.096.712-.288A.965.965 0 0 0 18 10a.968.968 0 0 0-.288-.713A.967.967 0 0 0 17 9h-2V7a.97.97 0 0 0-.287-.713A.97.97 0 0 0 14 6a.967.967 0 0 0-.712.287A.968.968 0 0 0 13 7v2h-2a.967.967 0 0 0-.712.287A.968.968 0 0 0 10 10c0 .283.096.52.288.712A.965.965 0 0 0 11 11h2v2c0 .283.096.52.288.712A.965.965 0 0 0 14 14Zm-6 4c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 6 16V4c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 8 2h12c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v12a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 20 18H8Zm-4 4c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 2 20V7c0-.283.096-.521.288-.713A.967.967 0 0 1 3 6a.97.97 0 0 1 .713.287A.97.97 0 0 1 4 7v13h13c.283 0 .52.096.712.288A.965.965 0 0 1 18 21c0 .283-.096.52-.288.712A.965.965 0 0 1 17 22H4Z"/></svg>', J5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M20.125 15 18 12.875l.725-.725a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l.725.725a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-.725.725ZM12 21v-2.125l5.3-5.3 2.125 2.125-5.3 5.3H12Zm-9-5v-2h7v2H3Zm0-4v-2h11v2H3Zm0-4V6h11v2H3Z"/></svg>', es = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 19h1.4l8.625-8.625-1.4-1.4L5 17.6V19ZM19.3 8.925l-4.25-4.2 1.4-1.4a1.92 1.92 0 0 1 1.413-.575 1.92 1.92 0 0 1 1.412.575l1.4 1.4c.383.383.583.846.6 1.388a1.807 1.807 0 0 1-.55 1.387L19.3 8.925ZM17.85 10.4 7.25 21H3v-4.25l10.6-10.6 4.25 4.25Z"/></svg>', ts = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m19.3 8.925-4.25-4.2 1.4-1.4a1.92 1.92 0 0 1 1.413-.575 1.92 1.92 0 0 1 1.412.575l1.4 1.4c.383.383.583.846.6 1.388a1.807 1.807 0 0 1-.55 1.387L19.3 8.925ZM17.85 10.4 7.25 21H3v-4.25l10.6-10.6 4.25 4.25Z"/></svg>', ss = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.95 21.575c-.133 0-.258-.02-.375-.063a.877.877 0 0 1-.325-.212l-2.85-2.85a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l2.15 2.15 4.95-4.95a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-5.65 5.65c-.1.1-.208.17-.325.212a1.106 1.106 0 0 1-.375.063ZM4 6l8 5 8-5H4Zm0 14c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h16c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v4.35l-2 2V8l-7.475 4.675a1.04 1.04 0 0 1-.525.15 1.04 1.04 0 0 1-.525-.15L4 8v10h5.15l2 2H4Z"/></svg>', rs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.95 21.575c-.133 0-.258-.02-.375-.063a.877.877 0 0 1-.325-.212l-2.85-2.85a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l2.15 2.15 4.95-4.95a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-5.65 5.65c-.1.1-.208.17-.325.212a1.106 1.106 0 0 1-.375.063Zm-4.475-8.9c.167.1.342.15.525.15s.358-.05.525-.15l7-4.375a.975.975 0 0 0 .338-.388A1.1 1.1 0 0 0 20 7.4c0-.317-.133-.546-.4-.688-.267-.141-.533-.129-.8.038L12 11 5.2 6.75c-.267-.167-.533-.175-.8-.025s-.4.383-.4.7c0 .167.046.333.138.5a.992.992 0 0 0 .337.375l7 4.375ZM4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h16c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v5.525a1.975 1.975 0 0 0-.9.063c-.317.091-.6.262-.85.512l-4.275 4.275-1.45-1.45c-.4-.4-.875-.6-1.425-.6s-1.025.2-1.425.6L10.3 16.3c-.4.4-.6.875-.6 1.425s.2 1.025.6 1.425l.85.85H4Z"/></svg>', os = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 19V8.15c0-.35.083-.683.25-1 .167-.317.4-.567.7-.75l8.025-4.8c.167-.1.333-.17.5-.213.167-.041.342-.062.525-.062s.358.02.525.062c.167.042.333.113.5.213l8.025 4.8c.3.183.533.433.7.75.167.317.25.65.25 1V19c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 21H4Zm8-8.525c.083 0 .167-.008.25-.025a.787.787 0 0 0 .25-.1L19.8 8l-7.3-4.35a.786.786 0 0 0-.25-.1 1.272 1.272 0 0 0-.5 0 .786.786 0 0 0-.25.1L4.2 8l7.3 4.35c.083.05.167.083.25.1.083.017.167.025.25.025ZM4 19h16v-8.8l-6.975 4.175c-.167.1-.333.175-.5.225-.167.05-.342.075-.525.075s-.358-.025-.525-.075a2.205 2.205 0 0 1-.5-.225L4 10.2V19Z"/></svg>', is = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 19V8.15c0-.35.083-.683.25-1 .167-.317.4-.567.7-.75l8.025-4.8c.167-.1.333-.17.5-.213.167-.041.342-.062.525-.062s.358.02.525.062c.167.042.333.113.5.213l8.025 4.8c.3.183.533.433.7.75.167.317.25.65.25 1V19c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 21H4Zm8-8.525c.083 0 .167-.008.25-.025a.787.787 0 0 0 .25-.1L19.8 8l-7.3-4.35a.786.786 0 0 0-.25-.1 1.272 1.272 0 0 0-.5 0 .786.786 0 0 0-.25.1L4.2 8l7.3 4.35c.083.05.167.083.25.1.083.017.167.025.25.025Z"/></svg>', as = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17.297 19.297a.994.994 0 0 1-.005-1.4l.883-.897H15a1 1 0 1 1 0-2h3.175l-.883-.897a.994.994 0 0 1 1.41-1.4l2.59 2.59a1 1 0 0 1 0 1.414l-2.59 2.59a.994.994 0 0 1-1.405 0Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M4 17c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 15V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 3h13c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v5.1l-.005-.001a1 1 0 0 1-1.992-.024H17V6.4l-5.442 3.793a2 2 0 0 1-2.306-.014L4 6.425V15h8.075v.003a1 1 0 0 1 .024 1.992L12.1 17H4ZM5.45 5l4.95 3.55L15.5 5H5.45Z" clip-rule="evenodd"/></svg>', ns = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M3.875 17c-.517 0-.958-.183-1.325-.55A1.806 1.806 0 0 1 2 15.125V4.875c0-.517.183-.958.55-1.325.367-.367.808-.55 1.325-.55h13.25c.517 0 .958.183 1.325.55.367.367.55.808.55 1.325V10.1a5.193 5.193 0 0 0-1-.1c-1.633 0-3.042.588-4.225 1.762C12.592 12.938 12 14.35 12 16a5.042 5.042 0 0 0 .1 1H3.875ZM4.947 4.606a1 1 0 1 0-.894 1.788l5.553 2.777a2 2 0 0 0 1.788 0l5.553-2.777a1 1 0 1 0-.894-1.788L10.5 7.382 4.947 4.606Z" clip-rule="evenodd"/><path fill="inherit" stroke="none" d="M17.297 19.297a.994.994 0 0 1-.005-1.4l.883-.897H15a1 1 0 0 1 0-2h3.175l-.883-.897a.994.994 0 0 1 1.41-1.4l2.591 2.59a1 1 0 0 1 0 1.414l-2.59 2.59a.993.993 0 0 1-1.406 0Z"/></svg>', ls = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h10.1a5.085 5.085 0 0 0 0 2H4v12h16V9.9c.383-.083.742-.2 1.075-.35.333-.15.642-.333.925-.55v9c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4ZM19 8a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 16 5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 19 2c.833 0 1.542.292 2.125.875S22 4.167 22 5s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 19 8Zm-7 3 3.65-2.275c.233.217.487.404.763.563.274.158.562.295.862.412l-4.75 2.975c-.167.1-.342.15-.525.15s-.358-.05-.525-.15L4 8V6l8 5Z"/></svg>', cs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 8a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 16 5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 19 2c.833 0 1.542.292 2.125.875S22 4.167 22 5s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 19 8ZM4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h10.1a5.085 5.085 0 0 0 0 2 5.109 5.109 0 0 0 1.55 2.7L12 11 5.3 6.8a.775.775 0 0 0-.862-.025.811.811 0 0 0-.438.75.827.827 0 0 0 .4.725l7.075 4.425c.167.1.342.15.525.15s.358-.05.525-.15l4.75-2.975A5.026 5.026 0 0 0 19 10a5.1 5.1 0 0 0 1.575-.25A4.674 4.674 0 0 0 22 9v9c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4Z"/></svg>', hs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h16c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4ZM20 8l-7.475 4.675a1.04 1.04 0 0 1-.525.15 1.04 1.04 0 0 1-.525-.15L4 8v10h16V8Zm-8 3 8-5H4l8 5ZM4 8.25V6.775 6.8v-.013V8.25Z"/></svg>', ds = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h16c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4Zm8-7.175a1.04 1.04 0 0 0 .525-.15L19.6 8.25a.827.827 0 0 0 .4-.725.815.815 0 0 0-.425-.75c-.283-.167-.575-.158-.875.025L12 11 5.3 6.8c-.3-.183-.592-.188-.875-.013-.283.176-.425.421-.425.738 0 .167.033.313.1.438s.167.22.3.287l7.075 4.425a1.04 1.04 0 0 0 .525.15Z"/></svg>', ps = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.732 8.269c.183-.184.425-.275.724-.275.3 0 .542.091.726.275l3.075 3.075c.1.1.17.208.212.325.042.116.063.241.063.374 0 .134-.021.263-.063.388a.678.678 0 0 1-.213.313l-3.1 3.1c-.183.183-.42.27-.712.262a1.007 1.007 0 0 1-.712-.288c-.184-.183-.275-.425-.275-.724 0-.3.091-.542.274-.726l2.325-2.325-2.35-2.35c-.183-.183-.27-.42-.262-.712.008-.292.104-.53.287-.712Zm-12.25 3.774c0-.133.024-.258.074-.374a1.3 1.3 0 0 1 .2-.325l3.075-3.075c.184-.184.425-.275.725-.275s.542.091.725.275c.184.183.275.425.275.725s-.091.541-.275.725l-2.325 2.325 2.35 2.35c.184.183.271.42.263.712-.008.292-.104.53-.288.713-.183.183-.425.274-.725.274s-.541-.091-.725-.274l-3.075-3.076a1.034 1.034 0 0 1-.275-.7Z"/></svg>', us = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8.225 8.325C8.042 8.142 7.95 7.9 7.95 7.6s.092-.542.275-.725L11.3 3.8c.1-.1.208-.17.325-.212a1.18 1.18 0 0 1 .762 0c.125.041.23.112.313.212l3.1 3.1c.183.183.27.42.262.713a1.006 1.006 0 0 1-.287.712c-.183.183-.425.275-.725.275s-.542-.092-.725-.275L12 6 9.65 8.35c-.183.183-.42.27-.713.263a1.007 1.007 0 0 1-.712-.288ZM12 20.575a.941.941 0 0 1-.375-.075 1.312 1.312 0 0 1-.325-.2l-3.075-3.075c-.183-.183-.275-.425-.275-.725s.092-.542.275-.725c.183-.183.425-.275.725-.275s.542.092.725.275L12 18.1l2.35-2.35c.183-.183.42-.27.713-.262.291.008.529.104.712.287.183.183.275.425.275.725s-.092.542-.275.725L12.7 20.3a1.034 1.034 0 0 1-.7.275Z"/></svg>', gs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m5 6.414 2.793 2.793a1 1 0 0 0 1.414-1.414L6.414 5H8.5a1 1 0 0 0 0-2H4a.998.998 0 0 0-.705.29l-.004.005A.997.997 0 0 0 3 4v4.5a1 1 0 1 0 2 0V6.414Zm4.207 9.793L6.414 19H8.5a1 1 0 1 1 0 2H4a.997.997 0 0 1-.705-.29l-.004-.005A.997.997 0 0 1 3 20v-4.5a1 1 0 1 1 2 0v2.086l2.793-2.793a1 1 0 0 1 1.414 1.414ZM19 17.586l-2.793-2.793a1 1 0 0 0-1.414 1.414L17.586 19H15.5a1 1 0 1 0 0 2H20a.997.997 0 0 0 .705-.29l.004-.005A.997.997 0 0 0 21 20v-4.5a1 1 0 1 0-2 0v2.086Zm-2.793-8.379L19 6.414V8.5a1 1 0 1 0 2 0V4a.997.997 0 0 0-.29-.705l-.005-.004A.997.997 0 0 0 20 3h-4.5a1 1 0 1 0 0 2h2.086l-2.793 2.793a1 1 0 0 0 1.414 1.414Z"/></svg>', vs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 16a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 15V4.825l-.9.9A.932.932 0 0 1 9.413 6a.974.974 0 0 1-.713-.3 1.02 1.02 0 0 1-.275-.713c0-.275.092-.504.275-.687l2.6-2.6a1.03 1.03 0 0 1 1.088-.2c.125.05.229.117.312.2l2.6 2.6c.2.2.3.442.3.725s-.1.517-.3.7a1.02 1.02 0 0 1-.712.275.933.933 0 0 1-.688-.275l-.9-.9V15a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 16Zm-6 7c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 21V10c0-.55.196-1.02.588-1.413A1.925 1.925 0 0 1 6 8h2a.97.97 0 0 1 .713.287A.97.97 0 0 1 9 9a.97.97 0 0 1-.287.712A.968.968 0 0 1 8 10H6v11h12V10h-2a.965.965 0 0 1-.712-.288A.965.965 0 0 1 15 9a.97.97 0 0 1 .288-.713A.967.967 0 0 1 16 8h2c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v11a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 23H6Z"/></svg>', ms = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 21a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 3 19V5c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 3h6a.97.97 0 0 1 .713.287A.97.97 0 0 1 12 4a.97.97 0 0 1-.287.712A.968.968 0 0 1 11 5H5v14h14v-6c0-.283.096-.521.288-.713A.967.967 0 0 1 20 12a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v6a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 19 21H5Zm4-6a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7L17.6 5H15a.965.965 0 0 1-.712-.288A.965.965 0 0 1 14 4c0-.283.096-.521.288-.713A.967.967 0 0 1 15 3h5a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v5c0 .283-.096.52-.288.712A.965.965 0 0 1 20 10a.965.965 0 0 1-.712-.288A.965.965 0 0 1 19 9V6.4l-8.625 8.625a.918.918 0 0 1-.675.275.96.96 0 0 1-.7-.3Z"/></svg>', fs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2 3a1 1 0 0 1 1-1h18a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm0 18a1 1 0 0 1 1-1h18a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M2 8a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8Zm2 7V9h16v6H4Z" clip-rule="evenodd"/></svg>', bs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" stroke="#1D2634" stroke-linejoin="round" stroke-width="2" d="M3 16V8h18v8H3Z"/></svg>', ws = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10.75 19c.633 0 1.167-.217 1.6-.65.433-.433.65-.967.65-1.6V13h2a.968.968 0 0 0 .713-.288A.967.967 0 0 0 16 12a.97.97 0 0 0-.287-.713A.97.97 0 0 0 15 11h-2a.967.967 0 0 0-.712.287A.968.968 0 0 0 12 12v2.875a1.845 1.845 0 0 0-.587-.288 2.466 2.466 0 0 0-.663-.087c-.633 0-1.167.217-1.6.65-.433.433-.65.967-.65 1.6 0 .633.217 1.167.65 1.6.433.433.967.65 1.6.65ZM6 22c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 20V4c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 2h7.175a1.978 1.978 0 0 1 1.4.575l4.85 4.85a1.978 1.978 0 0 1 .575 1.4V20a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 22H6Zm7-18H6v16h12V9h-4a.965.965 0 0 1-.712-.288A.965.965 0 0 1 13 8V4Z"/></svg>', xs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7.468 17.675h9.093c.182 0 .318-.092.41-.275a.475.475 0 0 0-.046-.525L14.424 13.2a.42.42 0 0 0-.364-.2.42.42 0 0 0-.364.2l-2.364 3.475L9.65 14.2a.42.42 0 0 0-.364-.2.42.42 0 0 0-.363.2l-1.82 2.675a.475.475 0 0 0-.045.525c.091.183.228.275.41.275Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M4.588 21.413C4.979 21.804 5.45 22 6 22h12a1.93 1.93 0 0 0 1.413-.587A1.93 1.93 0 0 0 20 20V8.825a1.978 1.978 0 0 0-.575-1.4l-4.85-4.85a1.978 1.978 0 0 0-1.4-.575H6c-.55 0-1.02.196-1.412.587A1.927 1.927 0 0 0 4 4v16c0 .55.196 1.021.588 1.413ZM13 4v4c0 .283.096.52.288.712A.965.965 0 0 0 14 9h4v11H6V4h7Z" clip-rule="evenodd"/></svg>', As = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M9 15H8v1.5a.48.48 0 0 1-.15.35.48.48 0 0 1-.7 0A.48.48 0 0 1 7 16.5v-4c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15H9a.97.97 0 0 1 .713.287A.97.97 0 0 1 10 13v1a.97.97 0 0 1-.287.712A.968.968 0 0 1 9 15Zm-1-2v1h1v-1H8Zm5.713 3.712A.968.968 0 0 1 13 17h-1.5a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-4c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15H13a.97.97 0 0 1 .713.287A.97.97 0 0 1 14 13v3a.97.97 0 0 1-.287.712ZM12 13v3h1v-3h-1Z" clip-rule="evenodd"/><path fill="inherit" stroke="none" d="M16.5 15H16v1.5a.48.48 0 0 1-.15.35.48.48 0 0 1-.7 0 .48.48 0 0 1-.15-.35v-4c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15a.48.48 0 0 1 0 .7.48.48 0 0 1-.35.15H16v1h.5c.133 0 .25.05.35.15a.48.48 0 0 1 0 .7.48.48 0 0 1-.35.15Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M4.588 21.413C4.979 21.804 5.45 22 6 22h12a1.93 1.93 0 0 0 1.413-.587A1.93 1.93 0 0 0 20 20V8.825a1.978 1.978 0 0 0-.575-1.4l-4.85-4.85a1.978 1.978 0 0 0-1.4-.575H6c-.55 0-1.02.196-1.412.587A1.927 1.927 0 0 0 4 4v16c0 .55.196 1.021.588 1.413ZM13 4v4c0 .283.096.52.288.712A.965.965 0 0 0 14 9h4v11H6V4h7Z" clip-rule="evenodd"/></svg>', ys = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.55 18.176 4.15-2.65c.15-.1.267-.225.35-.375a.964.964 0 0 0 0-.95 1.07 1.07 0 0 0-.35-.375l-4.15-2.65a.952.952 0 0 0-1.025-.05c-.35.183-.525.475-.525.875v5.35c0 .4.175.692.525.875a.952.952 0 0 0 1.025-.05Z"/><path fill="inherit" stroke="none" fill-rule="evenodd" d="M4.588 21.413C4.979 21.804 5.45 22 6 22h12a1.93 1.93 0 0 0 1.413-.587A1.93 1.93 0 0 0 20 20V8.825a1.978 1.978 0 0 0-.575-1.4l-4.85-4.85a1.978 1.978 0 0 0-1.4-.575H6c-.55 0-1.02.196-1.412.587A1.927 1.927 0 0 0 4 4v16c0 .55.196 1.021.588 1.413ZM6 4h7v4c0 .283.096.52.288.712A.965.965 0 0 0 14 9h4v11H6V4Z" clip-rule="evenodd"/></svg>', ks = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M4.588 21.413C4.979 21.804 5.45 22 6 22h12a1.93 1.93 0 0 0 1.413-.587A1.93 1.93 0 0 0 20 20V8.825a1.978 1.978 0 0 0-.575-1.4l-4.85-4.85a1.978 1.978 0 0 0-1.4-.575H6c-.55 0-1.02.196-1.412.587A1.927 1.927 0 0 0 4 4v16c0 .55.196 1.021.588 1.413ZM13 4v4c0 .283.096.52.288.712A.965.965 0 0 0 14 9h4v3H6V4h7ZM6 18v2h5v-2H6Zm0-2h5v-2H6v2Zm7 2v2h5v-2h-5Zm5-2h-5v-2h5v2Z" clip-rule="evenodd"/></svg>', _s = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 18h6a.968.968 0 0 0 .713-.288A.967.967 0 0 0 16 17a.967.967 0 0 0-.287-.712A.968.968 0 0 0 15 16H9a.965.965 0 0 0-.712.288A.965.965 0 0 0 8 17c0 .283.096.52.288.712A.965.965 0 0 0 9 18Zm0-4h6a.968.968 0 0 0 .713-.288A.967.967 0 0 0 16 13a.97.97 0 0 0-.287-.713A.97.97 0 0 0 15 12H9a.967.967 0 0 0-.712.287A.968.968 0 0 0 8 13c0 .283.096.52.288.712A.965.965 0 0 0 9 14Zm-3 8c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 20V4c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 6 2h7.175a1.978 1.978 0 0 1 1.4.575l4.85 4.85a1.978 1.978 0 0 1 .575 1.4V20a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 22H6Zm7-14V4H6v16h12V9h-4a.965.965 0 0 1-.712-.288A.965.965 0 0 1 13 8Z"/></svg>', Zs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15 2c-2.71 0-5.05 1.54-6.22 3.78a7.062 7.062 0 0 0-3 3A7.014 7.014 0 0 0 2 15c0 3.87 3.13 7 7 7 2.71 0 5.05-1.54 6.22-3.78a7.062 7.062 0 0 0 3-3A7.014 7.014 0 0 0 22 9c0-3.87-3.13-7-7-7ZM9 20a5.002 5.002 0 0 1-4-8c0 3.87 3.13 7 7 7-.84.63-1.88 1-3 1Zm3-3a5.002 5.002 0 0 1-4-8c0 3.86 3.13 6.99 7 7-.84.63-1.88 1-3 1Zm4.7-3.3c-.53.19-1.1.3-1.7.3-2.76 0-5-2.24-5-5 0-.6.11-1.17.3-1.7.53-.19 1.1-.3 1.7-.3 2.76 0 5 2.24 5 5 0 .6-.11 1.17-.3 1.7ZM19 12c0-3.86-3.13-6.99-7-7a5.002 5.002 0 0 1 7 7Z"/></svg>', Cs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 18a.965.965 0 0 1-.712-.288A.965.965 0 0 1 10 17c0-.283.096-.52.288-.712A.965.965 0 0 1 11 16h2c.283 0 .521.096.713.288A.967.967 0 0 1 14 17a.97.97 0 0 1-.287.712A.968.968 0 0 1 13 18h-2ZM4 8a.967.967 0 0 1-.712-.287A.968.968 0 0 1 3 7c0-.283.096-.521.288-.713A.967.967 0 0 1 4 6h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 20 8H4Zm3 5a.968.968 0 0 1-.713-.288A.967.967 0 0 1 6 12a.97.97 0 0 1 .287-.713A.97.97 0 0 1 7 11h10a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.965.965 0 0 1 17 13H7Z"/></svg>', $s = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3.25 9.65a.498.498 0 0 1-.212-.312c-.025-.125.012-.255.112-.388a10.691 10.691 0 0 1 3.888-3.3 10.954 10.954 0 0 1 4.987-1.175c1.767 0 3.433.38 5 1.138A10.448 10.448 0 0 1 20.95 8.9c.117.15.154.283.113.4a.686.686 0 0 1-.213.3.48.48 0 0 1-.35.113c-.133-.009-.25-.08-.35-.213a9.044 9.044 0 0 0-3.537-2.987 10.472 10.472 0 0 0-4.588-1.038 10.23 10.23 0 0 0-4.55 1.038A9.31 9.31 0 0 0 3.95 9.5c-.1.15-.217.233-.35.25a.44.44 0 0 1-.35-.1Zm11.6 12.325c-1.733-.433-3.15-1.296-4.25-2.588-1.1-1.291-1.65-2.87-1.65-4.737 0-.833.3-1.533.9-2.1.6-.567 1.325-.85 2.175-.85.85 0 1.575.283 2.175.85.6.567.9 1.267.9 2.1 0 .55.208 1.012.625 1.388.417.375.908.562 1.475.562.567 0 1.05-.188 1.45-.563.4-.374.6-.837.6-1.387 0-1.933-.708-3.558-2.125-4.875C15.708 8.458 14.017 7.8 12.05 7.8s-3.658.658-5.075 1.975c-1.417 1.317-2.125 2.933-2.125 4.85 0 .4.038.9.113 1.5.075.6.254 1.3.537 2.1.05.15.046.283-.012.4a.504.504 0 0 1-.288.25.496.496 0 0 1-.65-.3 9.959 9.959 0 0 1-.537-1.938 11.92 11.92 0 0 1-.163-1.987c0-2.217.804-4.075 2.413-5.575 1.608-1.5 3.529-2.25 5.762-2.25 2.25 0 4.183.75 5.8 2.25s2.425 3.358 2.425 5.575c0 .833-.296 1.53-.887 2.088-.592.558-1.313.837-2.163.837-.85 0-1.58-.28-2.188-.837-.608-.559-.912-1.255-.912-2.088a1.81 1.81 0 0 0-.612-1.388 2.088 2.088 0 0 0-1.463-.562 2.09 2.09 0 0 0-1.463.563 1.81 1.81 0 0 0-.612 1.387c0 1.617.48 2.967 1.438 4.05.958 1.083 2.195 1.842 3.712 2.275.15.05.25.133.3.25a.574.574 0 0 1-.175.675c-.1.083-.225.108-.375.075ZM6.5 4.425a.465.465 0 0 1-.4.063.49.49 0 0 1-.3-.263.482.482 0 0 1-.05-.362c.033-.109.117-.205.25-.288.933-.5 1.908-.883 2.925-1.15 1.017-.267 2.05-.4 3.1-.4 1.067 0 2.108.13 3.125.388s2 .629 2.95 1.112c.15.083.237.183.262.3a.568.568 0 0 1-.037.35.57.57 0 0 1-.25.275c-.117.067-.258.058-.425-.025a13.296 13.296 0 0 0-2.737-1.037 11.57 11.57 0 0 0-2.888-.363c-.967 0-1.917.113-2.85.338-.933.225-1.825.579-2.675 1.062Zm2.95 17.2c-.983-1.033-1.737-2.087-2.262-3.163-.525-1.075-.788-2.345-.788-3.812 0-1.517.55-2.796 1.65-3.838 1.1-1.041 2.425-1.562 3.975-1.562 1.55 0 2.883.52 4 1.563 1.117 1.041 1.675 2.32 1.675 3.837a.49.49 0 0 1-.137.362.49.49 0 0 1-.363.138.502.502 0 0 1-.35-.138.468.468 0 0 1-.15-.362c0-1.25-.462-2.296-1.387-3.138-.926-.841-2.021-1.262-3.288-1.262s-2.354.42-3.262 1.262C7.854 12.354 7.4 13.4 7.4 14.65c0 1.35.233 2.496.7 3.437.467.942 1.15 1.888 2.05 2.838a.48.48 0 0 1 0 .7.48.48 0 0 1-.7 0Zm7.55-1.7c-1.483 0-2.77-.5-3.863-1.5-1.091-1-1.637-2.258-1.637-3.775 0-.133.046-.25.137-.35.092-.1.213-.15.363-.15s.27.05.363.15c.091.1.137.217.137.35 0 1.25.45 2.275 1.35 3.075a4.593 4.593 0 0 0 3.575 1.175c.183-.017.375-.042.575-.075a.473.473 0 0 1 .387.063.53.53 0 0 1 .213.337.375.375 0 0 1-.075.35.6.6 0 0 1-.325.2c-.3.083-.563.13-.787.138-.226.008-.363.012-.413.012Z"/></svg>', Ms = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 21a.967.967 0 0 1-.713-.288A.968.968 0 0 1 5 20V5c0-.283.096-.52.287-.713A.968.968 0 0 1 6 4h7.175a.96.96 0 0 1 .625.225c.183.15.3.342.35.575L14.4 6H19a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v8a.97.97 0 0 1-.288.713A.968.968 0 0 1 19 16h-5.175a.96.96 0 0 1-.625-.225.99.99 0 0 1-.35-.575L12.6 14H7v6a.97.97 0 0 1-.287.712A.967.967 0 0 1 6 21Zm8.65-7H18V8h-5.25l-.4-2H7v6h7.25l.4 2Z"/></svg>', Vs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 21a.967.967 0 0 1-.713-.288A.968.968 0 0 1 5 20V5c0-.283.096-.52.287-.713A.968.968 0 0 1 6 4h7.175a.96.96 0 0 1 .625.225c.183.15.3.342.35.575L14.4 6H19a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v8a.97.97 0 0 1-.288.713A.968.968 0 0 1 19 16h-5.175a.96.96 0 0 1-.625-.225.99.99 0 0 1-.35-.575L12.6 14H7v6a.97.97 0 0 1-.287.712A.967.967 0 0 1 6 21Z"/></svg>', Hs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 16h2v-2h2v-2h-2v-2h-2v2h-2v2h2v2ZM4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h6l2 2h8c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4ZM4 6v12h16V8h-8.825l-2-2H4Z"/></svg>', Ss = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 16h2v-2h2v-2h-2v-2h-2v2h-2v2h2v2ZM4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h6l2 2h8c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4Z"/></svg>', Ds = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h5.175a1.975 1.975 0 0 1 1.4.575L12 6h8c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v3.25c-.3-.217-.617-.4-.95-.55a9.791 9.791 0 0 0-1.05-.4V8H4v10h7.075a7.013 7.013 0 0 0 .6 2H4Zm10 0v-.55c0-.75.367-1.346 1.1-1.787.733-.442 1.7-.663 2.9-.663s2.167.22 2.9.663c.733.441 1.1 1.037 1.1 1.787V20h-8Zm4-4c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 16 14c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 18 12c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 18 16Z"/></svg>', Ls = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 20v-.55c0-.75.367-1.346 1.1-1.787.733-.442 1.7-.663 2.9-.663s2.167.22 2.9.663c.733.441 1.1 1.037 1.1 1.787V20h-8Zm4-4c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 16 14c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 18 12c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 18 16ZM4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h5.175a1.975 1.975 0 0 1 1.4.575L12 6h8c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v3.25a6.517 6.517 0 0 0-1.9-.938A7.162 7.162 0 0 0 18 10c-1.95 0-3.604.68-4.963 2.037C11.68 13.396 11 15.05 11 17c0 .533.054 1.05.162 1.55.109.5.28.983.513 1.45H4Z"/></svg>', Os = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 2 18V6c0-.55.196-1.02.588-1.412A1.923 1.923 0 0 1 4 4h5.175a1.978 1.978 0 0 1 1.4.575L12 6h8c.55 0 1.021.196 1.413.588.391.391.587.862.587 1.412v10a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 20 20H4ZM4 6v12h16V8h-8.825l-2-2H4Z"/></svg>', Es = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h6l2 2h8c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 20H4Z"/></svg>', Ts = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 17v2h16v-2H4Zm0 4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 19V8c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 6h2.2a1.398 1.398 0 0 1-.163-.475A3.538 3.538 0 0 1 6 5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 9 2c.5 0 .963.13 1.387.388.425.258.796.579 1.113.962L12 4l.5-.65c.3-.4.667-.725 1.1-.975.433-.25.9-.375 1.4-.375.833 0 1.542.292 2.125.875S18 4.167 18 5c0 .183-.012.358-.038.525A1.38 1.38 0 0 1 17.8 6H20c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 21H4Zm0-7h16V8h-5.1l1.525 2.075a.8.8 0 0 1 .137.688 1.33 1.33 0 0 1-.362.662.875.875 0 0 1-.725.175 1.035 1.035 0 0 1-.65-.4L12 7.4l-2.825 3.825c-.183.233-.413.37-.688.412a.758.758 0 0 1-.687-.212.957.957 0 0 1-.412-.65.913.913 0 0 1 .187-.725L9.05 8H4v6Zm5-8c.283 0 .52-.096.713-.287A.968.968 0 0 0 10 5a.968.968 0 0 0-.287-.713A.968.968 0 0 0 9 4a.968.968 0 0 0-.713.287A.968.968 0 0 0 8 5c0 .283.096.52.287.713.192.191.43.287.713.287Zm6 0c.283 0 .52-.096.713-.287A.967.967 0 0 0 16 5a.967.967 0 0 0-.287-.713A.968.968 0 0 0 15 4a.968.968 0 0 0-.713.287A.967.967 0 0 0 14 5c0 .283.096.52.287.713.192.191.43.287.713.287Z"/></svg>', zs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 18a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 17c0-.283.096-.52.288-.712A.965.965 0 0 1 4 16h16c.283 0 .52.096.712.288A.965.965 0 0 1 21 17c0 .283-.096.52-.288.712A.965.965 0 0 1 20 18H4Zm0-5a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 12c0-.283.096-.521.288-.713A.967.967 0 0 1 4 11h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.965.965 0 0 1 20 13H4Zm0-5a.967.967 0 0 1-.712-.287A.968.968 0 0 1 3 7c0-.283.096-.521.288-.713A.967.967 0 0 1 4 6h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 20 8H4Z"/></svg>', Is = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7.25 20a.947.947 0 0 1-.775-.375.878.878 0 0 1-.175-.825L7 16H4.775a.96.96 0 0 1-.8-.387.947.947 0 0 1-.175-.863.883.883 0 0 1 .35-.55c.183-.133.392-.2.625-.2H7.5l1-4H5.775a.96.96 0 0 1-.8-.387.947.947 0 0 1-.175-.863.883.883 0 0 1 .35-.55c.183-.133.392-.2.625-.2H9l.825-3.275A.915.915 0 0 1 10.75 4c.317 0 .575.125.775.375.2.25.258.525.175.825L11 8h4l.825-3.275A.915.915 0 0 1 16.75 4c.317 0 .575.125.775.375.2.25.258.525.175.825L17 8h2.225c.333 0 .6.13.8.387.2.259.258.546.175.863a.883.883 0 0 1-.35.55c-.183.133-.392.2-.625.2H16.5l-1 4h2.725c.333 0 .6.13.8.387.2.259.258.546.175.863a.883.883 0 0 1-.35.55c-.183.133-.392.2-.625.2H15l-.825 3.275a.915.915 0 0 1-.925.725.948.948 0 0 1-.775-.375.878.878 0 0 1-.175-.825L13 16H9l-.825 3.275A.915.915 0 0 1 7.25 20Zm2.25-6h4l1-4h-4l-1 4Z"/></svg>', Ps = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.65 20.8-1.725-1.575a68.355 68.355 0 0 1-4.788-4.813C2.714 12.822 2 11.067 2 9.15c0-1.567.525-2.875 1.575-3.925C4.625 4.175 5.933 3.65 7.5 3.65c.883 0 1.717.188 2.5.563.783.375 1.45.887 2 1.537a5.956 5.956 0 0 1 2-1.537 5.717 5.717 0 0 1 2.5-.563c1.567 0 2.875.525 3.925 1.575C21.475 6.275 22 7.583 22 9.15c0 1.917-.708 3.675-2.125 5.275a59.847 59.847 0 0 1-4.825 4.825l-1.7 1.55a1.89 1.89 0 0 1-1.35.55 1.89 1.89 0 0 1-1.35-.55Zm.4-13.05c-.483-.683-1-1.204-1.55-1.562-.55-.359-1.217-.538-2-.538-1 0-1.833.333-2.5 1s-1 1.5-1 2.5c0 .867.308 1.787.925 2.762a19.094 19.094 0 0 0 2.213 2.838 38.412 38.412 0 0 0 2.65 2.575c.908.8 1.645 1.458 2.212 1.975a180.24 180.24 0 0 1 2.213-1.975c.908-.8 1.791-1.658 2.65-2.575a19.092 19.092 0 0 0 2.212-2.838c.617-.975.925-1.895.925-2.762 0-1-.333-1.833-1-2.5s-1.5-1-2.5-1c-.783 0-1.45.18-2 .538-.55.358-1.067.879-1.55 1.562a1.138 1.138 0 0 1-.95.5 1.138 1.138 0 0 1-.95-.5Z"/></svg>', Bs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.65 20.8-1.725-1.575a68.355 68.355 0 0 1-4.788-4.813C2.714 12.822 2 11.067 2 9.15c0-1.567.525-2.875 1.575-3.925C4.625 4.175 5.933 3.65 7.5 3.65c.883 0 1.717.188 2.5.563.783.375 1.45.887 2 1.537a5.956 5.956 0 0 1 2-1.537 5.717 5.717 0 0 1 2.5-.563c1.567 0 2.875.525 3.925 1.575C21.475 6.275 22 7.583 22 9.15c0 1.917-.708 3.675-2.125 5.275a59.847 59.847 0 0 1-4.825 4.825l-1.7 1.55c-.383.35-.833.525-1.35.525-.517 0-.967-.175-1.35-.525Z"/></svg>', Fs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.95 18c.35 0 .646-.121.888-.363.241-.241.362-.537.362-.887s-.12-.646-.362-.887a1.208 1.208 0 0 0-.888-.363c-.35 0-.646.121-.888.363a1.206 1.206 0 0 0-.362.887c0 .35.12.646.362.887.242.242.538.363.888.363Zm.15-10.3c.467 0 .842.129 1.125.387.283.259.425.596.425 1.013 0 .283-.096.57-.287.862a4.896 4.896 0 0 1-.813.913c-.5.433-.867.85-1.1 1.25-.233.4-.35.8-.35 1.2a.76.76 0 0 0 .263.587.904.904 0 0 0 1.237-.012c.183-.167.3-.375.35-.625.05-.283.163-.546.338-.787.175-.242.462-.555.862-.938.517-.483.88-.925 1.088-1.325.208-.4.312-.842.312-1.325 0-.85-.32-1.546-.962-2.088C13.946 6.271 13.117 6 12.1 6c-.7 0-1.32.133-1.862.4a2.844 2.844 0 0 0-1.263 1.225.99.99 0 0 0-.125.637c.033.209.15.38.35.513a.965.965 0 0 0 .713.125 1 1 0 0 0 .637-.425c.183-.25.404-.442.663-.575.258-.133.554-.2.887-.2ZM12 22a9.671 9.671 0 0 1-3.875-.788 10.238 10.238 0 0 1-3.187-2.137 9.983 9.983 0 0 1-2.15-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 9.983 9.983 0 0 1 2.15-3.175c.908-.9 1.97-1.613 3.187-2.138A9.681 9.681 0 0 1 12 2c1.4 0 2.708.262 3.925.787A10.105 10.105 0 0 1 19.1 4.925c.9.9 1.608 1.958 2.125 3.175A9.873 9.873 0 0 1 22 12a9.873 9.873 0 0 1-.775 3.9 9.927 9.927 0 0 1-2.125 3.175c-.9.9-1.958 1.612-3.175 2.137C14.708 21.737 13.4 22 12 22Zm0-2c2.233 0 4.125-.779 5.675-2.337C19.225 16.104 20 14.217 20 12s-.775-4.104-2.325-5.663C16.125 4.779 14.233 4 12 4c-2.183 0-4.063.779-5.638 2.337C4.787 7.896 4 9.783 4 12s.787 4.104 2.362 5.663C7.937 19.221 9.817 20 12 20Z"/></svg>', js = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11.95 18c.35 0 .646-.121.888-.363.241-.241.362-.537.362-.887s-.12-.646-.362-.887a1.208 1.208 0 0 0-.888-.363c-.35 0-.646.121-.888.363a1.206 1.206 0 0 0-.362.887c0 .35.12.646.362.887.242.242.538.363.888.363Zm.15-10.3c.467 0 .842.129 1.125.387.283.259.425.596.425 1.013 0 .283-.096.57-.287.862a4.896 4.896 0 0 1-.813.913c-.5.433-.867.85-1.1 1.25-.233.4-.35.8-.35 1.2a.76.76 0 0 0 .263.587.904.904 0 0 0 1.237-.012c.183-.167.3-.375.35-.625.05-.283.163-.546.338-.787.175-.242.462-.555.862-.938.517-.483.88-.925 1.088-1.325.208-.4.312-.842.312-1.325 0-.85-.32-1.546-.962-2.088C13.946 6.271 13.117 6 12.1 6c-.7 0-1.32.133-1.862.4a2.844 2.844 0 0 0-1.263 1.225.99.99 0 0 0-.125.637c.033.209.15.38.35.513a.965.965 0 0 0 .713.125 1 1 0 0 0 .637-.425c.183-.25.404-.442.663-.575.258-.133.554-.2.887-.2ZM12 22a9.671 9.671 0 0 1-3.875-.788 10.238 10.238 0 0 1-3.187-2.137 9.983 9.983 0 0 1-2.15-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 9.983 9.983 0 0 1 2.15-3.175c.908-.9 1.97-1.613 3.187-2.138A9.681 9.681 0 0 1 12 2c1.4 0 2.708.262 3.925.787A10.105 10.105 0 0 1 19.1 4.925c.9.9 1.608 1.958 2.125 3.175A9.873 9.873 0 0 1 22 12a9.873 9.873 0 0 1-.775 3.9 9.927 9.927 0 0 1-2.125 3.175c-.9.9-1.958 1.612-3.175 2.137C14.708 21.737 13.4 22 12 22Z"/></svg>', Rs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 20H6a.968.968 0 0 1-.713-.288A.968.968 0 0 1 5 19v-7H3.3a.468.468 0 0 1-.475-.325c-.083-.217-.033-.4.15-.55l8.35-7.525A.918.918 0 0 1 12 3.325c.267 0 .492.092.675.275 3.663 3.305 2.544 2.293 8.35 7.525.183.15.233.333.15.55A.469.469 0 0 1 20.7 12H19v7c0 .283-.096.52-.288.712A.968.968 0 0 1 18 20h-5v-6h-2v6Zm-4-2h2v-5c0-.283.096-.52.287-.713A.967.967 0 0 1 10 12h4c.283 0 .52.096.713.287.191.192.287.43.287.713v5h2v-7.8l-5-4.5-5 4.5V18Z"/></svg>', Ns = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 20h4v-5c0-.283.096-.52.287-.713A.967.967 0 0 1 11 14h2c.283 0 .52.096.713.287.191.192.287.43.287.713v5h4c.283 0 .52-.096.712-.288A.968.968 0 0 0 19 19v-7h1.7a.469.469 0 0 0 .475-.325c.083-.217.033-.4-.15-.55-3.313-2.986-5.056-4.553-8.35-7.525A.918.918 0 0 0 12 3.325a.918.918 0 0 0-.675.275l-8.35 7.525c-.183.15-.233.333-.15.55A.468.468 0 0 0 3.3 12H5v7c0 .283.096.52.287.712.192.192.43.288.713.288Z"/></svg>', Us = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4.5 11h-2V9H1v6h1.5v-2.5h2V15H6V9H4.5v2Zm2.5-.5h1.5V15H10v-4.5h1.5V9H7v1.5Zm5.5 0H14V15h1.5v-4.5H17V9h-4.5v1.5Zm9-1.5H18v6h1.5v-2h2c.8 0 1.5-.7 1.5-1.5v-1c0-.8-.7-1.5-1.5-1.5Zm0 2.5h-2v-1h2v1Z"/></svg>', Ws = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16v-4.025a.928.928 0 0 0-.287-.7A.993.993 0 0 0 12 11a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 12v4.025c0 .283.096.517.288.7A.99.99 0 0 0 12 17Zm0-8a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 8a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8c0 .283.096.52.288.712A.965.965 0 0 0 12 9Zm0 13a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Zm0-2c2.217 0 4.104-.779 5.663-2.337C19.221 16.104 20 14.217 20 12s-.779-4.104-2.337-5.663C16.104 4.779 14.217 4 12 4s-4.104.779-5.662 2.337C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.221 9.783 20 12 20Z"/></svg>', qs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 16v-4.025a.928.928 0 0 0-.287-.7A.993.993 0 0 0 12 11a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 12v4.025c0 .283.096.517.288.7A.99.99 0 0 0 12 17Zm0-8a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 8a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 7a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 8c0 .283.096.52.288.712A.965.965 0 0 0 12 9Zm0 13a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Z"/></svg>', Ks = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 20c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 1 18c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 3 16h.263a.64.64 0 0 1 .237.05l4.55-4.55a.641.641 0 0 1-.05-.238V11c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 10 9c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413 0 .033-.017.2-.05.5l2.55 2.55a.642.642 0 0 1 .238-.05h.524c.075 0 .155.017.238.05l3.55-3.55a.642.642 0 0 1-.05-.238V10c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 21 8c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 21 12h-.262a.642.642 0 0 1-.238-.05l-3.55 3.55c.033.083.05.163.05.238V16a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 15 18c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 13 16v-.262c0-.075.017-.155.05-.238l-2.55-2.55a.642.642 0 0 1-.238.05H10c-.033 0-.2-.017-.5-.05L4.95 17.5c.033.083.05.163.05.238V18c0 .55-.196 1.021-.588 1.413A1.925 1.925 0 0 1 3 20ZM4 9.975l-.625-1.35L2.025 8l1.35-.625L4 6.025l.625 1.35L5.975 8l-1.35.625L4 9.975ZM15 9l-.95-2.05L12 6l2.05-.95L15 3l.95 2.05L18 6l-2.05.95L15 9Z"/></svg>', Ys = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3.7 11a.94.94 0 0 1-.863-.488.937.937 0 0 1-.012-.987l3.3-5.95a.973.973 0 0 1 .375-.4c.15-.083.317-.125.5-.125s.35.042.5.125c.15.083.275.217.375.4l3.3 5.95a.937.937 0 0 1-.013.987c-.191.325-.479.488-.862.488H3.7ZM7 21c-1.1 0-2.042-.392-2.825-1.175C3.392 19.042 3 18.1 3 17c0-1.117.392-2.063 1.175-2.838C4.958 13.387 5.9 13 7 13s2.042.392 2.825 1.175C10.608 14.958 11 15.9 11 17s-.392 2.042-1.175 2.825C9.042 20.608 8.1 21 7 21Zm0-2c.55 0 1.02-.196 1.412-.587C8.804 18.02 9 17.55 9 17s-.196-1.02-.588-1.412A1.926 1.926 0 0 0 7 15c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 5 17c0 .55.196 1.02.588 1.413.391.391.862.587 1.412.587ZM5.4 9h3.2L7 6.125 5.4 9ZM14 21a.968.968 0 0 1-.713-.288A.968.968 0 0 1 13 20v-6c0-.283.096-.52.287-.713A.968.968 0 0 1 14 13h6a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v6c0 .283-.096.52-.288.712A.968.968 0 0 1 20 21h-6Zm1-2h4v-4h-4v4Zm2-8.375c-.133 0-.267-.02-.4-.063a1.013 1.013 0 0 1-.35-.187 89.717 89.717 0 0 1-2.213-1.9c-.558-.5-.987-.933-1.287-1.3-.3-.367-.5-.7-.6-1-.1-.3-.15-.633-.15-1 0-.75.262-1.383.787-1.9.525-.517 1.18-.775 1.963-.775.45 0 .87.104 1.262.313.392.208.721.495.988.862.267-.367.596-.654.988-.862A2.648 2.648 0 0 1 19.25 2.5c.783 0 1.438.258 1.962.775.526.517.788 1.15.788 1.9 0 .35-.046.675-.137.975-.092.3-.288.633-.588 1s-.73.804-1.287 1.313a72.3 72.3 0 0 1-2.213 1.912.999.999 0 0 1-.362.188c-.142.041-.28.062-.413.062Zm0-2.25c1.2-1 2-1.708 2.4-2.125.4-.417.6-.758.6-1.025a.737.737 0 0 0-.188-.525c-.125-.133-.295-.2-.512-.2a.961.961 0 0 0-.488.138c-.158.091-.354.254-.587.487L17 6.3l-1.225-1.175c-.233-.233-.43-.396-.588-.487A.961.961 0 0 0 14.7 4.5c-.217 0-.387.067-.512.2a.737.737 0 0 0-.188.525c0 .267.2.608.6 1.025.4.417 1.2 1.125 2.4 2.125Z"/></svg>', Gs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3.725 11c-.383 0-.67-.162-.862-.488a.937.937 0 0 1-.013-.987l3.3-5.95a.973.973 0 0 1 .375-.4c.15-.083.317-.125.5-.125s.35.042.5.125c.15.083.275.217.375.4l3.3 5.95a.937.937 0 0 1-.012.987.94.94 0 0 1-.863.488h-6.6Zm3.3 10c-1.1 0-2.042-.392-2.825-1.175C3.417 19.042 3.025 18.1 3.025 17c0-1.117.392-2.063 1.175-2.838C4.983 13.387 5.925 13 7.025 13s2.042.392 2.825 1.175c.783.783 1.175 1.725 1.175 2.825s-.392 2.042-1.175 2.825C9.067 20.608 8.125 21 7.025 21Zm7 0a.968.968 0 0 1-.713-.288.968.968 0 0 1-.287-.712v-6c0-.283.096-.52.287-.713a.968.968 0 0 1 .713-.287h6c.283 0 .52.096.713.287.191.192.287.43.287.713v6a.97.97 0 0 1-.287.712.967.967 0 0 1-.713.288h-6Zm3-10.375c-.133 0-.267-.02-.4-.063a1.013 1.013 0 0 1-.35-.187 90.094 90.094 0 0 1-2.212-1.9c-.559-.5-.988-.933-1.288-1.3-.3-.367-.5-.7-.6-1-.1-.3-.15-.633-.15-1 0-.75.262-1.383.787-1.9.526-.517 1.18-.775 1.963-.775.45 0 .87.104 1.263.313.391.208.72.495.987.862.267-.367.596-.654.988-.862a2.648 2.648 0 0 1 1.262-.313c.783 0 1.438.258 1.963.775.524.517.787 1.15.787 1.9 0 .35-.046.675-.137.975-.092.3-.288.633-.588 1s-.73.804-1.288 1.313a72.3 72.3 0 0 1-2.212 1.912.999.999 0 0 1-.363.188 1.477 1.477 0 0 1-.412.062Z"/></svg>', Qs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H1a1 1 0 1 0 0 2h22a1 1 0 1 0 0-2h-3ZM4 6h16v10H4V6Z"/></svg>', Xs = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M21.99 16c0 1.1-.89 2-1.99 2h3a1 1 0 1 1 0 2H1a1 1 0 1 1 0-2h3c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2l-.01 10Z"/></svg>', Js = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 10 20h4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 22Zm-3-3a.967.967 0 0 1-.713-.288A.968.968 0 0 1 8 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 9 17h6a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 15 19H9Zm-.75-3a7.662 7.662 0 0 1-2.737-2.75A7.278 7.278 0 0 1 4.5 9.5c0-2.083.73-3.854 2.188-5.313C8.146 2.73 9.916 2 12 2s3.854.73 5.313 2.188C18.77 5.646 19.5 7.417 19.5 9.5c0 1.35-.337 2.6-1.012 3.75A7.662 7.662 0 0 1 15.75 16h-7.5Zm.6-2h6.3a5.548 5.548 0 0 0 1.737-1.975A5.383 5.383 0 0 0 17.5 9.5c0-1.533-.533-2.833-1.6-3.9C14.833 4.533 13.533 4 12 4s-2.833.533-3.9 1.6C7.033 6.667 6.5 7.967 6.5 9.5c0 .9.204 1.742.612 2.525A5.549 5.549 0 0 0 8.85 14Z"/></svg>', e8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 10 20h4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 22Zm-3-3a.967.967 0 0 1-.713-.288A.968.968 0 0 1 8 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 9 17h6a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 15 19H9Zm-.75-3a7.662 7.662 0 0 1-2.737-2.75A7.278 7.278 0 0 1 4.5 9.5c0-2.083.73-3.854 2.188-5.313C8.146 2.73 9.916 2 12 2s3.854.73 5.313 2.188C18.77 5.646 19.5 7.417 19.5 9.5c0 1.35-.337 2.6-1.012 3.75A7.662 7.662 0 0 1 15.75 16h-7.5Z"/></svg>', t8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 17c-1.383 0-2.563-.488-3.538-1.463C2.487 14.563 2 13.383 2 12s.487-2.563 1.462-3.537C4.438 7.488 5.617 7 7 7h3c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 10 9H7c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 4 12c0 .833.292 1.542.875 2.125A2.893 2.893 0 0 0 7 15h3c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.712A.968.968 0 0 1 10 17H7Zm2-4a.967.967 0 0 1-.713-.287A.968.968 0 0 1 8 12c0-.283.096-.52.287-.713A.967.967 0 0 1 9 11h6c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.968.968 0 0 1 15 13H9Zm13-1h-2c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 17 9h-3a.968.968 0 0 1-.713-.287A.967.967 0 0 1 13 8c0-.283.096-.52.287-.713A.968.968 0 0 1 14 7h3c1.383 0 2.563.487 3.538 1.463C21.512 9.438 22 10.617 22 12Zm-4 8a.968.968 0 0 1-.712-.288A.968.968 0 0 1 17 19v-2h-2a.968.968 0 0 1-.713-.288A.968.968 0 0 1 14 16c0-.283.096-.52.287-.713A.968.968 0 0 1 15 15h2v-2a.97.97 0 0 1 .288-.713A.968.968 0 0 1 18 12a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2h2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.968.968 0 0 1 21 17h-2v2c0 .283-.096.52-.288.712A.968.968 0 0 1 18 20Z"/></svg>', s8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m15.825 13-2-2h2c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713a.968.968 0 0 1-.713.287Zm3.425 3.45-1.5-1.55a3.116 3.116 0 0 0 1.613-1.063A2.834 2.834 0 0 0 20 12c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 17 9h-3a.967.967 0 0 1-.713-.288A.967.967 0 0 1 13 8c0-.283.096-.52.287-.713A.967.967 0 0 1 14 7h3c1.383 0 2.563.48 3.538 1.437C21.512 9.396 22 10.583 22 12c0 .95-.25 1.83-.75 2.637a4.712 4.712 0 0 1-2 1.813Zm-.15 5.45-17-17a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.948.948 0 0 1 .275.7.949.949 0 0 1-.275.7.949.949 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM10 17H7c-1.383 0-2.563-.488-3.538-1.463C2.487 14.563 2 13.383 2 12c0-1.167.354-2.196 1.062-3.088A4.954 4.954 0 0 1 5.75 7.15L7.6 9H7c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 4 12c0 .833.292 1.542.875 2.125A2.893 2.893 0 0 0 7 15h3c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.712A.968.968 0 0 1 10 17Zm1.6-4H8.175a.968.968 0 0 1-.713-.287.968.968 0 0 1-.287-.713c0-.283.096-.52.287-.713A.967.967 0 0 1 8.175 11h1.45l1.975 2Z"/></svg>', r8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1ZM8 13h8v-2H8v2Zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5Z"/></svg>', o8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10 19a.965.965 0 0 1-.712-.288A.965.965 0 0 1 9 18c0-.283.096-.52.288-.712A.965.965 0 0 1 10 17h10c.283 0 .52.096.712.288A.965.965 0 0 1 21 18c0 .283-.096.52-.288.712A.965.965 0 0 1 20 19H10Zm0-6a.965.965 0 0 1-.712-.288A.965.965 0 0 1 9 12c0-.283.096-.521.288-.713A.967.967 0 0 1 10 11h10a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.965.965 0 0 1 20 13H10Zm0-6a.967.967 0 0 1-.712-.287A.968.968 0 0 1 9 6c0-.283.096-.521.288-.713A.967.967 0 0 1 10 5h10a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 20 7H10ZM5 20a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 3 18c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 16c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.021-.588 1.413A1.925 1.925 0 0 1 5 20Zm0-6c-.55 0-1.021-.196-1.413-.588A1.925 1.925 0 0 1 3 12c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 10c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.02-.588 1.412A1.923 1.923 0 0 1 5 14Zm0-6c-.55 0-1.021-.196-1.413-.588A1.925 1.925 0 0 1 3 6c0-.55.196-1.02.587-1.412A1.927 1.927 0 0 1 5 4c.55 0 1.02.196 1.412.588C6.804 4.979 7 5.45 7 6c0 .55-.196 1.02-.588 1.412A1.923 1.923 0 0 1 5 8Z"/></svg>', i8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22.95a.968.968 0 0 1-.713-.287.968.968 0 0 1-.287-.713v-1c-2.083-.233-3.87-1.096-5.362-2.587C4.146 16.87 3.283 15.083 3.05 13h-1a.967.967 0 0 1-.712-.287A.967.967 0 0 1 1.05 12a.97.97 0 0 1 .288-.713A.968.968 0 0 1 2.05 11h1c.233-2.083 1.096-3.87 2.588-5.362C7.129 4.146 8.917 3.283 11 3.05v-1a.97.97 0 0 1 .287-.712A.968.968 0 0 1 12 1.05a.97.97 0 0 1 .713.288.968.968 0 0 1 .287.712v1c2.083.233 3.87 1.096 5.363 2.588C19.854 7.129 20.716 8.917 20.95 11h1c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713a.967.967 0 0 1-.713.287h-1c-.233 2.083-1.096 3.87-2.587 5.363-1.492 1.491-3.28 2.354-5.363 2.587v1c0 .283-.096.52-.287.713a.967.967 0 0 1-.713.287ZM12 19c1.933 0 3.583-.683 4.95-2.05C18.317 15.583 19 13.933 19 12c0-1.933-.683-3.583-2.05-4.95C15.583 5.683 13.933 5 12 5c-1.933 0-3.583.683-4.95 2.05C5.683 8.417 5 10.067 5 12c0 1.933.683 3.583 2.05 4.95C8.417 18.317 10.067 19 12 19Zm0-3c-1.1 0-2.042-.392-2.825-1.175C8.392 14.042 8 13.1 8 12s.392-2.042 1.175-2.825C9.958 8.392 10.9 8 12 8s2.042.392 2.825 1.175C15.608 9.958 16 10.9 16 12s-.392 2.042-1.175 2.825C14.042 15.608 13.1 16 12 16Z"/></svg>', a8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 20V10c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 8h9V6c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 12 3a2.87 2.87 0 0 0-1.813.612A2.994 2.994 0 0 0 9.125 5.15c-.083.25-.213.454-.387.612A.884.884 0 0 1 8.125 6c-.333 0-.6-.112-.8-.338a.81.81 0 0 1-.2-.762c.25-1.117.825-2.046 1.725-2.788C9.75 1.371 10.8 1 12 1c1.383 0 2.563.488 3.537 1.462C16.512 3.438 17 4.617 17 6v2h1c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22H6Zm0-2h12V10H6v10Zm6-3c.55 0 1.02-.196 1.412-.587.392-.392.588-.863.588-1.413s-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 13c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 15c0 .55.196 1.02.588 1.413.391.391.862.587 1.412.587Z"/></svg>', n8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 20V10c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 8h9V6c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 12 3a2.87 2.87 0 0 0-1.813.612A2.994 2.994 0 0 0 9.125 5.15c-.083.25-.213.454-.387.612A.884.884 0 0 1 8.125 6c-.333 0-.6-.112-.8-.338a.81.81 0 0 1-.2-.762c.25-1.117.825-2.046 1.725-2.788C9.75 1.371 10.8 1 12 1c1.383 0 2.563.488 3.537 1.462C16.512 3.438 17 4.617 17 6v2h1c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22H6Zm6-5c.55 0 1.02-.196 1.412-.587.392-.392.588-.863.588-1.413s-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 13c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 15c0 .55.196 1.02.588 1.413.391.391.862.587 1.412.587Z"/></svg>', l8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 20V10c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 8h1V6c0-1.383.487-2.563 1.463-3.538C9.438 1.487 10.617 1 12 1s2.563.488 3.537 1.462C16.512 3.438 17 4.617 17 6v2h1c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22H6Zm0-2h12V10H6v10Zm6-3c.55 0 1.02-.196 1.412-.587.392-.392.588-.863.588-1.413s-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 13c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 15c0 .55.196 1.02.588 1.413.391.391.862.587 1.412.587ZM9 8h6V6c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 12 3c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 9 6v2Z"/></svg>', c8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 20V10c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 8h1V6c0-1.383.487-2.563 1.463-3.538C9.438 1.487 10.617 1 12 1s2.563.488 3.537 1.462C16.512 3.438 17 4.617 17 6v2h1c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22H6Zm6-5c.55 0 1.02-.196 1.412-.587.392-.392.588-.863.588-1.413s-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 13c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 15c0 .55.196 1.02.588 1.413.391.391.862.587 1.412.587ZM9 8h6V6c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 12 3c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 9 6v2Z"/></svg>', h8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M13 17.075c.133 0 .258-.02.375-.063a.877.877 0 0 0 .325-.212l2.8-2.8c.183-.183.33-.4.438-.65a1.99 1.99 0 0 0 .162-.8c0-.567-.2-1.05-.6-1.45-.4-.4-.883-.6-1.45-.6-.317 0-.63.092-.938.275-.308.183-.679.492-1.112.925-.5-.467-.892-.783-1.175-.95a1.704 1.704 0 0 0-.875-.25c-.567 0-1.05.2-1.45.6-.4.4-.6.883-.6 1.45 0 .283.054.55.162.8.109.25.255.467.438.65l2.8 2.8c.1.1.208.17.325.212.117.042.242.063.375.063ZM12.85 22a1.99 1.99 0 0 1-.8-.163 2.076 2.076 0 0 1-.65-.437l-8.8-8.8c-.183-.183-.33-.4-.438-.65a1.99 1.99 0 0 1-.162-.8V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h7.15a2.071 2.071 0 0 1 1.45.6l8.8 8.825c.383.383.575.854.575 1.412a1.92 1.92 0 0 1-.575 1.413l-7.15 7.15c-.183.183-.396.33-.637.438a1.844 1.844 0 0 1-.763.162Zm-.025-2 7.15-7.15L11.15 4H4v7.15L12.825 20ZM6.5 8c.417 0 .77-.146 1.063-.438C7.854 7.271 8 6.918 8 6.5c0-.417-.146-.77-.438-1.063A1.447 1.447 0 0 0 6.5 5c-.417 0-.77.146-1.063.438A1.447 1.447 0 0 0 5 6.5c0 .417.146.77.438 1.063.291.291.645.437 1.062.437Z"/></svg>', d8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M13 17.075c.133 0 .258-.02.375-.063a.877.877 0 0 0 .325-.212l2.8-2.8c.183-.183.33-.4.438-.65a1.99 1.99 0 0 0 .162-.8c0-.567-.2-1.05-.6-1.45-.4-.4-.883-.6-1.45-.6-.317 0-.63.092-.938.275-.308.183-.679.492-1.112.925-.5-.467-.892-.783-1.175-.95a1.704 1.704 0 0 0-.875-.25c-.567 0-1.05.2-1.45.6-.4.4-.6.883-.6 1.45 0 .283.054.55.162.8.109.25.255.467.438.65l2.8 2.8c.1.1.208.17.325.212.117.042.242.063.375.063ZM12.85 22a1.99 1.99 0 0 1-.8-.163 2.076 2.076 0 0 1-.65-.437l-8.8-8.8c-.183-.183-.33-.4-.438-.65a1.99 1.99 0 0 1-.162-.8V4c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 2h7.15a2.071 2.071 0 0 1 1.45.6l8.8 8.825c.383.383.575.854.575 1.412a1.92 1.92 0 0 1-.575 1.413l-7.15 7.15c-.183.183-.396.33-.637.438a1.844 1.844 0 0 1-.763.162ZM6.5 8c.417 0 .77-.146 1.063-.438C7.854 7.271 8 6.918 8 6.5c0-.417-.146-.77-.438-1.063A1.447 1.447 0 0 0 6.5 5c-.417 0-.77.146-1.063.438A1.447 1.447 0 0 0 5 6.5c0 .417.146.77.438 1.063.291.291.645.437 1.062.437Z"/></svg>', p8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 12c.55 0 1.02-.196 1.412-.588.392-.391.588-.862.588-1.412 0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 8c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 10c0 .55.196 1.02.588 1.412.391.392.862.588 1.412.588Zm0 7.35c2.033-1.867 3.542-3.563 4.525-5.088C17.508 12.738 18 11.383 18 10.2c0-1.817-.58-3.304-1.738-4.462C15.104 4.579 13.683 4 12 4c-1.683 0-3.104.58-4.263 1.737C6.58 6.896 6 8.383 6 10.2c0 1.183.492 2.538 1.475 4.063.983 1.524 2.492 3.22 4.525 5.087ZM12 22c-2.683-2.283-4.688-4.404-6.013-6.363C4.662 13.68 4 11.867 4 10.2c0-2.5.804-4.492 2.412-5.975C8.021 2.742 9.883 2 12 2s3.98.742 5.587 2.225C19.197 5.708 20 7.7 20 10.2c0 1.667-.663 3.48-1.988 5.438C16.688 17.595 14.683 19.716 12 22Z"/></svg>', u8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 12c.55 0 1.02-.196 1.412-.588.392-.391.588-.862.588-1.412 0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 8c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 10c0 .55.196 1.02.588 1.412.391.392.862.588 1.412.588Zm0 10c-2.683-2.283-4.688-4.404-6.013-6.363C4.662 13.68 4 11.867 4 10.2c0-2.5.804-4.492 2.412-5.975C8.021 2.742 9.883 2 12 2s3.98.742 5.587 2.225C19.197 5.708 20 7.7 20 10.2c0 1.667-.663 3.48-1.988 5.438C16.688 17.595 14.683 19.716 12 22Z"/></svg>', g8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17.75 14.95 16.3 13.5c.15-.25.28-.508.387-.775.109-.267.188-.542.238-.825.05-.25.17-.463.363-.638a.924.924 0 0 1 .637-.262c.317 0 .575.108.775.325a.84.84 0 0 1 .225.75 6.45 6.45 0 0 1-.425 1.513c-.2.475-.45.929-.75 1.362Zm-2.95-3-1.8-1.8V5a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 4a.967.967 0 0 0-.713.287A.967.967 0 0 0 11 5v3.15l-2-2V5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 2c.833 0 1.542.292 2.125.875S15 4.167 15 5v6c0 .183-.02.35-.063.5-.041.15-.087.3-.137.45Zm4.3 9.95-17-17a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.949.949 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM11 20v-2.075c-1.55-.217-2.867-.867-3.95-1.95a6.855 6.855 0 0 1-1.975-3.9.839.839 0 0 1 .225-.75c.2-.217.467-.325.8-.325.233 0 .442.088.625.262.183.175.3.388.35.638.217 1.167.783 2.142 1.7 2.925C9.692 15.608 10.767 16 12 16a4.92 4.92 0 0 0 3-1l1.425 1.425a7.882 7.882 0 0 1-1.588.975 6.218 6.218 0 0 1-1.837.525V20a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 21a.967.967 0 0 1-.713-.288A.968.968 0 0 1 11 20Z"/></svg>', v8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17.75 14.95 16.3 13.5c.15-.25.28-.508.387-.775.109-.267.188-.542.238-.825.05-.25.17-.463.363-.638a.924.924 0 0 1 .637-.262c.317 0 .575.108.775.325a.84.84 0 0 1 .225.75 6.45 6.45 0 0 1-.425 1.513c-.2.475-.45.929-.75 1.362Zm-2.95-3L9 6.15V5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 2c.833 0 1.542.292 2.125.875S15 4.167 15 5v6c0 .183-.02.35-.063.5-.041.15-.087.3-.137.45Zm4.3 9.95-17-17a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.949.949 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM11 20v-2.1c-1.55-.2-2.867-.842-3.95-1.925a6.855 6.855 0 0 1-1.975-3.9.839.839 0 0 1 .225-.75c.2-.217.467-.325.8-.325.233 0 .442.088.625.262.183.175.3.388.35.638.217 1.167.783 2.142 1.7 2.925C9.692 15.608 10.767 16 12 16a4.92 4.92 0 0 0 3-1l1.425 1.425a7.47 7.47 0 0 1-1.588.962A6.463 6.463 0 0 1 13 17.9V20a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 21a.967.967 0 0 1-.713-.288A.968.968 0 0 1 11 20Z"/></svg>', m8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 14a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 9 11V5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 2c.833 0 1.542.292 2.125.875S15 4.167 15 5v6c0 .833-.292 1.542-.875 2.125A2.893 2.893 0 0 1 12 14Zm0 7a.967.967 0 0 1-.712-.288A.968.968 0 0 1 11 20v-2.075c-1.55-.217-2.867-.867-3.95-1.95a6.855 6.855 0 0 1-1.975-3.9.839.839 0 0 1 .225-.75c.2-.217.467-.325.8-.325.233 0 .442.088.625.262.183.175.3.388.35.638.217 1.167.783 2.142 1.7 2.925C9.692 15.608 10.767 16 12 16s2.308-.392 3.225-1.175c.917-.783 1.483-1.758 1.7-2.925.05-.25.17-.463.363-.638a.924.924 0 0 1 .637-.262c.317 0 .575.108.775.325a.84.84 0 0 1 .225.75 6.856 6.856 0 0 1-1.975 3.9c-1.083 1.083-2.4 1.733-3.95 1.95V20a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 21Zm0-9c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 11V5a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 4a.967.967 0 0 0-.712.287A.967.967 0 0 0 11 5v6c0 .283.096.52.288.713A.968.968 0 0 0 12 12Z"/></svg>', f8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 14a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 9 11V5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 2c.833 0 1.542.292 2.125.875S15 4.167 15 5v6c0 .833-.292 1.542-.875 2.125A2.893 2.893 0 0 1 12 14Zm0 7a.967.967 0 0 1-.712-.288A.968.968 0 0 1 11 20v-2.1c-1.55-.2-2.867-.842-3.95-1.925a6.855 6.855 0 0 1-1.975-3.9.839.839 0 0 1 .225-.75c.2-.217.467-.325.8-.325.233 0 .442.088.625.262.183.175.3.388.35.638.217 1.167.783 2.142 1.7 2.925C9.692 15.608 10.767 16 12 16s2.308-.392 3.225-1.175c.917-.783 1.483-1.758 1.7-2.925.05-.25.17-.463.363-.638a.924.924 0 0 1 .637-.262c.317 0 .575.108.775.325a.84.84 0 0 1 .225.75 6.856 6.856 0 0 1-1.975 3.9C15.867 17.058 14.55 17.7 13 17.9V20a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 21Z"/></svg>', b8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8 13h8.025a.926.926 0 0 0 .7-.288A.99.99 0 0 0 17 12a.968.968 0 0 0-.288-.713A.967.967 0 0 0 16 11H7.975a.928.928 0 0 0-.7.287A.993.993 0 0 0 7 12c0 .283.096.52.287.712.192.192.43.288.713.288Zm4 9a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Zm0-2c2.217 0 4.104-.779 5.663-2.337C19.221 16.104 20 14.217 20 12s-.779-4.104-2.337-5.663C16.104 4.779 14.217 4 12 4s-4.104.779-5.662 2.337C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.221 9.783 20 12 20Z"/></svg>', w8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M8 13h8.025a.926.926 0 0 0 .7-.288A.99.99 0 0 0 17 12a.968.968 0 0 0-.288-.713A.967.967 0 0 0 16 11H7.975a.928.928 0 0 0-.7.287A.993.993 0 0 0 7 12c0 .283.096.52.287.712.192.192.43.288.713.288Zm4 9a9.733 9.733 0 0 1-3.9-.788 10.092 10.092 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.733 9.733 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.092 10.092 0 0 1 2.137-3.175c.9-.9 1.958-1.613 3.175-2.138A9.743 9.743 0 0 1 12 2a9.74 9.74 0 0 1 3.9.787 10.105 10.105 0 0 1 3.175 2.138c.9.9 1.612 1.958 2.137 3.175A9.733 9.733 0 0 1 22 12a9.733 9.733 0 0 1-.788 3.9 10.092 10.092 0 0 1-2.137 3.175c-.9.9-1.958 1.612-3.175 2.137A9.733 9.733 0 0 1 12 22Z"/></svg>', x8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 23c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 21V3c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 1h10c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v18c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 23H7Zm0-3v1h10v-1H7Zm0-2h10V6H7v12ZM7 4h10V3H7v1Z"/></svg>', A8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10 15a.967.967 0 0 1-.713-.287A.968.968 0 0 1 9 14v-.5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 10.5h1v-.8c0-.233.1-.392.3-.475.2-.083.383-.042.55.125l1.8 1.8a.47.47 0 0 1 .15.35c0 .033-.05.15-.15.35l-1.8 1.8c-.167.167-.35.208-.55.125-.2-.083-.3-.242-.3-.475v-.8h-1a.968.968 0 0 0-.713.287.968.968 0 0 0-.287.713v.5c0 .283-.096.52-.287.713A.968.968 0 0 1 10 15Zm-3 8c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 21V3c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 1h10c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v18c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 23H7Zm0-5h10V6H7v12Zm0 2v1h10v-1H7ZM7 4h10V3H7v1Z"/></svg>', y8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10 15a.967.967 0 0 1-.713-.287A.968.968 0 0 1 9 14v-.5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 10.5h1v-.8c0-.233.1-.392.3-.475.2-.083.383-.042.55.125l1.8 1.8a.47.47 0 0 1 .15.35c0 .033-.05.15-.15.35l-1.8 1.8c-.167.167-.35.208-.55.125-.2-.083-.3-.242-.3-.475v-.8h-1a.968.968 0 0 0-.713.287.968.968 0 0 0-.287.713v.5c0 .283-.096.52-.287.713A.968.968 0 0 1 10 15Zm-3 8c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 21V3c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 1h10c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v18c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 23H7Zm0-5h10V6H7v12Z"/></svg>', k8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 23c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 21V3c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 1h10c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v18c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 23H7Zm0-5h10V6H7v12Z"/></svg>', _8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 21c-2.517 0-4.646-.87-6.388-2.613C3.871 16.646 3 14.517 3 12c0-2.3.75-4.296 2.25-5.987C6.75 4.32 8.667 3.333 11 3.05c.417-.05.742.1.975.45.233.35.225.717-.025 1.1a5.15 5.15 0 0 0-.637 1.375A5.394 5.394 0 0 0 11.1 7.5c0 1.5.525 2.775 1.575 3.825C13.725 12.375 15 12.9 16.5 12.9c.517 0 1.03-.075 1.538-.225a4.97 4.97 0 0 0 1.362-.625c.35-.233.708-.246 1.075-.038.367.209.525.538.475.988-.233 2.3-1.212 4.208-2.938 5.725C16.288 20.242 14.284 21 12 21Zm0-2c1.467 0 2.783-.404 3.95-1.212a7.012 7.012 0 0 0 2.55-3.163c-.333.083-.667.15-1 .2-.333.05-.667.075-1 .075-2.05 0-3.796-.72-5.238-2.162C9.821 11.296 9.1 9.55 9.1 7.5c0-.333.025-.667.075-1 .05-.333.117-.667.2-1a7.013 7.013 0 0 0-3.162 2.55C5.404 9.217 5 10.533 5 12c0 1.933.683 3.583 2.05 4.95C8.417 18.317 10.067 19 12 19Z"/></svg>', Z8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 21c-2.517 0-4.646-.871-6.388-2.613C3.871 16.646 3 14.517 3 12c0-2.3.75-4.296 2.25-5.988C6.75 4.321 8.667 3.333 11 3.05c.417-.05.742.1.975.45.233.35.225.717-.025 1.1a5.16 5.16 0 0 0-.85 2.9c0 1.5.525 2.775 1.575 3.825C13.725 12.375 15 12.9 16.5 12.9c.517 0 1.03-.075 1.538-.225a4.98 4.98 0 0 0 1.362-.625c.35-.233.708-.246 1.075-.038.367.209.525.538.475.988-.233 2.3-1.212 4.208-2.937 5.725C16.288 20.242 14.283 21 12 21Z"/></svg>', C8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 14c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 4 12c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 10c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412 0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 6 14Zm6 0c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 10 12c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 12 10c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412 0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 12 14Zm6 0c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 16 12c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 18 10c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 18 14Z"/></svg>', $8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 20c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 10 18c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 12 16c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 20Zm0-6c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 10 12c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 12 10c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412 0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 12 14Zm0-6c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 10 6c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 12 4c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412 0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 12 8Z"/></svg>', M8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm0-2c2.233 0 4.125-.775 5.675-2.325C19.225 16.125 20 14.233 20 12c0-.9-.146-1.767-.438-2.6A7.951 7.951 0 0 0 18.3 7.1L7.1 18.3c.7.55 1.467.97 2.3 1.262.833.292 1.7.438 2.6.438Zm-6.3-3.1L16.9 5.7a7.95 7.95 0 0 0-2.3-1.263A7.813 7.813 0 0 0 12 4c-2.233 0-4.125.775-5.675 2.325C4.775 7.875 4 9.767 4 12c0 .9.146 1.767.438 2.6A7.95 7.95 0 0 0 5.7 16.9Z"/></svg>', V8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 10a.95.95 0 0 1-.713-.3.82.82 0 0 1-.237-.7 10.191 10.191 0 0 1 2.675-5.875.948.948 0 0 1 .7-.275c.283 0 .525.1.725.3a.918.918 0 0 1 .275.675c0 .267-.092.508-.275.725a8.483 8.483 0 0 0-1.413 2.063A7.447 7.447 0 0 0 4.05 9a1.17 1.17 0 0 1-.338.713A.968.968 0 0 1 3 10Zm18 0a.98.98 0 0 1-.7-.287A1.14 1.14 0 0 1 19.95 9c-.1-.85-.33-1.646-.688-2.388A8.483 8.483 0 0 0 17.85 4.55a1.095 1.095 0 0 1-.275-.725c0-.267.1-.492.3-.675.2-.2.437-.3.712-.3.275 0 .505.092.688.275.75.8 1.354 1.704 1.812 2.713.459 1.008.746 2.062.863 3.162a.82.82 0 0 1-.238.7c-.191.2-.429.3-.712.3ZM5 19a.967.967 0 0 1-.713-.288A.968.968 0 0 1 4 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 5 17h1v-7c0-1.383.417-2.612 1.25-3.688.833-1.075 1.917-1.779 3.25-2.112v-.7c0-.417.146-.77.437-1.063A1.447 1.447 0 0 1 12 2c.417 0 .77.146 1.062.438.292.291.438.645.438 1.062v.7c1.333.333 2.417 1.037 3.25 2.112C17.583 7.388 18 8.617 18 10v7h1c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 19 19H5Zm7 3c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 10 20h4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 22Zm-4-5h8v-7c0-1.1-.392-2.042-1.175-2.825C14.042 6.392 13.1 6 12 6s-2.042.392-2.825 1.175C8.392 7.958 8 8.9 8 10v7Z"/></svg>', H8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 10c-.283 0-.52-.1-.712-.3A.82.82 0 0 1 2.05 9a10.192 10.192 0 0 1 2.675-5.875.948.948 0 0 1 .7-.275c.283 0 .525.1.725.3a.918.918 0 0 1 .275.675c0 .267-.092.508-.275.725a8.483 8.483 0 0 0-1.412 2.063A7.447 7.447 0 0 0 4.05 9a1.17 1.17 0 0 1-.337.713A.968.968 0 0 1 3 10Zm18 0a.98.98 0 0 1-.7-.287A1.14 1.14 0 0 1 19.95 9c-.1-.85-.33-1.646-.688-2.388A8.483 8.483 0 0 0 17.85 4.55a1.095 1.095 0 0 1-.275-.725c0-.267.1-.492.3-.675.2-.2.438-.3.712-.3.276 0 .505.092.688.275A10.193 10.193 0 0 1 21.95 9a.82.82 0 0 1-.238.7c-.191.2-.429.3-.712.3ZM5 19a.967.967 0 0 1-.712-.288A.968.968 0 0 1 4 18c0-.283.096-.52.288-.712A.967.967 0 0 1 5 17h1v-7c0-1.383.417-2.612 1.25-3.688.833-1.075 1.917-1.779 3.25-2.112v-.7c0-.417.146-.77.438-1.063A1.447 1.447 0 0 1 12 2c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062v.7c1.333.333 2.417 1.037 3.25 2.112C17.583 7.388 18 8.617 18 10v7h1c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 19 19H5Zm7 3c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 10 20h4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 22Z"/></svg>', S8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 19a.968.968 0 0 1-.713-.288A.967.967 0 0 1 4 18a.97.97 0 0 1 .287-.712A.968.968 0 0 1 5 17h1v-7c0-1.383.417-2.613 1.25-3.688.833-1.075 1.917-1.779 3.25-2.112v-.7c0-.417.146-.77.438-1.062A1.444 1.444 0 0 1 12 2c.417 0 .77.146 1.062.438.292.291.438.645.438 1.062v.7c1.333.333 2.417 1.037 3.25 2.112C17.583 7.387 18 8.617 18 10v7h1c.283 0 .52.096.712.288A.965.965 0 0 1 20 18c0 .283-.096.52-.288.712A.965.965 0 0 1 19 19H5Zm7 3c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 10 20h4a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 12 22Zm-4-5h8v-7c0-1.1-.392-2.042-1.175-2.825C14.042 6.392 13.1 6 12 6s-2.042.392-2.825 1.175C8.392 7.958 8 8.9 8 10v7Z"/></svg>', D8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 13c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 12V9a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 8a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 9v3c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 3c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 15a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 14a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 15c0 .283.096.52.287.713.192.191.43.287.713.287Zm-7 3a.967.967 0 0 1-.713-.288A.968.968 0 0 1 4 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 5 17h1v-7c0-1.383.417-2.612 1.25-3.688.833-1.075 1.917-1.779 3.25-2.112v-.7c0-.417.146-.77.438-1.063A1.447 1.447 0 0 1 12 2c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062v.7c1.333.333 2.417 1.037 3.25 2.112C17.583 7.388 18 8.617 18 10v7h1c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 19 19H5Zm7 3c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 10 20h4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 22Zm-4-5h8v-7c0-1.1-.392-2.042-1.175-2.825C14.042 6.392 13.1 6 12 6s-2.042.392-2.825 1.175C8.392 7.958 8 8.9 8 10v7Z"/></svg>', L8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 13c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 12V9a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 8a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 9v3c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 3c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 15a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 14a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 15c0 .283.096.52.287.713.192.191.43.287.713.287Zm-7 3a.967.967 0 0 1-.713-.288A.968.968 0 0 1 4 18a.97.97 0 0 1 .287-.712A.967.967 0 0 1 5 17h1v-7c0-1.383.417-2.612 1.25-3.688.833-1.075 1.917-1.779 3.25-2.112v-.7c0-.417.146-.77.438-1.063A1.447 1.447 0 0 1 12 2c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062v.7c1.333.333 2.417 1.037 3.25 2.112C17.583 7.388 18 8.617 18 10v7h1c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 19 19H5Zm7 3c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 10 20h4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 12 22Z"/></svg>', O8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 19a.968.968 0 0 1-.713-.288A.967.967 0 0 1 4 18a.97.97 0 0 1 .287-.712A.968.968 0 0 1 5 17h1v-7c0-1.383.417-2.613 1.25-3.688.833-1.075 1.917-1.779 3.25-2.112v-.7c0-.417.146-.77.438-1.062A1.444 1.444 0 0 1 12 2c.417 0 .77.146 1.062.438.292.291.438.645.438 1.062v.7c1.333.333 2.417 1.037 3.25 2.112C17.583 7.387 18 8.617 18 10v7h1c.283 0 .52.096.712.288A.965.965 0 0 1 20 18c0 .283-.096.52-.288.712A.965.965 0 0 1 19 19H5Zm7 3c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 10 20h4a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 12 22Z"/></svg>', E8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M18 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1ZM6 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6Z" clip-rule="evenodd"/></svg>', T8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M12 4a7.99 7.99 0 0 0-8 8c0 4.425 3.575 8 8 8s8-3.575 8-8-3.575-8-8-8ZM2 12C2 6.47 6.47 2 12 2s10 4.47 10 10-4.47 10-10 10S2 17.53 2 12Z" clip-rule="evenodd"/></svg>', z8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m16.3 17.3-4.6-4.6a.877.877 0 0 1-.212-.325 1.106 1.106 0 0 1-.063-.375c0-.133.02-.258.063-.375a.877.877 0 0 1 .212-.325l4.6-4.6a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7L13.8 12l3.9 3.9a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM7 18a.967.967 0 0 1-.713-.288A.968.968 0 0 1 6 17V7c0-.283.096-.52.287-.713A.968.968 0 0 1 7 6c.283 0 .52.096.713.287.191.192.287.43.287.713v10a.97.97 0 0 1-.287.712A.967.967 0 0 1 7 18Z"/></svg>', I8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6.3 17.3a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l3.9-3.9-3.9-3.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l4.6 4.6c.1.1.17.208.212.325.042.117.063.242.063.375s-.02.258-.063.375a.876.876 0 0 1-.212.325l-4.6 4.6a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM17 18a.968.968 0 0 1-.712-.288A.968.968 0 0 1 16 17V7a.97.97 0 0 1 .288-.713A.968.968 0 0 1 17 6a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v10c0 .283-.096.52-.288.712A.967.967 0 0 1 17 18Z"/></svg>', P8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 20a.967.967 0 0 1-.712-.288A.968.968 0 0 1 2 19V5a.97.97 0 0 1 .288-.713A.968.968 0 0 1 3 4c.133 0 .43.08.888.237.458.159 1.07.33 1.837.513.767.183 1.67.354 2.713.513C9.479 5.42 10.666 5.5 12 5.5c1.333 0 2.52-.08 3.563-.237a33.43 33.43 0 0 0 2.712-.513c.767-.183 1.38-.354 1.838-.513C20.57 4.08 20.867 4 21 4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v14c0 .283-.096.52-.288.712A.968.968 0 0 1 21 20c-.133 0-.43-.08-.887-.238a20.436 20.436 0 0 0-1.838-.512 33.43 33.43 0 0 0-2.712-.512c-1.042-.159-2.23-.238-3.563-.238-1.333 0-2.52.08-3.563.238a33.43 33.43 0 0 0-2.712.512c-.767.183-1.38.354-1.837.512-.459.159-.755.238-.888.238Zm1-2.35c1.3-.383 2.62-.67 3.963-.862a28.509 28.509 0 0 1 8.075 0A27.95 27.95 0 0 1 20 17.65V6.375c-1.3.383-2.62.667-3.962.85a29.789 29.789 0 0 1-8.075 0A26.836 26.836 0 0 1 4 6.375V17.65Z"/></svg>', B8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 20a.967.967 0 0 1-.712-.288A.968.968 0 0 1 2 19V5a.97.97 0 0 1 .288-.713A.968.968 0 0 1 3 4c.133 0 .43.08.888.237.458.159 1.07.33 1.837.513.767.183 1.67.354 2.713.513C9.479 5.42 10.666 5.5 12 5.5c1.333 0 2.52-.08 3.563-.237a33.43 33.43 0 0 0 2.712-.513c.767-.183 1.38-.354 1.838-.513C20.57 4.08 20.867 4 21 4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v14c0 .283-.096.52-.288.712A.968.968 0 0 1 21 20c-.133 0-.43-.08-.887-.238a20.436 20.436 0 0 0-1.838-.512 33.43 33.43 0 0 0-2.712-.512c-1.042-.159-2.23-.238-3.563-.238-1.333 0-2.52.08-3.563.238a33.43 33.43 0 0 0-2.712.512c-.767.183-1.38.354-1.837.512-.459.159-.755.238-.888.238Z"/></svg>', F8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M13 17V7c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 15 5h2c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 19h-2c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 13 17Zm-8 0V7c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 7 5h2c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412v10c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 9 19H7c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 17Zm10 0h2V7h-2v10Zm-8 0h2V7H7v10Z"/></svg>', j8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 17V7c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 16 5c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 16 19c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 14 17Zm-8 0V7c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 8 5c.55 0 1.02.196 1.412.588C9.804 5.979 10 6.45 10 7v10c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 8 19c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 6 17Z"/></svg>', R8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 13a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 11 10c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 14 7c.833 0 1.542.292 2.125.875S17 9.167 17 10s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 14 13Zm-7 3c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 5 14V6c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 7 4h14c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v8c0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 21 16H7Zm2-2h10c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 21 12V8c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 19 6H9c0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 7 8v4c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412Zm-6 6c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 1 18V8a.97.97 0 0 1 .288-.713A.968.968 0 0 1 2 7a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v10h16c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 19 20H3Z"/></svg>', N8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 13c.833 0 1.542-.292 2.125-.875A2.893 2.893 0 0 0 17 10c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 14 7c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 11 10c0 .833.292 1.542.875 2.125A2.893 2.893 0 0 0 14 13Zm-7 3c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 5 14V6c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 7 4h14c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v8c0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 21 16H7Zm-4 4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 1 18V8a.97.97 0 0 1 .288-.713A.968.968 0 0 1 2 7a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v10h16c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 19 20H3ZM7 8c.55 0 1.02-.196 1.412-.588C8.804 7.021 9 6.55 9 6H7v2Zm14 0V6h-2c0 .55.196 1.02.587 1.412C19.98 7.804 20.45 8 21 8ZM7 14h2c0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 7 12v2Zm12 0h2v-2c-.55 0-1.02.196-1.413.588A1.926 1.926 0 0 0 19 14Z"/></svg>', U8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M1 18a.967.967 0 0 1-.713-.288A.968.968 0 0 1 0 17v-.575c0-.733.367-1.32 1.1-1.763C1.833 14.221 2.8 14 4 14a6.31 6.31 0 0 1 1.175.1 3.544 3.544 0 0 0-.513 1.075A4.486 4.486 0 0 0 4.5 16.4V18H1Zm6 0a.967.967 0 0 1-.713-.288A.968.968 0 0 1 6 17v-.6c0-1.083.554-1.962 1.662-2.637 1.109-.675 2.555-1.013 4.338-1.013 1.8 0 3.25.338 4.35 1.012 1.1.675 1.65 1.555 1.65 2.638v.6c0 .283-.096.52-.288.712A.968.968 0 0 1 17 18H7Zm12.5 0v-1.6c0-.433-.058-.842-.175-1.225a4.213 4.213 0 0 0-.5-1.075A6.262 6.262 0 0 1 20 14c1.2 0 2.167.22 2.9.662.733.442 1.1 1.03 1.1 1.763V17c0 .283-.096.52-.288.712A.968.968 0 0 1 23 18h-3.5ZM12 14.75c-.95 0-1.8.13-2.55.387-.75.259-1.192.546-1.325.863h7.75c-.15-.333-.596-.625-1.338-.875-.741-.25-1.587-.375-2.537-.375ZM4 13c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 2 11c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 9c.55 0 1.02.196 1.412.588C5.804 9.979 6 10.45 6 11c0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 4 13Zm16 0c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 18 11c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 20 9c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412 0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 20 13Zm-8-1a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 9 9c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 6c.833 0 1.542.292 2.125.875S15 8.167 15 9s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 12 12Zm0-4a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 9c0 .283.096.52.287.713.192.191.43.287.713.287s.52-.096.713-.287A.967.967 0 0 0 13 9a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 8Z"/></svg>', W8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M1 18a.965.965 0 0 1-.712-.288A.965.965 0 0 1 0 17v-.575c0-.733.367-1.321 1.1-1.763C1.833 14.221 2.8 14 4 14a6.337 6.337 0 0 1 1.175.1 3.553 3.553 0 0 0-.513 1.075A4.499 4.499 0 0 0 4.5 16.4V18H1Zm6 0a.968.968 0 0 1-.713-.288A.967.967 0 0 1 6 17v-.6c0-1.083.554-1.963 1.663-2.638 1.108-.675 2.554-1.012 4.337-1.012 1.8 0 3.25.337 4.35 1.012 1.1.675 1.65 1.555 1.65 2.638v.6c0 .283-.096.52-.288.712A.965.965 0 0 1 17 18H7Zm12.5 0v-1.6c0-.433-.058-.842-.175-1.225a4.213 4.213 0 0 0-.5-1.075A6.31 6.31 0 0 1 20 14c1.2 0 2.167.22 2.9.662.733.442 1.1 1.03 1.1 1.763V17c0 .283-.096.52-.288.712A.965.965 0 0 1 23 18h-3.5ZM4 13c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 2 11c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 4 9c.55 0 1.02.196 1.412.587C5.804 9.979 6 10.45 6 11s-.196 1.02-.588 1.412A1.923 1.923 0 0 1 4 13Zm16 0c-.55 0-1.021-.196-1.413-.588A1.925 1.925 0 0 1 18 11c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 20 9c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.412A1.927 1.927 0 0 1 20 13Zm-8-1a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 9 9c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 12 6c.833 0 1.542.292 2.125.875S15 8.167 15 9s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 12 12Z"/></svg>', q8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7.5 11a3.372 3.372 0 0 1-2.475-1.025A3.372 3.372 0 0 1 4 7.5c0-.967.342-1.792 1.025-2.475A3.372 3.372 0 0 1 7.5 4c.967 0 1.792.342 2.475 1.025A3.372 3.372 0 0 1 11 7.5c0 .967-.342 1.792-1.025 2.475A3.372 3.372 0 0 1 7.5 11Zm0-2c.417 0 .77-.146 1.062-.438C8.854 8.271 9 7.917 9 7.5c0-.417-.146-.77-.438-1.062A1.444 1.444 0 0 0 7.5 6c-.417 0-.77.146-1.062.438A1.444 1.444 0 0 0 6 7.5c0 .417.146.77.438 1.062.291.292.645.438 1.062.438Zm9 11a3.372 3.372 0 0 1-2.475-1.025A3.372 3.372 0 0 1 13 16.5c0-.967.342-1.792 1.025-2.475A3.372 3.372 0 0 1 16.5 13c.967 0 1.792.342 2.475 1.025A3.372 3.372 0 0 1 20 16.5c0 .967-.342 1.792-1.025 2.475A3.372 3.372 0 0 1 16.5 20Zm0-2c.417 0 .77-.146 1.062-.438.292-.291.438-.645.438-1.062 0-.417-.146-.77-.438-1.062A1.444 1.444 0 0 0 16.5 15c-.417 0-.77.146-1.062.438A1.444 1.444 0 0 0 15 16.5c0 .417.146.77.438 1.062.291.292.645.438 1.062.438ZM4.7 19.3a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7L17.9 4.7a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7L6.1 19.3a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', K8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 20a.967.967 0 0 1-.712-.288A.968.968 0 0 1 2 19v-1.8c0-.55.142-1.067.425-1.55.283-.483.675-.85 1.175-1.1.85-.433 1.808-.8 2.875-1.1C7.542 13.15 8.717 13 10 13c.5 0 .988.025 1.463.075.475.05.937.125 1.387.225l-1.75 1.75a3.004 3.004 0 0 0-.537-.05H10c-1.183 0-2.246.142-3.188.425-.941.283-1.712.592-2.312.925a.973.973 0 0 0-.5.85v.8h6.25l2 2H3Zm12.55-.025c-.133 0-.258-.02-.375-.063a.877.877 0 0 1-.325-.212l-2.05-2.05a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l1.35 1.35 4.35-4.35a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-5.05 5.05c-.1.1-.208.17-.325.213a1.106 1.106 0 0 1-.375.062ZM10 12c-1.1 0-2.042-.392-2.825-1.175C6.392 10.042 6 9.1 6 8s.392-2.042 1.175-2.825C7.958 4.392 8.9 4 10 4s2.042.392 2.825 1.175C13.608 5.958 14 6.9 14 8s-.392 2.042-1.175 2.825C12.042 11.608 11.1 12 10 12Zm0-2c.55 0 1.02-.196 1.412-.588C11.804 9.021 12 8.55 12 8c0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 10 6c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 8 8c0 .55.196 1.02.588 1.412.391.392.862.588 1.412.588Z"/></svg>', Y8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.55 19.975c-.133 0-.258-.02-.375-.063a.877.877 0 0 1-.325-.212l-2.05-2.05a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l1.35 1.35 4.35-4.35a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-5.05 5.05c-.1.1-.208.17-.325.213a1.106 1.106 0 0 1-.375.062ZM10 12c-1.1 0-2.042-.392-2.825-1.175C6.392 10.042 6 9.1 6 8s.392-2.042 1.175-2.825C7.958 4.392 8.9 4 10 4s2.042.392 2.825 1.175C13.608 5.958 14 6.9 14 8s-.392 2.042-1.175 2.825C12.042 11.608 11.1 12 10 12Zm-7 8a.967.967 0 0 1-.712-.288A.968.968 0 0 1 2 19v-1.8c0-.55.142-1.067.425-1.55.283-.483.675-.85 1.175-1.1.85-.433 1.808-.8 2.875-1.1C7.542 13.15 8.717 13 10 13c.5 0 .988.025 1.463.075.475.05.937.125 1.387.225l-2.25 2.25c-.383.383-.57.854-.563 1.412.009.559.205 1.03.588 1.413L12.25 20H3Z"/></svg>', G8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 12c-1.1 0-2.042-.392-2.825-1.175C8.392 10.042 8 9.1 8 8s.392-2.042 1.175-2.825C9.958 4.392 10.9 4 12 4s2.042.392 2.825 1.175C15.608 5.958 16 6.9 16 8s-.392 2.042-1.175 2.825C14.042 11.608 13.1 12 12 12Zm6 8H6c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 18v-.8c0-.567.146-1.087.438-1.563A2.911 2.911 0 0 1 5.6 14.55a14.843 14.843 0 0 1 3.15-1.163A13.76 13.76 0 0 1 12 13c1.1 0 2.183.13 3.25.387 1.067.259 2.117.646 3.15 1.163.483.25.87.612 1.163 1.087.291.476.437.996.437 1.563v.8c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 20ZM6 18h12v-.8a.973.973 0 0 0-.5-.85c-.9-.45-1.808-.787-2.725-1.012a11.6 11.6 0 0 0-5.55 0c-.917.225-1.825.562-2.725 1.012a.973.973 0 0 0-.5.85v.8Zm6-8c.55 0 1.02-.196 1.412-.588C13.804 9.021 14 8.55 14 8c0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 6c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 8c0 .55.196 1.02.588 1.412.391.392.862.588 1.412.588Z"/></svg>', Q8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 12c-1.1 0-2.042-.392-2.825-1.175C7.392 10.042 7 9.1 7 8s.392-2.042 1.175-2.825C8.958 4.392 9.9 4 11 4s2.042.392 2.825 1.175C14.608 5.958 15 6.9 15 8s-.392 2.042-1.175 2.825C13.042 11.608 12.1 12 11 12Zm0-2c.55 0 1.02-.196 1.412-.588C12.804 9.021 13 8.55 13 8c0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 11 6c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 9 8c0 .55.196 1.02.588 1.412.391.392.862.588 1.412.588Zm10.4 12.8-2.5-2.5c-.35.2-.725.367-1.125.5s-.825.2-1.275.2c-1.25 0-2.313-.438-3.188-1.313C12.438 18.813 12 17.75 12 16.5c0-1.25.438-2.313 1.313-3.188C14.187 12.438 15.25 12 16.5 12c1.25 0 2.313.438 3.188 1.313C20.563 14.187 21 15.25 21 16.5c0 .45-.067.875-.2 1.275-.133.4-.3.775-.5 1.125l2.5 2.5a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM16.5 19c.7 0 1.292-.242 1.775-.725.483-.483.725-1.075.725-1.775s-.242-1.292-.725-1.775C17.792 14.242 17.2 14 16.5 14s-1.292.242-1.775.725C14.242 15.208 14 15.8 14 16.5s.242 1.292.725 1.775c.483.483 1.075.725 1.775.725Zm-5.475 1H5c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 18v-.775c0-.567.142-1.092.425-1.575.283-.483.675-.85 1.175-1.1.85-.433 1.808-.8 2.875-1.1 1.067-.3 2.25-.45 3.55-.45-.2.3-.37.62-.513.963a6.564 6.564 0 0 0-.337 1.062c-1 .083-1.892.254-2.675.512-.783.259-1.442.53-1.975.813a.905.905 0 0 0-.388.362c-.091.159-.137.33-.137.513V18h5.175c.083.367.196.717.337 1.05.142.333.313.65.513.95Z"/></svg>', X8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 12c-1.1 0-2.042-.392-2.825-1.175C7.392 10.042 7 9.1 7 8s.392-2.042 1.175-2.825C8.958 4.392 9.9 4 11 4s2.042.392 2.825 1.175C14.608 5.958 15 6.9 15 8s-.392 2.042-1.175 2.825C13.042 11.608 12.1 12 11 12Zm10.4 10.8-2.5-2.5c-.35.2-.725.367-1.125.5s-.825.2-1.275.2c-1.25 0-2.313-.438-3.188-1.313C12.438 18.813 12 17.75 12 16.5c0-1.25.438-2.313 1.313-3.188C14.187 12.438 15.25 12 16.5 12c1.25 0 2.313.438 3.188 1.313C20.563 14.187 21 15.25 21 16.5c0 .45-.067.875-.2 1.275-.133.4-.3.775-.5 1.125l2.5 2.5a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM16.5 19c.7 0 1.292-.242 1.775-.725.483-.483.725-1.075.725-1.775s-.242-1.292-.725-1.775C17.792 14.242 17.2 14 16.5 14s-1.292.242-1.775.725C14.242 15.208 14 15.8 14 16.5s.242 1.292.725 1.775c.483.483 1.075.725 1.775.725ZM5 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 18v-.775c0-.567.142-1.092.425-1.575.283-.483.675-.85 1.175-1.1.85-.433 1.808-.8 2.875-1.1 1.067-.3 2.25-.45 3.55-.45A6.4 6.4 0 0 0 10 16.5c0 .617.088 1.22.262 1.813.175.591.43 1.154.763 1.687H5Z"/></svg>', J8 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 12c-1.1 0-2.042-.392-2.825-1.175C8.392 10.042 8 9.1 8 8s.392-2.042 1.175-2.825C9.958 4.392 10.9 4 12 4s2.042.392 2.825 1.175C15.608 5.958 16 6.9 16 8s-.392 2.042-1.175 2.825C14.042 11.608 13.1 12 12 12Zm-6 8c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 18v-.8c0-.567.146-1.087.438-1.563A2.911 2.911 0 0 1 5.6 14.55a14.843 14.843 0 0 1 3.15-1.163A13.76 13.76 0 0 1 12 13c1.1 0 2.183.13 3.25.387 1.067.259 2.117.646 3.15 1.163.483.25.87.612 1.163 1.087.291.476.437.996.437 1.563v.8c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 20H6Z"/></svg>', er = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19.95 21c-2.15 0-4.246-.48-6.287-1.438a19.066 19.066 0 0 1-5.425-3.8 19.067 19.067 0 0 1-3.8-5.425C3.478 8.296 3 6.2 3 4.05c0-.3.1-.55.3-.75.2-.2.45-.3.75-.3H8.1a.96.96 0 0 1 .625.225.88.88 0 0 1 .325.575l.65 3.5c.033.233.03.446-.012.638a1.023 1.023 0 0 1-.288.512L6.975 10.9a16.4 16.4 0 0 0 2.638 3.375A18.64 18.64 0 0 0 13.1 17l2.35-2.35a1.4 1.4 0 0 1 .588-.338 1.61 1.61 0 0 1 .712-.062l3.45.7c.233.05.425.163.575.338.15.175.225.379.225.612v4.05c0 .3-.1.55-.3.75-.2.2-.45.3-.75.3ZM6.025 9l1.65-1.65L7.25 5H5.025c.083.683.2 1.358.35 2.025.15.667.367 1.325.65 1.975ZM19 18.95v-2.2l-2.35-.475-1.675 1.675c.65.283 1.313.508 1.987.675.675.167 1.355.275 2.038.325Z"/></svg>', tr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19.95 21c-2.15 0-4.246-.48-6.287-1.438a19.066 19.066 0 0 1-5.425-3.8 19.067 19.067 0 0 1-3.8-5.425C3.478 8.296 3 6.2 3 4.05c0-.3.1-.55.3-.75.2-.2.45-.3.75-.3H8.1a.96.96 0 0 1 .625.225.88.88 0 0 1 .325.575l.65 3.5c.033.233.03.446-.012.638a1.023 1.023 0 0 1-.288.512L7 10.9c.7 1.2 1.575 2.325 2.625 3.375A18.293 18.293 0 0 0 13.1 17l2.35-2.35a1.4 1.4 0 0 1 .588-.338 1.61 1.61 0 0 1 .712-.062l3.45.7c.233.05.425.163.575.338.15.175.225.379.225.612v4.05c0 .3-.1.55-.3.75-.2.2-.45.3-.75.3Z"/></svg>', sr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9.525 18.025c-.333.217-.67.23-1.013.037-.341-.191-.512-.487-.512-.887V6.825c0-.4.17-.696.512-.888.342-.191.68-.179 1.013.038l8.15 5.175c.3.2.45.483.45.85s-.15.65-.45.85l-8.15 5.175ZM10 15.35 15.25 12 10 8.65v6.7Z"/></svg>', rr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9.525 18.025c-.333.217-.67.23-1.013.037-.341-.191-.512-.487-.512-.887V6.825c0-.4.17-.696.512-.888.342-.191.68-.179 1.013.038l8.15 5.175c.3.2.45.483.45.85s-.15.65-.45.85l-8.15 5.175Z"/></svg>', or = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M14 4v5c0 1.12.37 2.16 1 3H9c.65-.86 1-1.9 1-3V4h4Zm3-2H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.316-.842 2.43-2.018 2.836C5.46 12.016 5 12.448 5 13a1 1 0 0 0 1 1h4.97v7l1 1 1-1v-7H18a1 1 0 0 0 1-1c0-.552-.46-.984-.982-1.164A2.996 2.996 0 0 1 16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1Z"/></svg>', ir = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.316-.842 2.43-2.018 2.836C5.46 12.016 5 12.448 5 13a1 1 0 0 0 1 1h4.97v7l1 1 1-1v-7H18a1 1 0 0 0 1-1c0-.552-.46-.984-.982-1.164A2.996 2.996 0 0 1 16 9Z" clip-rule="evenodd"/></svg>', ar = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 11c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 3 9V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h4c.55 0 1.02.196 1.412.587C10.804 3.98 11 4.45 11 5v4c0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 9 11H5Zm0-2h4V5H5v4Zm0 12c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19v-4c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 5 13h4c.55 0 1.02.196 1.412.588.392.391.588.862.588 1.412v4c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 9 21H5Zm0-2h4v-4H5v4Zm10-8c-.55 0-1.02-.196-1.412-.588A1.926 1.926 0 0 1 13 9V5c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 15 3h4c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v4c0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 19 11h-4Zm0-2h4V5h-4v4Zm4.5 12a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm-6-6a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm2 2a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm-2 2a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm2 2a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm2-2a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm0-4a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Zm2 2a.48.48 0 0 1-.35-.15.48.48 0 0 1-.15-.35v-1c0-.133.05-.25.15-.35.1-.1.217-.15.35-.15h1c.133 0 .25.05.35.15.1.1.15.217.15.35v1a.48.48 0 0 1-.15.35.48.48 0 0 1-.35.15h-1Z"/></svg>', nr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M11 4.063A8 8 0 1 0 18.928 8a1 1 0 0 1 1.732-1A10 10 0 1 1 12 2a1 1 0 0 1 1 1v7.268A2 2 0 0 1 12 14a2 2 0 0 1-1-3.732V8.127A4 4 0 1 0 15.464 10a1 1 0 1 1 1.732-1A6 6 0 1 1 11 6.084V4.063Z"/></svg>', lr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><rect width="10" height="10" x="7" y="7" fill="inherit" rx="5"/></svg>', cr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2c.75 0 1.48.08 2.188.237a9.587 9.587 0 0 1 2.012.688 2.715 2.715 0 0 0-.15.525A3.1 3.1 0 0 0 16 4c0 .233.025.454.075.662.05.209.117.405.2.588a8.716 8.716 0 0 0-2-.912A7.552 7.552 0 0 0 12 4c-2.217 0-4.104.78-5.662 2.338C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.22 9.783 20 12 20s4.104-.78 5.663-2.337C19.22 16.104 20 14.217 20 12c0-.667-.08-1.308-.238-1.925a8.332 8.332 0 0 0-.662-1.75 2.942 2.942 0 0 0 2.425.625c.15.483.267.98.35 1.488.083.508.125 1.029.125 1.562a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm3.5-11c.433 0 .792-.142 1.075-.425.283-.283.425-.642.425-1.075 0-.433-.142-.792-.425-1.075C16.292 8.142 15.933 8 15.5 8c-.433 0-.792.142-1.075.425-.283.283-.425.642-.425 1.075 0 .433.142.792.425 1.075.283.283.642.425 1.075.425Zm-7 0c.433 0 .792-.142 1.075-.425.283-.283.425-.642.425-1.075 0-.433-.142-.792-.425-1.075C9.292 8.142 8.933 8 8.5 8c-.433 0-.792.142-1.075.425C7.142 8.708 7 9.067 7 9.5c0 .433.142.792.425 1.075.283.283.642.425 1.075.425Zm3.5 6.5c1.183 0 2.23-.325 3.137-.975A5.475 5.475 0 0 0 17.1 14H6.9a5.475 5.475 0 0 0 1.963 2.525c.908.65 1.954.975 3.137.975ZM20 5h-1a.968.968 0 0 1-.712-.287A.967.967 0 0 1 18 4c0-.283.096-.52.288-.712A.968.968 0 0 1 19 3h1V2c0-.283.096-.52.288-.712A.968.968 0 0 1 21 1c.283 0 .52.096.712.288A.965.965 0 0 1 22 2v1h1c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.713A.968.968 0 0 1 23 5h-1v1a.97.97 0 0 1-.288.713A.968.968 0 0 1 21 7a.968.968 0 0 1-.712-.287A.967.967 0 0 1 20 6V5Z"/></svg>', hr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M20 5h-1a.968.968 0 0 1-.712-.287A.967.967 0 0 1 18 4c0-.283.096-.52.288-.712A.968.968 0 0 1 19 3h1V2c0-.283.096-.52.288-.712A.968.968 0 0 1 21 1c.283 0 .52.096.712.288A.965.965 0 0 1 22 2v1h1c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.713A.968.968 0 0 1 23 5h-1v1a.97.97 0 0 1-.288.713A.968.968 0 0 1 21 7a.968.968 0 0 1-.712-.287A.967.967 0 0 1 20 6V5Zm-8 17a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2c.75 0 1.48.08 2.188.237a9.587 9.587 0 0 1 2.012.688 2.715 2.715 0 0 0-.15.525A3.1 3.1 0 0 0 16 4c0 .667.196 1.267.587 1.8.392.533.905.892 1.538 1.075A2.838 2.838 0 0 0 19.2 8.412a2.966 2.966 0 0 0 2.325.538c.15.483.267.98.35 1.488.083.508.125 1.029.125 1.562a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm3.5-11c.433 0 .792-.142 1.075-.425.283-.283.425-.642.425-1.075 0-.433-.142-.792-.425-1.075C16.292 8.142 15.933 8 15.5 8c-.433 0-.792.142-1.075.425-.283.283-.425.642-.425 1.075 0 .433.142.792.425 1.075.283.283.642.425 1.075.425Zm-7 0c.433 0 .792-.142 1.075-.425.283-.283.425-.642.425-1.075 0-.433-.142-.792-.425-1.075C9.292 8.142 8.933 8 8.5 8c-.433 0-.792.142-1.075.425C7.142 8.708 7 9.067 7 9.5c0 .433.142.792.425 1.075.283.283.642.425 1.075.425Zm3.5 6.5c1.183 0 2.23-.325 3.137-.975A5.475 5.475 0 0 0 17.1 14H6.9a5.475 5.475 0 0 0 1.963 2.525c.908.65 1.954.975 3.137.975Z"/></svg>', dr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 21.8V2.2c.033 0 .08.004.138.012.058.009.129.055.212.138l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8c.083-.083.154-.13.213-.138A1.07 1.07 0 0 1 21 2.2v19.6a1.04 1.04 0 0 1-.137-.012c-.059-.009-.13-.055-.213-.138l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8c-.083.083-.154.13-.212.138A1.04 1.04 0 0 1 3 21.8ZM7 17h10c.283 0 .52-.096.712-.288A.968.968 0 0 0 18 16a.967.967 0 0 0-.288-.713A.968.968 0 0 0 17 15H7a.967.967 0 0 0-.713.287A.967.967 0 0 0 6 16c0 .283.096.52.287.712.192.192.43.288.713.288Zm0-4h10a.97.97 0 0 0 .712-.287A.968.968 0 0 0 18 12a.967.967 0 0 0-.288-.713A.968.968 0 0 0 17 11H7a.967.967 0 0 0-.713.287A.967.967 0 0 0 6 12c0 .283.096.52.287.713.192.191.43.287.713.287Zm0-4h10c.283 0 .52-.096.712-.288A.967.967 0 0 0 18 8a.967.967 0 0 0-.288-.713A.968.968 0 0 0 17 7H7a.968.968 0 0 0-.713.287A.968.968 0 0 0 6 8c0 .283.096.52.287.712.192.192.43.288.713.288ZM5 19.1h14V4.9H5v14.2Z"/></svg>', pr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 21.8V2.2c.033 0 .08.004.138.012.058.009.129.055.212.138l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8a.48.48 0 0 1 .7 0l.8.8a.48.48 0 0 0 .7 0l.8-.8c.083-.083.154-.13.213-.138A1.07 1.07 0 0 1 21 2.2v19.6a1.04 1.04 0 0 1-.137-.012c-.059-.009-.13-.055-.213-.138l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8a.48.48 0 0 1-.7 0l-.8-.8a.48.48 0 0 0-.7 0l-.8.8c-.083.083-.154.13-.212.138A1.04 1.04 0 0 1 3 21.8ZM7 17h10c.283 0 .52-.096.712-.288A.968.968 0 0 0 18 16a.967.967 0 0 0-.288-.713A.968.968 0 0 0 17 15H7a.967.967 0 0 0-.713.287A.967.967 0 0 0 6 16c0 .283.096.52.287.712.192.192.43.288.713.288Zm0-4h10a.97.97 0 0 0 .712-.287A.968.968 0 0 0 18 12a.967.967 0 0 0-.288-.713A.968.968 0 0 0 17 11H7a.967.967 0 0 0-.713.287A.967.967 0 0 0 6 12c0 .283.096.52.287.713.192.191.43.287.713.287Zm0-4h10c.283 0 .52-.096.712-.288A.967.967 0 0 0 18 8a.967.967 0 0 0-.288-.713A.968.968 0 0 0 17 7H7a.968.968 0 0 0-.713.287A.968.968 0 0 0 6 8c0 .283.096.52.287.712.192.192.43.288.713.288Z"/></svg>', ur = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4.475 14.7a6.838 6.838 0 0 1-.362-1.313A7.974 7.974 0 0 1 4 12.05c0-2.233.775-4.133 2.325-5.7C7.875 4.783 9.767 4 12 4h.175l-.9-.9A.948.948 0 0 1 11 2.4a.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l2.6 2.6c.1.1.17.208.213.325.041.117.062.242.062.375s-.02.258-.063.375a.878.878 0 0 1-.212.325l-2.6 2.6a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l.9-.9H12c-1.667 0-3.083.588-4.25 1.763C6.583 8.938 6 10.367 6 12.05c0 .333.03.658.088.975.058.317.145.633.262.95a.972.972 0 0 1 .05.537.887.887 0 0 1-.25.488c-.3.3-.62.42-.963.363-.341-.059-.579-.28-.712-.663Zm6.85 7.6-2.6-2.6a.877.877 0 0 1-.213-.325A1.106 1.106 0 0 1 8.45 19c0-.133.02-.258.063-.375a.878.878 0 0 1 .212-.325l2.6-2.6a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275.949.949 0 0 1 .275.7.948.948 0 0 1-.275.7l-.9.9H12c1.667 0 3.083-.587 4.25-1.762C17.417 15.063 18 13.633 18 11.95c0-.333-.03-.658-.087-.975a5.765 5.765 0 0 0-.263-.95.972.972 0 0 1-.05-.537.887.887 0 0 1 .25-.488c.3-.3.62-.42.962-.362.342.058.58.279.713.662.167.433.288.87.363 1.313.074.441.112.887.112 1.337 0 2.233-.775 4.133-2.325 5.7C16.125 19.217 14.233 20 12 20h-.175l.9.9a.949.949 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', gr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12.05 20c-2.233 0-4.133-.775-5.7-2.325C4.783 16.125 4 14.233 4 12v-.175l-.9.9a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7l2.6-2.6c.1-.1.208-.17.325-.213.117-.041.242-.062.375-.062s.258.02.375.063a.877.877 0 0 1 .325.212l2.6 2.6a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275l-.9-.9V12c0 1.667.588 3.083 1.763 4.25C8.938 17.417 10.367 18 12.05 18c.333 0 .658-.03.975-.087a5.76 5.76 0 0 0 .95-.263.972.972 0 0 1 .537-.05.887.887 0 0 1 .488.25c.3.3.42.62.363.962-.059.342-.28.58-.663.713-.433.15-.87.267-1.313.35A7.2 7.2 0 0 1 12.05 20ZM19 15.55c-.133 0-.258-.02-.375-.063a.878.878 0 0 1-.325-.212l-2.6-2.6a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.949.949 0 0 1 .7-.275.95.95 0 0 1 .7.275l.9.9V12c0-1.667-.587-3.083-1.762-4.25C15.063 6.583 13.633 6 11.95 6c-.333 0-.658.03-.975.088a5.77 5.77 0 0 0-.95.262.972.972 0 0 1-.537.05A.887.887 0 0 1 9 6.15c-.3-.3-.42-.62-.362-.963.058-.341.279-.579.662-.712.433-.15.87-.267 1.313-.35A7.199 7.199 0 0 1 11.95 4c2.233 0 4.133.775 5.7 2.325C19.217 7.875 20 9.767 20 12v.175l.9-.9a.949.949 0 0 1 .7-.275.95.95 0 0 1 .7.275.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-2.6 2.6c-.1.1-.208.17-.325.213a1.105 1.105 0 0 1-.375.062Z"/></svg>', vr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 20c-2.233 0-4.125-.775-5.675-2.325C4.775 16.125 4 14.233 4 12c0-2.233.775-4.125 2.325-5.675C7.875 4.775 9.767 4 12 4c1.15 0 2.25.238 3.3.713A7.617 7.617 0 0 1 18 6.75V5c0-.283.096-.521.288-.713A.967.967 0 0 1 19 4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v5c0 .283-.096.52-.288.712A.965.965 0 0 1 19 11h-5a.965.965 0 0 1-.712-.288A.965.965 0 0 1 13 10c0-.283.096-.521.288-.713A.967.967 0 0 1 14 9h3.2a5.837 5.837 0 0 0-2.187-2.2A5.93 5.93 0 0 0 12 6c-1.667 0-3.083.583-4.25 1.75C6.583 8.917 6 10.333 6 12c0 1.667.583 3.083 1.75 4.25C8.917 17.417 10.333 18 12 18c1.15 0 2.213-.304 3.188-.913a5.715 5.715 0 0 0 2.187-2.437c.083-.183.221-.337.413-.462a1.06 1.06 0 0 1 .587-.188c.383 0 .671.133.863.4.191.267.212.567.062.9a7.794 7.794 0 0 1-2.925 3.412C15.058 19.571 13.6 20 12 20Z"/></svg>', mr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 13a.967.967 0 0 1-.713-.287A.968.968 0 0 1 5 12c0-.283.096-.52.287-.713A.967.967 0 0 1 6 11h12a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.713A.968.968 0 0 1 18 13H6Z"/></svg>', fr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M16 11a.97.97 0 0 0 .712-.287A.968.968 0 0 0 17 10a.967.967 0 0 0-.288-.713A.968.968 0 0 0 16 9a.968.968 0 0 0-.713.287A.967.967 0 0 0 15 10c0 .283.096.52.287.713.192.191.43.287.713.287ZM9 9h3c.283 0 .52-.096.713-.287A.967.967 0 0 0 13 8a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 7H9a.968.968 0 0 0-.713.287A.968.968 0 0 0 8 8c0 .283.096.52.287.713.192.191.43.287.713.287ZM5.925 21c-.383 0-.754-.133-1.112-.4-.359-.267-.596-.592-.713-.975-.417-1.4-.758-2.613-1.025-3.637a53.184 53.184 0 0 1-.638-2.7 23.314 23.314 0 0 1-.337-2.063c-.067-.6-.1-1.175-.1-1.725 0-1.533.533-2.833 1.6-3.9C4.667 4.533 5.967 4 7.5 4h5c.45-.6 1.02-1.083 1.713-1.45A4.807 4.807 0 0 1 16.5 2c.417 0 .77.146 1.063.438a1.446 1.446 0 0 1 .4 1.362c-.026.1-.055.192-.088.275-.067.183-.13.367-.188.55a3.972 3.972 0 0 0-.137.6L19.825 7.5H21a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v5.25a.933.933 0 0 1-.188.575 1.129 1.129 0 0 1-.512.375l-2.125.7-1.25 4.175c-.133.433-.375.78-.725 1.038S16.45 21 16 21h-2c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 12 19h-2c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 8 21H5.925ZM6 19h2v-2h6v2h2l1.55-5.15 2.45-.825V9.5h-1L15.5 6a7.249 7.249 0 0 1 .25-1.9 3.164 3.164 0 0 0-1.275.688 2.81 2.81 0 0 0-.8 1.212H7.5c-.967 0-1.792.342-2.475 1.025A3.372 3.372 0 0 0 4 9.5c0 .683.175 1.854.525 3.512.35 1.659.842 3.655 1.475 5.988Z"/></svg>', br = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M16 11a.97.97 0 0 0 .712-.287A.968.968 0 0 0 17 10a.967.967 0 0 0-.288-.713A.968.968 0 0 0 16 9a.968.968 0 0 0-.713.287A.967.967 0 0 0 15 10c0 .283.096.52.287.713.192.191.43.287.713.287ZM9 9h3c.283 0 .52-.096.713-.287A.967.967 0 0 0 13 8a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 7H9a.968.968 0 0 0-.713.287A.968.968 0 0 0 8 8c0 .283.096.52.287.713.192.191.43.287.713.287ZM5.925 21c-.383 0-.754-.133-1.112-.4-.359-.267-.596-.592-.713-.975-.417-1.4-.758-2.613-1.025-3.637a53.184 53.184 0 0 1-.638-2.7 23.314 23.314 0 0 1-.337-2.063c-.067-.6-.1-1.175-.1-1.725 0-1.533.533-2.833 1.6-3.9C4.667 4.533 5.967 4 7.5 4h5c.45-.6 1.02-1.083 1.713-1.45A4.807 4.807 0 0 1 16.5 2c.417 0 .77.146 1.063.438a1.446 1.446 0 0 1 .4 1.362c-.026.1-.055.192-.088.275-.067.183-.13.367-.188.55a3.972 3.972 0 0 0-.137.6L19.825 7.5H21a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v5.25a.933.933 0 0 1-.188.575 1.129 1.129 0 0 1-.512.375l-2.125.7-1.25 4.175c-.133.433-.375.78-.725 1.038S16.45 21 16 21h-2c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 12 19h-2c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 8 21H5.925Z"/></svg>', wr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 14c.283 0 .52-.096.713-.287A.968.968 0 0 0 10 13v-1c0-.283.096-.52.287-.713A.968.968 0 0 1 11 11h2v.8c0 .233.1.392.3.475.2.083.383.042.55-.125l1.8-1.8c.033-.033.083-.15.15-.35 0-.033-.05-.15-.15-.35l-1.8-1.8c-.167-.167-.35-.208-.55-.125-.2.083-.3.242-.3.475V9h-2c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 8 12v1c0 .283.096.52.287.713.192.191.43.287.713.287Zm-5 4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 16V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 3h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H4Zm0-2h16V5H4v11Zm-2 5a.967.967 0 0 1-.712-.288A.968.968 0 0 1 1 20c0-.283.096-.52.288-.712A.967.967 0 0 1 2 19h20c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 22 21H2Z"/></svg>', xr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M9 14c.283 0 .52-.096.713-.287A.968.968 0 0 0 10 13v-1c0-.283.096-.52.287-.713A.968.968 0 0 1 11 11h2v.8c0 .233.1.392.3.475.2.083.383.042.55-.125l1.8-1.8c.033-.033.083-.15.15-.35 0-.033-.05-.15-.15-.35l-1.8-1.8c-.167-.167-.35-.208-.55-.125-.2.083-.3.242-.3.475V9h-2c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 8 12v1c0 .283.096.52.287.713.192.191.43.287.713.287Zm-5 4c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 16V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 3h16c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v11c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 20 18H4Zm-2 3a.967.967 0 0 1-.712-.288A.968.968 0 0 1 1 20c0-.283.096-.52.288-.712A.967.967 0 0 1 2 19h20c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 22 21H2Z"/></svg>', Ar = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m18.9 20.3-5.6-5.6A6.096 6.096 0 0 1 9.5 16c-1.817 0-3.354-.629-4.612-1.887C3.629 12.854 3 11.317 3 9.5c0-1.817.63-3.354 1.888-4.613C6.146 3.629 7.683 3 9.5 3c1.817 0 3.354.629 4.613 1.887C15.371 6.146 16 7.683 16 9.5a6.096 6.096 0 0 1-1.3 3.8l5.625 5.625a.918.918 0 0 1 .275.675c0 .267-.1.5-.3.7a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM9.5 14c1.25 0 2.313-.437 3.188-1.312S14 10.75 14 9.5c0-1.25-.437-2.313-1.312-3.188S10.75 5 9.5 5c-1.25 0-2.313.437-3.188 1.312S5 8.25 5 9.5c0 1.25.437 2.313 1.312 3.188S8.25 14 9.5 14Z"/></svg>', yr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M13.875 22h-3.75a.934.934 0 0 1-.65-.25.997.997 0 0 1-.325-.625l-.3-2.325a3.771 3.771 0 0 1-.612-.3 8.194 8.194 0 0 1-.563-.375l-2.175.9a1.119 1.119 0 0 1-.7.025.93.93 0 0 1-.55-.425L2.4 15.4a.94.94 0 0 1-.125-.7.96.96 0 0 1 .375-.6l1.875-1.425a2.39 2.39 0 0 1-.025-.338v-.675c0-.108.008-.22.025-.337L2.65 9.9a.96.96 0 0 1-.375-.6.94.94 0 0 1 .125-.7l1.85-3.225a.788.788 0 0 1 .537-.438 1.23 1.23 0 0 1 .713.038l2.175.9c.183-.133.375-.258.575-.375.2-.117.4-.217.6-.3l.3-2.325a.997.997 0 0 1 .325-.625c.183-.167.4-.25.65-.25h3.75c.25 0 .467.083.65.25a.997.997 0 0 1 .325.625l.3 2.325c.217.083.421.183.613.3.191.117.379.242.562.375l2.175-.9c.233-.083.467-.092.7-.025a.93.93 0 0 1 .55.425L21.6 8.6a.94.94 0 0 1 .125.7.96.96 0 0 1-.375.6l-1.875 1.425c.017.117.025.229.025.337v.675c0 .109-.017.221-.05.338l1.875 1.425c.2.15.325.35.375.6a.94.94 0 0 1-.125.7l-1.85 3.2a.985.985 0 0 1-.562.438c-.242.075-.48.07-.713-.013l-2.125-.9a6.842 6.842 0 0 1-.575.375c-.2.117-.4.217-.6.3l-.3 2.325a.997.997 0 0 1-.325.625.934.934 0 0 1-.65.25Zm-1.825-6.5c.967 0 1.792-.342 2.475-1.025A3.372 3.372 0 0 0 15.55 12c0-.967-.342-1.792-1.025-2.475A3.373 3.373 0 0 0 12.05 8.5c-.983 0-1.813.342-2.488 1.025A3.394 3.394 0 0 0 8.55 12c0 .967.337 1.792 1.012 2.475S11.067 15.5 12.05 15.5Zm0-2c-.417 0-.77-.146-1.062-.438A1.444 1.444 0 0 1 10.55 12c0-.417.146-.77.438-1.062a1.444 1.444 0 0 1 1.062-.438c.417 0 .771.146 1.063.438.291.291.437.645.437 1.062 0 .417-.146.77-.437 1.062a1.447 1.447 0 0 1-1.063.438ZM11 20h1.975l.35-2.65a5.61 5.61 0 0 0 1.438-.588c.441-.258.845-.57 1.212-.937l2.475 1.025.975-1.7-2.15-1.625c.083-.233.142-.48.175-.738a6.142 6.142 0 0 0 0-1.575 3.535 3.535 0 0 0-.175-.737l2.15-1.625-.975-1.7-2.475 1.05a5.573 5.573 0 0 0-1.212-.963 5.625 5.625 0 0 0-1.438-.587L13 4h-1.975l-.35 2.65a5.608 5.608 0 0 0-1.437.587 5.984 5.984 0 0 0-1.213.938L5.55 7.15l-.975 1.7 2.15 1.6c-.083.25-.142.5-.175.75a6.122 6.122 0 0 0 0 1.575c.033.25.092.5.175.75l-2.15 1.625.975 1.7 2.475-1.05a5.537 5.537 0 0 0 2.65 1.55L11 20Z"/></svg>', kr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M13.875 22h-3.75a.934.934 0 0 1-.65-.25.997.997 0 0 1-.325-.625l-.3-2.325a3.771 3.771 0 0 1-.612-.3 8.194 8.194 0 0 1-.563-.375l-2.175.9a1.119 1.119 0 0 1-.7.025.93.93 0 0 1-.55-.425L2.4 15.4a.94.94 0 0 1-.125-.7.96.96 0 0 1 .375-.6l1.875-1.425a2.39 2.39 0 0 1-.025-.338v-.675c0-.108.008-.22.025-.337L2.65 9.9a.96.96 0 0 1-.375-.6.94.94 0 0 1 .125-.7l1.85-3.225a.788.788 0 0 1 .537-.438 1.23 1.23 0 0 1 .713.038l2.175.9c.183-.133.375-.258.575-.375.2-.117.4-.217.6-.3l.3-2.325a.997.997 0 0 1 .325-.625c.183-.167.4-.25.65-.25h3.75c.25 0 .467.083.65.25a.997.997 0 0 1 .325.625l.3 2.325c.217.083.421.183.613.3.191.117.379.242.562.375l2.175-.9c.233-.083.467-.092.7-.025a.93.93 0 0 1 .55.425L21.6 8.6a.94.94 0 0 1 .125.7.96.96 0 0 1-.375.6l-1.875 1.425c.017.117.025.229.025.337v.675c0 .109-.017.221-.05.338l1.875 1.425c.2.15.325.35.375.6a.94.94 0 0 1-.125.7l-1.85 3.2a.985.985 0 0 1-.562.438c-.242.075-.48.07-.713-.013l-2.125-.9a6.842 6.842 0 0 1-.575.375c-.2.117-.4.217-.6.3l-.3 2.325a.997.997 0 0 1-.325.625.934.934 0 0 1-.65.25Zm-1.825-6.5c.967 0 1.792-.342 2.475-1.025A3.372 3.372 0 0 0 15.55 12c0-.967-.342-1.792-1.025-2.475A3.373 3.373 0 0 0 12.05 8.5c-.983 0-1.813.342-2.488 1.025A3.394 3.394 0 0 0 8.55 12c0 .967.337 1.792 1.012 2.475S11.067 15.5 12.05 15.5Z"/></svg>', _r = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M18 22a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 15 19c0-.117.008-.238.025-.363s.042-.237.075-.337l-7.05-4.1c-.283.25-.6.446-.95.588-.35.141-.717.212-1.1.212a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 3 12c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 6 9c.383 0 .75.07 1.1.213.35.141.667.337.95.587l7.05-4.1a1.843 1.843 0 0 1-.075-.338A2.738 2.738 0 0 1 15 5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 18 2c.833 0 1.542.292 2.125.875S21 4.167 21 5s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 18 8c-.383 0-.75-.07-1.1-.213a3.293 3.293 0 0 1-.95-.587L8.9 11.3c.033.1.058.212.075.337a2.742 2.742 0 0 1 0 .726 1.838 1.838 0 0 1-.075.337l7.05 4.1c.283-.25.6-.446.95-.588.35-.141.717-.212 1.1-.212.833 0 1.542.292 2.125.875S21 18.167 21 19s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 18 22Zm0-16a.97.97 0 0 0 .712-.287A.967.967 0 0 0 19 5a.967.967 0 0 0-.288-.713A.968.968 0 0 0 18 4a.968.968 0 0 0-.712.287A.967.967 0 0 0 17 5c0 .283.096.52.288.713A.968.968 0 0 0 18 6ZM6 13c.283 0 .52-.096.713-.287A.968.968 0 0 0 7 12a.968.968 0 0 0-.287-.713A.967.967 0 0 0 6 11a.967.967 0 0 0-.713.287A.968.968 0 0 0 5 12c0 .283.096.52.287.713.192.191.43.287.713.287Zm12 7c.283 0 .52-.096.712-.288A.968.968 0 0 0 19 19a.968.968 0 0 0-.288-.712A.968.968 0 0 0 18 18a.968.968 0 0 0-.712.288A.968.968 0 0 0 17 19c0 .283.096.52.288.712A.965.965 0 0 0 18 20Z"/></svg>', Zr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M18 22a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 15 19c0-.117.008-.238.025-.363s.042-.237.075-.337l-7.05-4.1c-.283.25-.6.446-.95.587A2.9 2.9 0 0 1 6 15a2.893 2.893 0 0 1-2.125-.875A2.893 2.893 0 0 1 3 12c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 6 9c.383 0 .75.07 1.1.212.35.142.667.338.95.588l7.05-4.1a1.843 1.843 0 0 1-.075-.337A2.749 2.749 0 0 1 15 5c0-.833.292-1.542.875-2.125A2.893 2.893 0 0 1 18 2c.833 0 1.542.292 2.125.875S21 4.167 21 5s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 18 8a2.9 2.9 0 0 1-1.1-.213 3.284 3.284 0 0 1-.95-.587L8.9 11.3c.033.1.058.212.075.337a2.753 2.753 0 0 1 0 .725 1.838 1.838 0 0 1-.075.338l7.05 4.1c.283-.25.6-.446.95-.588.35-.141.717-.212 1.1-.212.833 0 1.542.292 2.125.875S21 18.167 21 19s-.292 1.542-.875 2.125A2.893 2.893 0 0 1 18 22Z"/></svg>', Cr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.95 12.7-1.375-1.4c-.2-.2-.438-.3-.712-.3-.275 0-.513.1-.713.3-.2.2-.304.438-.313.712a.93.93 0 0 0 .288.713l2.125 2.125c.2.2.433.3.7.3.267 0 .5-.1.7-.3l4.25-4.25c.2-.2.3-.438.3-.712 0-.275-.1-.513-.3-.713-.2-.2-.438-.3-.713-.3-.274 0-.512.1-.712.3L10.95 12.7ZM12 21.925h-.25a.495.495 0 0 1-.225-.05c-2.183-.683-3.983-2.038-5.4-4.063C4.708 15.787 4 13.55 4 11.1V6.375c0-.417.12-.792.362-1.125.242-.333.555-.575.938-.725l6-2.25a2.07 2.07 0 0 1 .7-.125c.233 0 .467.042.7.125l6 2.25c.383.15.696.392.938.725.241.333.362.708.362 1.125V11.1c0 2.45-.708 4.688-2.125 6.713-1.417 2.024-3.217 3.379-5.4 4.062-.083.033-.242.05-.475.05Zm0-2.025c1.733-.55 3.167-1.65 4.3-3.3s1.7-3.483 1.7-5.5V6.375l-6-2.25-6 2.25V11.1c0 2.017.567 3.85 1.7 5.5s2.567 2.75 4.3 3.3Z"/></svg>', $r = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m10.95 12.7-1.375-1.4c-.2-.2-.438-.3-.712-.3-.275 0-.513.1-.713.3-.2.2-.304.438-.313.712a.93.93 0 0 0 .288.713l2.125 2.125c.2.2.433.3.7.3.267 0 .5-.1.7-.3l4.25-4.25c.2-.2.3-.438.3-.712 0-.275-.1-.513-.3-.713-.2-.2-.438-.3-.713-.3-.274 0-.512.1-.712.3L10.95 12.7ZM12 21.925h-.25a.495.495 0 0 1-.225-.05c-2.183-.683-3.983-2.038-5.4-4.063C4.708 15.787 4 13.55 4 11.1V6.375c0-.417.12-.792.362-1.125.242-.333.555-.575.938-.725l6-2.25a2.07 2.07 0 0 1 .7-.125c.233 0 .467.042.7.125l6 2.25c.383.15.696.392.938.725.241.333.362.708.362 1.125V11.1c0 2.45-.708 4.688-2.125 6.713-1.417 2.024-3.217 3.379-5.4 4.062-.083.033-.242.05-.475.05Z"/></svg>', Mr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17a.97.97 0 0 0 .713-.288A.967.967 0 0 0 13 16v-4a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 11a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 12v4c0 .283.096.52.287.712.192.192.43.288.713.288Zm0-8c.283 0 .52-.096.713-.287A.967.967 0 0 0 13 8a.967.967 0 0 0-.287-.712A.968.968 0 0 0 12 7a.968.968 0 0 0-.713.288A.967.967 0 0 0 11 8c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 12.925h-.25a.495.495 0 0 1-.225-.05c-2.183-.683-3.983-2.038-5.4-4.063C4.708 15.787 4 13.55 4 11.1V6.375c0-.417.12-.792.362-1.125.242-.333.555-.575.938-.725l6-2.25a2.07 2.07 0 0 1 .7-.125c.233 0 .467.042.7.125l6 2.25c.383.15.696.392.938.725.241.333.362.708.362 1.125V11.1c0 2.45-.708 4.688-2.125 6.713-1.417 2.024-3.217 3.379-5.4 4.062-.083.033-.242.05-.475.05Zm0-2.025c1.733-.55 3.167-1.65 4.3-3.3s1.7-3.483 1.7-5.5V6.375l-6-2.25-6 2.25V11.1c0 2.017.567 3.85 1.7 5.5s2.567 2.75 4.3 3.3Z"/></svg>', Vr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17a.97.97 0 0 0 .713-.288A.967.967 0 0 0 13 16v-4a.968.968 0 0 0-.287-.713A.968.968 0 0 0 12 11a.968.968 0 0 0-.713.287A.968.968 0 0 0 11 12v4c0 .283.096.52.287.712.192.192.43.288.713.288Zm0-8c.283 0 .52-.096.713-.287A.967.967 0 0 0 13 8a.967.967 0 0 0-.287-.712A.968.968 0 0 0 12 7a.968.968 0 0 0-.713.288A.967.967 0 0 0 11 8c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 12.925h-.25a.495.495 0 0 1-.225-.05c-2.183-.683-3.983-2.038-5.4-4.063C4.708 15.787 4 13.55 4 11.1V6.375c0-.417.12-.792.362-1.125.242-.333.555-.575.938-.725l6-2.25a2.07 2.07 0 0 1 .7-.125c.233 0 .467.042.7.125l6 2.25c.383.15.696.392.938.725.241.333.362.708.362 1.125V11.1c0 2.45-.708 4.688-2.125 6.713-1.417 2.024-3.217 3.379-5.4 4.062-.083.033-.242.05-.475.05Z"/></svg>', Hr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m18.85 16.05-1.5-1.55c.2-.533.358-1.088.475-1.662A8.72 8.72 0 0 0 18 11.1V6.375l-6-2.25L8.35 5.5 6.8 3.95l4.5-1.675c.233-.083.467-.125.7-.125.233 0 .467.042.7.125l6 2.25c.383.15.696.392.938.725.241.333.362.708.362 1.125V11.1a11.233 11.233 0 0 1-1.15 4.95Zm.25 5.85-2.55-2.55a8.67 8.67 0 0 1-1.775 1.45 10.5 10.5 0 0 1-2.05.975 1.9 1.9 0 0 1-.338.075 2.925 2.925 0 0 1-.387.025c-.133 0-.262-.004-.387-.012a1.346 1.346 0 0 1-.338-.063c-2.133-.717-3.88-2.08-5.238-4.088C4.68 15.704 4 13.5 4 11.1V6.8L2.1 4.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.949.949 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Zm-7.1-2a7.253 7.253 0 0 0 1.675-.775 8.306 8.306 0 0 0 1.475-1.175L6 8.8v2.3c0 2.017.567 3.85 1.7 5.5s2.567 2.75 4.3 3.3Z"/></svg>', Sr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M18.85 16.05 6.8 3.95l4.5-1.675c.233-.083.467-.125.7-.125.233 0 .467.042.7.125l6 2.25c.383.15.696.392.938.725.241.333.362.708.362 1.125V11.1a11.233 11.233 0 0 1-1.15 4.95Zm.25 5.85-2.55-2.55a8.67 8.67 0 0 1-1.775 1.45 10.5 10.5 0 0 1-2.05.975 1.9 1.9 0 0 1-.338.075 2.925 2.925 0 0 1-.387.025c-.133 0-.262-.004-.387-.012a1.346 1.346 0 0 1-.338-.063c-2.133-.717-3.88-2.08-5.238-4.088C4.68 15.704 4 13.5 4 11.1V6.8L2.1 4.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.949.949 0 0 1 .275.7.948.948 0 0 1-.275.7.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z"/></svg>', Dr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 20V8c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 6h2c0-1.1.392-2.042 1.175-2.825C9.958 2.392 10.9 2 12 2s2.042.392 2.825 1.175C15.608 3.958 16 4.9 16 6h2c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22H6Zm0-2h12V8h-2v2c0 .283-.096.52-.287.713A.968.968 0 0 1 15 11a.968.968 0 0 1-.713-.287A.968.968 0 0 1 14 10V8h-4v2c0 .283-.096.52-.287.713A.967.967 0 0 1 9 11a.967.967 0 0 1-.713-.287A.968.968 0 0 1 8 10V8H6v12Zm4-14h4c0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 4c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 6Z"/></svg>', Lr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 4 20V8c0-.55.196-1.02.588-1.412A1.926 1.926 0 0 1 6 6h2c0-1.1.392-2.042 1.175-2.825C9.958 2.392 10.9 2 12 2s2.042.392 2.825 1.175C15.608 3.958 16 4.9 16 6h2c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v12c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22H6Zm3-11c.283 0 .52-.096.713-.287A.968.968 0 0 0 10 10V8H8v2c0 .283.096.52.287.713.192.191.43.287.713.287Zm1-5h4c0-.55-.196-1.02-.588-1.412A1.926 1.926 0 0 0 12 4c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 10 6Zm5 5c.283 0 .52-.096.713-.287A.968.968 0 0 0 16 10V8h-2v2c0 .283.096.52.287.713.192.191.43.287.713.287Z"/></svg>', Or = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 20c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 18c.55 0 1.02.196 1.412.587C8.804 18.98 9 19.45 9 20s-.196 1.02-.588 1.413A1.926 1.926 0 0 1 7 22Zm10 0c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 15 20c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 17 18c.55 0 1.02.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 22ZM6.15 6l2.4 5h7l2.75-5H6.15ZM7 17c-.75 0-1.317-.33-1.7-.988-.383-.658-.4-1.312-.05-1.962L6.6 11.6 3 4H1.975a.927.927 0 0 1-.7-.288A.99.99 0 0 1 1 3c0-.283.096-.52.288-.712A.968.968 0 0 1 2 2h1.625c.183 0 .358.05.525.15a.93.93 0 0 1 .375.425L5.2 4h14.75c.45 0 .758.167.925.5.167.333.158.683-.025 1.05l-3.55 6.4a2.034 2.034 0 0 1-.725.775A1.93 1.93 0 0 1 15.55 13H8.1L7 15h11.025c.283 0 .517.096.7.287.183.192.275.43.275.713s-.096.52-.288.712A.968.968 0 0 1 18 17H7Z"/></svg>', Er = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 5 20c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 7 18c.55 0 1.02.196 1.412.587C8.804 18.98 9 19.45 9 20s-.196 1.02-.588 1.413A1.926 1.926 0 0 1 7 22Zm10 0c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 15 20c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 17 18c.55 0 1.02.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.413A1.926 1.926 0 0 1 17 22ZM7 17c-.75 0-1.317-.33-1.7-.988-.383-.658-.4-1.312-.05-1.962L6.6 11.6 3 4H1.975a.927.927 0 0 1-.7-.288A.99.99 0 0 1 1 3c0-.283.096-.52.288-.712A.968.968 0 0 1 2 2h1.625c.183 0 .358.05.525.15a.93.93 0 0 1 .375.425L5.2 4h14.75c.45 0 .758.167.925.5.167.333.158.683-.025 1.05l-3.55 6.4a2.034 2.034 0 0 1-.725.775A1.93 1.93 0 0 1 15.55 13H8.1L7 15h11.025c.283 0 .517.096.7.287.183.192.275.43.275.713s-.096.52-.288.712A.968.968 0 0 1 18 17H7Z"/></svg>', Tr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.325 16.275a1.11 1.11 0 0 1-.275-.737c0-.276.092-.505.275-.688l1.85-1.85H10a.967.967 0 0 1-.713-.287A.968.968 0 0 1 9 12c0-.283.096-.52.287-.713A.967.967 0 0 1 10 11h7.175l-1.85-1.85c-.2-.2-.3-.438-.3-.713 0-.274.1-.512.3-.712.183-.2.413-.3.688-.3.274 0 .504.092.687.275l3.6 3.6c.1.1.17.208.212.325.042.117.063.242.063.375s-.02.258-.063.375a.877.877 0 0 1-.212.325l-3.6 3.6c-.217.217-.454.313-.712.287a1.054 1.054 0 0 1-.663-.312ZM5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h6a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.713A.968.968 0 0 1 11 5H5v14h6a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 11 21H5Z"/></svg>', zr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m19.1 21.9-2.325-2.325a30.46 30.46 0 0 1-.7.387 7.19 7.19 0 0 1-.725.338.888.888 0 0 1-.925-.125c-.283-.217-.425-.517-.425-.9a.78.78 0 0 1 .162-.487.956.956 0 0 1 .413-.313 5.419 5.419 0 0 0 .75-.35L12 14.8v2.775c0 .45-.204.762-.613.938-.408.174-.77.104-1.087-.213L7 15H4a.968.968 0 0 1-.713-.287A.968.968 0 0 1 3 14v-4c0-.283.096-.52.287-.713A.968.968 0 0 1 4 9h2.2L2.1 4.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.948.948 0 0 1 .275.7.949.949 0 0 1-.275.7.949.949 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM15.35 3.65a8.905 8.905 0 0 1 4.1 3.275c1.033 1.5 1.55 3.183 1.55 5.05 0 .883-.12 1.733-.363 2.55A8.804 8.804 0 0 1 19.6 16.8l-1.45-1.45a6.797 6.797 0 0 0 .638-1.625c.141-.567.212-1.15.212-1.75 0-1.467-.404-2.783-1.212-3.95a6.973 6.973 0 0 0-3.213-2.55.838.838 0 0 1-.425-.325.882.882 0 0 1-.15-.5c0-.367.142-.658.425-.875a.888.888 0 0 1 .925-.125Zm.9 9.8L14 11.2V7.95a4.151 4.151 0 0 1 1.838 1.65 4.567 4.567 0 0 1 .6 3.138 4.344 4.344 0 0 1-.188.712ZM12 9.2 9.4 6.6l.9-.9c.317-.317.68-.388 1.087-.213.409.175.613.488.613.938V9.2Zm-2 5.95V12.8L8.2 11H5v2h2.85L10 15.15Z"/></svg>', Ir = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m19.1 21.9-2.325-2.325a30.46 30.46 0 0 1-.7.387 7.19 7.19 0 0 1-.725.338.888.888 0 0 1-.925-.125c-.283-.217-.425-.517-.425-.9a.78.78 0 0 1 .162-.487.956.956 0 0 1 .413-.313 5.419 5.419 0 0 0 .75-.35L12 14.8v2.775c0 .45-.204.762-.613.938-.408.174-.77.104-1.087-.213L7 15H4a.968.968 0 0 1-.713-.287A.968.968 0 0 1 3 14v-4c0-.283.096-.52.287-.713A.968.968 0 0 1 4 9h2.2L2.1 4.9a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17a.948.948 0 0 1 .275.7.949.949 0 0 1-.275.7.949.949 0 0 1-.7.275.948.948 0 0 1-.7-.275ZM15.35 3.65a8.905 8.905 0 0 1 4.1 3.275c1.033 1.5 1.55 3.183 1.55 5.05 0 .883-.12 1.733-.363 2.55A8.804 8.804 0 0 1 19.6 16.8l-1.45-1.45a6.797 6.797 0 0 0 .638-1.625c.141-.567.212-1.15.212-1.75 0-1.467-.404-2.783-1.212-3.95a6.973 6.973 0 0 0-3.213-2.55.838.838 0 0 1-.425-.325.882.882 0 0 1-.15-.5c0-.367.142-.658.425-.875a.888.888 0 0 1 .925-.125Zm.9 9.8L14 11.2V7.95a4.151 4.151 0 0 1 1.838 1.65 4.567 4.567 0 0 1 .6 3.138 4.344 4.344 0 0 1-.188.712ZM12 9.2 9.4 6.6l.9-.9c.317-.317.68-.388 1.087-.213.409.175.613.488.613.938V9.2Z"/></svg>', Pr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.35 20.3a.888.888 0 0 1-.925-.125c-.283-.217-.425-.517-.425-.9a.78.78 0 0 1 .162-.487.958.958 0 0 1 .413-.313 6.972 6.972 0 0 0 3.213-2.55c.808-1.167 1.212-2.483 1.212-3.95s-.404-2.783-1.212-3.95a6.972 6.972 0 0 0-3.213-2.55.838.838 0 0 1-.425-.325.882.882 0 0 1-.15-.5c0-.367.142-.658.425-.875a.888.888 0 0 1 .925-.125 8.904 8.904 0 0 1 4.1 3.275c1.033 1.5 1.55 3.183 1.55 5.05s-.517 3.55-1.55 5.05a8.904 8.904 0 0 1-4.1 3.275ZM4 15a.967.967 0 0 1-.712-.287A.967.967 0 0 1 3 14v-4c0-.283.096-.52.288-.712A.968.968 0 0 1 4 9h3l3.3-3.3c.317-.317.68-.387 1.087-.212.409.175.613.487.613.937v11.15c0 .45-.204.762-.613.938-.408.174-.77.104-1.087-.213L7 15H4Zm10 1V7.95a4.21 4.21 0 0 1 1.813 1.625c.458.733.687 1.542.687 2.425 0 .883-.23 1.683-.688 2.4A4.251 4.251 0 0 1 14 16Zm-4-7.15L7.85 11H5v2h2.85L10 15.15v-6.3Z"/></svg>', Br = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M15.35 20.3a.888.888 0 0 1-.925-.125c-.283-.217-.425-.517-.425-.9a.78.78 0 0 1 .162-.487.958.958 0 0 1 .413-.313 6.972 6.972 0 0 0 3.213-2.55c.808-1.167 1.212-2.483 1.212-3.95s-.404-2.783-1.212-3.95a6.972 6.972 0 0 0-3.213-2.55.838.838 0 0 1-.425-.325.882.882 0 0 1-.15-.5c0-.367.142-.658.425-.875a.888.888 0 0 1 .925-.125 8.904 8.904 0 0 1 4.1 3.275c1.033 1.5 1.55 3.183 1.55 5.05s-.517 3.55-1.55 5.05a8.904 8.904 0 0 1-4.1 3.275ZM4 15a.967.967 0 0 1-.712-.287A.967.967 0 0 1 3 14v-4c0-.283.096-.52.288-.712A.968.968 0 0 1 4 9h3l3.3-3.3c.317-.317.68-.387 1.087-.212.409.175.613.487.613.937v11.15c0 .45-.204.762-.613.938-.408.174-.77.104-1.087-.213L7 15H4Zm10 1V7.95a4.21 4.21 0 0 1 1.813 1.625c.458.733.687 1.542.687 2.425 0 .883-.23 1.683-.688 2.4A4.251 4.251 0 0 1 14 16Z"/></svg>', Fr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 8.3a.55.55 0 0 1-.262-.075A.387.387 0 0 1 18.55 8l-.8-1.75-1.75-.8a.387.387 0 0 1-.225-.188A.55.55 0 0 1 15.7 5a.55.55 0 0 1 .075-.263A.387.387 0 0 1 16 4.55l1.75-.8.8-1.75a.387.387 0 0 1 .188-.225A.55.55 0 0 1 19 1.7a.55.55 0 0 1 .262.075c.092.05.155.125.188.225l.8 1.75 1.75.8c.1.033.175.096.225.187A.55.55 0 0 1 22.3 5a.55.55 0 0 1-.075.262.386.386 0 0 1-.225.188l-1.75.8-.8 1.75a.386.386 0 0 1-.188.225A.549.549 0 0 1 19 8.3Zm0 14a.55.55 0 0 1-.262-.075.387.387 0 0 1-.188-.225l-.8-1.75-1.75-.8a.387.387 0 0 1-.225-.188A.55.55 0 0 1 15.7 19a.55.55 0 0 1 .075-.262.386.386 0 0 1 .225-.188l1.75-.8.8-1.75a.387.387 0 0 1 .188-.225A.55.55 0 0 1 19 15.7a.55.55 0 0 1 .262.075c.092.05.155.125.188.225l.8 1.75 1.75.8c.1.033.175.096.225.188.05.091.075.179.075.262a.55.55 0 0 1-.075.262.387.387 0 0 1-.225.188l-1.75.8-.8 1.75a.386.386 0 0 1-.188.225.549.549 0 0 1-.262.075ZM9 18.575c-.183 0-.358-.05-.525-.15A.933.933 0 0 1 8.1 18l-1.6-3.5L3 12.9a.933.933 0 0 1-.425-.375c-.1-.167-.15-.342-.15-.525s.05-.358.15-.525A.932.932 0 0 1 3 11.1l3.5-1.6L8.1 6a.932.932 0 0 1 .375-.425c.167-.1.342-.15.525-.15s.358.05.525.15A.93.93 0 0 1 9.9 6l1.6 3.5 3.5 1.6a.932.932 0 0 1 .425.375c.1.167.15.342.15.525s-.05.358-.15.525A.933.933 0 0 1 15 12.9l-3.5 1.6L9.9 18a.933.933 0 0 1-.375.425c-.167.1-.342.15-.525.15Zm0-3.425L10 13l2.15-1L10 11 9 8.85 8 11l-2.15 1L8 13l1 2.15Z"/></svg>', jr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 8.3a.546.546 0 0 1-.262-.075A.387.387 0 0 1 18.55 8l-.8-1.75-1.75-.8a.386.386 0 0 1-.225-.188A.547.547 0 0 1 15.7 5c0-.083.025-.171.075-.263A.387.387 0 0 1 16 4.55l1.75-.8.8-1.75a.387.387 0 0 1 .188-.225A.546.546 0 0 1 19 1.7c.083 0 .171.025.263.075.091.05.154.125.187.225l.8 1.75 1.75.8c.1.033.175.096.225.187.05.092.075.18.075.263a.55.55 0 0 1-.075.262.386.386 0 0 1-.225.188l-1.75.8-.8 1.75a.387.387 0 0 1-.187.225A.552.552 0 0 1 19 8.3Zm0 14a.546.546 0 0 1-.262-.075.387.387 0 0 1-.188-.225l-.8-1.75-1.75-.8a.387.387 0 0 1-.225-.188A.546.546 0 0 1 15.7 19a.55.55 0 0 1 .075-.262.387.387 0 0 1 .225-.188l1.75-.8.8-1.75a.387.387 0 0 1 .188-.225A.546.546 0 0 1 19 15.7c.083 0 .171.025.263.075.091.05.154.125.187.225l.8 1.75 1.75.8c.1.033.175.096.225.188.05.091.075.179.075.262a.55.55 0 0 1-.075.262.386.386 0 0 1-.225.188l-1.75.8-.8 1.75a.387.387 0 0 1-.187.225.552.552 0 0 1-.263.075ZM9 18.575c-.183 0-.358-.05-.525-.15A.932.932 0 0 1 8.1 18l-1.6-3.5L3 12.9a.933.933 0 0 1-.425-.375c-.1-.167-.15-.342-.15-.525s.05-.358.15-.525A.932.932 0 0 1 3 11.1l3.5-1.6L8.1 6a.932.932 0 0 1 .375-.425c.167-.1.342-.15.525-.15s.358.05.525.15A.93.93 0 0 1 9.9 6l1.6 3.5 3.5 1.6a.932.932 0 0 1 .425.375c.1.167.15.342.15.525s-.05.358-.15.525A.933.933 0 0 1 15 12.9l-3.5 1.6L9.9 18a.932.932 0 0 1-.375.425c-.167.1-.342.15-.525.15Z"/></svg>', Rr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><g fill="none" stroke="inherit" stroke-width="3" style="animation:1.25s linear 0s infinite normal none running rotate-animation;transform-origin:12px center"><circle cx="12" cy="12" r="10" opacity=".3"/><circle cx="12" cy="12" r="10" stroke-dasharray="60" style="animation:1.25s ease-in-out 0s infinite alternate none running stroke-animation"/></g></svg>', Nr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m8.85 17.825 3.15-1.9 3.15 1.925-.825-3.6 2.775-2.4-3.65-.325-1.45-3.4-1.45 3.375-3.65.325 2.775 2.425-.825 3.575Zm3.15.45-4.15 2.5a.908.908 0 0 1-.575.15.966.966 0 0 1-.525-.2 1.199 1.199 0 0 1-.35-.438.874.874 0 0 1-.05-.587l1.1-4.725L3.775 11.8a.958.958 0 0 1-.275-1.075 1.07 1.07 0 0 1 .3-.45c.133-.117.317-.192.55-.225l4.85-.425 1.875-4.45c.083-.2.213-.35.388-.45.175-.1.354-.15.537-.15.183 0 .363.05.537.15.175.1.305.25.388.45l1.875 4.45 4.85.425c.233.033.417.108.55.225.133.117.233.267.3.45a.958.958 0 0 1-.275 1.075l-3.675 3.175 1.1 4.725a.874.874 0 0 1-.05.587 1.2 1.2 0 0 1-.35.438.966.966 0 0 1-.525.2.908.908 0 0 1-.575-.15l-4.15-2.5Z"/></svg>', Ur = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m12 18.275-4.15 2.5a.908.908 0 0 1-.575.15.966.966 0 0 1-.525-.2 1.199 1.199 0 0 1-.35-.438.874.874 0 0 1-.05-.587l1.1-4.725L3.775 11.8a.958.958 0 0 1-.275-1.075 1.07 1.07 0 0 1 .3-.45c.133-.117.317-.192.55-.225l4.85-.425 1.875-4.45c.083-.2.213-.35.388-.45.175-.1.354-.15.537-.15.183 0 .363.05.537.15.175.1.305.25.388.45l1.875 4.45 4.85.425c.233.033.417.108.55.225.133.117.233.267.3.45a.958.958 0 0 1-.275 1.075l-3.675 3.175 1.1 4.725a.874.874 0 0 1-.05.587 1.2 1.2 0 0 1-.35.438.966.966 0 0 1-.525.2.908.908 0 0 1-.575-.15l-4.15-2.5Z"/></svg>', Wr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 15c.833 0 1.542-.292 2.125-.875A2.893 2.893 0 0 0 15 12c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 12 9c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 9 12c0 .833.292 1.542.875 2.125A2.893 2.893 0 0 0 12 15Zm0 2c-1.383 0-2.563-.488-3.537-1.463C7.488 14.563 7 13.383 7 12s.487-2.563 1.463-3.537C9.438 7.488 10.617 7 12 7s2.563.487 3.537 1.463C16.512 9.438 17 10.617 17 12s-.488 2.563-1.463 3.537C14.563 16.512 13.383 17 12 17ZM2 13a.967.967 0 0 1-.712-.287A.968.968 0 0 1 1 12a.97.97 0 0 1 .288-.713A.967.967 0 0 1 2 11h2c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.967.967 0 0 1 4 13H2Zm18 0a.968.968 0 0 1-.712-.287A.968.968 0 0 1 19 12a.97.97 0 0 1 .288-.713A.968.968 0 0 1 20 11h2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.713A.968.968 0 0 1 22 13h-2Zm-8-8a.968.968 0 0 1-.713-.287A.967.967 0 0 1 11 4V2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 12 1a.97.97 0 0 1 .713.288A.968.968 0 0 1 13 2v2c0 .283-.096.52-.287.713A.968.968 0 0 1 12 5Zm0 18a.968.968 0 0 1-.713-.288A.968.968 0 0 1 11 22v-2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 12 19a.97.97 0 0 1 .713.288A.968.968 0 0 1 13 20v2a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 23ZM5.65 7.05 4.575 6a.87.87 0 0 1-.288-.7 1.09 1.09 0 0 1 .288-.725c.2-.2.442-.3.725-.3s.517.1.7.3L7.05 5.65c.183.2.275.433.275.7 0 .267-.092.5-.275.7a.856.856 0 0 1-.688.288 1.07 1.07 0 0 1-.712-.288ZM18 19.425l-1.05-1.075a1.019 1.019 0 0 1-.275-.713c0-.274.092-.504.275-.687a.856.856 0 0 1 .688-.287c.274.008.512.104.712.287L19.425 18a.87.87 0 0 1 .287.7 1.085 1.085 0 0 1-.287.725c-.2.2-.442.3-.725.3a.908.908 0 0 1-.7-.3ZM16.95 7.05a.856.856 0 0 1-.287-.688 1.07 1.07 0 0 1 .287-.712L18 4.575a.87.87 0 0 1 .7-.288c.283.009.525.105.725.288.2.2.3.442.3.725s-.1.517-.3.7L18.35 7.05c-.2.183-.433.275-.7.275-.267 0-.5-.092-.7-.275ZM4.575 19.425c-.2-.2-.3-.442-.3-.725s.1-.517.3-.7l1.075-1.05c.2-.183.438-.275.712-.275.276 0 .505.092.688.275.2.183.296.413.288.688a1.07 1.07 0 0 1-.288.712L6 19.425a.87.87 0 0 1-.7.287 1.085 1.085 0 0 1-.725-.287Z"/></svg>', qr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 17c-1.383 0-2.563-.488-3.537-1.463C7.488 14.563 7 13.383 7 12s.487-2.563 1.463-3.537C9.438 7.488 10.617 7 12 7s2.563.487 3.537 1.463C16.512 9.438 17 10.617 17 12s-.488 2.563-1.463 3.537C14.563 16.512 13.383 17 12 17ZM2 13a.967.967 0 0 1-.712-.287A.968.968 0 0 1 1 12a.97.97 0 0 1 .288-.713A.967.967 0 0 1 2 11h2c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.967.967 0 0 1 4 13H2Zm18 0a.968.968 0 0 1-.712-.287A.968.968 0 0 1 19 12a.97.97 0 0 1 .288-.713A.968.968 0 0 1 20 11h2a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.713A.968.968 0 0 1 22 13h-2Zm-8-8a.968.968 0 0 1-.713-.287A.967.967 0 0 1 11 4V2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 12 1a.97.97 0 0 1 .713.288A.968.968 0 0 1 13 2v2c0 .283-.096.52-.287.713A.968.968 0 0 1 12 5Zm0 18a.968.968 0 0 1-.713-.288A.968.968 0 0 1 11 22v-2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 12 19a.97.97 0 0 1 .713.288A.968.968 0 0 1 13 20v2a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 23ZM5.65 7.05 4.575 6a.87.87 0 0 1-.288-.7 1.09 1.09 0 0 1 .288-.725c.2-.2.442-.3.725-.3s.517.1.7.3L7.05 5.65c.183.2.275.433.275.7 0 .267-.092.5-.275.7a.856.856 0 0 1-.688.288 1.07 1.07 0 0 1-.712-.288ZM18 19.425l-1.05-1.075a1.019 1.019 0 0 1-.275-.713c0-.274.092-.504.275-.687a.856.856 0 0 1 .688-.287c.274.008.512.104.712.287L19.425 18a.87.87 0 0 1 .287.7 1.085 1.085 0 0 1-.287.725c-.2.2-.442.3-.725.3a.908.908 0 0 1-.7-.3ZM16.95 7.05a.856.856 0 0 1-.287-.688 1.07 1.07 0 0 1 .287-.712L18 4.575a.87.87 0 0 1 .7-.288c.283.009.525.105.725.288.2.2.3.442.3.725s-.1.517-.3.7L18.35 7.05c-.2.183-.433.275-.7.275-.267 0-.5-.092-.7-.275ZM4.575 19.425c-.2-.2-.3-.442-.3-.725s.1-.517.3-.7l1.075-1.05c.2-.183.438-.275.712-.275.276 0 .505.092.688.275.2.183.296.413.288.688a1.07 1.07 0 0 1-.288.712L6 19.425a.87.87 0 0 1-.7.287 1.085 1.085 0 0 1-.725-.287Z"/></svg>', Kr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m14.05 15.65 2.075-2.05c.133-.133.275-.196.425-.188a.65.65 0 0 1 .425.213c.133.133.2.275.2.425 0 .15-.067.292-.2.425l-2.525 2.55a.55.55 0 0 1-.4.175.55.55 0 0 1-.4-.175l-1.4-1.4a.61.61 0 0 1-.2-.438.61.61 0 0 1 .2-.437.622.622 0 0 1 .45-.2c.167 0 .317.067.45.2l.9.9ZM8.725 9.625l.975.975c.133.133.283.2.45.2a.622.622 0 0 0 .45-.2.61.61 0 0 0 .2-.438.61.61 0 0 0-.2-.437l-.975-.95.975-.975a.622.622 0 0 0 .2-.45.622.622 0 0 0-.2-.45.61.61 0 0 0-.438-.2.61.61 0 0 0-.437.2l-.95.975L7.8 6.9a.622.622 0 0 0-.45-.2.622.622 0 0 0-.45.2.61.61 0 0 0-.2.438.61.61 0 0 0 .2.437l.975.95L6.9 9.7a.622.622 0 0 0-.2.45c0 .167.067.317.2.45s.28.2.438.2a.61.61 0 0 0 .437-.2l.95-.975ZM12 22c-1.417 0-2.733-.254-3.95-.762a9.76 9.76 0 0 1-3.175-2.113 9.76 9.76 0 0 1-2.112-3.175C2.254 14.733 2 13.417 2 12c0-1.4.254-2.708.763-3.925A9.76 9.76 0 0 1 4.875 4.9c.9-.9 1.958-1.608 3.175-2.125C9.267 2.258 10.583 2 12 2c1.4 0 2.708.258 3.925.775A9.927 9.927 0 0 1 19.1 4.9c.9.9 1.608 1.958 2.125 3.175C21.742 9.292 22 10.6 22 12c0 1.417-.258 2.733-.775 3.95a9.927 9.927 0 0 1-2.125 3.175c-.9.9-1.958 1.604-3.175 2.113C14.708 21.745 13.4 22 12 22Zm0-1.5c2.417 0 4.438-.813 6.063-2.438C19.688 16.438 20.5 14.418 20.5 12c0-1.2-.217-2.317-.65-3.35a8.332 8.332 0 0 0-1.8-2.7l-12.1 12.1a8.332 8.332 0 0 0 2.7 1.8c1.033.433 2.15.65 3.35.65Z"/></svg>', Yr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M16.3 13.275a.948.948 0 0 1-.275-.7.95.95 0 0 1 .275-.7L18.175 10H12a.968.968 0 0 1-.713-.287A.968.968 0 0 1 11 9a.97.97 0 0 1 .287-.712A.968.968 0 0 1 12 8h6.175L16.3 6.125c-.183-.183-.275-.42-.275-.712s.092-.53.275-.713a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l3.6 3.6c.1.1.17.208.212.325.042.117.063.242.063.375s-.02.258-.063.375a.876.876 0 0 1-.212.325l-3.6 3.6a.907.907 0 0 1-.7.262.992.992 0 0 1-.7-.287ZM6.3 19.3l-3.6-3.6a.877.877 0 0 1-.212-.325A1.106 1.106 0 0 1 2.425 15c0-.133.02-.258.063-.375A.878.878 0 0 1 2.7 14.3l3.6-3.6a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275c.183.183.275.42.275.713 0 .291-.092.529-.275.712L5.825 14H12c.283 0 .52.096.713.287.191.192.287.43.287.713s-.096.52-.287.713A.967.967 0 0 1 12 16H5.825L7.7 17.875a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7.992.992 0 0 1-.7.288.907.907 0 0 1-.7-.263Z"/></svg>', Gr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 7a.968.968 0 0 1-.712-.287A.968.968 0 0 1 2 6V2.75A.728.728 0 0 1 2.75 2a.728.728 0 0 1 .75.75V4c1.2-.983 2.525-1.73 3.975-2.237A13.578 13.578 0 0 1 12 1c1.567 0 3.075.254 4.525.762A13.027 13.027 0 0 1 20.5 4V2.75a.728.728 0 0 1 .75-.75.728.728 0 0 1 .75.75V6a.97.97 0 0 1-.288.713A.968.968 0 0 1 21 7h-3.25a.728.728 0 0 1-.75-.75.728.728 0 0 1 .75-.75h2.15a12.056 12.056 0 0 0-3.675-2.225A11.72 11.72 0 0 0 12 2.5a11.72 11.72 0 0 0-4.225.775A12.056 12.056 0 0 0 4.1 5.5h2.15a.728.728 0 0 1 .75.75.728.728 0 0 1-.75.75H3Zm7.55 16a1.99 1.99 0 0 1-.8-.163 2.076 2.076 0 0 1-.65-.437l-3.725-3.725a1.935 1.935 0 0 1-.563-1.25c-.041-.5.055-.95.288-1.35a1.78 1.78 0 0 1 .888-.775c.374-.15.729-.183 1.062-.1l1.95.45V7.5c0-.7.242-1.292.725-1.775C10.208 5.242 10.8 5 11.5 5s1.292.242 1.775.725C13.758 6.208 14 6.8 14 7.5v4.3h.65a.746.746 0 0 1 .45.15l3.7 1.8c.4.183.696.48.887.887.192.409.246.83.163 1.263l-.9 5.45a1.89 1.89 0 0 1-.7 1.188 2.02 2.02 0 0 1-1.3.462h-6.4Zm0-2h6.4l.95-5.45-4.15-2.05H12v-6a.49.49 0 0 0-.137-.362A.49.49 0 0 0 11.5 7a.49.49 0 0 0-.363.138A.49.49 0 0 0 11 7.5v10.6l-4.25-.9 3.8 3.8Z"/></svg>', Qr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 7a.968.968 0 0 1-.712-.287A.968.968 0 0 1 2 6V2.75A.728.728 0 0 1 2.75 2a.728.728 0 0 1 .75.75V4c1.2-.983 2.525-1.73 3.975-2.237A13.578 13.578 0 0 1 12 1c1.567 0 3.075.254 4.525.762A13.027 13.027 0 0 1 20.5 4V2.75a.728.728 0 0 1 .75-.75.728.728 0 0 1 .75.75V6a.97.97 0 0 1-.288.713A.968.968 0 0 1 21 7h-3.25a.728.728 0 0 1-.75-.75.728.728 0 0 1 .75-.75h2.15a12.056 12.056 0 0 0-3.675-2.225A11.72 11.72 0 0 0 12 2.5a11.72 11.72 0 0 0-4.225.775A12.056 12.056 0 0 0 4.1 5.5h2.15a.728.728 0 0 1 .75.75.728.728 0 0 1-.75.75H3Zm8.2 16c-.3 0-.604-.054-.912-.163a1.928 1.928 0 0 1-.738-.437l-3.875-4.075a.992.992 0 0 1-.287-.7 1.22 1.22 0 0 1 .212-.75.987.987 0 0 1 .475-.363c.2-.075.425-.079.675-.012l3.25.75V6.5c0-.417.146-.77.438-1.063A1.447 1.447 0 0 1 11.5 5c.417 0 .77.146 1.063.438.291.291.437.645.437 1.062v6h.9c.133 0 .283.017.45.05.167.033.317.083.45.15l4.1 2.05c.383.183.675.47.875.863.2.391.267.795.2 1.212l-.625 4.45c-.083.5-.308.913-.675 1.238a1.9 1.9 0 0 1-1.3.487H11.2Z"/></svg>', Xr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 19a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 18c0-.283.096-.52.288-.712A.965.965 0 0 1 4 17h4c.283 0 .521.096.713.288A.967.967 0 0 1 9 18a.97.97 0 0 1-.287.712A.968.968 0 0 1 8 19H4ZM4 7a.967.967 0 0 1-.712-.287A.968.968 0 0 1 3 6c0-.283.096-.521.288-.713A.967.967 0 0 1 4 5h8a.97.97 0 0 1 .713.287A.97.97 0 0 1 13 6a.97.97 0 0 1-.287.713A.97.97 0 0 1 12 7H4Zm8 14a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 20v-4c0-.283.096-.521.288-.713A.967.967 0 0 1 12 15a.97.97 0 0 1 .713.287A.97.97 0 0 1 13 16v1h7c.283 0 .52.096.712.288A.965.965 0 0 1 21 18c0 .283-.096.52-.288.712A.965.965 0 0 1 20 19h-7v1a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 21Zm-4-6a.968.968 0 0 1-.713-.288A.967.967 0 0 1 7 14v-1H4a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 12c0-.283.096-.521.288-.713A.967.967 0 0 1 4 11h3v-1a.97.97 0 0 1 .287-.713A.97.97 0 0 1 8 9a.97.97 0 0 1 .713.287A.97.97 0 0 1 9 10v4a.97.97 0 0 1-.287.712A.968.968 0 0 1 8 15Zm4-2a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 12c0-.283.096-.521.288-.713A.967.967 0 0 1 12 11h8a.97.97 0 0 1 .712.287c.192.192.288.43.288.713s-.096.52-.288.712A.965.965 0 0 1 20 13h-8Zm4-4a.965.965 0 0 1-.712-.288A.965.965 0 0 1 15 8V4c0-.283.096-.521.288-.713A.967.967 0 0 1 16 3a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v1h3a.97.97 0 0 1 .712.287c.192.192.288.43.288.713a.968.968 0 0 1-.288.713A.967.967 0 0 1 20 7h-3v1c0 .283-.096.52-.288.712A.965.965 0 0 1 16 9Z"/></svg>', Jr = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22a9.676 9.676 0 0 1-3.875-.788 10.144 10.144 0 0 1-3.188-2.15 10.142 10.142 0 0 1-2.15-3.187A9.676 9.676 0 0 1 2 12c0-1.383.263-2.68.788-3.887a10.183 10.183 0 0 1 2.15-3.175 10.143 10.143 0 0 1 3.187-2.15A9.676 9.676 0 0 1 12 2c1.383 0 2.68.263 3.887.788a10.183 10.183 0 0 1 3.175 2.15 10.184 10.184 0 0 1 2.15 3.175A9.649 9.649 0 0 1 22 12v1.45c0 .983-.337 1.82-1.012 2.513C20.313 16.654 19.483 17 18.5 17c-.6 0-1.158-.133-1.675-.4a3.754 3.754 0 0 1-1.275-1.05A4.969 4.969 0 0 1 12 17c-1.383 0-2.563-.488-3.537-1.463C7.488 14.563 7 13.383 7 12s.487-2.563 1.463-3.537C9.438 7.488 10.617 7 12 7s2.563.487 3.537 1.463C16.512 9.438 17 10.617 17 12v1.45c0 .483.15.863.45 1.138.3.275.65.412 1.05.412s.75-.137 1.05-.412c.3-.275.45-.655.45-1.138V12c0-2.183-.788-4.063-2.363-5.638C16.063 4.787 14.183 4 12 4c-2.183 0-4.063.787-5.638 2.362C4.787 7.938 4 9.817 4 12s.787 4.063 2.362 5.637C7.938 19.212 9.817 20 12 20h4c.283 0 .52.096.712.288.192.191.288.429.288.712s-.096.52-.288.712A.968.968 0 0 1 16 22h-4Zm0-7c.833 0 1.542-.292 2.125-.875A2.893 2.893 0 0 0 15 12c0-.833-.292-1.542-.875-2.125A2.893 2.893 0 0 0 12 9c-.833 0-1.542.292-2.125.875A2.893 2.893 0 0 0 9 12c0 .833.292 1.542.875 2.125A2.893 2.893 0 0 0 12 15Z"/></svg>', e7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 16c-.533 0-1-.2-1.4-.6-.4-.4-.6-.867-.6-1.4v-2c0-.117.017-.242.05-.375s.067-.258.1-.375l3-7.05c.15-.333.4-.617.75-.85C5.25 3.117 5.617 3 6 3h11v13l-6 5.95a1.48 1.48 0 0 1-.887.438 1.652 1.652 0 0 1-.988-.188 1.58 1.58 0 0 1-.7-.7 1.28 1.28 0 0 1-.1-.925L9.45 16H3Zm12-.85V5H6l-3 7v2h9l-1.35 5.5L15 15.15ZM20 3c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v9c0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 20 16h-3v-2h3V5h-3V3h3Z"/></svg>', t7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 16c-.533 0-1-.2-1.4-.6-.4-.4-.6-.867-.6-1.4v-2a2.04 2.04 0 0 1 .15-.75l3-7.05c.15-.333.4-.617.75-.85C5.25 3.117 5.617 3 6 3h8c.55 0 1.02.196 1.412.587C15.804 3.98 16 4.45 16 5v10.175c0 .267-.054.52-.162.762a2.118 2.118 0 0 1-.438.638l-5.425 5.4c-.25.233-.546.375-.887.425a1.58 1.58 0 0 1-.988-.175 1.51 1.51 0 0 1-.688-.7c-.141-.3-.17-.608-.087-.925L8.45 16H3ZM20 3c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v9c0 .55-.196 1.02-.587 1.412A1.926 1.926 0 0 1 20 16c-.55 0-1.02-.196-1.413-.588A1.926 1.926 0 0 1 18 14V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 20 3Z"/></svg>', s7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M7 21V8l6-5.95c.25-.25.546-.396.887-.438.342-.041.671.021.988.188.317.167.55.4.7.7.15.3.183.608.1.925L14.55 8H21c.533 0 1 .2 1.4.6.4.4.6.867.6 1.4v2c0 .117-.017.242-.05.375s-.067.258-.1.375l-3 7.05c-.15.333-.4.617-.75.85-.35.233-.717.35-1.1.35H7ZM9 8.85V19h9l3-7v-2h-9l1.35-5.5L9 8.85ZM4 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 19v-9c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 8h3v2H4v9h3v2H4Z"/></svg>', r7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10 21c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 8 19V8.825c0-.267.054-.52.162-.763a2.14 2.14 0 0 1 .438-.637l5.425-5.4c.25-.233.546-.375.887-.425.342-.05.671.008.988.175.317.167.546.4.687.7.142.3.171.608.088.925L15.55 8H21c.533 0 1 .2 1.4.6.4.4.6.867.6 1.4v2a2.04 2.04 0 0 1-.15.75l-3 7.05c-.15.333-.4.617-.75.85-.35.233-.717.35-1.1.35h-8Zm-6 0c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 19v-9c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 4 8c.55 0 1.02.196 1.412.587C5.804 8.98 6 9.45 6 10v9c0 .55-.196 1.02-.588 1.413A1.926 1.926 0 0 1 4 21Z"/></svg>', o7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10 3a.968.968 0 0 1-.713-.288A.968.968 0 0 1 9 2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 10 1h4a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 14 3h-4Zm2 11c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 13V9a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 8a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 9v4c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 8a8.646 8.646 0 0 1-3.488-.712A9.192 9.192 0 0 1 5.65 19.35a9.193 9.193 0 0 1-1.938-2.863A8.646 8.646 0 0 1 3 13a8.65 8.65 0 0 1 .712-3.488A9.193 9.193 0 0 1 5.65 6.65a9.193 9.193 0 0 1 2.862-1.938A8.646 8.646 0 0 1 12 4a8.92 8.92 0 0 1 2.975.5c.95.333 1.842.817 2.675 1.45l.725-.725a.918.918 0 0 1 .675-.275c.267 0 .5.1.7.3a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-.7.7a9.723 9.723 0 0 1 1.45 2.675c.333.95.5 1.942.5 2.975a8.646 8.646 0 0 1-.712 3.488 9.192 9.192 0 0 1-1.938 2.862 9.192 9.192 0 0 1-2.863 1.938A8.646 8.646 0 0 1 12 22Zm0-2c1.933 0 3.583-.683 4.95-2.05C18.317 16.583 19 14.933 19 13c0-1.933-.683-3.583-2.05-4.95C15.583 6.683 13.933 6 12 6c-1.933 0-3.583.683-4.95 2.05C5.683 9.417 5 11.067 5 13c0 1.933.683 3.583 2.05 4.95C8.417 19.317 10.067 20 12 20Z"/></svg>', i7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M10 3a.968.968 0 0 1-.713-.288A.968.968 0 0 1 9 2a.97.97 0 0 1 .287-.712A.968.968 0 0 1 10 1h4a.97.97 0 0 1 .713.288c.191.191.287.429.287.712s-.096.52-.287.712A.968.968 0 0 1 14 3h-4Zm2 11c.283 0 .52-.096.713-.287A.968.968 0 0 0 13 13V9a.967.967 0 0 0-.287-.713A.968.968 0 0 0 12 8a.968.968 0 0 0-.713.287A.967.967 0 0 0 11 9v4c0 .283.096.52.287.713.192.191.43.287.713.287Zm0 8a8.646 8.646 0 0 1-3.488-.712A9.192 9.192 0 0 1 5.65 19.35a9.193 9.193 0 0 1-1.938-2.863A8.646 8.646 0 0 1 3 13a8.65 8.65 0 0 1 .712-3.488A9.193 9.193 0 0 1 5.65 6.65a9.193 9.193 0 0 1 2.862-1.938A8.646 8.646 0 0 1 12 4a8.92 8.92 0 0 1 2.975.5c.95.333 1.842.817 2.675 1.45l.725-.725a.918.918 0 0 1 .675-.275c.267 0 .5.1.7.3a.948.948 0 0 1 .275.7.948.948 0 0 1-.275.7l-.7.7a9.723 9.723 0 0 1 1.45 2.675c.333.95.5 1.942.5 2.975a8.646 8.646 0 0 1-.712 3.488 9.192 9.192 0 0 1-1.938 2.862 9.192 9.192 0 0 1-2.863 1.938A8.646 8.646 0 0 1 12 22Z"/></svg>', a7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M17 18a.968.968 0 0 1-.712-.288A.968.968 0 0 1 16 17c0-.283.096-.52.288-.712A.968.968 0 0 1 17 16h1.6l-5.2-5.15-3.3 3.3a1.034 1.034 0 0 1-.7.275.942.942 0 0 1-.375-.075 1.313 1.313 0 0 1-.325-.2L2.675 8.075A.918.918 0 0 1 2.4 7.4c0-.267.1-.5.3-.7a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275L9.4 12l3.3-3.3c.1-.1.208-.17.325-.212.117-.042.242-.063.375-.063s.258.02.375.063a.877.877 0 0 1 .325.212l5.9 5.9V13a.97.97 0 0 1 .288-.713A.968.968 0 0 1 21 12a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v4c0 .283-.096.52-.288.712A.967.967 0 0 1 21 18h-4Z"/></svg>', n7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2.7 17.3a.933.933 0 0 1-.275-.688c0-.274.092-.512.275-.712l6-6.05c.1-.083.208-.15.325-.2a.942.942 0 0 1 .375-.075 1.034 1.034 0 0 1 .7.275l3.3 3.3L18.6 8H17a.968.968 0 0 1-.712-.287A.968.968 0 0 1 16 7a.97.97 0 0 1 .288-.713A.968.968 0 0 1 17 6h4a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v4a.97.97 0 0 1-.288.713A.967.967 0 0 1 21 12a.968.968 0 0 1-.712-.287A.968.968 0 0 1 20 11V9.4l-5.9 5.9c-.1.1-.208.17-.325.212a1.105 1.105 0 0 1-.375.063c-.133 0-.258-.02-.375-.063a.878.878 0 0 1-.325-.212L9.4 12l-5.325 5.325a.918.918 0 0 1-.675.275.96.96 0 0 1-.7-.3Z"/></svg>', l7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 16a.965.965 0 0 1-.712-.288A.965.965 0 0 1 11 15V7.85L9.125 9.725c-.2.2-.433.3-.7.3-.267 0-.508-.108-.725-.325a.93.93 0 0 1-.287-.713A.975.975 0 0 1 7.7 8.3l3.6-3.6a.88.88 0 0 1 .325-.213c.117-.041.242-.062.375-.062s.258.02.375.062a.883.883 0 0 1 .325.213l3.6 3.6c.2.2.296.437.287.712a.976.976 0 0 1-.287.688c-.2.2-.437.304-.712.312a.93.93 0 0 1-.713-.287L13 7.85V15a.97.97 0 0 1-.287.712A.968.968 0 0 1 12 16Zm-6 4c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 4 18v-2a.97.97 0 0 1 .287-.713A.97.97 0 0 1 5 15a.97.97 0 0 1 .713.287A.97.97 0 0 1 6 16v2h12v-2c0-.283.096-.521.288-.713A.967.967 0 0 1 19 15a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 18 20H6Z"/></svg>', c7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h12c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v4.5l3.15-3.15c.167-.167.35-.208.55-.125.2.083.3.242.3.475v8.6c0 .233-.1.392-.3.475-.2.083-.383.042-.55-.125L18 13.5V18c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 16 20H4Zm0-2h12V6H4v12Z"/></svg>', h7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 20c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 2 18V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 4 4h12c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v4.5l3.15-3.15c.15-.15.33-.188.538-.112.208.074.312.229.312.462v8.6c0 .233-.104.387-.313.462-.208.075-.387.038-.537-.112L18 13.5V18c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 16 20H4Z"/></svg>', d7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 19.75a2 2 0 0 1-2-2V6.25a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v11.5a2 2 0 0 1-2 2H4Zm0-11h2.5v-2.5H4v2.5Zm4.5 0H11v-2.5H8.5v2.5Zm4.5 0h2.5v-2.5H13v2.5Zm4.5 0H20v-2.5h-2.5v2.5Zm0 4.5H20v-2.5h-2.5v2.5Zm-4.5 0h2.5v-2.5H13v2.5Zm-4.5 0H11v-2.5H8.5v2.5Zm-2-2.5H4v2.5h2.5v-2.5Zm11 7H20v-2.5h-2.5v2.5Zm-4.5 0h2.5v-2.5H13v2.5Zm-4.5 0H11v-2.5H8.5v2.5Zm-4.5 0h2.5v-2.5H4v2.5Z"/></svg>', p7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 8.75a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H3Zm5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H8.25Zm5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H13.5Zm5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1H21a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1h-2.25Zm0 5.5a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1H21a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1h-2.25Zm-5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H13.5Zm-5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H8.25Zm-5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H3Zm15.75 5.5a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1H21a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1h-2.25Zm-5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H13.5Zm-5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H8.25Zm-5.25 0a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1h2.25a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1H3Z"/></svg>', u7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 11c-.55 0-1.021-.196-1.413-.588A1.925 1.925 0 0 1 3 9V5c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 3h4c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v4c0 .55-.196 1.02-.587 1.412A1.927 1.927 0 0 1 9 11H5Zm0 10a1.93 1.93 0 0 1-1.413-.587A1.928 1.928 0 0 1 3 19v-4c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 13h4c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v4a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 9 21H5Zm10-10c-.55 0-1.02-.196-1.412-.588A1.923 1.923 0 0 1 13 9V5c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 15 3h4c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v4c0 .55-.196 1.02-.587 1.412A1.927 1.927 0 0 1 19 11h-4Zm0 10c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 13 19v-4c0-.55.196-1.021.588-1.413A1.925 1.925 0 0 1 15 13h4c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v4a1.93 1.93 0 0 1-.587 1.413A1.928 1.928 0 0 1 19 21h-4Z"/></svg>', g7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 17V7c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 5 5h14c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v10c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 19H5c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 17Zm2-8h2V7H5v2Zm4 0h10V7H9v2Zm0 4h10v-2H9v2Zm0 4h10v-2H9v2Zm-4 0h2v-2H5v2Zm0-4h2v-2H5v2Z"/></svg>', v7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 9a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 8V6c0-.283.096-.521.288-.713A.967.967 0 0 1 4 5h2a.97.97 0 0 1 .713.287A.97.97 0 0 1 7 6v2a.97.97 0 0 1-.287.712A.968.968 0 0 1 6 9H4Zm5 0a.965.965 0 0 1-.712-.288A.965.965 0 0 1 8 8V6c0-.283.096-.521.288-.713A.967.967 0 0 1 9 5h11a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2c0 .283-.096.52-.288.712A.965.965 0 0 1 20 9H9Zm0 5a.965.965 0 0 1-.712-.288A.965.965 0 0 1 8 13v-2c0-.283.096-.521.288-.713A.967.967 0 0 1 9 10h11a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2c0 .283-.096.52-.288.712A.965.965 0 0 1 20 14H9Zm-5 0a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 13v-2c0-.283.096-.521.288-.713A.967.967 0 0 1 4 10h2a.97.97 0 0 1 .713.287A.97.97 0 0 1 7 11v2a.97.97 0 0 1-.287.712A.968.968 0 0 1 6 14H4Zm5 5a.965.965 0 0 1-.712-.288A.965.965 0 0 1 8 18v-2c0-.283.096-.521.288-.713A.967.967 0 0 1 9 15h11a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2c0 .283-.096.52-.288.712A.965.965 0 0 1 20 19H9Zm-5 0a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 18v-2c0-.283.096-.521.288-.713A.967.967 0 0 1 4 15h2a.97.97 0 0 1 .713.287A.97.97 0 0 1 7 16v2a.97.97 0 0 1-.287.712A.968.968 0 0 1 6 19H4Z"/></svg>', m7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 5v2h-4V5h4ZM9 5v6H5V5h4Zm10 8v6h-4v-6h4ZM9 17v2H5v-2h4ZM21 5a2 2 0 0 0-2-2h-6v6h8V5ZM11 3H5a2 2 0 0 0-2 2v8h8V3Zm10 8h-8v10h6a2 2 0 0 0 2-2v-8Zm-10 4H3v4a2 2 0 0 0 2 2h6v-6Z"/></svg>', f7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M3 13h8V3H5a2 2 0 0 0-2 2v8Zm0 6a2 2 0 0 0 2 2h6v-6H3v4Zm10 2h6a2 2 0 0 0 2-2v-8h-8v10Zm0-18v6h8V5a2 2 0 0 0-2-2h-6Z"/></svg>', b7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 15v-2H5v2h14Zm0-4V9H5v2h14Zm0-4V5H5v2h14ZM5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h14c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Zm14-2v-2H5v2h14Z"/></svg>', w7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M4 15.5a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 14.5V14c0-.283.096-.521.288-.713A.967.967 0 0 1 4 13h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v.5c0 .283-.096.52-.288.712A.965.965 0 0 1 20 15.5H4ZM4 11a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 10v-.5c0-.283.096-.521.288-.713A.967.967 0 0 1 4 8.5h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v.5c0 .283-.096.52-.288.712A.965.965 0 0 1 20 11H4Zm0-4.5a.967.967 0 0 1-.712-.287A.968.968 0 0 1 3 5.5V5c0-.283.096-.521.288-.713A.967.967 0 0 1 4 4h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v.5a.968.968 0 0 1-.288.713A.967.967 0 0 1 20 6.5H4ZM4 20a.965.965 0 0 1-.712-.288A.965.965 0 0 1 3 19v-.5c0-.283.096-.52.288-.712A.965.965 0 0 1 4 17.5h16c.283 0 .52.096.712.288A.965.965 0 0 1 21 18.5v.5c0 .283-.096.52-.288.712A.965.965 0 0 1 20 20H4Z"/></svg>', x7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M19 19v-3H5v3h14Zm0-5v-4H5v4h14Zm0-6V5H5v3h14ZM5 21c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 19V5c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 5 3h14c.55 0 1.02.196 1.413.587.39.393.587.863.587 1.413v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 21H5Z"/></svg>', A7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M21 6.625a.97.97 0 0 1-.288.713.968.968 0 0 1-.712.287H4a.968.968 0 0 1-.712-.287A.968.968 0 0 1 3 6.625V4c0-.283.096-.52.288-.712A.968.968 0 0 1 4 3h16c.283 0 .52.096.712.288A.965.965 0 0 1 21 4v2.625Zm0 6.725a.97.97 0 0 1-.288.713.968.968 0 0 1-.712.287H4a.967.967 0 0 1-.712-.287A.968.968 0 0 1 3 13.35v-2.725a.97.97 0 0 1 .288-.713A.968.968 0 0 1 4 9.625h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713v2.725ZM21 20c0 .283-.096.52-.288.712A.968.968 0 0 1 20 21H4a.967.967 0 0 1-.712-.288A.968.968 0 0 1 3 20v-2.65a.97.97 0 0 1 .288-.713A.967.967 0 0 1 4 16.35h16a.97.97 0 0 1 .712.287c.192.192.288.43.288.713V20Z"/></svg>', y7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5Zm10 1h6v3h-6V6Zm-2 0H5v3h6V6Zm0 5H5v3h6v-3Zm0 5H5v3h6v-3Zm2 3v-3h6v3h-6Zm0-5v-3h6v3h-6Z" clip-rule="evenodd"/></svg>', k7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M5 3a2 2 0 0 0-2 2v1h18V5a2 2 0 0 0-2-2H5Zm16 5h-8v3h8V8Zm0 5h-8v3h8v-3Zm0 5h-8v3h6a2 2 0 0 0 2-2v-1Zm-10 3v-3H3v1a2 2 0 0 0 2 2h6Zm-8-5h8v-3H3v3Zm0-5h8V8H3v3Z"/></svg>', _7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m16.1 13.3-1.45-1.45c.15-.783-.075-1.517-.675-2.2-.6-.683-1.375-.95-2.325-.8L10.2 7.4c.283-.133.57-.233.863-.3A4.2 4.2 0 0 1 12 7c1.25 0 2.313.437 3.188 1.312.874.875 1.312 1.938 1.312 3.188 0 .333-.033.646-.1.938a4.286 4.286 0 0 1-.3.862Zm3.2 3.15-1.45-1.4a10.947 10.947 0 0 0 1.688-1.588A8.898 8.898 0 0 0 20.8 11.5a9.843 9.843 0 0 0-3.588-4.013C15.654 6.496 13.917 6 12 6c-.483 0-.958.033-1.425.1a9.624 9.624 0 0 0-1.375.3L7.65 4.85A11.08 11.08 0 0 1 12 4c2.383 0 4.525.63 6.425 1.887 1.9 1.259 3.325 2.896 4.275 4.913a.81.81 0 0 1 .1.313c.017.125.025.254.025.387a1.972 1.972 0 0 1-.125.7 10.896 10.896 0 0 1-3.4 4.25Zm-.2 5.45-3.5-3.45c-.583.183-1.17.32-1.762.413-.592.091-1.205.137-1.838.137-2.383 0-4.525-.63-6.425-1.887-1.9-1.259-3.325-2.896-4.275-4.913a.813.813 0 0 1-.1-.313 2.93 2.93 0 0 1 0-.762.796.796 0 0 1 .1-.3c.35-.75.767-1.442 1.25-2.075A13.291 13.291 0 0 1 4.15 7L2.075 4.9a.933.933 0 0 1-.275-.688c0-.275.1-.512.3-.712a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17c.183.183.28.413.288.688a.93.93 0 0 1-.288.712.948.948 0 0 1-.7.275.949.949 0 0 1-.7-.275ZM5.55 8.4a10.72 10.72 0 0 0-1.325 1.425A9.013 9.013 0 0 0 3.2 11.5a9.834 9.834 0 0 0 3.588 4.012C8.346 16.505 10.083 17 12 17c.333 0 .658-.02.975-.063.317-.041.642-.087.975-.137l-.9-.95c-.183.05-.358.088-.525.113A3.537 3.537 0 0 1 12 16c-1.25 0-2.312-.438-3.187-1.313C7.938 13.813 7.5 12.75 7.5 11.5c0-.183.013-.358.038-.525a4.55 4.55 0 0 1 .112-.525L5.55 8.4Z"/></svg>', Z7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="m19.3 16.5-3.2-3.2c.133-.283.233-.57.3-.863.067-.291.1-.604.1-.937 0-1.25-.438-2.313-1.313-3.188C14.313 7.437 13.25 7 12 7a4.2 4.2 0 0 0-.938.1 4.286 4.286 0 0 0-.862.3L7.65 4.85A11.08 11.08 0 0 1 12 4c2.383 0 4.525.63 6.425 1.887 1.9 1.259 3.325 2.896 4.275 4.913a.81.81 0 0 1 .1.313c.017.125.025.254.025.387a1.972 1.972 0 0 1-.125.7 11.47 11.47 0 0 1-1.438 2.375A10.464 10.464 0 0 1 19.3 16.5Zm-.2 5.4-3.5-3.45c-.583.183-1.17.32-1.762.413-.592.091-1.205.137-1.838.137-2.383 0-4.525-.63-6.425-1.887-1.9-1.259-3.325-2.896-4.275-4.913a.813.813 0 0 1-.1-.313 2.93 2.93 0 0 1 0-.762.796.796 0 0 1 .1-.3c.35-.75.767-1.442 1.25-2.075A13.291 13.291 0 0 1 4.15 7L2.075 4.9a.933.933 0 0 1-.275-.688c0-.275.1-.512.3-.712a.948.948 0 0 1 .7-.275.95.95 0 0 1 .7.275l17 17c.183.183.28.413.288.688a.93.93 0 0 1-.288.712.948.948 0 0 1-.7.275.949.949 0 0 1-.7-.275ZM12 16c.183 0 .354-.008.512-.025.159-.017.33-.05.513-.1l-5.4-5.4c-.05.183-.083.354-.1.513a4.81 4.81 0 0 0-.025.512c0 1.25.438 2.313 1.313 3.188C9.688 15.562 10.75 16 12 16Zm2.65-4.15-3-3c.95-.15 1.725.117 2.325.8.6.683.825 1.417.675 2.2Z"/></svg>', C7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 16c1.25 0 2.313-.438 3.188-1.313.874-.874 1.312-1.937 1.312-3.187 0-1.25-.438-2.313-1.313-3.188C14.313 7.439 13.25 7 12 7c-1.25 0-2.312.438-3.187 1.313C7.938 9.187 7.5 10.25 7.5 11.5c0 1.25.438 2.313 1.313 3.188C9.688 15.562 10.75 16 12 16Zm0-1.8c-.75 0-1.387-.262-1.912-.787A2.604 2.604 0 0 1 9.3 11.5c0-.75.263-1.387.787-1.912A2.604 2.604 0 0 1 12 8.8c.75 0 1.387.262 1.912.787.525.526.788 1.163.788 1.913s-.262 1.387-.787 1.912A2.604 2.604 0 0 1 12 14.2Zm0 4.8c-2.317 0-4.433-.613-6.35-1.837-1.917-1.226-3.367-2.88-4.35-4.963a.812.812 0 0 1-.1-.313 2.93 2.93 0 0 1 0-.774.812.812 0 0 1 .1-.313c.983-2.083 2.433-3.738 4.35-4.963C7.567 4.614 9.683 4 12 4c2.317 0 4.433.612 6.35 1.838 1.917 1.224 3.367 2.879 4.35 4.962a.81.81 0 0 1 .1.313 2.925 2.925 0 0 1 0 .774.81.81 0 0 1-.1.313c-.983 2.083-2.433 3.738-4.35 4.963C16.433 18.387 14.317 19 12 19Zm0-2a9.544 9.544 0 0 0 5.188-1.488A9.772 9.772 0 0 0 20.8 11.5a9.772 9.772 0 0 0-3.613-4.013A9.544 9.544 0 0 0 12 6a9.545 9.545 0 0 0-5.187 1.487A9.773 9.773 0 0 0 3.2 11.5a9.773 9.773 0 0 0 3.613 4.012A9.544 9.544 0 0 0 12 17Z"/></svg>', $7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 16c1.25 0 2.313-.438 3.188-1.313.874-.874 1.312-1.937 1.312-3.187 0-1.25-.438-2.313-1.313-3.188C14.313 7.439 13.25 7 12 7c-1.25 0-2.312.438-3.187 1.313C7.938 9.187 7.5 10.25 7.5 11.5c0 1.25.438 2.313 1.313 3.188C9.688 15.562 10.75 16 12 16Zm0-1.8c-.75 0-1.387-.262-1.912-.787A2.604 2.604 0 0 1 9.3 11.5c0-.75.263-1.387.787-1.912A2.604 2.604 0 0 1 12 8.8c.75 0 1.387.262 1.912.787.525.526.788 1.163.788 1.913s-.262 1.387-.787 1.912A2.604 2.604 0 0 1 12 14.2Zm0 4.8c-2.317 0-4.433-.613-6.35-1.837-1.917-1.226-3.367-2.88-4.35-4.963a.812.812 0 0 1-.1-.313 2.93 2.93 0 0 1 0-.774.812.812 0 0 1 .1-.313c.983-2.083 2.433-3.738 4.35-4.963C7.567 4.614 9.683 4 12 4c2.317 0 4.433.612 6.35 1.838 1.917 1.224 3.367 2.879 4.35 4.962a.81.81 0 0 1 .1.313 2.925 2.925 0 0 1 0 .774.81.81 0 0 1-.1.313c-.983 2.083-2.433 3.738-4.35 4.963C16.433 18.387 14.317 19 12 19Z"/></svg>', M7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" fill-rule="evenodd" d="M6 4a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V8a4 4 0 0 0-4-4H6Zm14 4.535V8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v.535A3.982 3.982 0 0 1 6 8h12c.729 0 1.412.195 2 .535ZM18 10c.665 0 1.255.325 1.618.825l-3.487 2.79a1 1 0 0 1-.85.193L4.15 11.239A2 2 0 0 1 6 10h12ZM4 13.257l10.832 2.5a3 3 0 0 0 2.549-.58L20 13.08V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2.744Z" clip-rule="evenodd"/></svg>', V7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M6 20c-1.1 0-2.042-.392-2.825-1.175C2.392 18.042 2 17.1 2 16V8c0-1.1.392-2.042 1.175-2.825C3.958 4.392 4.9 4 6 4h12c1.1 0 2.042.392 2.825 1.175C21.608 5.958 22 6.9 22 8v8c0 1.1-.392 2.042-1.175 2.825C20.042 19.608 19.1 20 18 20H6ZM6 8h12c.367 0 .717.042 1.05.125.333.083.65.217.95.4V8c0-.55-.196-1.02-.587-1.412A1.926 1.926 0 0 0 18 6H6c-.55 0-1.02.196-1.412.588A1.926 1.926 0 0 0 4 8v.525c.3-.183.617-.317.95-.4A4.315 4.315 0 0 1 6 8Zm-1.85 3.25 11.125 2.7c.15.033.3.033.45 0 .15-.033.292-.1.425-.2l3.475-2.9a2.163 2.163 0 0 0-.7-.612A1.865 1.865 0 0 0 18 10H6c-.433 0-.813.113-1.138.338a1.941 1.941 0 0 0-.712.912Z"/></svg>', H7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2.725 21a.943.943 0 0 1-.5-.137.977.977 0 0 1-.35-.363 1.094 1.094 0 0 1-.137-.488.894.894 0 0 1 .137-.512l9.25-16a.984.984 0 0 1 .387-.375C11.671 3.042 11.833 3 12 3c.167 0 .33.042.488.125a.984.984 0 0 1 .387.375l9.25 16a.9.9 0 0 1 .138.512 1.104 1.104 0 0 1-.138.488.977.977 0 0 1-.35.363.943.943 0 0 1-.5.137H2.725Zm1.725-2h15.1L12 6 4.45 19ZM12 18a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 17a.967.967 0 0 0-.287-.712A.968.968 0 0 0 12 16a.965.965 0 0 0-.712.288A.965.965 0 0 0 11 17c0 .283.096.52.288.712A.965.965 0 0 0 12 18Zm0-3a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 14v-3a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 10a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 11v3c0 .283.096.52.288.712A.965.965 0 0 0 12 15Z"/></svg>', S7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M2.725 21a.943.943 0 0 1-.5-.137.977.977 0 0 1-.35-.363 1.094 1.094 0 0 1-.137-.488.894.894 0 0 1 .137-.512l9.25-16a.984.984 0 0 1 .387-.375C11.671 3.042 11.833 3 12 3c.167 0 .33.042.488.125a.984.984 0 0 1 .387.375l9.25 16a.9.9 0 0 1 .138.512 1.104 1.104 0 0 1-.138.488.977.977 0 0 1-.35.363.943.943 0 0 1-.5.137H2.725ZM12 18a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 17a.967.967 0 0 0-.287-.712A.968.968 0 0 0 12 16a.965.965 0 0 0-.712.288A.965.965 0 0 0 11 17c0 .283.096.52.288.712A.965.965 0 0 0 12 18Zm0-3a.968.968 0 0 0 .713-.288A.967.967 0 0 0 13 14v-3a.97.97 0 0 0-.287-.713A.97.97 0 0 0 12 10a.967.967 0 0 0-.712.287A.968.968 0 0 0 11 11v3c0 .283.096.52.288.712A.965.965 0 0 0 12 15Z"/></svg>', D7 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fill="inherit" stroke="none" d="M12 22a9.738 9.738 0 0 1-3.9-.788 10.099 10.099 0 0 1-3.175-2.137c-.9-.9-1.612-1.958-2.137-3.175A9.738 9.738 0 0 1 2 12a9.74 9.74 0 0 1 .788-3.9 10.099 10.099 0 0 1 2.137-3.175c.9-.9 1.958-1.612 3.175-2.137A9.738 9.738 0 0 1 12 2a9.74 9.74 0 0 1 3.9.788 10.098 10.098 0 0 1 3.175 2.137c.9.9 1.613 1.958 2.137 3.175A9.738 9.738 0 0 1 22 12a9.738 9.738 0 0 1-.788 3.9 10.098 10.098 0 0 1-2.137 3.175c-.9.9-1.958 1.613-3.175 2.137A9.738 9.738 0 0 1 12 22Zm-1-2.05V18c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 9 16v-1l-4.8-4.8c-.05.3-.096.6-.138.9-.041.3-.062.6-.062.9 0 2.017.662 3.783 1.987 5.3 1.325 1.517 2.996 2.4 5.013 2.65Zm6.9-2.55c.333-.367.633-.762.9-1.188a7.45 7.45 0 0 0 .662-1.325c.175-.458.309-.929.4-1.412.092-.483.138-.975.138-1.475a7.845 7.845 0 0 0-1.363-4.475A7.701 7.701 0 0 0 15 4.6V5c0 .55-.196 1.02-.588 1.412A1.926 1.926 0 0 1 13 7h-2v2c0 .283-.096.52-.287.713A.968.968 0 0 1 10 10H8v2h6c.283 0 .52.096.713.287.191.192.287.43.287.713v3h1c.433 0 .825.13 1.175.387.35.259.592.596.725 1.013Z"/></svg>', g2 = {
  "ad-badge": it,
  "add-chart-outlined": at,
  "add-chart": nt,
  "add-circle-outline": lt,
  "add-circle": ct,
  add: ht,
  "alert-badge-outline": dt,
  "alert-badge": pt,
  "alt-route": ut,
  "app-alert": gt,
  apps: vt,
  "archive-outline": mt,
  archive: ft,
  "arrow-down": bt,
  "arrow-left": wt,
  "arrow-right": xt,
  "arrow-up": At,
  "avatar-outlined": yt,
  avatar: kt,
  "badge-outline": _t,
  badge: Zt,
  "bank-outline": Ct,
  bank: $t,
  blur: Mt,
  "bolt-outline": Vt,
  bolt: Ht,
  "bookmark-outline": St,
  bookmark: Dt,
  building: Lt,
  "calculate-outline": Ot,
  calculate: Et,
  calendar: Tt,
  "cancel-circle-outline": zt,
  "cancel-circle": It,
  "caret-down": Pt,
  "caret-left": Bt,
  "caret-right": Ft,
  "caret-up": jt,
  "chart-area-outlined": Rt,
  "chart-area": Nt,
  "chart-bar": Ut,
  "chart-full-stacked-bar": Wt,
  "chart-line": qt,
  "chart-stacked-bar": Kt,
  "chart-stacked-line": Yt,
  "chat-notification-outline": Gt,
  "chat-notification": Qt,
  "chat-outline": Xt,
  "chat-warning-outline": Jt,
  "chat-warning": e5,
  chat: t5,
  "check-circle-outline": s5,
  "check-circle": r5,
  checkmark: o5,
  "checkbox-checked": i5,
  "checkbox-indeterminate": a5,
  checklist: n5,
  "chevron-down": l5,
  "chevron-left": c5,
  "chevron-right": h5,
  "chevron-up": d5,
  clicked: p5,
  "clipboard-outline": u5,
  clipboard: g5,
  "clock-fast-forward": v5,
  "clock-rewind": m5,
  clock: f5,
  close: b5,
  "cloud-outline": w5,
  cloud: x5,
  "coin-uk-outline": A5,
  "coin-uk": y5,
  "coin-usa-outline": k5,
  "coin-usa": _5,
  "collapse-vertical": Z5,
  collapse: C5,
  "comment-outline": $5,
  comment: M5,
  "commute-outline": V5,
  commute: H5,
  "compass-outline": S5,
  compass: D5,
  "conversation-outline": L5,
  conversation: O5,
  "copy-outlined": E5,
  copy: T5,
  "credit-card-outline": z5,
  "credit-card": I5,
  "critical-outline": P5,
  critical: B5,
  "crop-rotate": F5,
  crop: j5,
  "delete-icon": R5,
  "delete-outline": N5,
  "desktop-outline": U5,
  desktop: W5,
  diamond: q5,
  "disqo-disc": K5,
  download: Y5,
  drag: G5,
  "duplicate-outline": Q5,
  duplicate: X5,
  "edit-note": J5,
  "edit-outline": es,
  edit: ts,
  "email-check-outline": ss,
  "email-check": rs,
  "email-draft-outline": os,
  "email-draft": is,
  "email-forward-outline": as,
  "email-forward": ns,
  "email-notification-outline": ls,
  "email-notification": cs,
  "email-outline": hs,
  email: ds,
  "expand-horizontal": ps,
  "expand-vertical": us,
  expand: gs,
  export: vs,
  "external-link": ms,
  "feed-outline": fs,
  feed: bs,
  "file-audio": ws,
  "file-image": xs,
  "file-pdf": As,
  "file-slideshow": ys,
  "file-spreadsheet": ks,
  file: _s,
  "filter-circles": Zs,
  filter: Cs,
  fingerprint: $s,
  "flag-outline": Ms,
  flag: Vs,
  "folder-add-outline": Hs,
  "folder-add": Ss,
  "folder-avatar-outline": Ds,
  "folder-avatar": Ls,
  "folder-outlined": Os,
  folder: Es,
  "gift-card": Ts,
  hamburger: zs,
  hash: Is,
  "heart-outline": Ps,
  heart: Bs,
  "help-outline": Fs,
  help: js,
  "house-outline": Rs,
  house: Ns,
  http: Us,
  "info-outline": Ws,
  info: qs,
  insights: Ks,
  "interests-outline": Ys,
  interests: Gs,
  "laptop-outline": Qs,
  laptop: Xs,
  "lightbulb-outline": Js,
  lightbulb: e8,
  "link-add": t8,
  "link-disabled": s8,
  link: r8,
  list: o8,
  location: i8,
  "lock-open-outline": a8,
  "lock-open": n8,
  "lock-outline": l8,
  lock: c8,
  "loyalty-outline": h8,
  loyalty: d8,
  "map-pin-outline": p8,
  "map-pin": u8,
  "mic-off-outline": g8,
  "mic-off": v8,
  "mic-outline": m8,
  mic: f8,
  "minus-circle-outline": b8,
  "minus-circle": w8,
  "mobile-outline": x8,
  "mobile-sharing-outline": A8,
  "mobile-sharing": y8,
  mobile: k8,
  "moon-outlined": _8,
  moon: Z8,
  "more-horizontal": C8,
  "more-vertical": $8,
  "not-allowed": M8,
  "notification-active-outline": V8,
  "notification-active": H8,
  "notification-outline": S8,
  "notification-urgent-outline": D8,
  "notification-urgent": L8,
  notification: O8,
  "option-checkbox": E8,
  "option-radio": T8,
  "page-first": z8,
  "page-last": I8,
  "panorama-outline": P8,
  panorama: B8,
  "pause-outline": F8,
  pause: j8,
  "payments-outline": R8,
  payments: N8,
  "people-outlined": U8,
  people: W8,
  percentage: q8,
  "person-check-outline": K8,
  "person-check": Y8,
  "person-outlined": G8,
  "person-search-outline": Q8,
  "person-search": X8,
  person: J8,
  "phone-outline": er,
  phone: tr,
  "play-outline": sr,
  play: rr,
  "push-pin-outline": or,
  "push-pin": ir,
  "qr-code": ar,
  radar: nr,
  "radio-checked": lr,
  "reaction-outline": cr,
  reaction: hr,
  "receipt-outline": dr,
  receipt: pr,
  "refresh-horizontal": ur,
  "refresh-vertical": gr,
  referesh: vr,
  remove: mr,
  "savings-outline": fr,
  savings: br,
  "screen-share-outline": wr,
  "screen-share": xr,
  search: Ar,
  "settings-outlined": yr,
  settings: kr,
  "share-outline": _r,
  share: Zr,
  "shield-check-outline": Cr,
  "shield-check": $r,
  "shield-info-outline": Mr,
  "shield-info": Vr,
  "shield-remove-outline": Hr,
  "shield-remove": Sr,
  "shopping-bag-outline": Dr,
  "shopping-bag": Lr,
  "shopping-cart-outline": Or,
  "shopping-cart": Er,
  "sign-out": Tr,
  "sound-off-outline": zr,
  "sound-off": Ir,
  "sound-on-outline": Pr,
  "sound-on": Br,
  "sparkles-outline": Fr,
  sparkles: jr,
  spinner: Rr,
  "star-outline": Nr,
  star: Ur,
  "sun-outline": Wr,
  sun: qr,
  "survey-badge": Kr,
  "swap-horizontal": Yr,
  "swipe-outline": Gr,
  swipe: Qr,
  switches: Xr,
  "tag-person": Jr,
  "thumb-down-outline": e7,
  "thumb-down": t7,
  "thumb-up-outline": s7,
  "thumb-up": r7,
  "timer-outline": o7,
  timer: i7,
  "trending-down": a7,
  "trending-up": n7,
  upload: l7,
  "video-outline": c7,
  video: h7,
  "view-grid-compact-outlined": d7,
  "view-grid-compact": p7,
  "view-grid": u7,
  "view-list-outlined": g7,
  "view-list": v7,
  "view-mosaic-outlined": m7,
  "view-mosaic": f7,
  "view-row-compact-outlined": b7,
  "view-row-compact": w7,
  "view-row-outlined": x7,
  "view-row": A7,
  "view-table-outlined": y7,
  "view-table": k7,
  "visibility-off-outline": _7,
  "visibility-off": Z7,
  "visibility-on-outline": C7,
  "visibility-on": $7,
  "wallet-outline": M7,
  wallet: V7,
  "warning-outline": H7,
  warning: S7,
  world: D7
}, de = { sm: 16, md: 20, lg: 24, xl: 40 }, L7 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:inline-block}:host svg{display:block;pointer-events:none}@keyframes rotate-animation{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes stroke-animation{0%{stroke-dashoffset:20px}to{stroke-dashoffset:50px}}
`;
var O7 = Object.defineProperty, E7 = Object.getOwnPropertyDescriptor, W0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? E7(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && O7(e, s, o), o;
};
let i0 = class extends e0 {
  constructor() {
    super(...arguments), this.name = "avatar", this.size = "md", this.fill = "currentColor";
  }
  makeSvg() {
    const t = 'xmlns="http://www.w3.org/2000/svg" width="24" height="24"', e = `width="${de[this.size]}" 
    height="${de[this.size]}" 
    viewBox="0 0 24 24" 
    fill="${this.fill}"
    stroke="${this.fill}"
    aria-hidden="true" 
    role="img"`;
    return g2[this.name].replace(t, e);
  }
  render() {
    return g`${rt(this.makeSvg())}`;
  }
};
i0.styles = $(L7);
W0([
  l(),
  x(Object.keys(g2), "avatar")
], i0.prototype, "name", 2);
W0([
  l(),
  x(Object.keys(de), "md")
], i0.prototype, "size", 2);
W0([
  l()
], i0.prototype, "fill", 2);
i0 = W0([
  S("vds-icon")
], i0);
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const E = N0(class extends U0 {
  constructor(t) {
    var e;
    if (super(t), t.type !== Z1.ATTRIBUTE || t.name !== "class" || ((e = t.strings) === null || e === void 0 ? void 0 : e.length) > 2)
      throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(t) {
    return " " + Object.keys(t).filter((e) => t[e]).join(" ") + " ";
  }
  update(t, [e]) {
    var s, r;
    if (this.nt === void 0) {
      this.nt = /* @__PURE__ */ new Set(), t.strings !== void 0 && (this.st = new Set(t.strings.join(" ").split(/\s/).filter((i) => i !== "")));
      for (const i in e)
        e[i] && !(!((s = this.st) === null || s === void 0) && s.has(i)) && this.nt.add(i);
      return this.render(e);
    }
    const o = t.element.classList;
    this.nt.forEach((i) => {
      i in e || (o.remove(i), this.nt.delete(i));
    });
    for (const i in e) {
      const a = !!e[i];
      a === this.nt.has(i) || !((r = this.st) === null || r === void 0) && r.has(i) || (a ? (o.add(i), this.nt.add(i)) : (o.remove(i), this.nt.delete(i)));
    }
    return X;
  }
});
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const A = (t) => t ?? O, d1 = "[default]";
class I1 {
  constructor(e, ...s) {
    this.slotNames = [], (this.host = e).addController(this), this.slotNames = s, this.handleSlotChange = this.handleSlotChange.bind(this);
  }
  hasDefaultSlot() {
    return [...this.host.childNodes].some((e) => {
      const { nodeType: s, TEXT_NODE: r, textContent: o, ELEMENT_NODE: i } = e;
      if (s === r && o.trim() !== "")
        return !0;
      if (s === i) {
        const a = e;
        if (a.tagName.toLowerCase() === "vds-visually-hidden")
          return !1;
        if (!a.hasAttribute("slot"))
          return !0;
      }
      return !1;
    });
  }
  hasNamedSlot(e) {
    return this.host.querySelector(`:scope > [slot="${e}"]`) !== null;
  }
  hasSlot(e) {
    return e === d1 ? this.hasDefaultSlot() : this.hasNamedSlot(e);
  }
  hostConnected() {
    this.host.shadowRoot.addEventListener("slotchange", this.handleSlotChange);
  }
  hostDisconnected() {
    this.host.shadowRoot.removeEventListener("slotchange", this.handleSlotChange);
  }
  // https://lit.dev/docs/components/shadow-dom/#accessing-slotted-children
  handleSlotChange(e) {
    const s = e.target, r = this.slotNames.includes(d1) && !s.name, o = s.name && this.slotNames.includes(s.name);
    (r || o) && this.host.requestUpdate();
  }
}
const Ae = ["end", "start"], c1 = {
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight"
}, v2 = Object.values(c1), a1 = {
  Home: "Home",
  End: "End",
  Escape: "Escape",
  Enter: "Enter",
  Backspace: "Backspace",
  SpaceBar: " ",
  Tab: "Tab",
  Shift: "Shift"
}, T7 = "label-slot-missing-message", m2 = g`
  <span id=${T7} style="color: var(--color-text-critical)">
    A label is REQUIRED!! Either provide a label attribute or a default slot.
    If you'd like to hide the label, use the hide-label attribute.
  </span>
`, z7 = "label-attr-missing-message", I7 = g`
  <span id=${z7} style="color: var(--color-text-critical)">
    A label is REQUIRED!! Please provide the label attribute.
    If you'd like to hide the label, use the hide-label attribute.
  </span>
`;
class P7 extends Event {
  constructor(e) {
    super("formdata"), this.formData = e;
  }
}
class B7 extends FormData {
  constructor(e) {
    var s = (...r) => {
      super(...r);
    };
    e ? (s(e), this.form = e, e.dispatchEvent(new P7(this))) : s();
  }
  append(e, s) {
    if (!this.form)
      return super.append(e, s);
    let r = this.form.elements[e];
    if (r || (r = document.createElement("input"), r.type = "hidden", r.name = e, this.form.appendChild(r)), this.has(e)) {
      const o = this.getAll(e), i = o.indexOf(r.value);
      i !== -1 && o.splice(i, 1), o.push(s), this.set(e, o);
    } else
      super.append(e, s);
    r.value = s;
  }
}
function F7() {
  const t = document.createElement("form");
  let e = !1;
  return document.body.append(t), t.addEventListener("submit", (s) => {
    new FormData(s.target), s.preventDefault();
  }), t.addEventListener("formdata", () => e = !0), t.dispatchEvent(new Event("submit", { cancelable: !0 })), t.remove(), e;
}
function Ne() {
  !window.FormData || F7() || (window.FormData = B7, window.addEventListener("submit", (t) => {
    t.defaultPrevented || new FormData(t.target);
  }));
}
document.readyState === "complete" ? Ne() : window.addEventListener("DOMContentLoaded", () => Ne());
const f0 = /* @__PURE__ */ new WeakMap(), Ue = /* @__PURE__ */ new WeakMap(), b0 = /* @__PURE__ */ new WeakMap(), w0 = /* @__PURE__ */ new WeakMap();
class q0 {
  constructor(e, s) {
    (this.host = e).addController(this), this.options = {
      // the options "el" arg can be a form control or a button
      form: (r) => r.closest("form"),
      name: (r) => r.name,
      value: (r) => r.value,
      // el.defaultValue returns undefined for vds-input
      // el.getAttribute('value') gets the host value attribute
      // optionally added returning '' as default so that reset clears when no value is set
      defaultValue: (r) => r.defaultValue || r.getAttribute("value") || "",
      disabled: (r) => r.disabled ?? !1,
      reportValidity: (r) => typeof r.reportValidity == "function" ? r.reportValidity() : !0,
      setValue: (r, o) => r.value = o,
      ...s
    }, this.handleFormData = this.handleFormData.bind(this), this.handleFormSubmit = this.handleFormSubmit.bind(this), this.handleFormReset = this.handleFormReset.bind(this), this.reportFormValidity = this.reportFormValidity.bind(this), this.handleUserInput = this.handleUserInput.bind(this);
  }
  hostConnected() {
    const e = this.options.form(this.host);
    e && this.attachForm(e), this.host.addEventListener("vds-input", this.handleUserInput);
  }
  hostDisconnected() {
    this.detachForm(), this.host.removeEventListener("vds-input", this.handleUserInput);
  }
  hostUpdated() {
    const e = this.options.form(this.host);
    e || this.detachForm(), e && this.form !== e && (this.detachForm(), this.attachForm(e)), this.host.hasUpdated && this.host.checkValidity && this.setValidity(this.host.checkValidity());
  }
  attachForm(e) {
    e ? (this.form = e, f0.has(this.form) ? f0.get(this.form).add(this.host) : f0.set(this.form, /* @__PURE__ */ new Set([this.host])), this.form.addEventListener("formdata", this.handleFormData), this.form.addEventListener("submit", this.handleFormSubmit), this.form.addEventListener("reset", this.handleFormReset), b0.has(this.form) || (b0.set(this.form, this.form.reportValidity), this.form.reportValidity = () => this.reportFormValidity()), w0.has(this.form) || (w0.set(this.form, this.form.checkValidity), this.form.checkValidity = () => this.checkValidity(this.form))) : this.form = void 0;
  }
  checkValidity(e) {
    const s = e.querySelectorAll("*");
    for (const r of s)
      if (typeof r.checkValidity == "function" && !r.checkValidity())
        return !1;
    return !0;
  }
  detachForm() {
    var e;
    this.form && ((e = f0.get(this.form)) == null || e.delete(this.host), this.form.removeEventListener("formdata", this.handleFormData), this.form.removeEventListener("submit", this.handleFormSubmit), this.form.removeEventListener("reset", this.handleFormReset), b0.has(this.form) && (this.form.reportValidity = b0.get(this.form), b0.delete(this.form)), w0.has(this.form) && (this.form.checkValidity = w0.get(this.form), w0.delete(this.form))), this.form = void 0;
  }
  handleFormData(e) {
    const s = this.options.disabled(this.host), r = this.options.name(this.host), o = this.options.value(this.host);
    !s && typeof r == "string" && typeof o < "u" && (Array.isArray(o) ? o.forEach((i) => {
      e.formData.append(r, i.toString());
    }) : e.formData.append(r, o.toString()));
  }
  handleFormSubmit(e) {
    var o;
    const s = this.options.disabled(this.host), r = this.options.reportValidity;
    this.form && !this.form.noValidate && ((o = f0.get(this.form)) == null || o.forEach((i) => {
      this.setUserInteracted(i, !0);
    })), this.form && !this.form.noValidate && !s && !r(this.host) && (e.preventDefault(), e.stopImmediatePropagation());
  }
  handleFormReset() {
    this.options.setValue(this.host, this.options.defaultValue(this.host)), this.setUserInteracted(this.host, !1);
  }
  async handleUserInput() {
    await this.host.updateComplete, this.setUserInteracted(this.host, !0);
  }
  reportFormValidity() {
    if (this.form && !this.form.noValidate) {
      const e = this.form.querySelectorAll("*");
      for (const s of e)
        if (typeof s.reportValidity == "function" && !s.reportValidity())
          return !1;
    }
    return !0;
  }
  setUserInteracted(e, s) {
    Ue.set(e, s), e.requestUpdate();
  }
  /**
   * form.submit() bypasses the submit event and constraint validation.
   * make a button, hide it visually, click it + remove it.
   */
  doAction(e) {
    if (this.form) {
      const s = document.createElement("button");
      s.type = e, s.style.position = "absolute", s.style.width = "0", s.style.height = "0", s.style.clipPath = "inset(50%)", s.style.overflow = "hidden", s.style.whiteSpace = "nowrap", this.form.append(s), s.click(), s.remove();
    }
  }
  // Resets the form, restoring all the control to their default value
  reset() {
    this.doAction("reset");
  }
  // Submits the form, triggering validation and form data injection.
  submit() {
    this.doAction("submit");
  }
  /**
   * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
   * the host element immediately, i.e. before Lit updates the component in the next update.
  */
  setValidity(e) {
    var i;
    const s = this.host, r = Boolean(Ue.get(s)), o = Boolean(s.required);
    (i = this.form) != null && i.noValidate ? (s.removeAttribute("data-required"), s.removeAttribute("data-optional"), s.removeAttribute("data-invalid"), s.removeAttribute("data-valid"), s.removeAttribute("data-user-invalid"), s.removeAttribute("data-user-valid")) : (s.toggleAttribute("data-required", o), s.toggleAttribute("data-optional", !o), s.toggleAttribute("data-invalid", !e), s.toggleAttribute("data-valid", e), s.toggleAttribute("data-user-invalid", !e && r), s.toggleAttribute("data-user-valid", e && r));
  }
  /**
   * Updates the form control's validity based on the current value of `host.checkValidity()`. Call this when anything
   * that affects constraint validation changes so the component receives the correct validity states.
   */
  updateValidity() {
    this.host.checkValidity && this.setValidity(this.host.checkValidity());
  }
}
var j7 = Object.defineProperty, R7 = Object.getOwnPropertyDescriptor, M1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? R7(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && j7(e, s, o), o;
};
class B extends e0 {
  /** Emits a custom event with more convenient defaults. */
  emit(e, s) {
    const r = new CustomEvent(e, {
      bubbles: !0,
      cancelable: !1,
      composed: !0,
      detail: {},
      ...s
    });
    return this.dispatchEvent(r), r;
  }
}
class j extends B {
  constructor() {
    super(...arguments), this.slotController = new I1(this, d1, "help-text"), this.formSubmitController = new q0(this), this.name = "", this.label = "", this.hideLabel = !1, this.helpText = "", this.disabled = !1, this.required = !1, this.validatyMessage = "", this.hasTouched = !1;
  }
  /**
   * Adds a class of `touched` when the input is interacted with.
   * This is useful because it prevents `required` errors from showing
   * on empty inputs before the user has interacted with them.
   * 
   * This came from: https://stackoverflow.com/a/17369386.
   * 
   * We previusly tried this: https://www.bram.us/2021/01/28/form-validation-you-want-notfocusinvalid-not-invalid/#v4
   * and it prevented `required` inputs from every showing as invalid when empty.
   */
  _handleTouched() {
    this.hasTouched = !0;
  }
  /**
   * Focuses the input.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus
   * */
  focus(e) {
    this.input.focus(e);
  }
  /**
   * Removes focus from the input.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/blur
   * */
  blur() {
    this.input.blur();
  }
  handleFocus() {
    this.emit("vds-focus");
  }
  handleBlur() {
    this.emit("vds-blur"), this._handleTouched(), this.updateValidityMessage();
  }
  /**
   * Returns true if the input's value passes validity checks;
   * otherwise, returns false and fires an invalid event at the input.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity
   */
  checkValidity() {
    return this.input.checkValidity();
  }
  /**
   * Returns true if the input's value passes validity checks;
   * otherwise, returns false, fires an invalid event at the input, and reports the problem to the user.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/reportValidity
   */
  reportValidity() {
    const e = this.input.checkValidity();
    return this.updateValidityMessage(), !e && this.focus(), e;
  }
  /**
   * Sets a custom message to display if the input input's value isn't valid.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setCustomValidity
   */
  setCustomValidity(e) {
    this.input.setCustomValidity(e), this.updateValidityMessage(), this.formSubmitController.updateValidity();
  }
  /**
   * Gets the validity state object
   * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   */
  get validity() {
    return this.input.validity;
  }
  updateValidityMessage() {
    if (this.validity.valid) {
      this.validatyMessage = "";
      return;
    }
    !this.hasTouched && this._handleTouched(), this.validity.customError ? this.validatyMessage = this.input.validationMessage : this.validity.valueMissing ? this.tagName === "VDS-CHECKBOX" ? this.validatyMessage = "You must check this box." : this.tagName === "VDS-RADIO-GROUP" || this.tagName === "VDS-SELECT" ? this.validatyMessage = "Please select an option." : this.validatyMessage = "Please fill out this field." : this.input.type === "email" && this.validity.typeMismatch ? this.validatyMessage = "Please enter a valid email address." : this.validity.patternMismatch ? this.validatyMessage = "Please match the requested format." : this.input.min && this.input.max && (this.validity.rangeOverflow || this.validity.rangeUnderflow) ? this.validatyMessage = `Please select a value between ${this.input.min} and ${this.input.max}.` : this.validity.tooShort ? this.validatyMessage = `Please lengthen to ${this.input.minLength} or more characters.` : this.validity.tooLong ? this.validatyMessage = `Please shorten to no more than ${this.input.maxLength} characters.` : this.validity.rangeUnderflow ? this.validatyMessage = `Please select a value that is no less than ${this.input.min}.` : this.validity.rangeOverflow ? this.validatyMessage = `Please select a value that is no more than ${this.input.max}.` : this.validity.stepMismatch ? this.validatyMessage = "Please select a valid value." : this.validity.badInput ? this.validatyMessage = "Please enter a valid number." : this.validatyMessage = this.input.validationMessage;
  }
  get hasValidationMessage() {
    return !!this.validatyMessage;
  }
  /**
   * Gets the validation message
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/validationMessage
   * */
  get validationMessage() {
    return this.validatyMessage;
  }
  hasLabel() {
    return this.slotController.hasSlot(d1) || !!this.label;
  }
  getLabel(e) {
    const s = g`
      <div part="label" class=${`${e}__label`}>
        <label for="control" part="label" id="label" class=${`${e}__label-text`}>
          <slot>${this.label || m2}</slot>
        </label>
        <slot name="after-label"></slot>
      </div>
    `;
    return this.hideLabel && this.hasLabel() ? g`<vds-visually-hidden>${s}</vds-visually-hidden>` : s;
  }
  get hasHelpText() {
    return this.slotController.hasSlot("help-text") || !!this.helpText;
  }
  getHelpText(e) {
    if (!this.hasHelpText && !this.hasValidationMessage)
      return "";
    const s = this.hasHelpText ? g`<slot name="help-text" id="help-text">${this.helpText}</slot>` : "", r = this.validatyMessage ? g`
          <div class="${e}__accordion">
            <div class="${e}__accordion-item">
              <div class="${e}__error">
              <vds-icon
                name="critical"
                class="${e}__error-icon"
              ></vds-icon>
              <div class="${e}__error-text" id="error-text">${this.validatyMessage}</div>
            </div>
          </div>
        </div>
      ` : "";
    return g`
      <div part="help" class="${e}__help-text">
        ${r}
        ${s}
      </div>
    `;
  }
}
M1([
  g1("#control")
], j.prototype, "input", 2);
M1([
  l()
], j.prototype, "name", 2);
M1([
  l()
], j.prototype, "label", 2);
M1([
  l({ type: Boolean, attribute: "hide-label" })
], j.prototype, "hideLabel", 2);
M1([
  l({ attribute: "help-text" })
], j.prototype, "helpText", 2);
M1([
  l({ type: Boolean, reflect: !0 })
], j.prototype, "disabled", 2);
M1([
  l({ type: Boolean, reflect: !0 })
], j.prototype, "required", 2);
M1([
  K()
], j.prototype, "validatyMessage", 2);
M1([
  K()
], j.prototype, "hasTouched", 2);
const N7 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:inline}.vds-link{color:var(--color-text-link);font-style:normal;font-weight:inherit;font-size:inherit;font-family:inherit;text-decoration-line:underline;cursor:pointer;border-radius:1px;transition:outline .15s}.vds-link:focus{outline:2px solid var(--color-focusOutline);outline-offset:2px}.vds-link:visited{color:var(--color-text-link-visited)}.vds-link:active{color:var(--color-text-link-pressed)}.vds-link:hover,:host(.hover) .vds-link{color:var(--color-text-link-hovered)}.vds-link--icon-only{display:inline-flex;gap:.25em}.vds-skip-link{position:fixed;top:var(--spacing-md);left:var(--spacing-md);box-shadow:var(--shadow-sm);text-decoration:none;border:1px solid var(--color-border-subdued);padding:var(--spacing-md);background-color:var(--color-background-primary);border-radius:var(--borderRadius-sm);color:var(--color-text-link);text-decoration-line:none;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);outline:none;box-shadow:var(--focus-shadow);transition:opacity .15s}.vds-skip-link:not(:focus){transform:translateY(-100vw);opacity:0}
`;
var U7 = Object.defineProperty, W7 = Object.getOwnPropertyDescriptor, q1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? W7(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && U7(e, s, o), o;
};
let C1 = class extends B {
  constructor() {
    super(...arguments), this.hasIcon = !1;
  }
  /** Targets the link on WC.click(). */
  click() {
    this.link.click();
  }
  /** Sets focus on the link on WC.focus(). */
  focus(t) {
    this.link.focus(t);
  }
  /** Removes focus from the link on WC.blur(). */
  blur() {
    this.link.blur();
  }
  handleBlur() {
    this.emit("vds-blur");
  }
  handleFocus() {
    this.emit("vds-focus");
  }
  checkForIcon() {
    var t, e;
    this.hasIcon = !!((e = (t = this.shadowRoot) == null ? void 0 : t.querySelector("slot")) != null && e.assignedNodes().filter((s) => {
      var r;
      return ((r = s.nodeValue) == null ? void 0 : r.trim()) !== "";
    }).some((s) => s.tagName === "VDS-ICON"));
  }
  firstUpdated() {
    this.checkForIcon();
  }
  render() {
    return g`<a
        id="link"
        class=${this.skipLink ? "vds-skip-link" : E({
      "vds-link": !0,
      "vds-link--icon-only": this.hasIcon
    })}
        href=${A(this.href)}
        download=${A(this.download)}
        target=${A(this.target)}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
        part='base'
      ><slot></slot></a>`;
  }
};
C1.styles = $(N7);
q1([
  K()
], C1.prototype, "hasIcon", 2);
q1([
  g1("#link")
], C1.prototype, "link", 2);
q1([
  l()
], C1.prototype, "href", 2);
q1([
  l()
], C1.prototype, "download", 2);
q1([
  l(),
  x(["_blank", "_parent", "_self", "_top", String])
], C1.prototype, "target", 2);
q1([
  l({ type: Boolean, attribute: "skip-link" })
], C1.prototype, "skipLink", 2);
C1 = q1([
  S("vds-link")
], C1);
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const q7 = (t) => t.strings === void 0, K7 = {}, Y7 = (t, e = K7) => t._$AH = e;
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const c0 = N0(class extends U0 {
  constructor(t) {
    if (super(t), t.type !== Z1.PROPERTY && t.type !== Z1.ATTRIBUTE && t.type !== Z1.BOOLEAN_ATTRIBUTE)
      throw Error("The `live` directive is not allowed on child or event bindings");
    if (!q7(t))
      throw Error("`live` bindings can only contain a single expression");
  }
  render(t) {
    return t;
  }
  update(t, [e]) {
    if (e === X || e === O)
      return e;
    const s = t.element, r = t.name;
    if (t.type === Z1.PROPERTY) {
      if (e === s[r])
        return X;
    } else if (t.type === Z1.BOOLEAN_ATTRIBUTE) {
      if (!!e === s.hasAttribute(r))
        return X;
    } else if (t.type === Z1.ATTRIBUTE && s.getAttribute(r) === e + "")
      return X;
    return Y7(t), e;
  }
}), G7 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:grid;gap:var(--spacing-4px);color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .input__label-text:after{content:" *";color:var(--color-text-critical)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}:host([disabled]) .input__wrapper{color:var(--color-text-disabled)}::placeholder{color:var(--color-text-placeholder)}.input__label{display:flex;align-items:flex-end;justify-content:space-between}.input__label-text{cursor:inherit;color:var(--color-text-normal)}.input__wrapper{position:relative;color:var(--color-text-normal)}.input__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.input__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.input__accordion-item{min-height:0}.input__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.input__error-icon{flex:0}.input__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.input__control{appearance:none;background-color:var(--color-background-primary);border:1px solid var(--color-border-normal);border-radius:var(--borderRadius-sm);box-sizing:border-box;min-height:3rem;padding:var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-sm);width:100%;margin:0;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);color:inherit;cursor:inherit;outline:none;transition:box-shadow .15s}.input__control::-webkit-calendar-picker-indicator{color:transparent;opacity:1;display:block;width:20px;height:20px;border-width:none;transition:.15s;cursor:pointer;padding:0;margin:0 var(--spacing-4px) 0 0;background-image:var(--calendar-icon);background-repeat:no-repeat;background-size:100%}.input__control::-webkit-calendar-picker-indicator:hover{opacity:.7}.input__control[type=time]::-webkit-calendar-picker-indicator{background-image:var(--time-icon)}.input__control::-webkit-datetime-edit-year-field,.input__control::-webkit-datetime-edit-month-field,.input__control::-webkit-datetime-edit-day-field,.input__control::-webkit-datetime-edit-hour-field,.input__control::-webkit-datetime-edit-minute-field,.input__control::-webkit-datetime-edit-second-field,.input__control::-webkit-datetime-edit-millisecond-field,.input__control::-webkit-datetime-edit-meridiem-field{margin:0;padding:0}.input__control:focus{box-shadow:var(--focus-shadow)}.input__control:is(.touched,:not([required])):is(:invalid,[aria-invalid=true]){background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical)}.input__control:disabled{background-color:var(--color-background-secondary-alpha);border:1px solid var(--color-border-light)}
`;
function V(t, e) {
  const s = {
    waitUntilFirstUpdate: !1,
    ...e
  };
  return (r, o) => {
    const { update: i } = r;
    if (t in r) {
      const a = t;
      r.update = function(c) {
        if (c.has(a)) {
          const h = c.get(a), d = this[a];
          h !== d && (!s.waitUntilFirstUpdate || this.hasUpdated) && this[o](h, d);
        }
        i.call(this, c);
      };
    }
  };
}
const Q7 = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PHBhdGggZmlsbD0iaW5oZXJpdCIgc3Ryb2tlPSJub25lIiBkPSJNMTQuNSAxOGMtLjcgMC0xLjI5Mi0uMjQyLTEuNzc1LS43MjVDMTIuMjQyIDE2Ljc5MiAxMiAxNi4yIDEyIDE1LjVzLjI0Mi0xLjI5Mi43MjUtMS43NzVDMTMuMjA4IDEzLjI0MiAxMy44IDEzIDE0LjUgMTNzMS4yOTIuMjQyIDEuNzc1LjcyNWMuNDgzLjQ4My43MjUgMS4wNzUuNzI1IDEuNzc1cy0uMjQyIDEuMjkyLS43MjUgMS43NzVjLS40ODMuNDgzLTEuMDc1LjcyNS0xLjc3NS43MjVaTTUgMjJhMS45MyAxLjkzIDAgMCAxLTEuNDEzLS41ODdBMS45MjggMS45MjggMCAwIDEgMyAyMFY2YzAtLjU1LjE5Ni0xLjAyLjU4Ny0xLjQxMkExLjkyNyAxLjkyNyAwIDAgMSA1IDRoMVYzYS45Ny45NyAwIDAgMSAuMjg3LS43MTNBLjk3Ljk3IDAgMCAxIDcgMmEuOTcuOTcgMCAwIDEgLjcxMy4yODdBLjk3Ljk3IDAgMCAxIDggM3YxaDhWM2MwLS4yODMuMDk2LS41MjEuMjg4LS43MTNBLjk2Ny45NjcgMCAwIDEgMTcgMmEuOTcuOTcgMCAwIDEgLjcxMi4yODdjLjE5Mi4xOTIuMjg4LjQzLjI4OC43MTN2MWgxYy41NSAwIDEuMDIxLjE5NiAxLjQxMy41ODguMzkxLjM5MS41ODcuODYyLjU4NyAxLjQxMnYxNGExLjkzIDEuOTMgMCAwIDEtLjU4NyAxLjQxM0ExLjkyOCAxLjkyOCAwIDAgMSAxOSAyMkg1Wm0wLTJoMTRWMTBINXYxMFoiLz48L3N2Zz4=", X7 = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PHBhdGggZmlsbD0iaW5oZXJpdCIgc3Ryb2tlPSJub25lIiBkPSJNMTQuNjI1IDE2LjAyNWEuOTE4LjkxOCAwIDAgMCAuNjc1LjI3NWMuMjY3IDAgLjUtLjEuNy0uM2EuOTQ4Ljk0OCAwIDAgMCAuMjc1LS43Ljk0OC45NDggMCAwIDAtLjI3NS0uN2wtMy0zVjcuOTc1YS45MjcuOTI3IDAgMCAwLS4yODctLjdBLjk5Ljk5IDAgMCAwIDEyIDdhLjk2OC45NjggMCAwIDAtLjcxMy4yODdBLjk2Ny45NjcgMCAwIDAgMTEgOHYzLjk3NWEuOTk3Ljk5NyAwIDAgMCAuMy43MjVsMy4zMjUgMy4zMjVaTTEyIDIyYTkuNzM4IDkuNzM4IDAgMCAxLTMuOS0uNzg4IDEwLjA5OSAxMC4wOTkgMCAwIDEtMy4xNzUtMi4xMzdjLS45LS45LTEuNjEyLTEuOTU4LTIuMTM3LTMuMTc1QTkuNzM4IDkuNzM4IDAgMCAxIDIgMTJhOS43NCA5Ljc0IDAgMCAxIC43ODgtMy45IDEwLjA5OSAxMC4wOTkgMCAwIDEgMi4xMzctMy4xNzVjLjktLjkgMS45NTgtMS42MTIgMy4xNzUtMi4xMzdBOS43MzggOS43MzggMCAwIDEgMTIgMmE5Ljc0IDkuNzQgMCAwIDEgMy45Ljc4OCAxMC4wOTggMTAuMDk4IDAgMCAxIDMuMTc1IDIuMTM3Yy45LjkgMS42MTMgMS45NTggMi4xMzcgMy4xNzVBOS43MzggOS43MzggMCAwIDEgMjIgMTJhOS43MzggOS43MzggMCAwIDEtLjc4OCAzLjkgMTAuMDk4IDEwLjA5OCAwIDAgMS0yLjEzNyAzLjE3NWMtLjkuOS0xLjk1OCAxLjYxMy0zLjE3NSAyLjEzN0E5LjczOCA5LjczOCAwIDAgMSAxMiAyMloiLz48L3N2Zz4=", J7 = `:host{position:absolute;height:1px;width:1px;margin:-1px;clip:rect(0 0 0 0);clip-path:inset(50%);overflow:hidden;white-space:nowrap}
`;
var eo = Object.defineProperty, to = Object.getOwnPropertyDescriptor, so = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? to(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && eo(e, s, o), o;
};
let pe = class extends B {
  render() {
    return g`<slot></slot>`;
  }
};
pe.styles = $(J7);
pe = so([
  S("vds-visually-hidden")
], pe);
var ro = Object.defineProperty, oo = Object.getOwnPropertyDescriptor, N = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? oo(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && ro(e, s, o), o;
};
const io = [
  "date",
  "datetime-local",
  "email",
  "number",
  "password",
  "tel",
  "text",
  "time"
], We = {
  cal: `url("${Q7}")`,
  time: `url("${X7}")`
};
let I = class extends j {
  constructor() {
    super(...arguments), this.value = "", this.type = "text", this.placeholder = "", this.readonly = !1;
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  /**
   * Selects the input's text.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/select
   * */
  select() {
    this.input.select();
  }
  handleStepChange() {
    this.input.step = String(this.step), this.formSubmitController.updateValidity();
  }
  handleInput() {
    this.value = this.input.value, this.formSubmitController.updateValidity(), this.emit("vds-input"), this.input.validity.valid && this.updateValidityMessage();
  }
  handleInvalid() {
    this.formSubmitController.setValidity(!1);
  }
  handleKeyDown(t) {
    t.key === "Enter" && !t.defaultPrevented && this.formSubmitController.submit();
  }
  async handleValueChange() {
    await this.updateComplete, this.formSubmitController.updateValidity();
  }
  handleDisabledChange() {
    this.formSubmitController.setValidity(this.disabled);
  }
  render() {
    return g`
      ${this.getLabel(qe)}
      <div class="input__wrapper">
        <input
          id="control"
          class=${E({
      input__control: !0,
      touched: this.hasTouched
    })}
          part="base"
          type=${this.type}
          name=${A(this.name ? this.name : void 0)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          minlength=${A(this.minlength)}
          maxlength=${A(this.maxlength)}
          min=${A(this.min)}
          max=${A(this.max)}
          step=${A(this.step)}
          .value=${c0(A(this.value))}
          autocomplete=${A(this.autocomplete)}
          spellcheck=${A(this.spellcheck)}
          pattern=${A(this.pattern)}
          aria-labelledby="label help-text"
          aria-errormessage=${A(this.hasValidationMessage ? "error-text" : void 0)}
          @input=${this.handleInput}
          @invalid=${this.handleInvalid}
          @keydown=${this.handleKeyDown}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          placeholder=${this.placeholder}
        />
      </div>
      ${this.getHelpText(qe)}
    `;
  }
};
I.styles = F2`
    ${$(G7)}

    .input__control {
      --calendar-icon: ${$(We.cal)};
      --time-icon: ${$(We.time)};
    }
  `;
N([
  l()
], I.prototype, "value", 2);
N([
  l({ reflect: !0 }),
  x(io, "text")
], I.prototype, "type", 2);
N([
  l({ type: Number })
], I.prototype, "minlength", 2);
N([
  l({ type: Number })
], I.prototype, "maxlength", 2);
N([
  l({ type: Number })
], I.prototype, "min", 2);
N([
  l({ type: Number })
], I.prototype, "max", 2);
N([
  l({ type: Number })
], I.prototype, "step", 2);
N([
  l()
], I.prototype, "pattern", 2);
N([
  l()
], I.prototype, "autocomplete", 2);
N([
  l({ type: Boolean })
], I.prototype, "spellcheck", 2);
N([
  l()
], I.prototype, "placeholder", 2);
N([
  l({ type: Boolean, reflect: !0 })
], I.prototype, "readonly", 2);
N([
  V("step", { waitUntilFirstUpdate: !0 })
], I.prototype, "handleStepChange", 1);
N([
  V("value", { waitUntilFirstUpdate: !0 })
], I.prototype, "handleValueChange", 1);
N([
  V("disabled", { waitUntilFirstUpdate: !0 })
], I.prototype, "handleDisabledChange", 1);
I = N([
  S("vds-input")
], I);
const qe = "input", ao = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:inline-block}.button{display:inline-flex;position:relative;height:3rem;padding:0 var(--spacing-lg);align-items:center;justify-content:center;gap:.5rem;box-sizing:border-box;border-radius:var(--borderRadius-round);border:1px solid transparent;text-decoration:none;transition:background-color 125ms,color 125ms;cursor:pointer;vertical-align:middle;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);outline:none;transition:box-shadow .15s}.button:focus{box-shadow:var(--focus-shadow)}.button--primary{color:var(--color-button-primary-text);background-color:var(--color-button-primary)}.button--primary:focus{background-color:var(--color-button-primary-focused)}.button--primary:hover{background-color:var(--color-button-primary-hovered)}.button--primary:active{background-color:var(--color-button-primary-pressed)}.button--primary:disabled,.button--primary.button-link-disabled{color:var(--color-button-primary-disabled-text);background-color:var(--color-button-primary-disabled);cursor:not-allowed}.button--auxiliary{color:var(--color-button-auxiliary-text);background-color:var(--color-button-auxiliary)}.button--auxiliary:focus{background-color:var(--color-button-auxiliary-focused)}.button--auxiliary:hover{background-color:var(--color-button-auxiliary-hovered)}.button--auxiliary:active{background-color:var(--color-button-auxiliary-pressed)}.button--auxiliary:disabled,.button--auxiliary.button-link-disabled{color:var(--color-button-auxiliary-disabled-text);background-color:var(--color-button-auxiliary-disabled);cursor:not-allowed}.button--secondary{color:var(--color-button-secondary-text);background-color:var(--color-button-secondary);border:1px solid var(--color-button-secondary-border)}.button--secondary:focus{background-color:var(--color-button-secondary-focused);border-color:var(--color-button-secondary-focused-border)}.button--secondary:hover{background-color:var(--color-button-secondary-hovered);border-color:var(--color-button-secondary-hovered-border)}.button--secondary:active{background-color:var(--color-button-secondary-pressed);border-color:var(--color-button-secondary-pressed-border)}.button--secondary:disabled,.button--secondary.button-link-disabled{color:var(--color-button-secondary-disabled-text);background-color:var(--color-button-secondary-disabled);cursor:not-allowed;border-color:var(--color-button-secondary-disabled-border)}.button--tertiary{color:var(--color-button-tertiary-text);background-color:var(--color-button-tertiary)}.button--tertiary:focus{background-color:var(--color-button-tertiary-focused)}.button--tertiary:hover{background-color:var(--color-button-tertiary-hovered)}.button--tertiary:active{background-color:var(--color-button-tertiary-pressed)}.button--tertiary:disabled,.button--tertiary.button-link-disabled{color:var(--color-button-tertiary-disabled-text);background-color:var(--color-button-tertiary-disabled);cursor:not-allowed}.button--warning{color:var(--color-button-warning-text);background-color:var(--color-button-warning)}.button--warning:focus{background-color:var(--color-button-warning-focused)}.button--warning:hover{background-color:var(--color-button-warning-hovered)}.button--warning:active{background-color:var(--color-button-warning-pressed)}.button--warning:disabled,.button--warning.button-link-disabled{color:var(--color-button-warning-disabled-text);background-color:var(--color-button-warning-disabled);cursor:not-allowed}.button--critical{color:var(--color-button-critical-text);background-color:var(--color-button-critical)}.button--critical:focus{background-color:var(--color-button-critical-focused)}.button--critical:hover{background-color:var(--color-button-critical-hovered)}.button--critical:active{background-color:var(--color-button-critical-pressed)}.button--critical:disabled,.button--critical.button-link-disabled{color:var(--color-button-critical-disabled-text);background-color:var(--color-button-critical-disabled);cursor:not-allowed}.button--icon-only{width:3rem;padding:0}.button--small{height:2.5rem;padding:0 var(--spacing-md)}.button--small.button--icon-only{width:2.5rem}.button--tiny{height:2rem;padding:0 var(--spacing-sm)}.button--tiny.button--icon-only{width:2rem}
`;
var no = Object.defineProperty, lo = Object.getOwnPropertyDescriptor, G = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? lo(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && no(e, s, o), o;
};
const co = ["button", "submit", "reset"], ho = [
  "primary",
  "auxiliary",
  "secondary",
  "tertiary",
  "warning",
  "critical"
], po = ["default", "small", "tiny"], uo = ["_blank", "_parent", "_self", "_top"];
let R = class extends B {
  constructor() {
    super(...arguments), this.formSubmitController = new q0(this), this.slotController = new I1(this, d1), this.disabled = !1, this.type = "button", this.color = "primary", this.size = "default", this.name = "", this.value = "", this.isIconButton = !1;
  }
  firstUpdated() {
    this.handleIconOnly(), this.isButton() && this.formSubmitController.updateValidity();
  }
  /** Clicks the button nested in the WC; on WC.blur() */
  click() {
    this.button.click();
  }
  /** focuses the button nested in the WC; on WC.focus() */
  focus(t) {
    this.button.focus(t);
  }
  /** Removes focus from the button nested in the WC; on WC.blur() */
  blur() {
    this.button.blur();
  }
  /**
   * Returns true if the input's value passes validity checks;
   *  otherwise, returns false and fires an invalid event at the input.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity
   * */
  checkValidity() {
    return this.isButton() ? this.button.checkValidity() : !0;
  }
  /**
   * Returns true if the input's value passes validity checks;
   *  otherwise, returns false, fires an invalid event at the input, and reports the problem to the user.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/reportValidity
   */
  reportValidity() {
    return this.isButton() ? this.button.reportValidity() : !0;
  }
  /**
   * Sets a custom message to display if the input input's value isn't valid.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setCustomValidity
   */
  setCustomValidity(t) {
    this.isButton() && (this.button.setCustomValidity(t), this.formSubmitController.updateValidity());
  }
  isButton() {
    return !this.href;
  }
  handleBlur() {
    this.emit("vds-blur");
  }
  handleFocus() {
    this.emit("vds-focus");
  }
  handleClick(t) {
    if (this.disabled) {
      t.preventDefault(), t.stopPropagation();
      return;
    }
    (this.type === "submit" || this.type === "reset") && this.formSubmitController[this.type]();
  }
  handleDisabledChange() {
    this.isButton() && this.formSubmitController.setValidity(this.disabled);
  }
  handleIconOnly() {
    var s, r;
    const t = (r = (s = this.shadowRoot) == null ? void 0 : s.querySelector("slot")) == null ? void 0 : r.assignedNodes().filter((o) => {
      var i;
      return ((i = o.nodeValue) == null ? void 0 : i.trim()) !== "";
    });
    t && (t == null ? void 0 : t.length) === 1 && t[0].tagName === "VDS-ICON" && !this.isIconButton && (this.isIconButton = !0);
  }
  maybeGetIconButtonDescription() {
    if (this.isIconButton)
      return this.iconDescription ? g`
      <vds-visually-hidden id="description">
        ${this.iconDescription}
      </vds-visually-hidden>
    ` : g`<span style="color:red">${go}</span>`;
  }
  render() {
    const t = !this.isButton(), e = E({
      button: !0,
      [`button--${this.color}`]: !0,
      [`button--${this.size}`]: !0,
      "button--icon-only": this.isIconButton,
      "button-link-disabled": t && this.disabled
    });
    if (t) {
      const s = this.disabled ? void 0 : this.href, r = this.disabled ? "link" : void 0;
      return g`
        <a
          role=${A(r)}
          aria-disabled=${this.disabled}
          href=${A(s)}
          part="base"
          download=${A(this.download)}
          target=${A(this.target)}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          class=${e}
        >
          <slot></slot>
          ${this.maybeGetIconButtonDescription()}
        </a>
      `;
    }
    return g`
      <button
        part="base"
        class=${e}
        ?disabled=${this.disabled}
        type=${A(this.type)}
        name=${A(this.name)}
        value=${A(this.value)}
        aria-disabled=${this.disabled}
        tabindex=${this.disabled ? "-1" : "0"}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        @click=${this.handleClick}
      >
        <slot></slot>
        ${this.maybeGetIconButtonDescription()}
      </button>
    `;
  }
};
R.styles = $(ao);
G([
  g1(".button")
], R.prototype, "button", 2);
G([
  l({ type: Boolean, reflect: !0 })
], R.prototype, "disabled", 2);
G([
  l(),
  x(co)
], R.prototype, "type", 2);
G([
  l(),
  x(ho)
], R.prototype, "color", 2);
G([
  l(),
  x(po)
], R.prototype, "size", 2);
G([
  l()
], R.prototype, "name", 2);
G([
  l()
], R.prototype, "value", 2);
G([
  l()
], R.prototype, "href", 2);
G([
  l()
], R.prototype, "download", 2);
G([
  l(),
  x([...uo, String])
], R.prototype, "target", 2);
G([
  l({ attribute: "icon-description" })
], R.prototype, "iconDescription", 2);
G([
  K()
], R.prototype, "isIconButton", 2);
G([
  V("disabled", { waitUntilFirstUpdate: !0 })
], R.prototype, "handleDisabledChange", 1);
R = G([
  S("vds-button")
], R);
const go = `
  When the <vds-button> ONLY has an icon, no other children,
  then it REQUIRES the iconDescription attribute to be accessible;
  so the screen reader can tell the user what the button does.
`, vo = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:grid;gap:var(--spacing-4px);color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .search__label-text:after{content:" *";color:var(--color-text-critical)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}:host([disabled]) .search__wrapper{color:var(--color-text-disabled)}::placeholder{color:var(--color-text-placeholder)}.search__label{display:flex;align-items:flex-end;justify-content:space-between}.search__label-text{cursor:inherit;color:var(--color-text-normal)}.search__wrapper{position:relative;color:var(--color-text-normal)}.search__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.search__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.search__accordion-item{min-height:0}.search__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.search__error-icon{flex:0}.search__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.search__control{appearance:none;background-color:var(--color-background-primary);border:1px solid var(--color-border-normal);border-radius:var(--borderRadius-sm);box-sizing:border-box;min-height:3rem;padding:var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-sm);width:100%;margin:0;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);color:inherit;cursor:inherit;outline:none;transition:box-shadow .15s}.search__control:focus{box-shadow:var(--focus-shadow)}.search__control:is(.touched,:not([required])):is(:invalid,[aria-invalid=true]){background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical)}.search__control:disabled{background-color:var(--color-background-secondary-alpha);border:1px solid var(--color-border-light)}.search__control{padding:var(--spacing-sm) var(--spacing-2xl) var(--spacing-sm) var(--spacing-xl)}.search__control::-webkit-search-cancel-button{display:none}.search__icon{position:absolute;top:50%;transform:translateY(-50%);left:var(--spacing-xs)}.search__clear{position:absolute;top:50%;transform:translateY(-50%);right:var(--spacing-xs)}.search__clear::part(base){color:var(--color-button-subtle-text);background-color:var(--color-button-subtle)}.search__clear::part(base):hover{background-color:var(--color-button-subtle-hovered)}.search__clear::part(base):active{background-color:var(--color-button-subtle-pressed)}
`;
var mo = Object.defineProperty, fo = Object.getOwnPropertyDescriptor, v1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? fo(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && mo(e, s, o), o;
};
let e1 = class extends j {
  constructor() {
    super(...arguments), this.value = "", this.readonly = !1, this.placeholder = "";
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  /**
   * Selects the search input's text.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/select
   * */
  select() {
    this.input.select();
  }
  handleInput() {
    this.value = this.input.value, this.formSubmitController.updateValidity(), this.emit("vds-input"), this.input.validity.valid && this.updateValidityMessage();
  }
  handleInvalid() {
    this.formSubmitController.setValidity(!1);
  }
  handleKeyDown(t) {
    t.key === "Enter" && !t.defaultPrevented && this.formSubmitController.submit();
  }
  maybeGetClearButton() {
    return this.value === "" ? void 0 : g`
      <vds-button class="search__clear" size="tiny" icon-description="clear search input" @click=${() => {
      this.input.value = "", this.handleInput(), this.focus();
    }}>
        <vds-icon name="close"></vds-icon>
      </vds-button>
    `;
  }
  async handleValueChange() {
    await this.updateComplete, this.formSubmitController.updateValidity();
  }
  handleDisabledChange() {
    this.formSubmitController.setValidity(this.disabled);
  }
  render() {
    return g`
      ${this.getLabel("search")}
      <div class="search__wrapper">
        <vds-icon name="search" class="search__icon"></vds-icon>
        <input
          id="control"
          type="search"
          class=${E({
      search__control: !0,
      touched: this.hasTouched
    })}
          part="base"
          name=${A(this.name ? this.name : void 0)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          minlength=${A(this.minlength)}
          maxlength=${A(this.maxlength)}
          .value=${c0(A(this.value))}
          autocomplete=${A(this.autocomplete)}
          spellcheck=${A(this.spellcheck)}
          pattern=${A(this.pattern)}
          aria-labelledby="label help-text"
          @input=${this.handleInput}
          @invalid=${this.handleInvalid}
          @keydown=${this.handleKeyDown}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          placeholder=${this.placeholder}
        />
        ${this.maybeGetClearButton()}
      </div>
      ${this.getHelpText("search")}
    `;
  }
};
e1.styles = $(vo);
v1([
  l()
], e1.prototype, "value", 2);
v1([
  l({ type: Number })
], e1.prototype, "minlength", 2);
v1([
  l({ type: Number })
], e1.prototype, "maxlength", 2);
v1([
  l()
], e1.prototype, "pattern", 2);
v1([
  l()
], e1.prototype, "autocomplete", 2);
v1([
  l({ type: Boolean })
], e1.prototype, "spellcheck", 2);
v1([
  l({ type: Boolean, reflect: !0 })
], e1.prototype, "readonly", 2);
v1([
  l()
], e1.prototype, "placeholder", 2);
v1([
  V("value", { waitUntilFirstUpdate: !0 })
], e1.prototype, "handleValueChange", 1);
v1([
  V("disabled", { waitUntilFirstUpdate: !0 })
], e1.prototype, "handleDisabledChange", 1);
e1 = v1([
  S("vds-search")
], e1);
const f2 = (t = "value") => (e, s) => {
  const r = e.constructor, o = r.prototype.attributeChangedCallback;
  r.prototype.attributeChangedCallback = function(i, a, c) {
    const h = r.getPropertyOptions(t), d = typeof h.attribute == "string" ? h.attribute : t;
    if (i === d) {
      const v = h.converter || _0, u = (typeof v == "function" ? v : (v == null ? void 0 : v.fromAttribute) ?? _0.fromAttribute)(c, h.type);
      this[t] !== u && (this[s] = u);
    }
    o.call(this, i, a, c);
  };
}, bo = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{--label-color: var(--color-text-normal);--icon-bg: var(--color-background-primary);--icon-color: transparent;--icon-border-color: var(--color-border-normal);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);display:flex;gap:var(--spacing-xs);align-items:flex-start;position:relative}:host([label-position=start]){--label-justify: flex-end;--label-direction: row-reverse}:host([checked]),:host([indeterminate]){--icon-bg: var(--color-background-selected);--icon-color: var(--color-text-onSelected);--icon-border-color: transparent}:host([disabled]){--label-color: var(--color-text-disabled);--icon-bg: var(--color-background-disabled);--icon-border-color: var(--color-border-disabled)}:host([checked][disabled]),:host([indeterminate][disabled]){--icon-bg: var(--color-background-disabled);--icon-color: var(--color-text-disabled);--icon-border-color: var(--color-border-disabled)}:host([required]) .checkbox__label:after{content:" *";color:var(--color-text-critical)}:host(:focus) .checkbox__icon-label{box-shadow:var(--focus-shadow)}.checkbox__label{display:flex;align-items:flex-end;justify-content:space-between}.checkbox__label-text{cursor:inherit;color:var(--color-text-normal)}.checkbox__wrapper{position:relative;color:var(--color-text-normal)}.checkbox__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.checkbox__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.checkbox__accordion-item{min-height:0}.checkbox__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.checkbox__error-icon{flex:0}.checkbox__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.checkbox__wrapper{display:flex;align-items:flex-start;justify-content:flex-start;gap:var(--spacing-xs);flex-direction:column}.checkbox__label{display:flex;align-items:flex-start;justify-content:var(--label-justify);flex-direction:var(--label-direction);gap:var(--spacing-xs);color:var(--label-color);cursor:pointer}.checkbox__icon-label{display:flex;cursor:pointer;border:1px solid var(--icon-border-color);border-radius:var(--borderRadius-sm);background-color:var(--icon-bg);outline:none;transition:box-shadow .15s}.checkbox__icon{color:var(--icon-color)}.checkbox__visually-hidden{position:absolute;bottom:0;left:11px}.checkbox__control{padding:0;margin:0;width:1px;height:1px}
`;
var wo = Object.defineProperty, xo = Object.getOwnPropertyDescriptor, x1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? xo(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && wo(e, s, o), o;
};
const Ao = {
  value: (t) => t.value,
  setValue: (t, e) => {
    t.checked = e;
  },
  defaultValue: (t) => t.defaultChecked
};
let n1 = class extends j {
  constructor() {
    super(...arguments), this.formSubmitController = new q0(this, Ao), this.value = "on", this.title = "", this.checked = !1, this.indeterminate = !1, this.labelPosition = "end", this.defaultChecked = !1, this.defaultValue = "on";
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  /** Clicks the checkbox. */
  click() {
    this.input.click();
  }
  handleClick() {
    this.checked = !this.checked, this.indeterminate = !1, this.emit("vds-change");
  }
  handleDisabledChange() {
    this.formSubmitController.setValidity(this.disabled);
  }
  handleStateChange() {
    this.input.checked = this.checked, this.input.indeterminate = this.indeterminate, this.formSubmitController.updateValidity(), this.updateValidityMessage();
  }
  getIcon() {
    const e = !this.checked && this.indeterminate ? "checkbox-indeterminate" : "checkbox-checked";
    return g`
      <label class="checkbox__icon-label" for="control">
        <vds-icon
          class="checkbox__icon"
          name=${e}
          for="control"
        ></vds-icon>
      </label>
    `;
  }
  getLabelAndHelpText() {
    const t = g`
      <div class="checkbox__wrapper">
        <label class="checkbox__label" for="control">
          <slot>${this.label || m2}</slot>
        </label>
        ${this.getHelpText("checkbox")}
      </div>
    `;
    return this.hideLabel && this.hasLabel() ? g`<vds-visually-hidden>${t}</vds-visually-hidden>` : t;
  }
  render() {
    return g`
      ${this.getIcon()}
      ${this.getLabelAndHelpText()}
      <vds-visually-hidden class="checkbox__visually-hidden">
        <input
          id="control"
          class="checkbox__control"
          type="checkbox"
          title=${this.title}
          name=${this.name}
          value=${A(this.value)}
          .indeterminate=${c0(this.indeterminate)}
          ?checked=${this.checked}
          ?disabled=${this.disabled}
          ?required=${this.required}
          aria-checked=${this.checked}
          aria-errormessage=${A(this.hasValidationMessage ? "error-text" : void 0)}
          @click=${this.handleClick}
          @blur=${this.handleBlur}
          @focus=${this.handleFocus}
        />
      </vds-visually-hidden>
    `;
  }
};
n1.styles = $(bo);
x1([
  l()
], n1.prototype, "value", 2);
x1([
  l()
], n1.prototype, "title", 2);
x1([
  l({ type: Boolean, reflect: !0 })
], n1.prototype, "checked", 2);
x1([
  l({ type: Boolean, reflect: !0 })
], n1.prototype, "indeterminate", 2);
x1([
  l({ type: String, attribute: "label-position" }),
  x(Ae, "end")
], n1.prototype, "labelPosition", 2);
x1([
  f2("checked")
], n1.prototype, "defaultChecked", 2);
x1([
  f2("value")
], n1.prototype, "defaultValue", 2);
x1([
  V("disabled", { waitUntilFirstUpdate: !0 })
], n1.prototype, "handleDisabledChange", 1);
x1([
  V("checked", { waitUntilFirstUpdate: !0 }),
  V("indeterminate", { waitUntilFirstUpdate: !0 })
], n1.prototype, "handleStateChange", 1);
n1 = x1([
  S("vds-checkbox")
], n1);
const yo = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:grid;gap:var(--spacing-4px);color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .datepicker__label-text:after{content:" *";color:var(--color-text-critical)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}:host([disabled]) .datepicker__wrapper{color:var(--color-text-disabled)}@supports (-moz-appearance: none){.datepicker__calendar-toggle-button:not(:disabled):after{content:"";background:#fff;width:30px;height:20px;position:absolute;z-index:-1;display:block;top:7px;right:-3px}}.datepicker__label{display:flex;align-items:flex-end;justify-content:space-between}.datepicker__label-text{cursor:inherit;color:var(--color-text-normal)}.datepicker__wrapper{position:relative;color:var(--color-text-normal)}.datepicker__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.datepicker__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.datepicker__accordion-item{min-height:0}.datepicker__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.datepicker__error-icon{flex:0}.datepicker__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.datepicker__control{appearance:none;background-color:var(--color-background-primary);border:1px solid var(--color-border-normal);border-radius:var(--borderRadius-sm);box-sizing:border-box;min-height:3rem;padding:var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-sm);width:100%;margin:0;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);color:inherit;cursor:inherit;outline:none;transition:box-shadow .15s}.datepicker__control::-webkit-calendar-picker-indicator{display:none}.datepicker__control::-webkit-datetime-edit-year-field,.datepicker__control::-webkit-datetime-edit-month-field,.datepicker__control::-webkit-datetime-edit-day-field,.datepicker__control::-webkit-datetime-edit-hour-field,.datepicker__control::-webkit-datetime-edit-minute-field,.datepicker__control::-webkit-datetime-edit-second-field,.datepicker__control::-webkit-datetime-edit-millisecond-field,.datepicker__control::-webkit-datetime-edit-meridiem-field{margin:0;padding:0}.datepicker__control:focus{box-shadow:var(--focus-shadow)}.datepicker__control:is(.touched,:not([required])):is(:invalid,[aria-invalid=true]){background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical)}.datepicker__control:disabled{background-color:var(--color-background-secondary-alpha);border:1px solid var(--color-border-light)}.datepicker__calendar{border:1px solid var(--color-border-light);background:var(--color-background-primary);border-radius:var(--borderRadius-md);padding:var(--spacing-sm);box-shadow:var(--shadow-md);width:312px;position:absolute;right:0;top:calc(100% + 2px);user-select:none;z-index:2}.datepicker__calendar-toggle-button{background:none;border:none;width:2rem;height:2rem;border-radius:var(--borderRadius-round);position:absolute;top:50%;right:var(--spacing-xs);transform:translateY(-50%);color:var(--color-text-normal);display:flex;align-items:center;justify-content:center;cursor:pointer}.datepicker__calendar-toggle-button:not(:disabled){outline:none;transition:box-shadow .15s}.datepicker__calendar-toggle-button:not(:disabled):focus{box-shadow:var(--focus-shadow)}.datepicker__calendar-toggle-button:not(:disabled):hover{background-color:var(--color-button-subtle-hovered)}.datepicker__calendar-toggle-button:not(:disabled):active{background-color:var(--color-button-subtle-pressed)}.datepicker__calendar-toggle-button:disabled{cursor:not-allowed;color:var(--color-text-disabled)}.datepicker__calendar-header{display:flex;gap:var(--spacing-xs)}.datepicker__calendar-header-select{display:flex;position:relative}.datepicker__calendar-header-select-label{display:flex;gap:var(--spacing-2px);align-items:center;padding:var(--spacing-4px);border-radius:var(--borderRadius-sm);color:var(--color-button-subtle-text);background-color:var(--color-button-subtle);outline:none;transition:box-shadow .15s}.datepicker__calendar-header-select-dropdown:hover+.datepicker__calendar-header-select-label{background-color:var(--color-button-subtle-hovered)}.datepicker__calendar-header-select-dropdown:active+.datepicker__calendar-header-select-label{background-color:var(--color-button-subtle-pressed)}.datepicker__calendar-header-select-dropdown:focus+.datepicker__calendar-header-select-label{box-shadow:var(--focus-shadow)}.datepicker__calendar-header-select-dropdown{position:absolute;opacity:0;inset:0;cursor:pointer}.datepicker__calendar-header-year{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-number-fontFamily);font-weight:var(--text-label-fontWeight)}.datepicker__calendar-header-month{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}.datepicker__calendar-header-control{margin-left:auto;display:flex;gap:var(--spacing-xs)}.datepicker__calendar-header-control-button{border:none;appearance:none;color:var(--color-button-subtle-text);background-color:var(--color-button-subtle);padding:var(--spacing-4px);border-radius:var(--borderRadius-sm);display:flex;align-items:center;justify-content:center;cursor:pointer;outline:none;transition:box-shadow .15s}.datepicker__calendar-header-control-button:focus{box-shadow:var(--focus-shadow)}.datepicker__calendar-header-control-button:hover{background-color:var(--color-button-subtle-hovered)}.datepicker__calendar-header-control-button:active{background-color:var(--color-button-subtle-pressed)}.datepicker__calendar-body{width:100%}.datepicker__calendar-day{color:var(--color-text-secondary);font-size:var(--text-label-sm);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);text-align:center;padding-top:var(--spacing-md)}.datepicker__calendar-cell{padding:var(--spacing-2px);outline:none}.datepicker__calendar-cell-date{color:var(--color-text-normal);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-number-fontFamily);font-weight:var(--text-label-fontWeight);text-align:center;border-radius:var(--borderRadius-round);display:block;width:2rem;line-height:2rem}.datepicker__calendar-cell-date--today{background:var(--color-background-selected-light)}.datepicker__calendar-cell-date--selectable{cursor:pointer;outline:none;transition:box-shadow .15s}.datepicker__calendar-cell-date--selectable:focus{box-shadow:var(--focus-shadow)}.datepicker__calendar-cell-date--selected{cursor:default;background:var(--color-background-selected);color:var(--color-text-onSelected)}.datepicker__calendar-cell-date--selectable:not(.datepicker__calendar-cell-date--selected):hover{background-color:var(--color-button-subtle-hovered)}.datepicker__calendar-cell-date--unselectable{cursor:not-allowed;color:var(--color-text-disabled)}
`, C = {
  inert: ":not([inert])",
  negTabIndex: ':not([tabindex^="-"])',
  disabled: ":not(:disabled)"
}, ko = [
  `a[href]${C.inert}${C.negTabIndex}`,
  `area[href]${C.inert}${C.negTabIndex}`,
  `input:not([type="hidden"]):not([type="radio"])${C.inert}${C.negTabIndex}${C.disabled}`,
  `input[type="radio"]${C.inert}${C.negTabIndex}${C.disabled}`,
  `select${C.inert}${C.negTabIndex}${C.disabled}`,
  `textarea${C.inert}${C.negTabIndex}${C.disabled}`,
  `button${C.inert}${C.negTabIndex}${C.disabled}`,
  `details${C.inert} > summary:first-of-type${C.negTabIndex}`,
  // Discard until Firefox supports `:has()`
  // See: https://github.com/KittyGiraudel/focusable-selectors/issues/12
  // `details:not(:has(> summary))${not.inert}${not.negTabIndex}`,
  `iframe${C.inert}${C.negTabIndex}`,
  `audio[controls]${C.inert}${C.negTabIndex}`,
  `video[controls]${C.inert}${C.negTabIndex}`,
  `[contenteditable]${C.inert}${C.negTabIndex}`,
  `[tabindex]${C.inert}${C.negTabIndex}`
], _o = ko.join(",");
class b2 {
  constructor(e, s, r = !1) {
    (this.host = e).addController(this), this.containerQuery = s, this.options = r, this.handleKeydown = this.handleKeydown.bind(this);
  }
  async hostConnected() {
    await this.host.updateComplete, this.container = this.host.shadowRoot.querySelector(this.containerQuery), this.container && this.container.addEventListener("keydown", this.handleKeydown, this.options);
  }
  async hostDisconnected() {
    await this.host.updateComplete, this.container && this.container.removeEventListener("keydown", this.handleKeydown, this.options);
  }
  handleKeydown(e) {
    var i;
    if (e.key !== "Tab")
      return;
    let s = document.activeElement;
    for (; (i = s == null ? void 0 : s.shadowRoot) != null && i.activeElement; )
      s = s.shadowRoot.activeElement;
    const { firstFocusableElement: r, lastFocusableElement: o } = this.getFocusableElements();
    if (e.shiftKey) {
      if (s !== r)
        return;
      e.preventDefault(), o.focus();
    } else {
      if (s !== o)
        return;
      e.preventDefault(), r.focus();
    }
  }
  getFocusableElements() {
    const e = [];
    function s(i) {
      i.forEach((a) => {
        a.matches(_o) && e.push(a), a instanceof HTMLSlotElement && a.assignedElements().length > 0 ? s(a.assignedElements()) : a.shadowRoot ? s([...a.shadowRoot.children]) : s([...a.children]);
      });
    }
    s([...this.container.children]);
    const r = e[0] || this.container, o = e[e.length - 1];
    return { firstFocusableElement: r, lastFocusableElement: o };
  }
}
var Zo = Object.defineProperty, Co = Object.getOwnPropertyDescriptor, m1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Co(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && Zo(e, s, o), o;
};
const Ke = "datepicker__calendar";
let t1 = class extends j {
  constructor() {
    super(...arguments), this.focusTrapController = new b2(this, `.${Ke}`), this.todaysDate = new Date(), this.value = "", this.readonly = !1, this.isCalendarVisible = !1;
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  handleInput() {
    this.value = this.input.value, this.formSubmitController.updateValidity(), this.emit("vds-input"), this.input.validity.valid && this.updateValidityMessage();
  }
  handleInvalid() {
    this.formSubmitController.setValidity(!1);
  }
  /**
   * Handles the functionality of the keyboard inputs
   * @param event
   */
  handleKeyDown(t) {
    t.key === "Enter" && !t.defaultPrevented ? this.formSubmitController.submit() : t.key === " " && (t.preventDefault(), this.showCalendar());
  }
  /**
   * When a person clicks on the input field
   * preventDefault so that default calendar
   * doesn't show up automatically
   * @param event
   */
  handleClick(t) {
    t.preventDefault();
  }
  /**
   * Selects the date the user has chosen on the calendar
   * @param year - chosen year
   * @param month - chosen month of year
   * @param day - chosen day of month
   * @param event - optional keyboard input used for enter detection
   * @returns void
   */
  handleCalendarDayClick(t, e, s) {
    this.selectedCalendarDate = new Date(t, e, s), this.value = `${t}-${this.getDoubleDigitNumber(e + 1)}-${this.getDoubleDigitNumber(s)}`, this.hideCalendar(), this.emit("vds-input");
  }
  getInputFormattedDateString(t) {
    return t.toISOString().split("T")[0];
  }
  onCalendarDayKeydownUpdates({ updateBy: t, year: e, month: s, day: r }) {
    const o = this.getTimestampFromInput(this.min, -1 / 0), i = this.getTimestampFromInput(this.max, 1 / 0);
    let a = new Date(e, s, r + t);
    const c = a.getTime();
    !isNaN(o) && o > c ? a = new Date(o) : !isNaN(i) && i < c && (a = new Date(i)), this.value = this.getInputFormattedDateString(a), this.selectedCalendarDate = a, this.focus();
  }
  /**
   * Used to handle calendar date navigation using arrow keys and allowing
   * the ability to select a date by pressing Enter.
   */
  handleCalendarDayKeydown(t, e, s, r) {
    const o = r == null ? void 0 : r.key;
    if ([...v2, "Enter"].includes(o)) {
      if (o === "Enter") {
        this.onCalendarDayKeydownUpdates({ year: t, month: e, day: s, updateBy: 0 }), this.isCalendarVisible = !1;
        return;
      }
      o === c1.ArrowRight && (r.preventDefault(), this.onCalendarDayKeydownUpdates({ year: t, month: e, day: s, updateBy: 1 })), o === c1.ArrowLeft && (r.preventDefault(), this.onCalendarDayKeydownUpdates({ year: t, month: e, day: s, updateBy: -1 })), o === c1.ArrowUp && (r.preventDefault(), this.onCalendarDayKeydownUpdates({ year: t, month: e, day: s, updateBy: -7 })), o === c1.ArrowDown && (r.preventDefault(), this.onCalendarDayKeydownUpdates({ year: t, month: e, day: s, updateBy: 7 })), this.emit("vds-input");
    }
  }
  /**
   * Used to detect when the user has clicked out of the calendar
   * @param event
   */
  handleCalendarHideClick(t) {
    (!t.target || t.target.nodeName !== "VDS-DATEPICKER") && this.hideCalendar();
  }
  /**
   * Used to detect when the user has clicked out of the calendar
   * @param event
   */
  handleCalendarHideKeydown(t) {
    t.key === "Escape" && this.hideCalendar();
  }
  /**
   * Used to show the calendar and bind the click out event to window
   */
  showCalendar() {
    if (!this.disabled) {
      if (this.isCalendarVisible) {
        this.hideCalendar();
        return;
      }
      this.isCalendarVisible = !0, this.handleCalendarHideClick = this.handleCalendarHideClick.bind(this), window.addEventListener("click", this.handleCalendarHideClick), this.handleCalendarHideKeydown = this.handleCalendarHideKeydown.bind(this), window.addEventListener("keydown", this.handleCalendarHideKeydown), this.syncSelectedCalendarDateWithInput();
    }
  }
  /**
   * Hides the calendar and unbinds the window event
   */
  hideCalendar() {
    this.isCalendarVisible = !1, window.removeEventListener("click", this.handleCalendarHideClick), window.removeEventListener("keydown", this.handleCalendarHideKeydown);
  }
  /**
   * Uses the browser API to get the native word for the weekday names
   * @param locale
   * @returns
   */
  getWeekdayNames(t) {
    const e = new Date(2022, 7, 21), s = [], r = 7;
    for (let o = 0; o < r; o++)
      s.push({
        short: e.toLocaleDateString(t, { weekday: "short" }),
        long: e.toLocaleDateString(t, { weekday: "long" })
      }), e.setDate(e.getDate() + 1);
    return s;
  }
  /**
   * Renders the column weekday headers for the calendar
   * @param locale
   * @returns
   */
  getDayColumnHeaders(t) {
    return this.getWeekdayNames(t).map((e) => g`<td
          abbr="${e.long}"
          class="datepicker__calendar-day"
        >
          ${e.short}
        </td>`);
  }
  /**
   * Renders the days in the given month and highlights the user selected date
   * @returns
   */
  getDaysInMonth() {
    const t = this.getUserInputDate(), e = this.selectedCalendarDate.getFullYear(), s = this.selectedCalendarDate.getMonth(), r = new Date(e, s + 1, 0).getDate(), o = [], i = new Date(e, s, 1).getDay(), a = !isNaN(t.getTime()), c = this.getTimestampFromInput(this.min, -1 / 0), h = this.getTimestampFromInput(this.max, 1 / 0, !0), d = this.todaysDate.getTime() >= c && this.todaysDate.getTime() <= h, v = a && t.getTime() >= c && t.getTime() <= h;
    let p = a ? t.getFullYear() : null, u = a ? t.getMonth() : null, m = a ? t.getDate() : null, b = null;
    for (let y = 0; y < i; y++)
      o.push(null);
    for (let y = 1; y <= r; y++)
      o.push(y);
    const w = (y) => {
      const _ = new Date(`
        ${e}/${this.getDoubleDigitNumber(s + 1)}/${this.getDoubleDigitNumber(y || 1)}
      `).getTime(), Z = !!y && _ >= c && _ <= h && !this.readonly, T = a && y === m && s === u && e === p, L = y === this.todaysDate.getDate() && s === this.todaysDate.getMonth() && e === this.todaysDate.getFullYear(), r1 = () => {
        if (!y)
          return;
        if (typeof b == "number")
          return -1;
        const Q = s === u && e === p, Q1 = s === this.todaysDate.getMonth() && e === this.todaysDate.getFullYear();
        if (Q && v)
          return T ? (b = y, 0) : -1;
        if (Q1 && Z) {
          if (L || !d)
            return b = y, 0;
        } else if (Z)
          return b = y, 0;
        return -1;
      }, z = y ? g`
        <div
          class=${E({
        "datepicker__calendar-cell-date": !0,
        "datepicker__calendar-cell-date--selectable": Z,
        "datepicker__calendar-cell-date--unselectable": !Z,
        "datepicker__calendar-cell-date--selected": T,
        "datepicker__calendar-cell-date--today": L
      })}
          @click=${() => Z && this.handleCalendarDayClick(e, s, y)}
          tabindex=${A(r1())}
          @keydown=${(Q) => !!y && this.handleCalendarDayKeydown(e, s, y, Q)}
          >
          ${y}
        </div>
        ` : "";
      return g`
        <td
          class="datepicker__calendar-cell"
          aria-selected=${T}
        >
          ${z || ""}
        </td>
      `;
    }, k = [];
    for (; o.length; )
      k.push(g`
        <tr>
          ${w(o.shift())}
          ${w(o.shift())}
          ${w(o.shift())}
          ${w(o.shift())}
          ${w(o.shift())}
          ${w(o.shift())}
          ${w(o.shift())}
        </tr>
      `);
    return k;
  }
  /**
   * Allows user to choose the calendar month view
   * @param event
   */
  selectMonthDropdown(t) {
    this.setCalendarMonth(parseInt(t.target.value));
  }
  /**
   * Selects the month in the calendar view and re-creates the date the trigger lit to do a new calendar render
   * @param month
   */
  setCalendarMonth(t) {
    this.selectedCalendarDate.setDate(1), this.selectedCalendarDate.setMonth(t), this.selectedCalendarDate = new Date(this.selectedCalendarDate);
  }
  /**
   * Selects the year in the calendar view and re-creates the date the trigger lit to do a new calendar render
   * @param event
   */
  selectYearDropdown(t) {
    this.selectedCalendarDate.setFullYear(parseInt(t.target.value)), this.selectedCalendarDate = new Date(this.selectedCalendarDate);
  }
  /**
   * Returns a list of options
   * @param month - Calendar month dropdown option
   * @param calendarViewYear - Current calendar view
   * @returns boolean
   */
  isMonthDropdownOptionDisabled(t, e) {
    const s = this.max ? this.getDateFromInput(this.max) : new Date("-"), r = this.min ? this.getDateFromInput(this.min) : new Date("-"), o = !isNaN(s.getTime()), i = !isNaN(r.getTime()), a = o ? s.getFullYear() : 1 / 0, c = i ? r.getFullYear() : 0, h = o ? s.getMonth() : 12, d = i ? r.getMonth() : 0;
    return !!(o && (a < e || a === e && h < t) || i && (c > e || c === e && d > t));
  }
  /**
   * Uses the browser API to get the native word for the month names and returns the dropdown
   * @param locale
   * @returns
   */
  getMonthDropdown(t) {
    const e = this.selectedCalendarDate.getFullYear(), s = this.selectedCalendarDate.getMonth(), r = this.todaysDate.getFullYear(), o = Array.from({ length: 12 }, (i, a) => new Date(r, a, 1).toLocaleDateString(t, { month: "long" })).map((i, a) => g`
        <option
          value="${a}"
          aria-label="${i}"
          ?disabled=${this.isMonthDropdownOptionDisabled(a, e)}
          ?selected=${a === s}>
          ${i}
        </option>
      `);
    return g`
      <select
        class="datepicker__calendar-header-select-dropdown datepicker__calendar-header-select-dropdown-month"
        @input="${(i) => this.selectMonthDropdown(i)}"
        aria-label="calendar month"
      >
        ${o}
      </select>
    `;
  }
  /**
   * Creates a dropdown from the current year minus the last 100 years
   * @returns
   */
  getYearDropdown() {
    const t = this.selectedCalendarDate.getFullYear();
    let e, s;
    if (this.min && this.max)
      e = this.getDateFromInput(this.min).getFullYear(), s = this.getDateFromInput(this.max).getFullYear();
    else if (this.max)
      s = this.getDateFromInput(this.max).getFullYear(), e = s - 100;
    else if (this.min)
      e = this.getDateFromInput(this.min).getFullYear(), s = e + 100;
    else {
      const i = this.todaysDate.getFullYear();
      e = i - 100, s = i;
    }
    const r = [];
    let o = !1;
    for (let i = e; i <= s; i++)
      i === t && (o = !0), r.push(g`
        <option
          value="${i}"
          aria-label="${i}"
          ?selected=${i === t}>
          ${i}
        </option>
      `);
    return o || (e > t ? r.unshift(g`
          <option
            value="${t}"
            ?selected=${!0}
            aria-label="current year"
            disabled>
            ${t}
            </option>
        `) : r.push(g`
          <option
            value="${t}"
            ?selected=${!0}
            aria-label="current year"
            disabled>
            ${t}
            </option>
        `)), g`
      <select
        class="datepicker__calendar-header-select-dropdown"
        @input="${(i) => this.selectYearDropdown(i)}"
        aria-label="calendar year"
      >
        ${r}
      </select>
    `;
  }
  /**
   * Returns date from string input
   * @param date
   * @returns
   */
  getDateFromInput(t, e = !1) {
    return e ? new Date(new Date(t.replace(/\-/g, "/")).getTime() + 86399e3) : new Date(t.replace(/\-/g, "/"));
  }
  /**
   * Reads the user input and returns the date selected
   * @returns Date
   */
  getUserInputDate() {
    return this.value ? this.getDateFromInput(this.value) : new Date("-");
  }
  /**
   * Parses date string and returns a timestamp with a fallback value
   * @param date
   * @param fallback
   * @param endOfDay
   * @returns timestamp
   */
  getTimestampFromInput(t, e, s = !1) {
    return t ? this.getDateFromInput(t, s).getTime() : e;
  }
  /**
   * Converts single digit number to double digit (1 -> 01)
   * @param number
   * @returns
   */
  getDoubleDigitNumber(t) {
    return t < 10 ? `0${t}` : t + "";
  }
  /**
   * Syncs the selected calendar date with the input
   */
  syncSelectedCalendarDateWithInput() {
    const t = this.getUserInputDate();
    isNaN(t.getTime()) ? this.selectedCalendarDate = new Date(this.todaysDate.getFullYear(), this.todaysDate.getMonth(), this.todaysDate.getDate()) : this.selectedCalendarDate = t;
    const e = this.getTimestampFromInput(this.min, -1 / 0), s = this.getTimestampFromInput(this.max, 1 / 0);
    this.selectedCalendarDate.getTime() > s && (this.selectedCalendarDate = this.getDateFromInput(this.max)), this.selectedCalendarDate.getTime() < e && (this.selectedCalendarDate = this.getDateFromInput(this.min));
  }
  /**
   * Renders the calendar
   * @returns
   */
  getCalendarInput() {
    const t = new Intl.DateTimeFormat().resolvedOptions().locale;
    return this.selectedCalendarDate || this.syncSelectedCalendarDateWithInput(), g`
      <div
        aria-label="choose date"
        aria-modal="true"
        role="dialog"
        class=${Ke}
        ?hidden=${!this.isCalendarVisible}
        aria-hidden=${!this.isCalendarVisible}
      >
        <div class="datepicker__calendar-header">
          <div class="datepicker__calendar-header-select datepicker__calendar-header-month">
            ${this.getMonthDropdown(t)}
            <div class="datepicker__calendar-header-select-label">
              ${this.selectedCalendarDate.toLocaleDateString(t, { month: "long" })}
              <vds-icon name="chevron-down"></vds-icon>
            </div>
          </div>
          <div class="datepicker__calendar-header-select datepicker__calendar-header-year">
            ${this.getYearDropdown()}
            <div class="datepicker__calendar-header-select-label">
              ${this.selectedCalendarDate.getFullYear()}
              <vds-icon name="chevron-down"></vds-icon>
            </div>
          </div>
          <div class="datepicker__calendar-header-control">
            <button
              class="datepicker__calendar-header-control-button"
              aria-label="previous month"
              @click="${() => this.setCalendarMonth(this.selectedCalendarDate.getMonth() + -1)}"
            >
              <vds-icon name="chevron-left"></vds-icon>
            </button>
            <button
              class="datepicker__calendar-header-control-button"
              aria-label="next month"
              @click="${() => this.setCalendarMonth(this.selectedCalendarDate.getMonth() + 1)}"
            >
              <vds-icon name="chevron-right"></vds-icon>
            </button>
          </div>
        </div>
        <table class="datepicker__calendar-body" role="grid">
          <tr>
            ${this.getDayColumnHeaders(t)}
          </tr>
          ${this.getDaysInMonth()}
        </table>
      </div>
    `;
  }
  async handleValueChange() {
    await this.updateComplete, this.formSubmitController.updateValidity();
  }
  focus(t) {
    if (!this.isCalendarVisible) {
      this.input.focus(t);
      return;
    }
    requestAnimationFrame(() => {
      const e = this.calendarBody.querySelector('[tabindex="0"]');
      e && e.focus(t);
    });
  }
  handleDisabledChange() {
    this.formSubmitController.setValidity(this.disabled);
  }
  focusWhenCalendarVisible() {
    this.focus();
  }
  render() {
    return g`
      ${this.getLabel(Ye)}
      <div class="datepicker__wrapper">
        <input
          id="control"
          class=${E({
      datepicker__control: !0,
      touched: this.hasTouched
    })}
          part="base"
          type="date"
          name=${A(this.name ? this.name : void 0)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          min=${A(this.min)}
          max=${A(this.max)}
          .value=${c0(A(this.value))}
          aria-labelledby="label help-text"
          @input=${this.handleInput}
          @invalid=${this.handleInvalid}
          @keydown=${this.handleKeyDown}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          @click=${this.handleClick}
        />
        <button
          @click=${this.showCalendar}
          class="datepicker__calendar-toggle-button"
          ?disabled=${this.disabled}
          part="button"
        >
          <vds-icon name="calendar"></vds-icon>
        </button>
        ${this.getCalendarInput()}
      </div>
      ${this.getHelpText(Ye)}
    `;
  }
};
t1.styles = $(yo);
m1([
  l()
], t1.prototype, "value", 2);
m1([
  g1(".datepicker__calendar-body")
], t1.prototype, "calendarBody", 2);
m1([
  l()
], t1.prototype, "min", 2);
m1([
  l()
], t1.prototype, "max", 2);
m1([
  l({ type: Boolean, reflect: !0 })
], t1.prototype, "readonly", 2);
m1([
  K()
], t1.prototype, "isCalendarVisible", 2);
m1([
  K()
], t1.prototype, "selectedCalendarDate", 2);
m1([
  V("value", { waitUntilFirstUpdate: !0 })
], t1.prototype, "handleValueChange", 1);
m1([
  V("disabled", { waitUntilFirstUpdate: !0 })
], t1.prototype, "handleDisabledChange", 1);
m1([
  V("isCalendarVisible", { waitUntilFirstUpdate: !0 })
], t1.prototype, "focusWhenCalendarVisible", 1);
t1 = m1([
  S("vds-datepicker")
], t1);
const Ye = "datepicker", $o = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:flex;align-items:flex-start;gap:var(--spacing-xs);color:var(--color-text-normal);cursor:pointer;outline:none}:host([label-position=start]){flex-direction:row-reverse;justify-content:flex-end}:host([aria-checked=true]) .radio{background-color:var(--color-background-selected);color:var(--color-text-onSelected);border-color:transparent}:host(:focus:not([disabled])) .radio{box-shadow:var(--focus-shadow)}:host([disabled][aria-checked=true]) .radio{background-color:var(--color-background-disabled);color:var(--color-text-disabled)}:host([disabled]){color:var(--color-text-disabled);cursor:not-allowed}:host([disabled]) .radio{background-color:var(--color-action-disabled);border-color:var(--color-border-disabled)}.radio{border:1px solid var(--color-border-normal);border-radius:var(--borderRadius-round);background-color:var(--color-background-primary);color:transparent;outline:none;transition:box-shadow .15s}.radio__label{cursor:inherit}
`;
var Mo = Object.defineProperty, Vo = Object.getOwnPropertyDescriptor, K1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Vo(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && Mo(e, s, o), o;
};
let $1 = class extends B {
  constructor() {
    super(...arguments), this.slotController = new I1(this, d1), this.checked = !1, this.labelPosition = "end", this.disabled = !1, this.setListeners = (t) => [
      ["blur", this.handleBlur],
      ["click", this.handleClick],
      ["focus", this.handleFocus]
    ].forEach(([e, s]) => this[`${t}EventListener`](e, s));
  }
  connectedCallback() {
    super.connectedCallback(), this.handleBlur = this.handleBlur.bind(this), this.handleClick = this.handleClick.bind(this), this.handleFocus = this.handleFocus.bind(this), this.setDefaultAttrs(), this.setListeners("add");
  }
  disconnectedCallback() {
    this.setListeners("remove");
  }
  handleFocus() {
    this.emit("vds-focus");
  }
  handleBlur() {
    this.emit("vds-blur");
  }
  handleClick() {
    this.disabled || (this.checked = !0);
  }
  setDefaultAttrs() {
    this.setAttribute("role", "radio"), this.setAttribute("tabindex", "-1"), this.setAttribute("aria-disabled", `${this.disabled}`);
  }
  handleCheckedChange() {
    this.setAttribute("aria-checked", `${this.checked}`), this.setAttribute("tabindex", this.checked ? "0" : "-1");
  }
  handleDisabledChange() {
    this.setAttribute("aria-disabled", `${this.disabled}`);
  }
  render() {
    return g`
      <vds-icon class="radio" name="radio-checked"></vds-icon>
      <label class="radio__label"><slot></slot></label>
    `;
  }
};
$1.styles = $($o);
K1([
  K()
], $1.prototype, "checked", 2);
K1([
  l()
], $1.prototype, "value", 2);
K1([
  l({ attribute: "label-position" }),
  x(Ae, "end")
], $1.prototype, "labelPosition", 2);
K1([
  l({ type: Boolean, reflect: !0 })
], $1.prototype, "disabled", 2);
K1([
  V("checked")
], $1.prototype, "handleCheckedChange", 1);
K1([
  V("disabled", { waitUntilFirstUpdate: !0 })
], $1.prototype, "handleDisabledChange", 1);
$1 = K1([
  S("vds-radio")
], $1);
const Ho = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:block;color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .radio-group__label:after{content:" *";color:var(--color-text-critical)}.radio-group{width:fit-content;border:none;padding:0;margin:0}.radio-group__label{display:flex;align-items:flex-end;justify-content:space-between}.radio-group__label-text{cursor:inherit;color:var(--color-text-normal)}.radio-group__wrapper{position:relative;color:var(--color-text-normal)}.radio-group__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.radio-group__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.radio-group__accordion-item{min-height:0}.radio-group__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.radio-group__error-icon{flex:0}.radio-group__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.radio-group__wrapper{position:relative;margin:var(--spacing-xs) 0}.radio-group__radios{display:grid;gap:var(--spacing-xs)}.radio-group__radios--inline{display:flex;gap:var(--spacing-md)}.radio-group__visually-hidden{position:absolute;bottom:0;left:11px}.radio-group__control{padding:0;margin:0;width:1px;height:1px}
`;
var So = Object.defineProperty, Do = Object.getOwnPropertyDescriptor, h0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Do(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && So(e, s, o), o;
};
let E1 = class extends j {
  constructor() {
    super(...arguments), this.formSubmitController = new q0(this, {
      defaultValue: (t) => t.defaultValue
    }), this.value = "", this.inline = !1, this.radioLabelPosition = "end", this.defaultValue = "";
  }
  connectedCallback() {
    super.connectedCallback(), this.defaultValue = this.value, this.radioLabelPosition === "start" && this.getAllRadios().forEach((t) => {
      t.setAttribute("label-position", "start");
    });
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  getAllRadios() {
    return [...this.querySelectorAll("vds-radio")];
  }
  handleRadioClick(t) {
    const e = t.target;
    if (e.disabled)
      return;
    const s = this.getAllRadios(), r = this.value;
    this.value = e.value, s.forEach((o) => {
      o.checked = o === e;
    }), this.value !== r && this.emit("vds-change", { detail: { value: this.value, name: this.name } });
  }
  handleKeyDown(t) {
    if (![...v2, " "].includes(t.key))
      return;
    const e = this.getAllRadios().filter((c) => !c.disabled), s = e.find((c) => c.checked) ?? e[0], r = this.value, o = [c1.ArrowUp, c1.ArrowLeft], i = t.key === " " ? 0 : o.includes(t.key) ? -1 : 1;
    let a = e.indexOf(s) + i;
    a < 0 && (a = e.length - 1), a > e.length - 1 && (a = 0), this.getAllRadios().forEach((c) => {
      c.checked = !1, c.tabIndex = -1;
    }), this.value = e[a].value, e[a].tabIndex = 0, e[a].checked = !0, e[a].focus(), this.value !== r && this.emit("vds-change", { detail: { value: this.value, name: this.name } }), t.preventDefault();
  }
  handleLabelClick() {
    const t = this.getAllRadios(), e = t.find((o) => o.checked), s = t.find((o) => !o.disabled), r = e || s;
    r && r.focus();
  }
  // occurs on init + when default slot contents change
  handleControlSlotChange() {
    const t = this.getAllRadios();
    t.forEach((r) => r.checked = r.value === this.value);
    const e = !t.some((r) => r.checked), s = t.filter((r) => !r.disabled)[0];
    e && s && (s.tabIndex = 0);
  }
  updateCheckedRadio() {
    this.getAllRadios().forEach((e) => e.checked = e.value === this.value), this.formSubmitController.setValidity(this.validity.valid);
  }
  handleValueChange() {
    this.hasUpdated && this.updateCheckedRadio(), this.validity.valid && this.updateValidityMessage();
  }
  get validity() {
    const t = !(this.value && this.required || !this.required), e = this.input && this.input.validity.customError;
    return {
      badInput: !1,
      customError: e,
      patternMismatch: !1,
      rangeOverflow: !1,
      rangeUnderflow: !1,
      stepMismatch: !1,
      tooLong: !1,
      tooShort: !1,
      typeMismatch: !1,
      valid: !(t || e),
      valueMissing: t
    };
  }
  getLegend() {
    const t = g`
      <legend
        id="legend"
        class="radio-group__label"
        @click=${this.handleLabelClick}
        aria-hidden=${!this.label}
      >
        ${this.label || I7}
      </legend>
    `;
    return this.hideLabel && this.label ? g`<vds-visually-hidden>${t}</vds-visually-hidden>` : t;
  }
  render() {
    return g`
      <fieldset
        class=${E({
      "radio-group": !0,
      "radio-group--required": this.required
    })}
        part="base"
        role="radiogroup"
        aria-labelledby="legend help-text"
        aria-errormessage=${A(this.hasValidationMessage ? "error-text" : void 0)}
      >
        ${this.getLegend()}
        <div class="radio-group__wrapper">
          <slot
            class=${E({
      "radio-group__radios": !0,
      "radio-group__radios--inline": this.inline
    })}
            @click=${this.handleRadioClick}
            @keydown=${this.handleKeyDown}
            role="presentation"
            @slotchange=${this.handleControlSlotChange}
          ></slot>
          <vds-visually-hidden class="radio-group__visually-hidden">
            <label>
              <input
                class="radio-group__control"
                type="text"
                id="control"
                .value=${this.value}
                ?required=${this.required}
                tabindex=${-1}
                hidden
              />
            </label>
          </vds-visually-hidden>
        </div>
        ${this.getHelpText(Lo)}
      </fieldset>
    `;
  }
};
E1.styles = $(Ho);
h0([
  l()
], E1.prototype, "value", 2);
h0([
  l({ type: Boolean })
], E1.prototype, "inline", 2);
h0([
  l({ attribute: "radio-label-position" }),
  x(Ae, "end")
], E1.prototype, "radioLabelPosition", 2);
h0([
  K()
], E1.prototype, "defaultValue", 2);
h0([
  V("value")
], E1.prototype, "handleValueChange", 1);
E1 = h0([
  S("vds-radio-group")
], E1);
const Lo = "radio-group", Oo = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:grid;gap:var(--spacing-4px);color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .textarea__label-text:after{content:" *";color:var(--color-text-critical)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}:host([disabled]) .textarea__wrapper{color:var(--color-text-disabled)}::placeholder{color:var(--color-text-placeholder)}.textarea__label{display:flex;align-items:flex-end;justify-content:space-between}.textarea__label-text{cursor:inherit;color:var(--color-text-normal)}.textarea__wrapper{position:relative;color:var(--color-text-normal)}.textarea__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.textarea__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.textarea__accordion-item{min-height:0}.textarea__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.textarea__error-icon{flex:0}.textarea__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.textarea__control{appearance:none;background-color:var(--color-background-primary);border:1px solid var(--color-border-normal);border-radius:var(--borderRadius-sm);box-sizing:border-box;min-height:3rem;padding:var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-sm);width:100%;margin:0;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);color:inherit;cursor:inherit;padding:var(--spacing-sm);outline:none;transition:box-shadow .15s}.textarea__control.resize-vertical{resize:vertical}.textarea__control.resize-horizontal{resize:horizontal}.textarea__control.resize-both{resize:both}.textarea__control.resize-none{resize:none}.textarea__control[cols]{width:auto}.textarea__control:focus{box-shadow:var(--focus-shadow)}.textarea__control:is(.touched,:not([required])):is(:invalid,[aria-invalid=true]){background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical)}.textarea__control:disabled{background-color:var(--color-background-secondary-alpha);border:1px solid var(--color-border-light)}
`;
var Eo = Object.defineProperty, To = Object.getOwnPropertyDescriptor, s1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? To(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && Eo(e, s, o), o;
};
const zo = ["vertical", "horizontal", "both", "none"];
let q = class extends j {
  constructor() {
    super(...arguments), this.value = "", this.readonly = !1, this.resize = "vertical", this.placeholder = "";
  }
  /**
   * Selects the textarea's text.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/select
   */
  select() {
    this.input.select();
  }
  async handleValueChange() {
    await this.updateComplete, this.formSubmitController.updateValidity();
  }
  handleDisabledChange() {
    this.formSubmitController.setValidity(this.disabled);
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  handleInput() {
    this.value = this.input.value, this.formSubmitController.updateValidity(), this.emit("vds-input"), this.input.validity.valid && this.updateValidityMessage();
  }
  handleInvalid() {
    this.formSubmitController.setValidity(!1);
  }
  render() {
    return g`
      ${this.getLabel(Ge)}
      <div class="textarea__wrapper">
        <textarea
          id="control"
          class=${E({
      textarea__control: !0,
      "resize-${this.resize}": !0,
      touched: this.hasTouched
    })}
          part="base"
          name=${A(this.name ? this.name : void 0)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          minlength=${A(this.minlength)}
          maxlength=${A(this.maxlength)}
          .value=${c0(A(this.value))}
          autocomplete=${A(this.autocomplete)}
          spellcheck=${A(this.spellcheck)}
          rows=${A(this.rows)}
          cols=${A(this.cols)}
          aria-labelledby="label help-text"
          @input=${this.handleInput}
          @invalid=${this.handleInvalid}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          placeholder=${this.placeholder}
        ></textarea>
      </div>
      ${this.getHelpText(Ge)}
    `;
  }
};
q.styles = $(Oo);
s1([
  l()
], q.prototype, "value", 2);
s1([
  l({ type: Number })
], q.prototype, "minlength", 2);
s1([
  l({ type: Number })
], q.prototype, "maxlength", 2);
s1([
  l()
], q.prototype, "autocomplete", 2);
s1([
  l({ type: Boolean })
], q.prototype, "spellcheck", 2);
s1([
  l({ type: Boolean, reflect: !0 })
], q.prototype, "readonly", 2);
s1([
  l({ type: Number })
], q.prototype, "rows", 2);
s1([
  l({ type: Number })
], q.prototype, "cols", 2);
s1([
  l({ reflect: !0 }),
  x(zo, "vertical")
], q.prototype, "resize", 2);
s1([
  l()
], q.prototype, "placeholder", 2);
s1([
  V("value", { waitUntilFirstUpdate: !0 })
], q.prototype, "handleValueChange", 1);
s1([
  V("disabled", { waitUntilFirstUpdate: !0 })
], q.prototype, "handleDisabledChange", 1);
q = s1([
  S("vds-textarea")
], q);
const Ge = "textarea", Io = `:host{display:inline-block}.tag{display:flex;align-items:center;padding:2px 4px;gap:5px;border-radius:var(--borderRadius-sm)}.tag vds-button{display:flex}.tag--success{color:var(--color-text-success);background-color:var(--color-background-success-light)}.tag--success vds-button::part(base){height:1rem;width:1rem;padding:0;color:inherit;background-color:var(--color-button-subtle)}.tag--success vds-button::part(base):focus{outline:none;transition:box-shadow .15s}.tag--success vds-button::part(base):focus:focus{box-shadow:var(--focus-shadow)}.tag--success vds-button::part(base):hover{background-color:var(--color-button-subtle-hovered)}.tag--success vds-button::part(base):active{background-color:var(--color-button-subtle-pressed)}.tag--warning{color:var(--color-text-warning);background-color:var(--color-background-warning-light)}.tag--warning vds-button::part(base){height:1rem;width:1rem;padding:0;color:inherit;background-color:var(--color-button-subtle)}.tag--warning vds-button::part(base):focus{outline:none;transition:box-shadow .15s}.tag--warning vds-button::part(base):focus:focus{box-shadow:var(--focus-shadow)}.tag--warning vds-button::part(base):hover{background-color:var(--color-button-subtle-hovered)}.tag--warning vds-button::part(base):active{background-color:var(--color-button-subtle-pressed)}.tag--critical{color:var(--color-text-critical);background-color:var(--color-background-critical-light)}.tag--critical vds-button::part(base){height:1rem;width:1rem;padding:0;color:inherit;background-color:var(--color-button-subtle)}.tag--critical vds-button::part(base):focus{outline:none;transition:box-shadow .15s}.tag--critical vds-button::part(base):focus:focus{box-shadow:var(--focus-shadow)}.tag--critical vds-button::part(base):hover{background-color:var(--color-button-subtle-hovered)}.tag--critical vds-button::part(base):active{background-color:var(--color-button-subtle-pressed)}.tag--default{color:var(--color-text-secondary);background-color:var(--color-background-secondary-alpha)}.tag--default vds-button::part(base){height:1rem;width:1rem;padding:0;color:inherit;background-color:var(--color-button-subtle)}.tag--default vds-button::part(base):focus{outline:none;transition:box-shadow .15s}.tag--default vds-button::part(base):focus:focus{box-shadow:var(--focus-shadow)}.tag--default vds-button::part(base):hover{background-color:var(--color-button-subtle-hovered)}.tag--default vds-button::part(base):active{background-color:var(--color-button-subtle-pressed)}
`;
var Po = Object.defineProperty, Bo = Object.getOwnPropertyDescriptor, ye = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Bo(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && Po(e, s, o), o;
};
const Fo = ["success", "default", "warning", "critical"];
let $0 = class extends B {
  constructor() {
    super(...arguments), this.color = "default", this.deletable = !1, this.slotController = new I1(this, d1);
  }
  get iconDescription() {
    return `Delete ${this.getText()}`;
  }
  /**
   * get the text content of the tag slot
   * use it for the button's, icon only, screenreader description
   */
  getText() {
    var e, s, r;
    const t = (s = (e = this.shadowRoot) == null ? void 0 : e.querySelector("slot")) == null ? void 0 : s.assignedNodes().filter((o) => {
      var i;
      return ((i = o.nodeValue) == null ? void 0 : i.trim()) !== "" && o.nodeName === "#text";
    })[0];
    return t ? (r = t.nodeValue) == null ? void 0 : r.replace(/\n/g, "").trim() : "";
  }
  handleIconClick() {
    this.emit("vds-delete");
  }
  maybeGetDeleteIcon() {
    return this.deletable ? g`
      <vds-button
        icon-description=${this.iconDescription}
        size="tiny"
        @click=${this.handleIconClick}
      >
        <vds-icon name="close" size="sm"></vds-icon>
      </vds-button>
    ` : "";
  }
  render() {
    return g`<span
      class=${E({
      tag: !0,
      [`tag--${this.color}`]: !0
    })}
    >
      <slot></slot>
      ${this.maybeGetDeleteIcon()}
    </span>`;
  }
};
$0.styles = $(Io);
ye([
  l(),
  x(Fo, "default")
], $0.prototype, "color", 2);
ye([
  l({ type: Boolean })
], $0.prototype, "deletable", 2);
$0 = ye([
  S("vds-tag")
], $0);
function d0(t) {
  return t.split("-")[1];
}
function ke(t) {
  return t === "y" ? "height" : "width";
}
function L1(t) {
  return t.split("-")[0];
}
function p0(t) {
  return ["top", "bottom"].includes(L1(t)) ? "x" : "y";
}
function Qe(t, e, s) {
  let { reference: r, floating: o } = t;
  const i = r.x + r.width / 2 - o.width / 2, a = r.y + r.height / 2 - o.height / 2, c = p0(e), h = ke(c), d = r[h] / 2 - o[h] / 2, v = c === "x";
  let p;
  switch (L1(e)) {
    case "top":
      p = { x: i, y: r.y - o.height };
      break;
    case "bottom":
      p = { x: i, y: r.y + r.height };
      break;
    case "right":
      p = { x: r.x + r.width, y: a };
      break;
    case "left":
      p = { x: r.x - o.width, y: a };
      break;
    default:
      p = { x: r.x, y: r.y };
  }
  switch (d0(e)) {
    case "start":
      p[c] -= d * (s && v ? -1 : 1);
      break;
    case "end":
      p[c] += d * (s && v ? -1 : 1);
  }
  return p;
}
const jo = async (t, e, s) => {
  const { placement: r = "bottom", strategy: o = "absolute", middleware: i = [], platform: a } = s, c = i.filter(Boolean), h = await (a.isRTL == null ? void 0 : a.isRTL(e));
  let d = await a.getElementRects({ reference: t, floating: e, strategy: o }), { x: v, y: p } = Qe(d, r, h), u = r, m = {}, b = 0;
  for (let w = 0; w < c.length; w++) {
    const { name: k, fn: y } = c[w], { x: _, y: Z, data: T, reset: L } = await y({ x: v, y: p, initialPlacement: r, placement: u, strategy: o, middlewareData: m, rects: d, platform: a, elements: { reference: t, floating: e } });
    v = _ ?? v, p = Z ?? p, m = { ...m, [k]: { ...m[k], ...T } }, L && b <= 50 && (b++, typeof L == "object" && (L.placement && (u = L.placement), L.rects && (d = L.rects === !0 ? await a.getElementRects({ reference: t, floating: e, strategy: o }) : L.rects), { x: v, y: p } = Qe(d, u, h)), w = -1);
  }
  return { x: v, y: p, placement: u, strategy: o, middlewareData: m };
};
function w2(t) {
  return typeof t != "number" ? function(e) {
    return { top: 0, right: 0, bottom: 0, left: 0, ...e };
  }(t) : { top: t, right: t, bottom: t, left: t };
}
function ue(t) {
  return { ...t, top: t.y, left: t.x, right: t.x + t.width, bottom: t.y + t.height };
}
async function _e(t, e) {
  var s;
  e === void 0 && (e = {});
  const { x: r, y: o, platform: i, rects: a, elements: c, strategy: h } = t, { boundary: d = "clippingAncestors", rootBoundary: v = "viewport", elementContext: p = "floating", altBoundary: u = !1, padding: m = 0 } = e, b = w2(m), w = c[u ? p === "floating" ? "reference" : "floating" : p], k = ue(await i.getClippingRect({ element: (s = await (i.isElement == null ? void 0 : i.isElement(w))) == null || s ? w : w.contextElement || await (i.getDocumentElement == null ? void 0 : i.getDocumentElement(c.floating)), boundary: d, rootBoundary: v, strategy: h })), y = p === "floating" ? { ...a.floating, x: r, y: o } : a.reference, _ = await (i.getOffsetParent == null ? void 0 : i.getOffsetParent(c.floating)), Z = await (i.isElement == null ? void 0 : i.isElement(_)) && await (i.getScale == null ? void 0 : i.getScale(_)) || { x: 1, y: 1 }, T = ue(i.convertOffsetParentRelativeRectToViewportRelativeRect ? await i.convertOffsetParentRelativeRectToViewportRelativeRect({ rect: y, offsetParent: _, strategy: h }) : y);
  return { top: (k.top - T.top + b.top) / Z.y, bottom: (T.bottom - k.bottom + b.bottom) / Z.y, left: (k.left - T.left + b.left) / Z.x, right: (T.right - k.right + b.right) / Z.x };
}
const ge = Math.min, N1 = Math.max;
function ve(t, e, s) {
  return N1(t, ge(e, s));
}
const Ro = (t) => ({ name: "arrow", options: t, async fn(e) {
  const { element: s, padding: r = 0 } = t || {}, { x: o, y: i, placement: a, rects: c, platform: h } = e;
  if (s == null)
    return {};
  const d = w2(r), v = { x: o, y: i }, p = p0(a), u = ke(p), m = await h.getDimensions(s), b = p === "y" ? "top" : "left", w = p === "y" ? "bottom" : "right", k = c.reference[u] + c.reference[p] - v[p] - c.floating[u], y = v[p] - c.reference[p], _ = await (h.getOffsetParent == null ? void 0 : h.getOffsetParent(s));
  let Z = _ ? p === "y" ? _.clientHeight || 0 : _.clientWidth || 0 : 0;
  Z === 0 && (Z = c.floating[u]);
  const T = k / 2 - y / 2, L = d[b], r1 = Z - m[u] - d[w], z = Z / 2 - m[u] / 2 + T, Q = ve(L, z, r1), Q1 = d0(a) != null && z != Q && c.reference[u] / 2 - (z < L ? d[b] : d[w]) - m[u] / 2 < 0;
  return { [p]: v[p] - (Q1 ? z < L ? L - z : r1 - z : 0), data: { [p]: Q, centerOffset: z - Q } };
} }), No = ["top", "right", "bottom", "left"];
No.reduce((t, e) => t.concat(e, e + "-start", e + "-end"), []);
const Uo = { left: "right", right: "left", bottom: "top", top: "bottom" };
function B0(t) {
  return t.replace(/left|right|bottom|top/g, (e) => Uo[e]);
}
function Wo(t, e, s) {
  s === void 0 && (s = !1);
  const r = d0(t), o = p0(t), i = ke(o);
  let a = o === "x" ? r === (s ? "end" : "start") ? "right" : "left" : r === "start" ? "bottom" : "top";
  return e.reference[i] > e.floating[i] && (a = B0(a)), { main: a, cross: B0(a) };
}
const qo = { start: "end", end: "start" };
function ne(t) {
  return t.replace(/start|end/g, (e) => qo[e]);
}
const Ko = function(t) {
  return t === void 0 && (t = {}), { name: "flip", options: t, async fn(e) {
    var s;
    const { placement: r, middlewareData: o, rects: i, initialPlacement: a, platform: c, elements: h } = e, { mainAxis: d = !0, crossAxis: v = !0, fallbackPlacements: p, fallbackStrategy: u = "bestFit", fallbackAxisSideDirection: m = "none", flipAlignment: b = !0, ...w } = t, k = L1(r), y = L1(a) === a, _ = await (c.isRTL == null ? void 0 : c.isRTL(h.floating)), Z = p || (y || !b ? [B0(a)] : function(o1) {
      const y1 = B0(o1);
      return [ne(o1), y1, ne(y1)];
    }(a));
    p || m === "none" || Z.push(...function(o1, y1, j1, S1) {
      const k1 = d0(o1);
      let i1 = function(v0, ee, I2) {
        const He = ["left", "right"], Se = ["right", "left"], P2 = ["top", "bottom"], B2 = ["bottom", "top"];
        switch (v0) {
          case "top":
          case "bottom":
            return I2 ? ee ? Se : He : ee ? He : Se;
          case "left":
          case "right":
            return ee ? P2 : B2;
          default:
            return [];
        }
      }(L1(o1), j1 === "start", S1);
      return k1 && (i1 = i1.map((v0) => v0 + "-" + k1), y1 && (i1 = i1.concat(i1.map(ne)))), i1;
    }(a, b, m, _));
    const T = [a, ...Z], L = await _e(e, w), r1 = [];
    let z = ((s = o.flip) == null ? void 0 : s.overflows) || [];
    if (d && r1.push(L[k]), v) {
      const { main: o1, cross: y1 } = Wo(r, i, _);
      r1.push(L[o1], L[y1]);
    }
    if (z = [...z, { placement: r, overflows: r1 }], !r1.every((o1) => o1 <= 0)) {
      var Q, Q1;
      const o1 = (((Q = o.flip) == null ? void 0 : Q.index) || 0) + 1, y1 = T[o1];
      if (y1)
        return { data: { index: o1, overflows: z }, reset: { placement: y1 } };
      let j1 = (Q1 = z.filter((S1) => S1.overflows[0] <= 0).sort((S1, k1) => S1.overflows[1] - k1.overflows[1])[0]) == null ? void 0 : Q1.placement;
      if (!j1)
        switch (u) {
          case "bestFit": {
            var Ve;
            const S1 = (Ve = z.map((k1) => [k1.placement, k1.overflows.filter((i1) => i1 > 0).reduce((i1, v0) => i1 + v0, 0)]).sort((k1, i1) => k1[1] - i1[1])[0]) == null ? void 0 : Ve[0];
            S1 && (j1 = S1);
            break;
          }
          case "initialPlacement":
            j1 = a;
        }
      if (r !== j1)
        return { reset: { placement: j1 } };
    }
    return {};
  } };
}, Yo = function(t) {
  return t === void 0 && (t = 0), { name: "offset", options: t, async fn(e) {
    const { x: s, y: r } = e, o = await async function(i, a) {
      const { placement: c, platform: h, elements: d } = i, v = await (h.isRTL == null ? void 0 : h.isRTL(d.floating)), p = L1(c), u = d0(c), m = p0(c) === "x", b = ["left", "top"].includes(p) ? -1 : 1, w = v && m ? -1 : 1, k = typeof a == "function" ? a(i) : a;
      let { mainAxis: y, crossAxis: _, alignmentAxis: Z } = typeof k == "number" ? { mainAxis: k, crossAxis: 0, alignmentAxis: null } : { mainAxis: 0, crossAxis: 0, alignmentAxis: null, ...k };
      return u && typeof Z == "number" && (_ = u === "end" ? -1 * Z : Z), m ? { x: _ * w, y: y * b } : { x: y * b, y: _ * w };
    }(e, t);
    return { x: s + o.x, y: r + o.y, data: o };
  } };
};
function Go(t) {
  return t === "x" ? "y" : "x";
}
const Qo = function(t) {
  return t === void 0 && (t = {}), { name: "shift", options: t, async fn(e) {
    const { x: s, y: r, placement: o } = e, { mainAxis: i = !0, crossAxis: a = !1, limiter: c = { fn: (k) => {
      let { x: y, y: _ } = k;
      return { x: y, y: _ };
    } }, ...h } = t, d = { x: s, y: r }, v = await _e(e, h), p = p0(L1(o)), u = Go(p);
    let m = d[p], b = d[u];
    if (i) {
      const k = p === "y" ? "bottom" : "right";
      m = ve(m + v[p === "y" ? "top" : "left"], m, m - v[k]);
    }
    if (a) {
      const k = u === "y" ? "bottom" : "right";
      b = ve(b + v[u === "y" ? "top" : "left"], b, b - v[k]);
    }
    const w = c.fn({ ...e, [p]: m, [u]: b });
    return { ...w, data: { x: w.x - s, y: w.y - r } };
  } };
}, Xe = function(t) {
  return t === void 0 && (t = {}), { name: "size", options: t, async fn(e) {
    const { placement: s, rects: r, platform: o, elements: i } = e, { apply: a = () => {
    }, ...c } = t, h = await _e(e, c), d = L1(s), v = d0(s), p = p0(s) === "x", { width: u, height: m } = r.floating;
    let b, w;
    d === "top" || d === "bottom" ? (b = d, w = v === (await (o.isRTL == null ? void 0 : o.isRTL(i.floating)) ? "start" : "end") ? "left" : "right") : (w = d, b = v === "end" ? "top" : "bottom");
    const k = m - h[b], y = u - h[w];
    let _ = k, Z = y;
    if (p ? Z = ge(u - h.right - h.left, y) : _ = ge(m - h.bottom - h.top, k), !e.middlewareData.shift && !v) {
      const L = N1(h.left, 0), r1 = N1(h.right, 0), z = N1(h.top, 0), Q = N1(h.bottom, 0);
      p ? Z = u - 2 * (L !== 0 || r1 !== 0 ? L + r1 : N1(h.left, h.right)) : _ = m - 2 * (z !== 0 || Q !== 0 ? z + Q : N1(h.top, h.bottom));
    }
    await a({ ...e, availableWidth: Z, availableHeight: _ });
    const T = await o.getDimensions(i.floating);
    return u !== T.width || m !== T.height ? { reset: { rects: !0 } } : {};
  } };
};
function J(t) {
  var e;
  return ((e = t.ownerDocument) == null ? void 0 : e.defaultView) || window;
}
function f1(t) {
  return J(t).getComputedStyle(t);
}
const Je = Math.min, y0 = Math.max, F0 = Math.round;
function x2(t) {
  const e = f1(t);
  let s = parseFloat(e.width), r = parseFloat(e.height);
  const o = t.offsetWidth, i = t.offsetHeight, a = F0(s) !== o || F0(r) !== i;
  return a && (s = o, r = i), { width: s, height: r, fallback: a };
}
function T1(t) {
  return y2(t) ? (t.nodeName || "").toLowerCase() : "";
}
let D0;
function A2() {
  if (D0)
    return D0;
  const t = navigator.userAgentData;
  return t && Array.isArray(t.brands) ? (D0 = t.brands.map((e) => e.brand + "/" + e.version).join(" "), D0) : navigator.userAgent;
}
function b1(t) {
  return t instanceof J(t).HTMLElement;
}
function h1(t) {
  return t instanceof J(t).Element;
}
function y2(t) {
  return t instanceof J(t).Node;
}
function e2(t) {
  return typeof ShadowRoot > "u" ? !1 : t instanceof J(t).ShadowRoot || t instanceof ShadowRoot;
}
function K0(t) {
  const { overflow: e, overflowX: s, overflowY: r, display: o } = f1(t);
  return /auto|scroll|overlay|hidden|clip/.test(e + r + s) && !["inline", "contents"].includes(o);
}
function Xo(t) {
  return ["table", "td", "th"].includes(T1(t));
}
function me(t) {
  const e = /firefox/i.test(A2()), s = f1(t), r = s.backdropFilter || s.WebkitBackdropFilter;
  return s.transform !== "none" || s.perspective !== "none" || !!r && r !== "none" || e && s.willChange === "filter" || e && !!s.filter && s.filter !== "none" || ["transform", "perspective"].some((o) => s.willChange.includes(o)) || ["paint", "layout", "strict", "content"].some((o) => {
    const i = s.contain;
    return i != null && i.includes(o);
  });
}
function fe() {
  return /^((?!chrome|android).)*safari/i.test(A2());
}
function Ze(t) {
  return ["html", "body", "#document"].includes(T1(t));
}
function k2(t) {
  return h1(t) ? t : t.contextElement;
}
const _2 = { x: 1, y: 1 };
function t0(t) {
  const e = k2(t);
  if (!b1(e))
    return _2;
  const s = e.getBoundingClientRect(), { width: r, height: o, fallback: i } = x2(e);
  let a = (i ? F0(s.width) : s.width) / r, c = (i ? F0(s.height) : s.height) / o;
  return a && Number.isFinite(a) || (a = 1), c && Number.isFinite(c) || (c = 1), { x: a, y: c };
}
function U1(t, e, s, r) {
  var o, i;
  e === void 0 && (e = !1), s === void 0 && (s = !1);
  const a = t.getBoundingClientRect(), c = k2(t);
  let h = _2;
  e && (r ? h1(r) && (h = t0(r)) : h = t0(t));
  const d = c ? J(c) : window, v = fe() && s;
  let p = (a.left + (v && ((o = d.visualViewport) == null ? void 0 : o.offsetLeft) || 0)) / h.x, u = (a.top + (v && ((i = d.visualViewport) == null ? void 0 : i.offsetTop) || 0)) / h.y, m = a.width / h.x, b = a.height / h.y;
  if (c) {
    const w = J(c), k = r && h1(r) ? J(r) : r;
    let y = w.frameElement;
    for (; y && r && k !== w; ) {
      const _ = t0(y), Z = y.getBoundingClientRect(), T = getComputedStyle(y);
      Z.x += (y.clientLeft + parseFloat(T.paddingLeft)) * _.x, Z.y += (y.clientTop + parseFloat(T.paddingTop)) * _.y, p *= _.x, u *= _.y, m *= _.x, b *= _.y, p += Z.x, u += Z.y, y = J(y).frameElement;
    }
  }
  return { width: m, height: b, top: u, right: p + m, bottom: u + b, left: p, x: p, y: u };
}
function O1(t) {
  return ((y2(t) ? t.ownerDocument : t.document) || window.document).documentElement;
}
function Y0(t) {
  return h1(t) ? { scrollLeft: t.scrollLeft, scrollTop: t.scrollTop } : { scrollLeft: t.pageXOffset, scrollTop: t.pageYOffset };
}
function Z2(t) {
  return U1(O1(t)).left + Y0(t).scrollLeft;
}
function M0(t) {
  if (T1(t) === "html")
    return t;
  const e = t.assignedSlot || t.parentNode || e2(t) && t.host || O1(t);
  return e2(e) ? e.host : e;
}
function C2(t) {
  const e = M0(t);
  return Ze(e) ? e.ownerDocument.body : b1(e) && K0(e) ? e : C2(e);
}
function k0(t, e) {
  var s;
  e === void 0 && (e = []);
  const r = C2(t), o = r === ((s = t.ownerDocument) == null ? void 0 : s.body), i = J(r);
  return o ? e.concat(i, i.visualViewport || [], K0(r) ? r : []) : e.concat(r, k0(r));
}
function t2(t, e, s) {
  let r;
  if (e === "viewport")
    r = function(a, c) {
      const h = J(a), d = O1(a), v = h.visualViewport;
      let p = d.clientWidth, u = d.clientHeight, m = 0, b = 0;
      if (v) {
        p = v.width, u = v.height;
        const w = fe();
        (!w || w && c === "fixed") && (m = v.offsetLeft, b = v.offsetTop);
      }
      return { width: p, height: u, x: m, y: b };
    }(t, s);
  else if (e === "document")
    r = function(a) {
      const c = O1(a), h = Y0(a), d = a.ownerDocument.body, v = y0(c.scrollWidth, c.clientWidth, d.scrollWidth, d.clientWidth), p = y0(c.scrollHeight, c.clientHeight, d.scrollHeight, d.clientHeight);
      let u = -h.scrollLeft + Z2(a);
      const m = -h.scrollTop;
      return f1(d).direction === "rtl" && (u += y0(c.clientWidth, d.clientWidth) - v), { width: v, height: p, x: u, y: m };
    }(O1(t));
  else if (h1(e))
    r = function(a, c) {
      const h = U1(a, !0, c === "fixed"), d = h.top + a.clientTop, v = h.left + a.clientLeft, p = b1(a) ? t0(a) : { x: 1, y: 1 };
      return { width: a.clientWidth * p.x, height: a.clientHeight * p.y, x: v * p.x, y: d * p.y };
    }(e, s);
  else {
    const a = { ...e };
    if (fe()) {
      var o, i;
      const c = J(t);
      a.x -= ((o = c.visualViewport) == null ? void 0 : o.offsetLeft) || 0, a.y -= ((i = c.visualViewport) == null ? void 0 : i.offsetTop) || 0;
    }
    r = a;
  }
  return ue(r);
}
function s2(t, e) {
  return b1(t) && f1(t).position !== "fixed" ? e ? e(t) : t.offsetParent : null;
}
function r2(t, e) {
  const s = J(t);
  let r = s2(t, e);
  for (; r && Xo(r) && f1(r).position === "static"; )
    r = s2(r, e);
  return r && (T1(r) === "html" || T1(r) === "body" && f1(r).position === "static" && !me(r)) ? s : r || function(o) {
    let i = M0(o);
    for (; b1(i) && !Ze(i); ) {
      if (me(i))
        return i;
      i = M0(i);
    }
    return null;
  }(t) || s;
}
function Jo(t, e, s) {
  const r = b1(e), o = O1(e), i = U1(t, !0, s === "fixed", e);
  let a = { scrollLeft: 0, scrollTop: 0 };
  const c = { x: 0, y: 0 };
  if (r || !r && s !== "fixed")
    if ((T1(e) !== "body" || K0(o)) && (a = Y0(e)), b1(e)) {
      const h = U1(e, !0);
      c.x = h.x + e.clientLeft, c.y = h.y + e.clientTop;
    } else
      o && (c.x = Z2(o));
  return { x: i.left + a.scrollLeft - c.x, y: i.top + a.scrollTop - c.y, width: i.width, height: i.height };
}
const ei = { getClippingRect: function(t) {
  let { element: e, boundary: s, rootBoundary: r, strategy: o } = t;
  const i = s === "clippingAncestors" ? function(d, v) {
    const p = v.get(d);
    if (p)
      return p;
    let u = k0(d).filter((k) => h1(k) && T1(k) !== "body"), m = null;
    const b = f1(d).position === "fixed";
    let w = b ? M0(d) : d;
    for (; h1(w) && !Ze(w); ) {
      const k = f1(w), y = me(w);
      (b ? y || m : y || k.position !== "static" || !m || !["absolute", "fixed"].includes(m.position)) ? m = k : u = u.filter((_) => _ !== w), w = M0(w);
    }
    return v.set(d, u), u;
  }(e, this._c) : [].concat(s), a = [...i, r], c = a[0], h = a.reduce((d, v) => {
    const p = t2(e, v, o);
    return d.top = y0(p.top, d.top), d.right = Je(p.right, d.right), d.bottom = Je(p.bottom, d.bottom), d.left = y0(p.left, d.left), d;
  }, t2(e, c, o));
  return { width: h.right - h.left, height: h.bottom - h.top, x: h.left, y: h.top };
}, convertOffsetParentRelativeRectToViewportRelativeRect: function(t) {
  let { rect: e, offsetParent: s, strategy: r } = t;
  const o = b1(s), i = O1(s);
  if (s === i)
    return e;
  let a = { scrollLeft: 0, scrollTop: 0 }, c = { x: 1, y: 1 };
  const h = { x: 0, y: 0 };
  if ((o || !o && r !== "fixed") && ((T1(s) !== "body" || K0(i)) && (a = Y0(s)), b1(s))) {
    const d = U1(s);
    c = t0(s), h.x = d.x + s.clientLeft, h.y = d.y + s.clientTop;
  }
  return { width: e.width * c.x, height: e.height * c.y, x: e.x * c.x - a.scrollLeft * c.x + h.x, y: e.y * c.y - a.scrollTop * c.y + h.y };
}, isElement: h1, getDimensions: function(t) {
  return b1(t) ? x2(t) : t.getBoundingClientRect();
}, getOffsetParent: r2, getDocumentElement: O1, getScale: t0, async getElementRects(t) {
  let { reference: e, floating: s, strategy: r } = t;
  const o = this.getOffsetParent || r2, i = this.getDimensions;
  return { reference: Jo(e, await o(s), r), floating: { x: 0, y: 0, ...await i(s) } };
}, getClientRects: (t) => Array.from(t.getClientRects()), isRTL: (t) => f1(t).direction === "rtl" };
function ti(t, e, s, r) {
  r === void 0 && (r = {});
  const { ancestorScroll: o = !0, ancestorResize: i = !0, elementResize: a = !0, animationFrame: c = !1 } = r, h = o && !c, d = h || i ? [...h1(t) ? k0(t) : t.contextElement ? k0(t.contextElement) : [], ...k0(e)] : [];
  d.forEach((m) => {
    h && m.addEventListener("scroll", s, { passive: !0 }), i && m.addEventListener("resize", s);
  });
  let v, p = null;
  if (a) {
    let m = !0;
    p = new ResizeObserver(() => {
      m || s(), m = !1;
    }), h1(t) && !c && p.observe(t), h1(t) || !t.contextElement || c || p.observe(t.contextElement), p.observe(e);
  }
  let u = c ? U1(t) : null;
  return c && function m() {
    const b = U1(t);
    !u || b.x === u.x && b.y === u.y && b.width === u.width && b.height === u.height || s(), u = b, v = requestAnimationFrame(m);
  }(), s(), () => {
    var m;
    d.forEach((b) => {
      h && b.removeEventListener("scroll", s), i && b.removeEventListener("resize", s);
    }), (m = p) == null || m.disconnect(), p = null, c && cancelAnimationFrame(v);
  };
}
const si = (t, e, s) => {
  const r = /* @__PURE__ */ new Map(), o = { platform: ei, ...s }, i = { ...o.platform, _c: r };
  return jo(t, e, { ...o, platform: i });
}, ri = `@charset "UTF-8";:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{--arrow-color: var(--color-background-primary);--arrow-size: 6px;--arrow-size-diagonal: calc(var(--arrow-size) * .7071);--arrow-padding-offset: calc(var(--arrow-size-diagonal) - var(--arrow-size));display:contents}.popup{position:absolute;isolation:isolate;max-width:var(--auto-size-available-width, none);max-height:var(--auto-size-available-height, none);display:none}.popup--active{display:block}.popup--fixed{position:fixed}.popup__arrow{position:absolute;width:calc(var(--arrow-size-diagonal) * 2);height:calc(var(--arrow-size-diagonal) * 2);rotate:45deg;background:var(--arrow-color);z-index:-1}
`;
var oi = Object.defineProperty, ii = Object.getOwnPropertyDescriptor, D = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? ii(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && oi(e, s, o), o;
};
const $2 = [
  "top",
  "top-start",
  "top-end",
  "right",
  "right-start",
  "right-end",
  "bottom",
  "bottom-start",
  "bottom-end",
  "left",
  "left-start",
  "left-end"
], ai = ["start", "end", "center", "anchor"], ni = ["width", "height", "both"], _1 = {
  // used in the CSS
  arrowSizeDiagonal: "--arrow-size-diagonal",
  arrowPaddingDiagonal: "--arrow-padding-offset",
  autoSizeAvailableWidth: "--auto-size-available-width",
  autoSizeAvailableHeight: "--auto-size-available-height"
}, o2 = "data-current-placement";
let H = class extends B {
  constructor() {
    super(...arguments), this.active = !1, this.arrow = !1, this.arrowPlacement = "anchor", this.arrowPadding = 10, this.placement = "top", this.strategy = "absolute", this.distance = 0, this.skidding = 0, this.flip = !1, this.flipFallbackPlacements = "", this.flipFallbackStrategy = "best-fit", this.flipPadding = 0, this.shift = !1, this.shiftPadding = 0, this.autoSizePadding = 0;
  }
  // https://floating-ui.com/docs/detectOverflow#padding
  async connectedCallback() {
    super.connectedCallback(), await this.updateComplete, this.startReposition();
  }
  disconnectedCallback() {
    this.stopReposition();
  }
  async updated(t) {
    super.updated(t), t.has("active") && (this.active ? this.startReposition() : this.stopReposition()), t.has("anchor") && this.handleAnchorChange(), this.active && (await this.updateComplete, await this.repositionPopup());
  }
  async repositionPopup() {
    if (!this.active || !this.anchorEl)
      return;
    const t = [
      // offset goes at the beginning of the array
      // https://floating-ui.com/docs/offset
      Yo({ mainAxis: this.distance, crossAxis: this.skidding })
    ];
    this.sync ? t.push(this.syncPopUpWidthHeight()) : (this.popup.style.width = "", this.popup.style.height = ""), this.flip && t.push(this.flipPopup()), this.shift && t.push(
      Qo({
        // https://floating-ui.com/docs/shift
        boundary: this.shiftBoundary,
        padding: this.shiftPadding
      })
    ), this.autoSize ? t.push(this.autoSizePopup()) : (this.style.removeProperty(_1.autoSizeAvailableWidth), this.style.removeProperty(_1.autoSizeAvailableHeight)), this.arrow && t.push(
      Ro({
        // https://floating-ui.com/docs/arrow
        element: this.arrowEl,
        padding: this.arrowPadding
      })
    ), await si(this.anchorEl, this.popup, {
      // https://floating-ui.com/docs/computeposition
      placement: this.placement,
      middleware: t,
      strategy: this.strategy
    }).then(({ x: e, y: s, middlewareData: r, placement: o }) => this.setFinalPopupStyles({ x: e, y: s, middlewareData: r, placement: o })), this.emit("vds-reposition");
  }
  async handleAnchorChange() {
    await this.stopReposition();
    const t = (s) => !!s && typeof s == "string", e = (s) => s instanceof HTMLElement;
    if (t(this.anchor)) {
      const s = this.getRootNode();
      this.anchorEl = s.getElementById(this.anchor);
    } else
      e(this.anchor) ? this.anchorEl = this.anchor : this.anchorEl = this.querySelector('[slot="anchor"]');
    if (this.anchorEl instanceof HTMLSlotElement && (console.log("this.anchorEl instanceof HTMLSlotElement"), this.anchorEl = this.anchorEl.assignedElements({ flatten: !0 })[0]), !this.anchorEl) {
      const s = [...document.querySelectorAll("vds-popup")].filter((r) => r === this)[0];
      throw console.warn(s), new Error("vds-popup has no anchor slot or anchor attribute.");
    }
    this.startReposition();
  }
  startReposition() {
    this.anchorEl && (this.cleanup = ti(
      this.anchorEl,
      this.popup,
      () => {
        this.repositionPopup();
      }
    ));
  }
  async stopReposition() {
    return new Promise((t) => {
      this.cleanup ? (this.cleanup(), this.cleanup = void 0, this.removeAttribute(o2), this.style.removeProperty(_1.autoSizeAvailableWidth), this.style.removeProperty(_1.autoSizeAvailableHeight), requestAnimationFrame(() => t())) : t();
    });
  }
  syncPopUpWidthHeight() {
    return Xe({
      apply: ({ rects: { reference: { width: t, height: e } } }) => {
        const s = this.sync === "both", r = this.sync === "width" || s, o = this.sync === "height" || s;
        this.popup.style.width = r ? `${t}px` : "", this.popup.style.height = o ? `${e}px` : "";
      }
    });
  }
  flipPopup() {
    return Ko({
      boundary: this.flipBoundary,
      // @ts-expect-error - converting a string attribute to an array
      fallbackPlacements: this.flipFallbackPlacements,
      fallbackStrategy: this.flipFallbackStrategy === "best-fit" ? "bestFit" : "initialPlacement",
      padding: this.flipPadding
    });
  }
  autoSizePopup() {
    return Xe({
      boundary: this.autoSizeBoundary,
      padding: this.autoSizePadding,
      apply: ({ availableWidth: t, availableHeight: e }) => {
        this.autoSize === "vertical" || this.autoSize === "both" ? this.style.setProperty(_1.autoSizeAvailableHeight, `${e}px`) : this.style.removeProperty(_1.autoSizeAvailableHeight), this.autoSize === "horizontal" || this.autoSize === "both" ? this.style.setProperty(_1.autoSizeAvailableWidth, `${t}px`) : this.style.removeProperty(_1.autoSizeAvailableWidth);
      }
    });
  }
  setFinalPopupStyles({ x: t, y: e, middlewareData: s, placement: r }) {
    if (this.setAttribute(o2, r), Object.assign(this.popup.style, {
      left: `${t}px`,
      top: `${e}px`
    }), !this.arrow)
      return;
    const o = s.arrow.x, i = s.arrow.y, { arrowPaddingDiagonal: a, arrowSizeDiagonal: c } = _1, h = getComputedStyle(this).direction === "rtl";
    let d = "", v = "", p = "", u = "";
    if (this.arrowPlacement === "start") {
      const b = typeof o == "number" ? `calc(${this.arrowPadding}px - var(${a}))` : "";
      d = typeof i == "number" ? `calc(${this.arrowPadding}px - var(${a}))` : "", v = h ? b : "", u = h ? "" : b;
    } else if (this.arrowPlacement === "end") {
      const b = typeof o == "number" ? `calc(${this.arrowPadding}px - var(${a}))` : "";
      v = h ? "" : b, u = h ? b : "", p = typeof i == "number" ? `calc(${this.arrowPadding}px - var(${a}))` : "";
    } else
      this.arrowPlacement === "center" ? (u = typeof o == "number" ? `calc(50% - var(${c}))` : "", d = typeof i == "number" ? `calc(50% - var(${c}))` : "") : (u = typeof o == "number" ? `${o}px` : "", d = typeof i == "number" ? `${i}px` : "");
    const m = { top: "bottom", right: "left", bottom: "top", left: "right" }[r.split("-")[0]];
    Object.assign(this.arrowEl.style, {
      top: d,
      right: v,
      bottom: p,
      left: u,
      [m]: `calc(var(${c}) * -1)`
    });
  }
  maybeGetArrowEl() {
    return this.arrow ? g`<div part="arrow" class="popup__arrow" role="presentation"></div>` : "";
  }
  render() {
    return g`
      <slot name="anchor" @slotchange=${this.handleAnchorChange}></slot>

      <div
        part="popup"
        class=${E({
      popup: !0,
      "popup--active": this.active,
      "popup--fixed": this.strategy === "fixed",
      "popup--has-arrow": this.arrow
    })}
      >
        <slot></slot>
        ${this.maybeGetArrowEl()}
      </div>
    `;
  }
};
H.styles = $(ri);
D([
  g1(".popup")
], H.prototype, "popup", 2);
D([
  g1(".popup__arrow")
], H.prototype, "arrowEl", 2);
D([
  l({ type: Boolean, reflect: !0 })
], H.prototype, "active", 2);
D([
  l()
], H.prototype, "anchor", 2);
D([
  l({ type: Boolean })
], H.prototype, "arrow", 2);
D([
  l({ attribute: "arrow-placement" }),
  x(ai, "anchor")
], H.prototype, "arrowPlacement", 2);
D([
  l({ attribute: "arrow-padding", type: Number })
], H.prototype, "arrowPadding", 2);
D([
  l({ reflect: !0 }),
  x($2, "top")
], H.prototype, "placement", 2);
D([
  l({ reflect: !0 }),
  x(["absolute", "fixed"], "absolute")
], H.prototype, "strategy", 2);
D([
  l({ type: Number })
], H.prototype, "distance", 2);
D([
  l({ type: Number })
], H.prototype, "skidding", 2);
D([
  l(),
  x(ni)
], H.prototype, "sync", 2);
D([
  l({ type: Boolean })
], H.prototype, "flip", 2);
D([
  l({
    attribute: "flip-fallback-placements",
    converter: {
      // https://lit.dev/docs/components/properties/#conversion-converter
      // flip-fallback-placements value = a string of this.placements separated by a space; E.g. "top bottom left"
      fromAttribute: (t) => t.split(" ").map((e) => e.trim()).filter((e) => e !== ""),
      // this.flipFallbackPlacements = Placement[]
      toAttribute: (t) => t.join(" ")
    }
  })
], H.prototype, "flipFallbackPlacements", 2);
D([
  l({ attribute: "flip-fallback-strategy" }),
  x(["best-fit", "initial"], "best-fit")
], H.prototype, "flipFallbackStrategy", 2);
D([
  l({ type: Object })
], H.prototype, "flipBoundary", 2);
D([
  l({ attribute: "flip-padding", type: Number })
], H.prototype, "flipPadding", 2);
D([
  l({ type: Boolean })
], H.prototype, "shift", 2);
D([
  l({ type: Object })
], H.prototype, "shiftBoundary", 2);
D([
  l({ attribute: "shift-padding", type: Number })
], H.prototype, "shiftPadding", 2);
D([
  l({ attribute: "auto-size" }),
  x(["horizontal", "vertical", "both"])
], H.prototype, "autoSize", 2);
D([
  l({ type: Object })
], H.prototype, "autoSizeBoundary", 2);
D([
  l({ attribute: "auto-size-padding", type: Number })
], H.prototype, "autoSizePadding", 2);
H = D([
  S("vds-popup")
], H);
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ce = N0(class extends U0 {
  constructor(t) {
    var e;
    if (super(t), t.type !== Z1.ATTRIBUTE || t.name !== "style" || ((e = t.strings) === null || e === void 0 ? void 0 : e.length) > 2)
      throw Error("The `styleMap` directive must be used in the `style` attribute and must be the only part in the attribute.");
  }
  render(t) {
    return Object.keys(t).reduce((e, s) => {
      const r = t[s];
      return r == null ? e : e + `${s = s.replace(/(?:^(webkit|moz|ms|o)|)(?=[A-Z])/g, "-$&").toLowerCase()}:${r};`;
    }, "");
  }
  update(t, [e]) {
    const { style: s } = t.element;
    if (this.vt === void 0) {
      this.vt = /* @__PURE__ */ new Set();
      for (const r in e)
        this.vt.add(r);
      return this.render(e);
    }
    this.vt.forEach((r) => {
      e[r] == null && (this.vt.delete(r), r.includes("-") ? s.removeProperty(r) : s[r] = "");
    });
    for (const r in e) {
      const o = e[r];
      o != null && (this.vt.add(r), r.includes("-") ? s.setProperty(r, o) : s[r] = o);
    }
    return X;
  }
}), li = {
  none: {
    enterprise: "0",
    consumer: "0"
    // sync with Ravi, 2023-03-01
  },
  sm: {
    enterprise: "0.25rem",
    consumer: "0.25rem",
    // sync with Ravi, 2023-03-01
    comment: "Use on inputs"
  },
  md: {
    enterprise: "0.5rem",
    consumer: "0.5rem",
    // sync with Ravi, 2023-03-01
    comment: "Use on cards and popovers"
  },
  lg: {
    enterprise: "1rem",
    consumer: "1rem",
    // sync with Ravi, 2023-03-01
    comment: "Use on modals"
  },
  xl: {
    enterprise: "1.5rem",
    consumer: "1.5rem",
    // sync with Ravi, 2023-03-01
    comment: "Use when extra rounded corners are needed"
  },
  round: {
    enterprise: "100rem",
    consumer: "100rem",
    // sync with Ravi, 2023-03-01
    comment: "Use to make elements pill shaped"
  }
}, ci = {
  xs: {
    enterprise: "0em",
    consumer: "0em",
    comment: "Small devices such as phones. 0px and up."
  },
  sm: {
    enterprise: "40em",
    consumer: "40em",
    comment: "Small tablets and phones in landscape. 640px and up."
  },
  md: {
    enterprise: "48em",
    consumer: "48em",
    comment: "Average sized tablets. 768px and up."
  },
  lg: {
    enterprise: "64em",
    consumer: "64em",
    comment: "Small laptops and large tablets. 1024px and up."
  },
  xl: {
    enterprise: "80em",
    consumer: "80em",
    comment: "Average laptops and small monitors. 1280px and up."
  },
  xl2: {
    enterprise: "90em",
    consumer: "90em",
    comment: "Large laptops and medium monitors. 1440px and up."
  },
  xl3: {
    enterprise: "100em",
    consumer: "100em",
    comment: "Large laptops and large monitors. 1600px and up."
  }
}, n = {
  white: {
    enterprise: "#ffffff",
    consumer: "#ffffff"
    // sync with Ravi, 2023-02-28
  },
  black: {
    enterprise: "#05070a",
    consumer: "#000000"
    // sync with Ravi, 2023-02-28
  },
  gray: {
    50: {
      enterprise: "#f0f1f4",
      consumer: "#fafafa"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#e0e3e8",
      consumer: "#f5f5f5"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#c1c7d1",
      consumer: "#eaeaea"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#a3acba",
      consumer: "#e1e1e1"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#8691a4",
      consumer: "#cbcbcb"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#5d6b83",
      consumer: "#b3b3b3"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#44546f",
      consumer: "#8e8e8e"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#303c50",
      consumer: "#6e6e6e"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#1D2634",
      consumer: "#4b4b4b"
      // sync with Ravi, 2023-02-28
    },
    900: {
      enterprise: "#121823",
      consumer: "#2c2c2c"
      // sync with Ravi, 2023-02-28
    }
  },
  alphaBlack: {
    50: {
      enterprise: "#0B0C0E0F",
      consumer: "rgba(0,0,0,0.03)"
    },
    100: {
      enterprise: "#0C0E121F",
      consumer: "rgba(0,0,0,0.08)"
    },
    200: {
      enterprise: "#0E11153D",
      consumer: "rgba(0,0,0,0.2)"
    },
    300: {
      enterprise: "#14181F5E",
      consumer: "rgba(0,0,0,0.3)"
    },
    400: {
      enterprise: "#14181F80",
      consumer: "rgba(0,0,0,0.4)"
    },
    500: {
      enterprise: "#131820A1",
      consumer: "rgba(0,0,0,0.5)"
    },
    600: {
      enterprise: "#11161DBA",
      consumer: "rgba(0,0,0,0.6)"
    },
    700: {
      enterprise: "#11151DD6",
      consumer: "rgba(0,0,0,0.7)"
    },
    800: {
      enterprise: "#0E131BE8",
      consumer: "rgba(0,0,0,0.8)"
    },
    900: {
      enterprise: "#05070BED",
      consumer: "rgba(0,0,0,0.9)"
    }
  },
  alphaWhite: {
    50: {
      enterprise: "#FCFCFDF7",
      consumer: "rgba(255,255,255,0.97)"
    },
    100: {
      enterprise: "#F8F9FCE5",
      consumer: "rgba(255,255,255,0.92)"
    },
    200: {
      enterprise: "#E1E6EFD9",
      consumer: "rgba(255,255,255,0.8)"
    },
    300: {
      enterprise: "#CCD4E1CC",
      consumer: "rgba(255,255,255,0.7)"
    },
    400: {
      enterprise: "#BBC9E2B2",
      consumer: "rgba(255,255,255,0.6)"
    },
    500: {
      enterprise: "#B6C8E799",
      consumer: "rgba(255,255,255,0.5)"
    },
    600: {
      enterprise: "#AFC9F373",
      consumer: "rgba(255,255,255,0.4)"
    },
    700: {
      enterprise: "#B4CEF84D",
      consumer: "rgba(255,255,255,0.3)"
    },
    800: {
      enterprise: "#A3C9F533",
      consumer: "rgba(255,255,255,0.2)"
    },
    900: {
      enterprise: "#B7D3FA1A",
      consumer: "rgba(255,255,255,0.1)"
    }
  },
  yellow: {
    50: {
      enterprise: "#fbefdd",
      consumer: "#fff9e6"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#f8dfbb",
      consumer: "#fff4cc"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#f6d39d",
      consumer: "#ffe99a"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#ffba2e",
      consumer: "#ffd84e"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#e59e00",
      consumer: "#ffc702"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#996706",
      consumer: "#dead00"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#6e4c07",
      consumer: "#a47f00"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#513604",
      consumer: "#634d00"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#352204",
      consumer: "#564300"
      // follows same as enterprise
    },
    900: {
      enterprise: "#1a0f01",
      consumer: "#2b2100"
      // follows same as enterprise
    }
  },
  aqua: {
    50: {
      enterprise: "#D0F8FE",
      consumer: "#e6fcff"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#90F1FF",
      consumer: "#ccfaff"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#00E5FF",
      consumer: "#b3f7ff"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#0DBCD1",
      consumer: "#80f2ff"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#0F9FB1",
      consumer: "#00e5ff"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#007685",
      consumer: "#00b7cc"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#025B66",
      consumer: "#008999"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#00424B",
      consumer: "#00454c"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#002B31",
      consumer: "#004c55"
      // follows same as enterprise
    },
    900: {
      enterprise: "#001418",
      consumer: "#00262a"
      // follows same as enterprise
    }
  },
  pink: {
    50: {
      enterprise: "#feedf1",
      consumer: "#fff5ff"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#ffd8e4",
      consumer: "#ffe8fe"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#feb1c8",
      consumer: "#ffd0fc"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#fe6798",
      consumer: "#ffbefc"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#ff007f",
      consumer: "#f78ff2"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#d10167",
      consumer: "#e04dd9"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#a40250",
      consumer: "#ba27b3"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#790239",
      consumer: "#840d7e"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#510224",
      consumer: "#671263"
      // follows same as enterprise
    },
    900: {
      enterprise: "#2a0110",
      consumer: "#330931"
      // follows same as enterprise
    }
  },
  purple: {
    50: {
      enterprise: "#f9edff",
      consumer: "#f8f8f8"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#efdbfb",
      consumer: "#f4eef5"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#e2b6fb",
      consumer: "#e5d5e8"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#d192f4",
      consumer: "#c9b2cd"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#c463f7",
      consumer: "#9e74a4"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#ad2be5",
      consumer: "#76507b"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#801faa",
      consumer: "#583c5c"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#5f1280",
      consumer: "#37253e"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#3e0a55",
      consumer: "#251929"
      // missing
    },
    900: {
      enterprise: "#1e052c",
      consumer: "#120c15"
      // missing
    }
  },
  blue: {
    50: {
      enterprise: "#eaf1fd",
      consumer: "#f6f9ff"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#d3e4ff",
      consumer: "#eaefff"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#a7c8ff",
      consumer: "#b0c3ff"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#7cabff",
      consumer: "#5982ff"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#528dff",
      consumer: "#3063ff"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#005BF5",
      consumer: "#0e3fd4"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#0548c1",
      consumer: "#01289f"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#00338d",
      consumer: "#001659"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#00225d",
      consumer: "#001965"
      // follows same as enterprise
    },
    900: {
      enterprise: "#000f29",
      consumer: "#000c32"
      // follows same as enterprise
    }
  },
  green: {
    50: {
      enterprise: "#eaf3ec",
      consumer: "#d8ffee"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#d4e7d9",
      consumer: "#a8f5d4"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#aacfb4",
      consumer: "#59e4a9"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#80b891",
      consumer: "#26db8d"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#55a06e",
      consumer: "#19bc76"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#078648",
      consumer: "#078648"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#075f31",
      consumer: "#016137"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#034523",
      consumer: "#013d23"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#022d14",
      consumer: "#03361d"
      // follows same as enterprise
    },
    900: {
      enterprise: "#001507",
      consumer: "#011b0e"
      // follows same as enterprise
    }
  },
  orange: {
    50: {
      enterprise: "#fdede7",
      consumer: "#ffeee7"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#ffdbce",
      consumer: "#ffddd0"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#ffb59c",
      consumer: "#ffccb8"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#ff784c",
      consumer: "#ff9971"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#ff5513",
      consumer: "#ff5512"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#c03d09",
      consumer: "#cc440e"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#972e05",
      consumer: "#99330b"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#701f02",
      consumer: "#662207"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#4b1200",
      consumer: "#5b1a00"
      // follows same as enterprise
    },
    900: {
      enterprise: "#260702",
      consumer: "#2d0d00"
      // follows same as enterprise
    }
  },
  red: {
    50: {
      enterprise: "#fdede9",
      consumer: "#ffefed"
      // sync with Ravi, 2023-02-28
    },
    100: {
      enterprise: "#ffdad2",
      consumer: "#fed7d2"
      // sync with Ravi, 2023-02-28
    },
    200: {
      enterprise: "#fcaea8",
      consumer: "#f1998e"
      // sync with Ravi, 2023-02-28
    },
    300: {
      enterprise: "#f57460",
      consumer: "#e85c4a"
      // sync with Ravi, 2023-02-28
    },
    400: {
      enterprise: "#ff3224",
      consumer: "#e11900"
      // sync with Ravi, 2023-02-28
    },
    500: {
      enterprise: "#c71f0a",
      consumer: "#ab1300"
      // sync with Ravi, 2023-02-28
    },
    600: {
      enterprise: "#a70c00",
      consumer: "#870f00"
      // sync with Ravi, 2023-02-28
    },
    700: {
      enterprise: "#7c0500",
      consumer: "#5a0a00"
      // sync with Ravi, 2023-02-28
    },
    800: {
      enterprise: "#540200",
      consumer: "#4b0800"
      // follows same as enterprise
    },
    900: {
      enterprise: "#2b0201",
      consumer: "#250400"
      // follows same as enterprise
    }
  }
}, M = {
  normal: {
    enterprise: n.gray[800].enterprise,
    consumer: n.gray[900].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Default text color",
    inverse: {
      enterprise: n.alphaWhite[100].enterprise,
      consumer: n.white.consumer,
      // sync with Ravi, 2023-05-18
      comment: "Dark mode default text color"
    }
  },
  title: {
    enterprise: n.gray[700].enterprise,
    consumer: n.gray[900].consumer,
    comment: "Default text color",
    inverse: {
      enterprise: n.alphaWhite[200].enterprise,
      consumer: n.white.consumer,
      comment: "Dark mode default text color"
    }
  },
  secondary: {
    enterprise: n.gray[600].enterprise,
    consumer: n.gray[700].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Secondary text color",
    inverse: {
      enterprise: n.alphaWhite[300].enterprise,
      consumer: n.alphaWhite[300].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode secondary text color"
    }
  },
  placeholder: {
    enterprise: n.gray[500].enterprise,
    consumer: n.gray[600].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Accessible placeholder text color",
    inverse: {
      enterprise: n.alphaWhite[400].enterprise,
      consumer: n.alphaWhite[400].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode accessible text color"
    }
  },
  disabled: {
    enterprise: n.alphaBlack[500].enterprise,
    consumer: n.gray[500].consumer,
    comment: "Accessible disabled text color",
    inverse: {
      enterprise: n.alphaWhite[400].enterprise,
      consumer: n.alphaWhite[400].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode accessible disabled text color"
    }
  },
  success: {
    enterprise: n.green[600].enterprise,
    consumer: n.green[500].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Success text color",
    inverse: {
      enterprise: n.green[300].enterprise,
      consumer: n.green[300].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode success color"
    }
  },
  warning: {
    enterprise: n.yellow[600].enterprise,
    consumer: n.yellow[500].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Warning text color",
    inverse: {
      enterprise: n.yellow[300].enterprise,
      consumer: n.yellow[300].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode warning text color"
    }
  },
  critical: {
    enterprise: n.red[600].enterprise,
    consumer: n.red[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Critical text color",
    inverse: {
      enterprise: n.red[300].enterprise,
      consumer: n.red[300].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode critical text color"
    }
  },
  onSuccess: {
    enterprise: n.white.enterprise,
    consumer: n.white.consumer,
    // sync with Ravi, 2023-02-28
    comment: "Accessible color on Success Background"
  },
  onWarning: {
    enterprise: n.yellow[900].enterprise,
    consumer: n.gray[900].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Accessible color on Warning Background"
  },
  onCritical: {
    enterprise: n.white.enterprise,
    consumer: n.white.consumer,
    // sync with Ravi, 2023-02-28
    comment: "Accessible color on Critical Background"
  },
  onSelected: {
    enterprise: n.white.enterprise,
    consumer: n.white.consumer,
    // sync with Ravi, 2023-02-28
    comment: "Accessible color on Selected Background",
    inverse: {
      enterprise: n.white.enterprise,
      consumer: n.white.consumer,
      // sync with Ravi, 2023-02-28
      comment: "Accessible color on Selected Inverse Background"
    }
  },
  link: {
    enterprise: n.blue[500].enterprise,
    consumer: n.blue[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Link color",
    inverse: {
      enterprise: n.blue[400].enterprise,
      consumer: n.blue[400].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode link color"
    },
    hovered: {
      enterprise: n.blue[600].enterprise,
      consumer: n.blue[500].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Hover state link color",
      inverse: {
        enterprise: n.blue[300].enterprise,
        consumer: n.blue[300].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode hover state link color"
      }
    },
    pressed: {
      enterprise: n.blue[700].enterprise,
      consumer: n.blue[600].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Pressed state link color",
      inverse: {
        enterprise: n.blue[200].enterprise,
        consumer: n.blue[200].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode pressed state link color"
      }
    },
    visited: {
      enterprise: n.purple[700].enterprise,
      consumer: n.blue[400].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Visited link color",
      inverse: {
        enterprise: n.purple[400].enterprise,
        consumer: n.blue[300].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode visited link color"
      }
    }
  }
}, W = {
  primary: {
    enterprise: n.white.enterprise,
    consumer: n.white.consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for pages and cards",
    inverse: {
      enterprise: n.gray[800].enterprise,
      consumer: n.black.consumer,
      // follows same as enterprise
      comment: "Background for dark mode pages and cards"
    }
  },
  secondary: {
    enterprise: n.gray[50].enterprise,
    consumer: n.gray[50].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Alternative background for pages",
    inverse: {
      enterprise: n.gray[900].enterprise,
      consumer: n.gray[900].consumer,
      // follows same as enterprise
      comment: "Dark mode alternative background for pages"
    },
    alpha: {
      enterprise: n.alphaBlack[50].enterprise,
      consumer: n.alphaBlack[50].consumer,
      // follows same as enterprise
      comment: "Use on elements whose background should darken the background it is already on, such as zebra striped tables and disabled inputs",
      inverse: {
        enterprise: n.alphaWhite[900].enterprise,
        consumer: n.alphaWhite[900].consumer,
        // follows same as enterprise
        comment: "Dark mode for elements whose background should lighten the background it is already on, such as zebra striped tables and disabled inputs"
      }
    }
  },
  tertiary: {
    enterprise: n.gray[100].enterprise,
    consumer: n.gray[100].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for elements that need a differentiating background on top of Primary Background",
    inverse: {
      enterprise: n.gray[800].enterprise,
      consumer: n.gray[800].consumer,
      // follows same as enterprise
      comment: "Dark mode background for elements that need a differentiating background on top of Primary Background Inverse"
    },
    alpha: {
      enterprise: n.alphaBlack[100].enterprise,
      consumer: n.alphaBlack[100].consumer,
      // follows same as enterprise
      comment: "Use on elements whose background should darken the background it is already on more than Background Secondary Alpha, such as chips and hover state for selectable elements",
      inverse: {
        enterprise: n.alphaWhite[800].enterprise,
        consumer: n.alphaWhite[800].consumer,
        // follows same as enterprise
        comment: "Dark mode for elements whose background should lighten the background it is already on more than Background Secondary Alpha Inverse, such as chips and hover state for selectable elements"
      }
    }
  },
  selected: {
    enterprise: n.blue[800].enterprise,
    consumer: n.green[500].consumer,
    // sync with Ravi, 2023-05-09
    comment: "Selected state background",
    inverse: {
      enterprise: n.blue[400].enterprise,
      consumer: n.blue[400].consumer,
      comment: "Dark mode selected state background"
    },
    light: {
      enterprise: n.blue[50].enterprise,
      consumer: n.alphaBlack[100].consumer,
      // sync with Ravi, 2023-05-09
      comment: "Subdued selected state background",
      inverse: {
        enterprise: n.blue[600].enterprise,
        consumer: n.blue[600].consumer,
        comment: "Dark mode subdued selected state background"
      }
    }
  },
  disabled: {
    enterprise: n.alphaBlack[50].enterprise,
    consumer: n.alphaBlack[50].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Disabled state background",
    inverse: {
      enterprise: n.alphaWhite[900].enterprise,
      consumer: n.alphaWhite[900].consumer,
      // follows same as enterprise
      comment: "Dark mode disabled state background"
    }
  },
  info: {
    enterprise: n.purple[500].enterprise,
    consumer: n.blue[300].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for information elements",
    light: {
      enterprise: n.purple[50].enterprise,
      consumer: n.blue[50].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued background for information elements",
      inverse: {
        enterprise: n.purple[800].enterprise,
        consumer: n.blue[800].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued background for information elements"
      }
    }
  },
  success: {
    enterprise: n.green[500].enterprise,
    consumer: n.green[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for success elements",
    light: {
      enterprise: n.green[50].enterprise,
      consumer: n.green[50].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued background for success elements",
      inverse: {
        enterprise: n.green[800].enterprise,
        consumer: n.green[800].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued background for success elements"
      }
    }
  },
  warning: {
    enterprise: n.yellow[300].enterprise,
    consumer: n.yellow[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for warning elements",
    light: {
      enterprise: n.yellow[50].enterprise,
      consumer: n.yellow[50].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued background for warning elements",
      inverse: {
        enterprise: n.yellow[800].enterprise,
        consumer: n.yellow[800].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued background for warning elements"
      }
    }
  },
  critical: {
    enterprise: n.red[500].enterprise,
    consumer: n.red[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for critical elements",
    light: {
      enterprise: n.red[50].enterprise,
      consumer: n.red[50].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued background for critical elements",
      inverse: {
        enterprise: n.red[800].enterprise,
        consumer: n.red[800].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued background for critical elements"
      }
    }
  },
  overlay: {
    enterprise: n.alphaBlack[400].enterprise,
    consumer: n.alphaBlack[600].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background for overlay elements"
  }
}, hi = {
  primary: {
    enterprise: n.blue[500].enterprise,
    consumer: n.green[500].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Background of primary buttons",
    text: {
      enterprise: n.white.enterprise,
      consumer: n.white.consumer,
      // sync with Ravi, 2023-02-28
      comment: "Accessible color on Primary Button"
    },
    hovered: {
      enterprise: n.blue[600].enterprise,
      consumer: n.green[600].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Background of primary button hovered state"
    },
    pressed: {
      enterprise: n.blue[700].enterprise,
      consumer: n.green[700].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Background of primary button pressed state"
    },
    focused: {
      enterprise: n.blue[500].enterprise,
      consumer: n.green[600].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Background of primary button focused state"
    },
    disabled: {
      enterprise: W.disabled.enterprise,
      consumer: n.gray[200].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Background of primary button disabled state",
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        // sync with Ravi, 2023-02-28
        comment: "Accessible color on primary button disabled state"
      }
    }
  },
  auxiliary: {
    enterprise: n.gray[800].enterprise,
    consumer: n.black.consumer,
    // sync with Ravi, 2023-05-09
    comment: "Background of auxiliary buttons",
    inverse: {
      enterprise: n.gray[100].enterprise,
      consumer: n.white.consumer
      // sync with Ravi, 2023-05-09
    },
    text: {
      enterprise: n.white.enterprise,
      consumer: n.white.consumer,
      // sync with Ravi, 2023-05-09
      comment: "Accessible color on auxiliary Button",
      inverse: {
        enterprise: n.gray[800].enterprise,
        consumer: n.black.consumer
        // sync with Ravi, 2023-05-09
      }
    },
    hovered: {
      enterprise: n.gray[700].enterprise,
      consumer: n.gray[900].consumer,
      // sync with Ravi, 2023-05-09
      comment: "Background of auxiliary button hovered state",
      inverse: {
        enterprise: n.alphaWhite[100].enterprise,
        consumer: n.alphaWhite[100].consumer
        // sync with Ravi, 2023-05-09
      }
    },
    pressed: {
      enterprise: n.gray[600].enterprise,
      consumer: n.gray[800].consumer,
      // sync with Ravi, 2023-05-09
      comment: "Background of auxiliary button pressed state",
      inverse: {
        enterprise: n.alphaWhite[200].enterprise,
        consumer: n.alphaWhite[200].consumer
        // sync with Ravi, 2023-05-09
      }
    },
    focused: {
      enterprise: n.gray[800].enterprise,
      consumer: n.black.consumer,
      // sync with Ravi, 2023-05-09
      comment: "Background of auxiliary button focused state",
      inverse: {
        enterprise: n.gray[100].enterprise,
        consumer: n.white.consumer
        // sync with Ravi, 2023-05-09
      }
    },
    disabled: {
      enterprise: W.disabled.enterprise,
      consumer: W.disabled.consumer,
      // sync with Ravi, 2023-05-09
      comment: "Background of auxiliary button pressed state",
      inverse: {
        enterprise: W.disabled.inverse.enterprise,
        consumer: W.disabled.inverse.consumer
        // sync with Ravi, 2023-05-09
      },
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        // sync with Ravi, 2023-05-09
        comment: "Accessible color on auxiliary button disabled state",
        inverse: {
          enterprise: M.disabled.inverse.enterprise,
          consumer: M.disabled.inverse.consumer
          // sync with Ravi, 2023-05-09
        }
      }
    }
  },
  secondary: {
    enterprise: n.alphaBlack[100].enterprise,
    consumer: "transparent",
    // sync with Ravi, 2023-03-01
    comment: "Background of secondary buttons",
    inverse: {
      enterprise: n.alphaWhite[800].enterprise,
      consumer: "transparent",
      // sync with Ravi, 2023-03-01
      comment: "Dark mode secondary buttons"
    },
    border: {
      enterprise: "transparent",
      consumer: n.gray[400].consumer,
      // sync with Ravi, 2023-03-01
      comment: "Border of secondary buttons",
      inverse: {
        enterprise: "transparent",
        consumer: "transparent",
        // sync with Ravi, 2023-03-01
        comment: "Dark mode secondary button border"
      }
    },
    text: {
      enterprise: M.normal.enterprise,
      consumer: M.normal.consumer,
      // sync with Ravi, 2023-03-01
      comment: "Accessible color on Secondary Button",
      inverse: {
        enterprise: M.normal.inverse.enterprise,
        consumer: M.normal.inverse.consumer,
        // sync with Ravi, 2023-03-01
        comment: "Accessible color on Secondary Inverse Button"
      }
    },
    hovered: {
      enterprise: n.alphaBlack[200].enterprise,
      consumer: n.gray[400].consumer,
      // sync with Ravi, 2023-03-01
      comment: "Background of secondary button hovered state",
      inverse: {
        enterprise: n.alphaWhite[700].enterprise,
        consumer: n.gray[800].consumer,
        // sync with Ravi, 2023-03-01
        comment: "Background of dark mode secondary button hovered state"
      },
      border: {
        enterprise: "transparent",
        consumer: n.gray[900].consumer,
        // sync with Ravi, 2023-03-01
        comment: "Border of secondary button hovered state",
        inverse: {
          enterprise: "transparent",
          consumer: n.gray[400].consumer,
          // sync with Ravi, 2023-03-01
          comment: "Dark mode border of secondary button hovered state"
        }
      }
    },
    pressed: {
      enterprise: n.alphaBlack[300].enterprise,
      consumer: n.gray[600].consumer,
      // sync with Ravi, 2023-03-01
      comment: "Background of secondary button pressed state",
      inverse: {
        enterprise: n.alphaWhite[600].enterprise,
        consumer: n.gray[200].consumer,
        // sync with Ravi, 2023-03-01
        comment: "Background of dark mode secondary button pressed state"
      },
      border: {
        enterprise: "transparent",
        consumer: "transparent",
        // sync with Ravi, 2023-03-01
        comment: "Border of secondary button pressed state",
        inverse: {
          enterprise: "transparent",
          consumer: "transparent",
          // sync with Ravi, 2023-03-01
          comment: "Dark mode border of secondary button pressed state"
        }
      }
    },
    focused: {
      enterprise: n.alphaBlack[100].enterprise,
      consumer: n.gray[400].consumer,
      // sync with Ravi, 2023-03-01
      comment: "Background of secondary button focused state",
      inverse: {
        enterprise: n.alphaWhite[800].enterprise,
        consumer: n.gray[800].consumer,
        // sync with Ravi, 2023-03-01
        comment: "Dark mode secondary button focused state"
      },
      border: {
        enterprise: "transparent",
        consumer: n.gray[900].consumer,
        // sync with Ravi, 2023-03-01
        comment: "Border of secondary button focused state",
        inverse: {
          enterprise: "transparent",
          consumer: n.gray[400].consumer,
          // sync with Ravi, 2023-03-01
          comment: "Dark mode border of secondary button focused state"
        }
      }
    },
    disabled: {
      enterprise: W.disabled.enterprise,
      consumer: "transparent",
      // sync with Ravi, 2023-03-03
      comment: "Background of secondary button pressed state",
      inverse: {
        enterprise: W.disabled.inverse.enterprise,
        consumer: "transparent",
        // sync with Ravi, 2023-03-03
        comment: "Dark mode background of secondary button pressed state"
      },
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        // sync with Ravi, 2023-03-03
        comment: "Accessible color on primary button disabled state",
        inverse: {
          enterprise: M.disabled.inverse.enterprise,
          consumer: M.disabled.inverse.consumer,
          // sync with Ravi, 2023-03-03
          comment: "Dark mode accessible color on primary button disabled state"
        }
      },
      border: {
        enterprise: "transparent",
        consumer: "transparent",
        // sync with Ravi, 2023-03-03
        comment: "Border of secondary button disabled state",
        inverse: {
          enterprise: "transparent",
          consumer: "transparent",
          // sync with Ravi, 2023-03-03
          comment: "Dark mode border of secondary button disabled state"
        }
      }
    }
  },
  tertiary: {
    enterprise: "transparent",
    consumer: "transparent",
    // sync with Ravi, 2023-05-09
    comment: "Background of tertiary buttons",
    text: {
      enterprise: n.blue[500].enterprise,
      consumer: n.green[500].consumer,
      // sync with Ravi, 2023-05-09
      comment: "Accessible color on tertiary Button"
    },
    hovered: {
      enterprise: n.blue[50].enterprise,
      consumer: n.alphaBlack[50].consumer,
      // sync with Ravi, 2023-05-09
      comment: "Background of tertiary button hovered state"
    },
    pressed: {
      enterprise: n.blue[100].enterprise,
      consumer: n.alphaBlack[100].consumer,
      // sync with Ravi, 2023-05-09
      comment: "Background of tertiary button pressed state"
    },
    focused: {
      enterprise: "transparent",
      consumer: "transparent",
      // sync with Ravi, 2023-05-09
      comment: "Background of tertiary button focused state"
    },
    disabled: {
      enterprise: "transparent",
      consumer: "transparent",
      // sync with Ravi, 2023-05-09
      comment: "Background of tertiary button pressed state",
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        // sync with Ravi, 2023-05-09
        comment: "Accessible color on tertiary button disabled state"
      }
    }
  },
  subtle: {
    enterprise: "transparent",
    consumer: "transparent",
    comment: "Background of subtle buttons",
    inverse: {
      enterprise: "transparent",
      consumer: "transparent"
    },
    text: {
      enterprise: n.gray[900].enterprise,
      consumer: n.gray[900].consumer,
      comment: "Accessible color on Subtle Button",
      inverse: {
        enterprise: M.normal.inverse.enterprise,
        consumer: M.normal.inverse.consumer
      }
    },
    hovered: {
      enterprise: W.disabled.enterprise,
      consumer: W.disabled.consumer,
      comment: "Background of subtle button hovered state",
      inverse: {
        enterprise: n.alphaWhite[900].enterprise,
        consumer: n.alphaWhite[900].consumer
      }
    },
    pressed: {
      enterprise: n.alphaBlack[100].enterprise,
      consumer: n.alphaBlack[100].consumer,
      comment: "Background of subtle button pressed state",
      inverse: {
        enterprise: n.alphaWhite[800].enterprise,
        consumer: n.alphaWhite[800].consumer
      }
    },
    focused: {
      enterprise: "transparent",
      consumer: "transparent",
      comment: "Background of subtle button focused state",
      inverse: {
        enterprise: "transparent",
        consumer: "transparent"
      }
    },
    disabled: {
      enterprise: "transparent",
      consumer: W.disabled.consumer,
      comment: "Background of subtle button pressed state",
      inverse: {
        enterprise: "transparent",
        consumer: "transparent"
      },
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        comment: "Accessible color on subtle button disabled state",
        inverse: {
          enterprise: M.disabled.inverse.enterprise,
          consumer: M.disabled.inverse.consumer
        }
      }
    }
  },
  warning: {
    enterprise: n.yellow[300].enterprise,
    consumer: n.yellow[400].consumer,
    // sync with Ravi, 2023-03-03
    comment: "Background of warning buttons",
    text: {
      enterprise: n.gray[900].enterprise,
      consumer: n.gray[900].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Accessible color on Warning Button"
    },
    hovered: {
      enterprise: n.yellow[400].enterprise,
      consumer: n.yellow[500].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of warning button hovered state"
    },
    pressed: {
      enterprise: n.yellow[500].enterprise,
      consumer: n.yellow[500].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of warning button pressed state"
    },
    focused: {
      enterprise: n.yellow[300].enterprise,
      consumer: n.yellow[400].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of warning button focused state"
    },
    disabled: {
      enterprise: W.disabled.enterprise,
      consumer: W.disabled.consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of warning button pressed state",
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        // sync with Ravi, 2023-03-03
        comment: "Accessible color on warning button disabled state"
      }
    }
  },
  critical: {
    enterprise: n.red[500].enterprise,
    consumer: n.red[400].consumer,
    // sync with Ravi, 2023-03-03
    comment: "Background of critical buttons",
    text: {
      enterprise: n.white.enterprise,
      consumer: n.white.consumer,
      // sync with Ravi, 2023-03-03
      comment: "Accessible color on Critical Button"
    },
    hovered: {
      enterprise: n.red[600].enterprise,
      consumer: n.red[500].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of critical button hovered state"
    },
    pressed: {
      enterprise: n.red[700].enterprise,
      consumer: n.red[600].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of critical button pressed state"
    },
    focused: {
      enterprise: n.red[500].enterprise,
      consumer: n.red[500].consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of critical button focused state"
    },
    disabled: {
      enterprise: W.disabled.enterprise,
      consumer: W.disabled.consumer,
      // sync with Ravi, 2023-03-03
      comment: "Background of critical button pressed state",
      text: {
        enterprise: M.disabled.enterprise,
        consumer: M.disabled.consumer,
        // sync with Ravi, 2023-03-03
        comment: "Accessible color on critical button disabled state"
      }
    }
  }
}, H0 = {
  enterprise: n.blue[800].enterprise,
  consumer: n.gray[800].consumer,
  light: {
    enterprise: n.blue[100].enterprise,
    consumer: n.white.consumer
  }
}, di = {
  normal: {
    enterprise: n.alphaBlack[200].enterprise,
    consumer: n.gray[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Default border color",
    inverse: {
      enterprise: n.alphaWhite[700].enterprise,
      consumer: n.alphaWhite[700].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode default border color"
    }
  },
  light: {
    enterprise: n.alphaBlack[100].enterprise,
    consumer: n.alphaBlack[100].consumer,
    // What is the use for this color?
    comment: "Subdued border color. Example: Thin lines in tables.",
    inverse: {
      enterprise: n.alphaWhite[800].enterprise,
      consumer: n.alphaWhite[800].consumer,
      comment: "Dark mode subdued border color"
    }
  },
  disabled: {
    enterprise: n.alphaBlack[50].enterprise,
    consumer: n.gray[100].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Disabled border color"
  },
  selected: {
    enterprise: n.blue[500].enterprise,
    consumer: n.blue[300].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Selected border color",
    light: {
      enterprise: n.blue[200].enterprise,
      consumer: n.blue[200].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued border color"
    },
    inverse: {
      enterprise: n.blue[400].enterprise,
      consumer: n.blue[400].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Dark mode selected border color"
    }
  },
  info: {
    enterprise: n.purple[500].enterprise,
    consumer: n.blue[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Information border color",
    light: {
      enterprise: n.purple[200].enterprise,
      consumer: n.blue[200].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued information border color",
      inverse: {
        enterprise: n.purple[600].enterprise,
        consumer: n.blue[600].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued information border color"
      }
    }
  },
  success: {
    enterprise: n.green[500].enterprise,
    consumer: n.green[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Success border color",
    light: {
      enterprise: n.green[200].enterprise,
      consumer: n.green[200].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued success border color",
      inverse: {
        enterprise: n.green[600].enterprise,
        consumer: n.green[600].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued success border color"
      }
    }
  },
  warning: {
    enterprise: n.yellow[300].enterprise,
    consumer: n.yellow[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Warning border color",
    light: {
      enterprise: n.yellow[200].enterprise,
      consumer: n.yellow[200].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued warning border color",
      inverse: {
        enterprise: n.yellow[600].enterprise,
        consumer: n.yellow[600].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued warning border color"
      }
    }
  },
  critical: {
    enterprise: n.red[500].enterprise,
    consumer: n.red[400].consumer,
    // sync with Ravi, 2023-02-28
    comment: "Critical border color",
    light: {
      enterprise: n.red[200].enterprise,
      consumer: n.red[200].consumer,
      // sync with Ravi, 2023-02-28
      comment: "Subdued critical border color",
      inverse: {
        enterprise: n.red[600].enterprise,
        consumer: n.red[600].consumer,
        // sync with Ravi, 2023-02-28
        comment: "Dark mode subdued critical border color"
      }
    }
  }
}, pi = {
  DISQO: {
    enterprise: "#ff5512",
    consumer: "#ff5512",
    // follows same as enterprise
    comment: "DISQO brand color"
  },
  SurveyJunkie: {
    enterprise: "#078648",
    consumer: "#078648",
    // follows same as enterprise
    comment: "Survey Junkie brand color"
  }
}, ui = {
  1: {
    enterprise: n.blue[800].enterprise,
    consumer: n.blue[400].consumer
  },
  2: {
    enterprise: n.orange[400].enterprise,
    consumer: n.purple[700].consumer
  },
  3: {
    enterprise: n.yellow[300].enterprise,
    consumer: n.orange[400].consumer
  },
  4: {
    enterprise: n.pink[400].enterprise,
    consumer: n.yellow[400].consumer
  },
  5: {
    enterprise: n.purple[500].enterprise,
    consumer: n.aqua[400].consumer
  },
  6: {
    enterprise: n.aqua[200].enterprise,
    consumer: n.green[500].consumer
  },
  7: {
    enterprise: n.green[500].enterprise,
    consumer: n.pink[500].consumer
  },
  8: {
    enterprise: n.blue[500].enterprise,
    consumer: n.aqua[700].consumer
  },
  9: {
    enterprise: n.green[700].enterprise,
    consumer: n.blue[700].consumer
  },
  10: {
    enterprise: n.purple[700].enterprise,
    consumer: n.green[300].consumer
  },
  11: {
    enterprise: n.pink[600].enterprise,
    consumer: n.orange[700].consumer
  },
  12: {
    enterprise: n.red[400].enterprise,
    consumer: n.pink[800].consumer
  }
}, gi = {
  1: {
    enterprise: n.blue[200].enterprise,
    consumer: n.blue[200].consumer
  },
  2: {
    enterprise: n.orange[200].enterprise,
    consumer: n.purple[300].consumer
  },
  3: {
    enterprise: n.yellow[200].enterprise,
    consumer: n.orange[200].consumer
  },
  4: {
    enterprise: n.pink[200].enterprise,
    consumer: n.yellow[200].consumer
  },
  5: {
    enterprise: n.purple[200].enterprise,
    consumer: n.aqua[200].consumer
  },
  6: {
    enterprise: n.aqua[100].enterprise,
    consumer: n.green[100].consumer
  }
}, vi = {
  1: {
    enterprise: n.blue[500].enterprise,
    consumer: n.blue[400].consumer
  },
  2: {
    enterprise: n.blue[200].enterprise,
    consumer: n.aqua[400].consumer
  },
  3: {
    enterprise: n.yellow[100].enterprise,
    consumer: n.yellow[400].consumer
  },
  4: {
    enterprise: n.red[200].enterprise,
    consumer: n.orange[400].consumer
  },
  5: {
    enterprise: n.red[400].enterprise,
    consumer: n.red[500].consumer
  }
}, mi = {
  gradient: {
    enterprise: `linear-gradient(90deg, ${n.aqua[50].enterprise} 0%, ${n.aqua[200].enterprise} 25%, ${n.blue[500].enterprise} 70%, ${n.blue[900].enterprise} 100%)`,
    consumer: `linear-gradient(90deg, ${n.aqua[50].consumer} 0%, ${n.aqua[400].consumer} 25%, ${n.blue[500].consumer} 70%, ${n.blue[900].consumer} 100%)`
  },
  1: {
    enterprise: "#D0F8FE",
    consumer: n.aqua[50].consumer
  },
  2: {
    enterprise: "#9FF4FE",
    consumer: n.aqua[100].consumer
  },
  3: {
    enterprise: "#61EEFF",
    consumer: n.aqua[200].consumer
  },
  4: {
    enterprise: "#00E5FF",
    consumer: n.aqua[300].consumer
  },
  5: {
    enterprise: "#00CFFD",
    consumer: n.aqua[400].consumer
  },
  6: {
    enterprise: "#00B8FC",
    consumer: "#05d8ff"
  },
  7: {
    enterprise: "#00A2FA",
    consumer: "#0acbff"
  },
  8: {
    enterprise: "#008BF8",
    consumer: "#13b1ff"
  },
  9: {
    enterprise: "#0074F7",
    consumer: "#1d97ff"
  },
  10: {
    enterprise: "#005BF5",
    consumer: "#267dff"
  },
  11: {
    enterprise: "#004DD0",
    consumer: n.blue[400].consumer
  },
  12: {
    enterprise: "#0040AD",
    consumer: "#2652d6"
  },
  13: {
    enterprise: "#00338A",
    consumer: "#1d40ad"
  },
  14: {
    enterprise: "#002769",
    consumer: "#132f84"
  },
  15: {
    enterprise: "#001B4A",
    consumer: "#0a1d5b"
  },
  16: {
    enterprise: "#000F29",
    consumer: n.blue[900].consumer
  }
}, Y1 = {
  // base colors are not for consumption/consumer use, but they are exposed here just in case
  primitive: n,
  focusOutline: H0,
  button: hi,
  background: W,
  border: di,
  brand: pi,
  dataViz: {
    categorical: ui,
    categoricalSubdued: gi,
    likert: vi,
    sequential: mi
  },
  text: M
}, fi = H0.consumer, bi = H0.light.consumer, wi = H0.enterprise, xi = H0.light.enterprise, Ai = {
  shadow: {
    enterprise: `0 0 0 2px ${wi}, 0 0 0 6px ${xi}`,
    consumer: `0 0 0 2px ${bi}, 0 0 0 4px ${fi}`
  }
}, L0 = Y1.primitive.alphaBlack[50].enterprise, x0 = Y1.primitive.alphaBlack[100].enterprise, yi = Y1.primitive.alphaBlack[200].enterprise, O0 = Y1.primitive.alphaBlack[50].consumer, E0 = Y1.primitive.alphaBlack[100].consumer, ki = Y1.primitive.alphaBlack[200].consumer, _i = {
  none: {
    enterprise: "none",
    consumer: "none",
    comment: "Used for when no shadow is needed. This token exists for components that support different shadows. It is not needed otherwise."
  },
  xs: {
    enterprise: `0px 3px 6px 0px ${x0}, 0px 0px 4px 0px ${L0}`,
    consumer: `0px 3px 6px 0px ${E0}, 0px 0px 4px 0px ${O0}`,
    // sync with Ravi, 2023-02-28
    comment: "Used for hover state of inputs."
  },
  sm: {
    enterprise: `0px 5px 15px 0px ${x0}, 0px 2px 6px 0px ${L0}`,
    consumer: `0px 5px 15px 0px ${E0}, 0px 2px 6px 0px ${O0}`,
    // sync with Ravi, 2023-02-28
    comment: "Used for cards in active state and other elements like popovers and tooltips."
  },
  md: {
    enterprise: `0px 10px 25px 0px ${x0}, 0px 5px 15px 0px ${x0}, 0px 0px 6px 0px ${L0}`,
    consumer: `0px 10px 25px 0px ${E0}, 0px 5px 15px 0px ${E0}, 0px 0px 6px 0px ${O0}`,
    // sync with Ravi, 2023-02-28
    comment: "Used for active dropdown menus."
  },
  lg: {
    enterprise: `0px 0px 4px ${L0}, 0px 4px 16px ${x0}, 0px 25px 40px ${yi}`,
    consumer: `0px 25px 40px 0px ${ki}, 0px 0px 6px 0px ${O0}`,
    // sync with Ravi, 2023-02-28
    comment: "Used for dialogs and modals."
  }
}, Zi = {
  0: {
    enterprise: "0",
    consumer: "0",
    comment: "Used for no spacing."
  },
  "1px": {
    enterprise: "1px",
    consumer: "1px",
    comment: "Used for slight nudge."
  },
  "2px": {
    enterprise: "2px",
    consumer: "2px",
    comment: "Used for spacing within a component."
  },
  "4px": {
    enterprise: "4px",
    consumer: "4px",
    comment: "Used for spacing within a component."
  },
  xs: {
    enterprise: "0.5rem",
    consumer: "0.5rem",
    comment: "Used to separate related elements and for small padding or text and a related icon."
  },
  sm: {
    enterprise: "0.75rem",
    consumer: "0.75rem",
    comment: "Used only for right and left padding of form elements and horizontal tabs"
  },
  md: {
    enterprise: "1rem",
    consumer: "1rem",
    comment: "Used to separate unrelated elements or groups and for normal padding."
  },
  lg: {
    enterprise: "1.5rem",
    consumer: "1.5rem",
    comment: "Used to separate sub-sections of content."
  },
  xl: {
    enterprise: "2rem",
    consumer: "2rem",
    comment: "Used to separate sections of content."
  },
  "2xl": {
    enterprise: "3rem",
    consumer: "3rem",
    comment: "Used when more generous white space is needed."
  },
  "3xl": {
    enterprise: "6rem",
    consumer: "6rem",
    comment: "Used when more extra generous white space is needed."
  }
}, f = {
  fontFamilies: {
    paragraph: {
      enterprise: "Poppins, sans-serif",
      consumer: "Poppins, sans-serif"
    },
    heading: {
      enterprise: "Poppins, sans-serif",
      consumer: "Poppins, sans-serif"
    },
    number: {
      enterprise: 'ui-monospace, Menlo, Monaco, "Cascadia Mono", "Segoe UI Mono",  "Roboto Mono",  "Oxygen Mono",  "Ubuntu Monospace",  "Source Code Pro", "Fira Mono",  "Droid Sans Mono",  "Courier New", monospace',
      consumer: "Roboto, sans-serif"
    }
  },
  lineHeights: {
    none: {
      enterprise: "1",
      consumer: "1"
      // follows same as enterprise
    },
    sm: {
      enterprise: "1.25",
      consumer: "1.25"
      // follows same as enterprise
    },
    md: {
      enterprise: "1.5",
      consumer: "1.5"
      // follows same as enterprise
    }
  },
  fontWeights: {
    regular: {
      enterprise: "500",
      consumer: "500"
      // follows same as enterprise
    },
    bold: {
      enterprise: "700",
      consumer: "700"
      // follows same as enterprise
    },
    black: {
      enterprise: "900",
      consumer: "800"
      // sync with Ravi, 2023-02-28. This is called black but it's technically "extra bold".
    }
  },
  fontSize: {
    "-2": {
      enterprise: "0.75rem",
      consumer: "0.75rem"
      // follows same as enterprise
    },
    "-1": {
      enterprise: "0.875rem",
      consumer: "0.875rem"
      // follows same as enterprise
    },
    0: {
      enterprise: "1rem",
      consumer: "1rem"
      // follows same as enterprise
    },
    1: {
      enterprise: "1.25rem",
      consumer: "1.25rem"
      // follows same as enterprise
    },
    2: {
      enterprise: "1.5rem",
      consumer: "1.5rem"
      // follows same as enterprise
    },
    3: {
      enterprise: "2rem",
      consumer: "2rem"
      // follows same as enterprise
    },
    4: {
      enterprise: "2.5rem",
      consumer: "2.5rem"
      // follows same as enterprise
    },
    5: {
      enterprise: "3rem",
      consumer: "3rem"
      // follows same as enterprise
    },
    6: {
      enterprise: "4rem",
      consumer: "4rem"
      // follows same as enterprise
    },
    7: {
      enterprise: "4.75rem",
      consumer: "4.75rem"
      // follows same as enterprise
    },
    8: {
      enterprise: "6rem",
      consumer: "6rem"
      // follows same as enterprise
    }
  },
  letterSpacing: {
    normal: {
      enterprise: "0",
      consumer: "0"
      // follows same as enterprise
    },
    wide: {
      enterprise: "0.1em",
      consumer: "0.1em"
      // follows same as enterprise
    }
  },
  paragraphSpacing: {
    0: {
      enterprise: "0",
      consumer: "0"
      // follows same as enterprise
    },
    1: {
      enterprise: "1rem",
      consumer: "1rem"
      // follows same as enterprise
    }
  }
}, Ci = {
  lg: {
    enterprise: f.fontSize[8].enterprise,
    consumer: f.fontSize[8].consumer
  },
  md: {
    enterprise: f.fontSize[7].enterprise,
    consumer: f.fontSize[7].consumer
  },
  sm: {
    enterprise: f.fontSize[6].enterprise,
    consumer: f.fontSize[6].consumer
  },
  xs: {
    enterprise: f.fontSize[5].enterprise,
    consumer: f.fontSize[5].consumer
  }
}, $i = {
  fontFamily: {
    enterprise: f.fontFamilies.heading.enterprise,
    consumer: f.fontFamilies.heading.consumer
  },
  fontWeight: {
    enterprise: f.fontWeights.bold.enterprise,
    consumer: f.fontWeights.black.consumer
  },
  lineHeight: {
    enterprise: f.lineHeights.sm.enterprise,
    consumer: f.lineHeights.sm.consumer
  },
  paragraphSpacing: {
    enterprise: f.paragraphSpacing[0].enterprise,
    consumer: f.paragraphSpacing[0].consumer
  }
}, M2 = {
  xl: {
    enterprise: f.fontSize[4].enterprise,
    consumer: f.fontSize[4].consumer
  },
  lg: {
    enterprise: f.fontSize[3].enterprise,
    consumer: f.fontSize[3].consumer
  },
  md: {
    enterprise: f.fontSize[2].enterprise,
    consumer: f.fontSize[2].consumer
  },
  sm: {
    enterprise: f.fontSize[1].enterprise,
    consumer: f.fontSize[1].consumer
  },
  xs: {
    enterprise: f.fontSize[0].enterprise,
    consumer: f.fontSize[0].consumer
  }
}, Mi = {
  fontFamily: {
    enterprise: f.fontFamilies.heading.enterprise,
    consumer: f.fontFamilies.heading.consumer
  },
  fontWeight: {
    enterprise: f.fontWeights.bold.enterprise,
    consumer: f.fontWeights.bold.consumer
  },
  lineHeight: {
    enterprise: f.lineHeights.sm.enterprise,
    consumer: f.lineHeights.sm.consumer
  },
  paragraphSpacing: {
    enterprise: f.paragraphSpacing[0].enterprise,
    consumer: f.paragraphSpacing[0].consumer
  }
}, Vi = {
  lg: {
    enterprise: f.fontSize[1].enterprise,
    consumer: f.fontSize[1].consumer
  },
  md: {
    enterprise: f.fontSize[0].enterprise,
    consumer: f.fontSize[0].consumer
  },
  sm: {
    enterprise: f.fontSize[-1].enterprise,
    consumer: f.fontSize[-1].consumer
  }
}, Hi = {
  fontFamily: {
    enterprise: f.fontFamilies.paragraph.enterprise,
    consumer: f.fontFamilies.paragraph.consumer
  },
  fontWeight: {
    enterprise: f.fontWeights.regular.enterprise,
    consumer: f.fontWeights.regular.consumer
  },
  lineHeight: {
    enterprise: f.lineHeights.sm.enterprise,
    consumer: f.lineHeights.sm.consumer
  },
  paragraphSpacing: {
    enterprise: f.paragraphSpacing[0].enterprise,
    consumer: f.paragraphSpacing[0].consumer
  }
}, Si = {
  letterSpacing: {
    enterprise: f.letterSpacing.wide.enterprise,
    consumer: f.letterSpacing.wide.consumer
  },
  textCase: {
    enterprise: "uppercase",
    consumer: "uppercase"
  }
}, V2 = {
  lg: {
    enterprise: f.fontSize[1].enterprise,
    consumer: f.fontSize[1].consumer
  },
  md: {
    enterprise: f.fontSize[0].enterprise,
    consumer: f.fontSize[0].consumer
  },
  sm: {
    enterprise: f.fontSize[-1].enterprise,
    consumer: f.fontSize[-1].consumer
  }
}, Di = {
  fontFamily: {
    enterprise: f.fontFamilies.paragraph.enterprise,
    consumer: f.fontFamilies.paragraph.consumer
  },
  fontWeight: {
    enterprise: f.fontWeights.regular.enterprise,
    consumer: f.fontWeights.regular.consumer
  },
  lineHeight: {
    enterprise: f.lineHeights.md.enterprise,
    consumer: f.lineHeights.md.consumer
  },
  paragraphSpacing: {
    enterprise: f.paragraphSpacing[0].enterprise,
    consumer: f.paragraphSpacing[0].consumer
  }
}, Li = {
  fontFamily: {
    enterprise: f.fontFamilies.number.enterprise,
    consumer: f.fontFamilies.number.consumer
  }
}, Oi = {
  fontWeight: {
    enterprise: f.fontWeights.bold.enterprise,
    consumer: f.fontWeights.bold.consumer
  }
}, Ei = {
  // primitives are not for consumption/consumer use, but they are exposed here just in case
  primitives: f,
  display: {
    ...$i,
    ...Ci
  },
  headline: {
    ...Mi,
    ...M2
  },
  label: {
    ...Hi,
    ...Vi,
    uppercase: Si
  },
  paragraph: {
    ...Di,
    ...V2
  },
  bold: {
    ...Oi
  },
  number: {
    ...Li
  }
}, Ti = {
  modal: {
    enterprise: "800",
    consumer: "800",
    comment: "Used for modals."
  },
  dropdown: {
    enterprise: "900",
    consumer: "900",
    comment: "Used for dropdowns."
  },
  toast: {
    enterprise: "1000",
    consumer: "1000",
    comment: "Used for toast notifications."
    // 🍞
  },
  tooltip: {
    enterprise: "1100",
    consumer: "1100",
    comment: "Used for tooltips."
  }
}, zi = (t) => {
  const e = JSON.parse(JSON.stringify(t)), s = ["enterprise", "consumer"], r = (o, i = []) => (Object.keys(o).forEach((a) => {
    typeof o[a] == "object" ? o[a] = r(o[a], [...i, a]) : s.includes(a) && (delete o[a], o.value = `var(--${i.join("-")})`);
  }), o);
  return r(e);
}, Ii = {
  borderRadius: li,
  breakpoint: ci,
  color: Y1,
  focus: Ai,
  shadow: _i,
  spacing: Zi,
  text: Ei,
  zIndex: Ti
}, u0 = zi(Ii), H2 = {
  inverse: "inverse",
  light: "light",
  alpha: "alpha"
}, $e = Object.entries(u0.borderRadius).reduce((t, [e, s]) => ({
  ...t,
  [e]: s.value
}), {}), S2 = Object.keys($e);
Object.entries(u0.spacing).reduce((t, [e, s]) => ({
  ...t,
  [e]: s.value
}), {});
const D2 = Object.entries(u0.shadow).reduce((t, [e, s]) => ({
  ...t,
  [e]: s.value
}), {}), Pi = Object.keys(D2), L2 = Object.entries(u0.color.background).reduce((t, [e, s]) => {
  const { light: r, alpha: o } = H2, i = r in s && s.light.value, a = o in s && s.alpha.value;
  return {
    ...t,
    [e]: s.value,
    ...i && { [`${e}-${r}`]: i },
    ...a && { [`${e}-${o}`]: a }
  };
}, {}), Bi = Object.keys(L2), O2 = Object.entries(u0.color.border).reduce((t, [e, s]) => {
  const { light: r } = H2, o = r in s && s.light.value;
  return {
    ...t,
    [e]: s.value,
    ...o && { [`${e}-${r}`]: o }
  };
}, {}), Fi = Object.keys(O2), ji = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:block;--vds-card-padding: var(--spacing-md)}@media screen and (min-width: 40em){:host{--vds-card-padding: var(--spacing-lg)}}.card{display:flex;flex-direction:column}.card__contents{display:block;padding:var(--vds-card-padding)}
`;
var Ri = Object.defineProperty, Ni = Object.getOwnPropertyDescriptor, g0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Ni(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && Ri(e, s, o), o;
};
const E2 = { ...L2 };
delete E2.overlay;
const Ui = [...Bi].filter((t) => t !== "overlay");
let z1 = class extends B {
  constructor() {
    super(...arguments), this.slotController = new I1(this, d1), this.borderRadius = "md", this.backgroundColor = "primary", this.shadow = "sm", this.borderColor = "light", this.borderWidth = "1";
  }
  get cardStyles() {
    const t = D2[this.shadow], e = O2[this.borderColor], s = {
      borderRadius: $e[this.borderRadius],
      boxShadow: `${t}, inset 0 0 0 ${this.borderWidth}px ${e}`,
      backgroundColor: E2[this.backgroundColor]
    };
    return Ce(s);
  }
  render() {
    return g`
      <div
        part="base"
        class="card"
        style=${this.cardStyles}
      >
        <slot
          class="card__contents"
          aria-live="polite"
        ></slot>
      </div>
    `;
  }
};
z1.styles = $(ji);
g0([
  l({ attribute: "border-radius" }),
  x(S2, "md")
], z1.prototype, "borderRadius", 2);
g0([
  l({ attribute: "background-color" }),
  x(Ui, "primary")
], z1.prototype, "backgroundColor", 2);
g0([
  l(),
  x(Pi, "sm")
], z1.prototype, "shadow", 2);
g0([
  l({ attribute: "border-color" }),
  x(Fi, "light")
], z1.prototype, "borderColor", 2);
g0([
  l({ attribute: "border-width" }),
  x(["1", "2"], "1")
], z1.prototype, "borderWidth", 2);
z1 = g0([
  S("vds-card")
], z1);
function i2(t, e) {
  return new Promise((s) => {
    function r(o) {
      o.target === t && (t.removeEventListener(e, r), s());
    }
    t.addEventListener(e, r);
  });
}
const Wi = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:grid;gap:var(--spacing-4px);color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .select__label-text:after{content:" *";color:var(--color-text-critical)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}:host([disabled]) .select__wrapper{color:var(--color-text-disabled)}:host([required]) .select__label:after{content:" *";color:var(--color-text-critical)}:host([data-invalid]) .select__contents.touched,:host([data-invalid]:not([required])) .select__contents{background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical)}:host([disabled]){cursor:inherit}:host([disabled]) .select__contents{background-color:var(--color-background-secondary-alpha)}:host([disabled]) .select__display-input{cursor:not-allowed}:host(:focus-within) .select__contents{outline:none;transition:box-shadow .15s;box-shadow:var(--focus-shadow)}.select{width:100%}.select__label{display:flex;align-items:flex-end;justify-content:space-between}.select__label-text{cursor:inherit;color:var(--color-text-normal)}.select__wrapper{position:relative;color:var(--color-text-normal)}.select__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.select__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.select__accordion-item{min-height:0}.select__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.select__error-icon{flex:0}.select__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.select::part(popup){margin-top:var(--spacing-4px);z-index:var(--zIndex-dropdown)}.select__contents{position:relative;display:flex;align-items:center;justify-content:start;gap:var(--spacing-xs);min-height:2.5rem;padding:0px var(--spacing-sm) 0px var(--spacing-xs);border-radius:var(--borderRadius-sm);border:1px solid var(--color-border-normal)}.select__display-input{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);position:relative;height:3rem;width:100%;padding:0;margin:0;border:none;background:none;overflow:hidden;appearance:none;cursor:pointer;color:var(--color-text-normal)}.select__display-input:focus{outline:none}.select--multi .select__display-input{position:absolute;z-index:-1;top:0px;left:0px;width:100%;height:100%;opacity:0}.select__value-input{position:absolute;top:0;left:0;width:100%;height:100%;padding:0;margin:0;opacity:0;z-index:-1}.select__options{display:grid;position:relative;max-height:10rem;padding:var(--spacing-4px);gap:var(--spacing-4px);background-color:var(--color-background-primary);box-shadow:var(--shadow-sm);border-radius:var(--borderRadius-sm);border:1px solid var(--color-border-normal);overflow-y:auto}.select__selected-options{display:flex;flex:1 1 0%;align-items:center;flex-wrap:wrap;gap:var(--spacing-xs)}.select__toggle{width:fit-content;transition:.25s rotate ease;rotate:0}.select__toggle--open{rotate:-180deg}.select__clear::part(base){color:var(--color-button-subtle-text);background-color:var(--color-button-subtle)}.select__clear::part(base):hover{background-color:var(--color-button-subtle-hovered)}.select__clear::part(base):active{background-color:var(--color-button-subtle-pressed)}
`;
var qi = Object.defineProperty, Ki = Object.getOwnPropertyDescriptor, U = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Ki(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && qi(e, s, o), o;
};
const G0 = "select";
let P = class extends j {
  constructor() {
    super(...arguments), this.selectedOptions = [], this.visibleSelectedOptions = "", this.value = "", this.open = !1, this.hoist = !1, this.clearable = !1, this.multi = !1, this.maxOptionsVisible = 3, this.typeToSelectAnOptionStringBuffer = "", this.setListeners = (t) => [
      /**
       * WHY MOUSE DOWN INSTEAD OF CLICK?!?!?!?!
       * click is triggered by many things; left, right, space, enter...
       * these event listeners are specific to the document + we don't want em triggered by click
       */
      ["focusin", this.handleDocumentInteraction],
      ["mousedown", this.handleDocumentInteraction],
      ["keydown", this.handleDocumentSelectKeyDown]
    ].forEach(([e, s]) => document[`${t}EventListener`](e, s));
  }
  connectedCallback() {
    super.connectedCallback(), this.handleDocumentInteraction = this.handleDocumentInteraction.bind(this), this.handleDocumentSelectKeyDown = this.handleDocumentSelectKeyDown.bind(this);
  }
  getAllOptions() {
    return [...this.querySelectorAll("vds-option")];
  }
  getFirstOption() {
    return this.querySelector("vds-option");
  }
  setCurrentOption(t) {
    this.getAllOptions().forEach((s) => {
      s.current = !1, s.tabIndex = -1;
    }), t && (this.currentOption = t, t.current = !0, t.tabIndex = 0, t.focus());
  }
  handleDisabledChange() {
    this.disabled && (this.open = !1, this.handleOpenChange());
  }
  handleValueChange() {
    const t = this.getAllOptions(), e = Array.isArray(this.value) ? this.value : [this.value];
    this.setSelectedOptions(t.filter((s) => e.includes(s.value)));
  }
  async handleOpenChange() {
    this.open && !this.disabled ? (this.setCurrentOption(this.selectedOptions[0] || this.getFirstOption()), this.emit("vds-show"), this.setListeners("add"), this.options.hidden = !1, this.popup.active = !0, requestAnimationFrame(() => {
      this.setCurrentOption(this.currentOption);
    }), this.emit("vds-after-show")) : (this.emit("vds-hide"), this.setListeners("remove"), this.options.hidden = !0, this.popup.active = !1, this._handleTouched(), this.updateValidityMessage(), this.emit("vds-after-hide"));
  }
  /** Shows the options. */
  async show() {
    if (this.open || this.disabled) {
      this.open = !1;
      return;
    }
    return this.open = !0, i2(this, "vds-after-show");
  }
  /** Hides the options. */
  async hide() {
    if (!this.open || this.disabled) {
      this.open = !1;
      return;
    }
    return this.open = !1, i2(this, "vds-after-hide");
  }
  handleDocumentInteraction(t) {
    const e = t.composedPath();
    this && !e.includes(this) && this.hide();
  }
  handleSelectKeyboardNavigation(t) {
    if (![c1.ArrowUp, c1.ArrowDown, a1.Home, a1.End].includes(t.key))
      return;
    const s = this.getAllOptions(), r = s.indexOf(this.currentOption);
    let o = Math.max(0, r);
    t.preventDefault(), !(!this.open && (this.show(), this.currentOption)) && (t.key === c1.ArrowDown ? o = o < s.length - 1 ? r + 1 : 0 : t.key === c1.ArrowUp ? o = o > 0 ? r - 1 : s.length - 1 : t.key === a1.Home ? o = 0 : t.key === a1.End && (o = s.length - 1), this.setCurrentOption(s[o]));
  }
  handleComboboxKeyDown(t) {
    t.stopPropagation(), this.handleDocumentSelectKeyDown(t);
  }
  // selectionHasChangedUpdates MUST BE CALLED whenever this.visibleSelectedOptions changes.
  // - updates the selected options cache, the current value, and the display value
  selectionHasChangedUpdates() {
    var t, e;
    this.selectedOptions = this.getAllOptions().filter((s) => s.selected), this.multi ? this.value = this.selectedOptions.map((s) => s.value) : (this.value = ((t = this.selectedOptions[0]) == null ? void 0 : t.value) ?? "", this.visibleSelectedOptions = ((e = this.selectedOptions[0]) == null ? void 0 : e.getOptionText()) ?? ""), this.updateComplete.then(() => {
      this.formSubmitController.updateValidity();
    });
  }
  setSelectedOptions(t) {
    const e = this.getAllOptions(), s = Array.isArray(t) ? t : [t];
    e.forEach((r) => r.selected = !1), s.length && s.forEach((r) => r.selected = !0), this.selectionHasChangedUpdates();
  }
  // Toggles a <vds-option>'s selected state
  toggleOptionSelection(t, e) {
    t.selected = e !== void 0 ? e : !t.selected, this.selectionHasChangedUpdates();
  }
  handleTypeToSelectOptionBehavior(t) {
    if (t.key.length === 1 || t.key === a1.Backspace) {
      const e = this.getAllOptions();
      if (t.metaKey || t.ctrlKey || t.altKey)
        return;
      if (!this.open) {
        if (t.key === a1.Backspace)
          return;
        this.show();
      }
      t.stopPropagation(), t.preventDefault(), clearTimeout(this.typeToSelectAnOptionTimeout), this.typeToSelectAnOptionTimeout = window.setTimeout(() => this.typeToSelectAnOptionStringBuffer = "", 1e3), t.key === a1.Backspace ? this.typeToSelectAnOptionStringBuffer = this.typeToSelectAnOptionStringBuffer.slice(0, -1) : this.typeToSelectAnOptionStringBuffer += t.key.toLowerCase();
      for (const s of e)
        if (s.getOptionText().toLowerCase().startsWith(this.typeToSelectAnOptionStringBuffer)) {
          this.setCurrentOption(s);
          break;
        }
    }
  }
  async handleOptionClick(t) {
    const s = t.target.closest("vds-option"), r = this.value;
    s && !s.disabled && (this.multi ? this.toggleOptionSelection(s) : this.setSelectedOptions(s), await this.updateComplete.then(() => this.displayInput.focus({ preventScroll: !0 })), this.value !== r && this.emit("vds-change"), this.multi || (this.hide(), this.displayInput.focus({ preventScroll: !0 })));
  }
  handleInvalid(t) {
    this.formSubmitController.setValidity(!1);
  }
  handleOptionsSlotChange() {
    const t = this.getAllOptions(), e = Array.isArray(this.value) ? this.value : [this.value], s = [];
    t.forEach((r) => {
      if (s.includes(r.value)) {
        const o = `
          An option with a duplicate value of "${r.value}" has been found in <vds-select>.
          All options must have unique values.
        `;
        console.error(o, r);
      }
      s.push(r.value);
    }), this.setSelectedOptions(t.filter((r) => e.includes(r.value)));
  }
  async handleDocumentSelectKeyDown(t) {
    const e = t.target, s = e.closest(".select__clear") !== null, r = e.closest("vds-tag") !== null;
    if (!(s || r)) {
      if (t.key === a1.Tab && this.open) {
        this.hide();
        return;
      }
      if (t.key === a1.Escape && this.open && (t.preventDefault(), t.stopPropagation(), this.hide(), this.displayInput.focus({ preventScroll: !0 })), t.key === a1.Enter || t.key === a1.SpaceBar && this.typeToSelectAnOptionStringBuffer === "") {
        if (t.preventDefault(), t.stopImmediatePropagation(), !this.open) {
          this.show();
          return;
        }
        this.currentOption && !this.currentOption.disabled && (this.multi ? this.toggleOptionSelection(this.currentOption) : this.setSelectedOptions(this.currentOption), await this.updateComplete, this.emit("vds-change"), this.multi || (this.hide(), this.displayInput.focus({ preventScroll: !0 })));
        return;
      }
      this.handleSelectKeyboardNavigation(t), this.handleTypeToSelectOptionBehavior(t);
    }
  }
  handleComboboxMouseDown(t) {
    const s = t.composedPath().some((r) => r instanceof Element && r.tagName.toLowerCase() === "vds-button");
    this.disabled || s || (t.preventDefault(), this.displayInput.focus({ preventScroll: !0 }), this.open = !this.open);
  }
  handleBlur() {
    this.emit("vds-blur"), this.updateValidityMessage();
  }
  async handleClearValueClick(t) {
    t.stopPropagation(), this.value !== "" && (this.setSelectedOptions([]), await this.updateComplete.then(() => this.displayInput.focus({ preventScroll: !0 })), this.emit("vds-clear"), this.emit("vds-change"));
  }
  handleClearValueMouseDown(t) {
    t.stopPropagation(), t.preventDefault();
  }
  clearValueButton() {
    return this.clearable && !this.disabled && this.value.length > 0 ? g`
      <vds-button
        class="select__clear"
        icon-description="clear select value"
        size="tiny"
        @click=${this.handleClearValueClick}
        @mousedown=${this.handleClearValueMouseDown}
      >
        <vds-icon name="close"></vds-icon>
      </vds-button>
    ` : "";
  }
  async handleTagDelete(t, e) {
    t.stopPropagation(), this.disabled || (this.toggleOptionSelection(e, !1), await this.updateComplete, this.emit("vds-change"));
  }
  getBeyondMaxSelectedOptionsText() {
    return this.selectedOptions.slice(this.maxOptionsVisible).map((t) => t.getOptionText()).join(", ");
  }
  // the select's default slot is .select__options
  // can't use VdsFormControlElementWC.getLabel cuz it uses the default slot
  getSelectLabel() {
    if (!this.label)
      return g`
        <label for="control" id="label" style="color: var(--color-text-critical)">
          ${Yi}
        </label>
      `;
    const t = g`
      <label for="control" id="label" class=${`${G0}__label`}>${this.label}</label>
    `;
    return this.hideLabel ? g`<vds-visually-hidden>${t}</vds-visually-hidden>` : t;
  }
  multipleOptions() {
    if (!this.multi)
      return "";
    const t = this.selectedOptions.map((e, s) => {
      if (s < this.maxOptionsVisible || this.maxOptionsVisible <= 0)
        return g`
          <vds-tag
            ?deletable=${!this.disabled}
            @vds-delete=${(r) => this.handleTagDelete(r, e)}
          >
            ${e.getOptionText()}
          </vds-tag>
        `;
      if (s === this.maxOptionsVisible) {
        const r = this.getBeyondMaxSelectedOptionsText();
        return g`<vds-tag role="none" title=${r}>
          +${this.selectedOptions.length - s}
          <vds-visually-hidden>
            ${r}
          </vds-visually-hidden>
        </vds-tag>`;
      }
      return null;
    });
    return g`
      <div class="select__selected-options">${t}</div>
    `;
  }
  render() {
    return g`
      ${this.getSelectLabel()}
      <vds-popup
        class=${E({
      select: !0,
      "select--multi": this.multi
    })}
        placement="bottom"
        strategy=${this.hoist ? "fixed" : "absolute"}
        sync="width"
        auto-size="vertical"
      >
        <div
          slot="anchor"
          class=${E({
      select__contents: !0,
      touched: this.hasTouched
    })}
          part="base"
          @keydown=${this.handleComboboxKeyDown}
          @mousedown=${this.handleComboboxMouseDown}
        >
          <input
            type="text"
            role="combobox"
            class="select__display-input"
            readonly
            autocomplete="off"
            spellcheck="false"
            autocapitalize="off"
            aria-controls="listbox"
            aria-expanded=${this.open}
            aria-haspopup="listbox"
            aria-labelledby="label"
            aria-disabled=${this.disabled}
            aria-describedby=${"help-text"}
            aria-errormessage=${A(this.hasValidationMessage ? "error-text" : void 0)}
            ?required=${this.required}
            ?disabled=${this.disabled}
            .value=${this.visibleSelectedOptions}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
            placeholder="Choose an option..."
          />
          ${this.multipleOptions()}
          ${this.clearValueButton()}
          <input
            id="control"
            class="select__value-input"
            type="text"
            ?disabled=${this.disabled}
            ?required=${this.required}
            .value=${Array.isArray(this.value) ? this.value.join(", ") : this.value}
            tabindex="-1"
            aria-hidden="true"
            @focus=${() => this.focus()}
            @invalid=${this.handleInvalid}
          />
          <vds-icon
            name="chevron-down"
            class=${E({
      select__toggle: !0,
      "select__toggle--open": this.open
    })}
          ></vds-icon>
        </div>
        <span
          id="listbox"
          role="listbox"
          aria-expanded=${this.open}
          aria-labelledby="label"
          aria-multiselectable=${this.multi}
          tabindex="-1"
          @mouseup=${this.handleOptionClick}
          @slotchange=${this.handleOptionsSlotChange}
        >
          <slot class="select__options"></slot>
        </span>
      </vds-popup>
      ${this.getHelpText("select")}
    `;
  }
};
P.styles = $(Wi);
U([
  g1(`.${G0}__display-input`)
], P.prototype, "displayInput", 2);
U([
  g1(`.${G0}`)
], P.prototype, "popup", 2);
U([
  g1(`.${G0}__options`)
], P.prototype, "options", 2);
U([
  K()
], P.prototype, "selectedOptions", 2);
U([
  K()
], P.prototype, "currentOption", 2);
U([
  K()
], P.prototype, "visibleSelectedOptions", 2);
U([
  l({
    // FROM: "cool stuff and things"
    // TO: ["cool", "stuff", "and", "things"]
    converter: (t = "") => t ? t.split(" ") : []
  })
], P.prototype, "value", 2);
U([
  l({ type: Boolean, reflect: !0 })
], P.prototype, "open", 2);
U([
  l({ type: Boolean })
], P.prototype, "hoist", 2);
U([
  l({ type: Boolean })
], P.prototype, "clearable", 2);
U([
  l({ type: Boolean, reflect: !0 })
], P.prototype, "multi", 2);
U([
  l({ attribute: "max-options-visible", type: Number })
], P.prototype, "maxOptionsVisible", 2);
U([
  V("disabled", { waitUntilFirstUpdate: !0 })
], P.prototype, "handleDisabledChange", 1);
U([
  V("value", { waitUntilFirstUpdate: !0 })
], P.prototype, "handleValueChange", 1);
U([
  V("open", { waitUntilFirstUpdate: !0 })
], P.prototype, "handleOpenChange", 1);
P = U([
  S("vds-select")
], P);
const Yi = `
A label is REQUIRED! Provide a label attribute.
If you'd like to hide the label, use the hide-label attribute.
`, Gi = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);display:block;user-select:none;color:var(--color-text-secondary);border-radius:var(--borderRadius-sm)}:host(:focus){background-color:var(--color-background-selected-light);outline:none}:host([disabled]) .option{cursor:not-allowed;color:var(--color-text-disabled);background-color:var(--color-background-disabled)}:host(:hover),:host(.hover){background-color:var(--color-background-tertiary-alpha)}.option{position:relative;display:grid;height:2.5rem;grid-template-columns:min-content max-content 1fr min-content;align-items:center;color:var(--color-text-normal);padding:var(--spacing-4px);border-radius:var(--borderRadius-sm);gap:var(--spacing-xs);cursor:pointer}.option__selected-icon{color:transparent}.option--selected .option__selected-icon{color:var(--color-text-normal)}
`;
var Qi = Object.defineProperty, Xi = Object.getOwnPropertyDescriptor, P1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Xi(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && Qi(e, s, o), o;
};
let w1 = class extends B {
  constructor() {
    super(...arguments), this.current = !1, this.selected = !1, this.value = "", this.disabled = !1;
  }
  connectedCallback() {
    super.connectedCallback(), this.setAttribute("role", "option"), this.setAttribute("aria-selected", "false");
  }
  handleDefaultSlotChange() {
    const t = this.getOptionText();
    if (typeof this.cachedTextLabel > "u") {
      this.cachedTextLabel = t;
      return;
    }
    t !== this.cachedTextLabel && (this.cachedTextLabel = t, this.emit("slotchange", { bubbles: !0, composed: !1, cancelable: !1 }));
  }
  handleDisabledChange() {
    this.setAttribute("aria-disabled", `${this.disabled}`);
  }
  handleSelectedChange() {
    this.setAttribute("aria-selected", `${this.selected}`);
  }
  handleValueChange() {
    this.value.includes(" ") && (console.error("Option values cannot include a space. All spaces have been replaced with underscores.", this), this.value = this.value.replace(/ /g, "_"));
  }
  // Returns the option's text, used in <vds-select>
  getOptionText() {
    return (this.textContent ?? "").trim();
  }
  render() {
    return g`
      <div
        part="base"
        class=${E(
      {
        option: !0,
        "option--selected": this.selected
      }
    )}>
        <vds-icon name="check" class="option__selected-icon"></vds-icon>
        <slot part="prefix" name="prefix"></slot>
        <slot class="option__text" @slotchange=${this.handleDefaultSlotChange}></slot>
        <slot part="suffix" name="suffix"></slot>
      </div>
    `;
  }
};
w1.styles = $(Gi);
P1([
  K()
], w1.prototype, "current", 2);
P1([
  K()
], w1.prototype, "selected", 2);
P1([
  l({ reflect: !0 })
], w1.prototype, "value", 2);
P1([
  l({ type: Boolean, reflect: !0 })
], w1.prototype, "disabled", 2);
P1([
  V("disabled")
], w1.prototype, "handleDisabledChange", 1);
P1([
  V("selected")
], w1.prototype, "handleSelectedChange", 1);
P1([
  V("value")
], w1.prototype, "handleValueChange", 1);
w1 = P1([
  S("vds-option")
], w1);
const Ji = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}.skeleton{display:flex;flex-direction:column;gap:var(--spacing-xs);width:100%;height:100%;cursor:progress}
`, e3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}.skeletonbar{height:1rem;background:linear-gradient(-45deg,var(--color-background-tertiary-alpha) 0%,var(--color-background-secondary-alpha) 25%,var(--color-background-tertiary-alpha) 50%,var(--color-background-secondary-alpha) 75%,var(--color-background-tertiary-alpha) 100%);background-size:200%;animation:skeleton 1s linear infinite}@media (prefers-reduced-motion: reduce){.skeletonbar{animation-iteration-count:1}}.skeletonbar--width-sm{width:12rem;max-width:60%}.skeletonbar--width-md{width:20rem;max-width:70%}.skeletonbar--width-lg{width:98%;max-width:calc(100% - 3rem)}.skeletonbar--width-full{width:100%;max-width:none}.skeletonbar--width-button{width:100%;max-width:8rem}.skeletonbar--height-headline-xs{height:var(--text-headline-xs)}.skeletonbar--height-headline-sm{height:var(--text-headline-sm)}.skeletonbar--height-headline-md{height:var(--text-headline-md)}.skeletonbar--height-headline-lg{height:var(--text-headline-lg)}.skeletonbar--height-headline-xl{height:var(--text-headline-xl)}.skeletonbar--height-paragraph-sm{height:var(--text-paragraph-sm)}.skeletonbar--height-paragraph-md{height:var(--text-paragraph-md)}.skeletonbar--height-paragraph-lg{height:var(--text-paragraph-lg)}.skeletonbar--height-form{height:3rem}.skeletonbar--height-image{height:8rem}@keyframes skeleton{0%{background-position-x:right}to{background-position-x:left}}
`;
var t3 = Object.defineProperty, s3 = Object.getOwnPropertyDescriptor, Q0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? s3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && t3(e, s, o), o;
};
const r3 = Object.keys(V2).map(
  (t) => `paragraph-${t}`
), o3 = Object.keys(M2).map(
  (t) => `headline-${t}`
), i3 = [
  "form",
  ...o3,
  ...r3,
  "image"
], a3 = ["sm", "md", "lg", "button", "full"];
let a0 = class extends B {
  constructor() {
    super(...arguments), this.height = "paragraph-md", this.width = "full", this.borderRadius = "sm";
  }
  get skeletonbarStyles() {
    const t = {
      borderRadius: $e[this.borderRadius]
    };
    return Ce(t);
  }
  render() {
    return g`
      <div
        class=${`skeletonbar skeletonbar--width-${this.width} skeletonbar--height-${this.height}`}
        style=${this.skeletonbarStyles}
      ></div>
    `;
  }
};
a0.styles = $(e3);
Q0([
  l(),
  x(i3, "paragraph-md")
], a0.prototype, "height", 2);
Q0([
  l(),
  x(a3, "full")
], a0.prototype, "width", 2);
Q0([
  l({ attribute: "border-radius" }),
  x(S2, "sm")
], a0.prototype, "borderRadius", 2);
a0 = Q0([
  S("vds-skeletonbar")
], a0);
var n3 = Object.defineProperty, l3 = Object.getOwnPropertyDescriptor, T2 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? l3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && n3(e, s, o), o;
};
const z2 = {
  full: g`<vds-skeletonbar />`,
  item: g`<vds-skeletonbar width="md" />`,
  title: g`<vds-skeletonbar width="md" height="headline-lg" />`,
  heading: g`
    <vds-skeletonbar width="md" height="headline-lg"></vds-skeletonbar>
    <vds-skeletonbar width="sm"></vds-skeletonbar>
  `,
  paragraph: g`
    <vds-skeletonbar></vds-skeletonbar>
    <vds-skeletonbar width="lg"></vds-skeletonbar>
    <vds-skeletonbar></vds-skeletonbar>
    <vds-skeletonbar width="lg"></vds-skeletonbar>
    <vds-skeletonbar width="md"></vds-skeletonbar>
  `,
  form: g`
    <vds-skeletonbar width="sm"></vds-skeletonbar>
    <vds-skeletonbar height="form"></vds-skeletonbar>
  `,
  button: g`<vds-skeletonbar
    width="button"
    height="form"
    border-radius="round"
  />`,
  image: g`<vds-skeletonbar height="image" />`
}, c3 = Object.keys(z2);
let j0 = class extends B {
  constructor() {
    super(...arguments), this.slotController = new I1(this, d1), this.type = "form";
  }
  render() {
    const t = this.type ? z2[this.type] : null;
    return g` <div class="skeleton" role="status">
      <vds-visually-hidden>Loading Content</vds-visually-hidden>
      <slot>${t}</slot>
    </div>`;
  }
};
j0.styles = $(Ji);
T2([
  l(),
  x(c3, "form")
], j0.prototype, "type", 2);
j0 = T2([
  S("vds-skeleton")
], j0);
const h3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{width:100%;--vds-grid-cols: 12}.grid{display:flex;flex-wrap:wrap;width:calc(100% + var(--vds-grid-gap));margin-right:calc(-1 * var(--vds-grid-gap));margin-bottom:calc(-1 * var(--vds-grid-gap))}:host([cols-xs="1"]){--vds-grid-cols: 1}:host([cols-xs="2"]){--vds-grid-cols: 2}:host([cols-xs="3"]){--vds-grid-cols: 3}:host([cols-xs="4"]){--vds-grid-cols: 4}:host([cols-xs="5"]){--vds-grid-cols: 5}:host([cols-xs="6"]){--vds-grid-cols: 6}:host([cols-xs="7"]){--vds-grid-cols: 7}:host([cols-xs="8"]){--vds-grid-cols: 8}:host([cols-xs="9"]){--vds-grid-cols: 9}:host([cols-xs="10"]){--vds-grid-cols: 10}:host([cols-xs="11"]){--vds-grid-cols: 11}:host([cols-xs="12"]){--vds-grid-cols: 12}@media only screen and (min-width: 40em){:host([cols-sm="1"]){--vds-grid-cols: 1}:host([cols-sm="2"]){--vds-grid-cols: 2}:host([cols-sm="3"]){--vds-grid-cols: 3}:host([cols-sm="4"]){--vds-grid-cols: 4}:host([cols-sm="5"]){--vds-grid-cols: 5}:host([cols-sm="6"]){--vds-grid-cols: 6}:host([cols-sm="7"]){--vds-grid-cols: 7}:host([cols-sm="8"]){--vds-grid-cols: 8}:host([cols-sm="9"]){--vds-grid-cols: 9}:host([cols-sm="10"]){--vds-grid-cols: 10}:host([cols-sm="11"]){--vds-grid-cols: 11}:host([cols-sm="12"]){--vds-grid-cols: 12}}@media only screen and (min-width: 48em){:host([cols-md="1"]){--vds-grid-cols: 1}:host([cols-md="2"]){--vds-grid-cols: 2}:host([cols-md="3"]){--vds-grid-cols: 3}:host([cols-md="4"]){--vds-grid-cols: 4}:host([cols-md="5"]){--vds-grid-cols: 5}:host([cols-md="6"]){--vds-grid-cols: 6}:host([cols-md="7"]){--vds-grid-cols: 7}:host([cols-md="8"]){--vds-grid-cols: 8}:host([cols-md="9"]){--vds-grid-cols: 9}:host([cols-md="10"]){--vds-grid-cols: 10}:host([cols-md="11"]){--vds-grid-cols: 11}:host([cols-md="12"]){--vds-grid-cols: 12}}@media only screen and (min-width: 64em){:host([cols-lg="1"]){--vds-grid-cols: 1}:host([cols-lg="2"]){--vds-grid-cols: 2}:host([cols-lg="3"]){--vds-grid-cols: 3}:host([cols-lg="4"]){--vds-grid-cols: 4}:host([cols-lg="5"]){--vds-grid-cols: 5}:host([cols-lg="6"]){--vds-grid-cols: 6}:host([cols-lg="7"]){--vds-grid-cols: 7}:host([cols-lg="8"]){--vds-grid-cols: 8}:host([cols-lg="9"]){--vds-grid-cols: 9}:host([cols-lg="10"]){--vds-grid-cols: 10}:host([cols-lg="11"]){--vds-grid-cols: 11}:host([cols-lg="12"]){--vds-grid-cols: 12}}@media only screen and (min-width: 80em){:host([cols-xl="1"]){--vds-grid-cols: 1}:host([cols-xl="2"]){--vds-grid-cols: 2}:host([cols-xl="3"]){--vds-grid-cols: 3}:host([cols-xl="4"]){--vds-grid-cols: 4}:host([cols-xl="5"]){--vds-grid-cols: 5}:host([cols-xl="6"]){--vds-grid-cols: 6}:host([cols-xl="7"]){--vds-grid-cols: 7}:host([cols-xl="8"]){--vds-grid-cols: 8}:host([cols-xl="9"]){--vds-grid-cols: 9}:host([cols-xl="10"]){--vds-grid-cols: 10}:host([cols-xl="11"]){--vds-grid-cols: 11}:host([cols-xl="12"]){--vds-grid-cols: 12}}@media only screen and (min-width: 90em){:host([cols-xl2="1"]){--vds-grid-cols: 1}:host([cols-xl2="2"]){--vds-grid-cols: 2}:host([cols-xl2="3"]){--vds-grid-cols: 3}:host([cols-xl2="4"]){--vds-grid-cols: 4}:host([cols-xl2="5"]){--vds-grid-cols: 5}:host([cols-xl2="6"]){--vds-grid-cols: 6}:host([cols-xl2="7"]){--vds-grid-cols: 7}:host([cols-xl2="8"]){--vds-grid-cols: 8}:host([cols-xl2="9"]){--vds-grid-cols: 9}:host([cols-xl2="10"]){--vds-grid-cols: 10}:host([cols-xl2="11"]){--vds-grid-cols: 11}:host([cols-xl2="12"]){--vds-grid-cols: 12}}@media only screen and (min-width: 100em){:host([cols-xl3="1"]){--vds-grid-cols: 1}:host([cols-xl3="2"]){--vds-grid-cols: 2}:host([cols-xl3="3"]){--vds-grid-cols: 3}:host([cols-xl3="4"]){--vds-grid-cols: 4}:host([cols-xl3="5"]){--vds-grid-cols: 5}:host([cols-xl3="6"]){--vds-grid-cols: 6}:host([cols-xl3="7"]){--vds-grid-cols: 7}:host([cols-xl3="8"]){--vds-grid-cols: 8}:host([cols-xl3="9"]){--vds-grid-cols: 9}:host([cols-xl3="10"]){--vds-grid-cols: 10}:host([cols-xl3="11"]){--vds-grid-cols: 11}:host([cols-xl3="12"]){--vds-grid-cols: 12}}
`;
var d3 = Object.defineProperty, p3 = Object.getOwnPropertyDescriptor, V1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? p3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && d3(e, s, o), o;
};
const G1 = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"], u3 = Object.keys(u0.spacing);
let p1 = class extends B {
  constructor() {
    super(...arguments), this.gap = "lg", this.colsXs = "12";
  }
  render() {
    const t = Ce({
      "--vds-grid-gap": `var(--spacing-${this.gap})`
    });
    return g`
      <div class='grid' part='base' style=${t}>
        <slot></slot>
      </div>
    `;
  }
};
p1.styles = $(h3);
V1([
  l(),
  x(u3, "lg")
], p1.prototype, "gap", 2);
V1([
  l({ reflect: !0, attribute: "cols-xs" }),
  x(G1, "12")
], p1.prototype, "colsXs", 2);
V1([
  l({ reflect: !0, attribute: "cols-sm" }),
  x(G1)
], p1.prototype, "colsSm", 2);
V1([
  l({ reflect: !0, attribute: "cols-md" }),
  x(G1)
], p1.prototype, "colsMd", 2);
V1([
  l({ reflect: !0, attribute: "cols-lg" }),
  x(G1)
], p1.prototype, "colsLg", 2);
V1([
  l({ reflect: !0, attribute: "cols-xl" }),
  x(G1)
], p1.prototype, "colsXl", 2);
V1([
  l({ reflect: !0, attribute: "cols-xl2" }),
  x(G1)
], p1.prototype, "colsXl2", 2);
V1([
  l({ reflect: !0, attribute: "cols-xl3" }),
  x(G1)
], p1.prototype, "colsXl3", 2);
p1 = V1([
  S("vds-grid")
], p1);
const g3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{padding-right:var(--vds-grid-gap);padding-bottom:var(--vds-grid-gap);flex-shrink:0;box-sizing:border-box;flex-basis:100%;max-width:100%;flex-grow:0}:host([xs="0"]){display:none}:host([xs="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([xs="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([xs="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([xs="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([xs="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([xs="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([xs="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([xs="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([xs="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([xs="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([xs="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([xs="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xs="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([xs=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([xs=fill]){flex-basis:content;max-width:100%;flex-grow:1}@media only screen and (min-width: 40em){:host([sm="0"]){display:none}:host([sm="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([sm="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([sm="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([sm="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([sm="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([sm="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([sm="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([sm="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([sm="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([sm="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([sm="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([sm="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-sm="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([sm=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([sm=fill]){flex-basis:content;max-width:100%;flex-grow:1}}@media only screen and (min-width: 48em){:host([md="0"]){display:none}:host([md="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([md="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([md="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([md="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([md="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([md="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([md="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([md="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([md="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([md="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([md="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([md="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-md="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([md=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([md=fill]){flex-basis:content;max-width:100%;flex-grow:1}}@media only screen and (min-width: 64em){:host([lg="0"]){display:none}:host([lg="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([lg="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([lg="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([lg="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([lg="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([lg="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([lg="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([lg="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([lg="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([lg="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([lg="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([lg="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-lg="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([lg=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([lg=fill]){flex-basis:content;max-width:100%;flex-grow:1}}@media only screen and (min-width: 80em){:host([xl="0"]){display:none}:host([xl="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([xl="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([xl="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([xl="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([xl="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([xl="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([xl="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([xl="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([xl="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([xl="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([xl="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([xl="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([xl=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([xl=fill]){flex-basis:content;max-width:100%;flex-grow:1}}@media only screen and (min-width: 90em){:host([xl2="0"]){display:none}:host([xl2="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([xl2="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([xl2="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([xl2="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([xl2="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([xl2="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([xl2="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([xl2="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([xl2="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([xl2="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([xl2="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([xl2="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl2="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([xl2=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([xl2=fill]){flex-basis:content;max-width:100%;flex-grow:1}}@media only screen and (min-width: 100em){:host([xl3="0"]){display:none}:host([xl3="1"]){display:block;flex-basis:calc(1 / var(--vds-grid-cols) * 100%);max-width:calc(1 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="1"]){margin-left:calc(1 / var(--vds-grid-cols) * 100%)}:host([xl3="2"]){display:block;flex-basis:calc(2 / var(--vds-grid-cols) * 100%);max-width:calc(2 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="2"]){margin-left:calc(2 / var(--vds-grid-cols) * 100%)}:host([xl3="3"]){display:block;flex-basis:calc(3 / var(--vds-grid-cols) * 100%);max-width:calc(3 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="3"]){margin-left:calc(3 / var(--vds-grid-cols) * 100%)}:host([xl3="4"]){display:block;flex-basis:calc(4 / var(--vds-grid-cols) * 100%);max-width:calc(4 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="4"]){margin-left:calc(4 / var(--vds-grid-cols) * 100%)}:host([xl3="5"]){display:block;flex-basis:calc(5 / var(--vds-grid-cols) * 100%);max-width:calc(5 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="5"]){margin-left:calc(5 / var(--vds-grid-cols) * 100%)}:host([xl3="6"]){display:block;flex-basis:calc(6 / var(--vds-grid-cols) * 100%);max-width:calc(6 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="6"]){margin-left:calc(6 / var(--vds-grid-cols) * 100%)}:host([xl3="7"]){display:block;flex-basis:calc(7 / var(--vds-grid-cols) * 100%);max-width:calc(7 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="7"]){margin-left:calc(7 / var(--vds-grid-cols) * 100%)}:host([xl3="8"]){display:block;flex-basis:calc(8 / var(--vds-grid-cols) * 100%);max-width:calc(8 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="8"]){margin-left:calc(8 / var(--vds-grid-cols) * 100%)}:host([xl3="9"]){display:block;flex-basis:calc(9 / var(--vds-grid-cols) * 100%);max-width:calc(9 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="9"]){margin-left:calc(9 / var(--vds-grid-cols) * 100%)}:host([xl3="10"]){display:block;flex-basis:calc(10 / var(--vds-grid-cols) * 100%);max-width:calc(10 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="10"]){margin-left:calc(10 / var(--vds-grid-cols) * 100%)}:host([xl3="11"]){display:block;flex-basis:calc(11 / var(--vds-grid-cols) * 100%);max-width:calc(11 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="11"]){margin-left:calc(11 / var(--vds-grid-cols) * 100%)}:host([xl3="12"]){display:block;flex-basis:calc(12 / var(--vds-grid-cols) * 100%);max-width:calc(12 / var(--vds-grid-cols) * 100%);flex-shrink:0;flex-grow:0}:host([offset-xl3="12"]){margin-left:calc(12 / var(--vds-grid-cols) * 100%)}:host([xl3=hug]){flex-basis:content;max-width:100%;flex-grow:0}:host([xl3=fill]){flex-basis:content;max-width:100%;flex-grow:1}}
`;
var v3 = Object.defineProperty, m3 = Object.getOwnPropertyDescriptor, Y = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? m3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && v3(e, s, o), o;
};
const B1 = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"], F1 = [...B1, "fill", "hug"];
let F = class extends B {
  updated(t) {
    super.updated(t), this._validateAllSizes(t);
  }
  _validateAllSizes(t) {
    const { xs: e, sm: s, md: r, lg: o, xl: i, xl2: a, xl3: c, offsetXs: h, offsetSm: d, offsetMd: v, offsetLg: p, offsetXl: u, offsetXl2: m, offsetXl3: b } = this;
    Object.entries({
      xs: e,
      sm: s,
      md: r,
      lg: o,
      xl: i,
      xl2: a,
      xl3: c,
      offsetXs: h,
      offsetSm: d,
      offsetMd: v,
      offsetLg: p,
      offsetXl: u,
      offsetXl2: m,
      offsetXl3: b
    }).forEach(([w, k]) => t.has(w) && this._validateSize(w, k, F1));
  }
  _validateSize(t, e, s) {
    e !== void 0 && (s.includes(e) || console.warn(this, `Attribute ${t}='${e}' is invalid.`));
  }
  render() {
    return g`<slot part='base'></slot>`;
  }
};
F.styles = $(g3);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "xs", 2);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "sm", 2);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "md", 2);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "lg", 2);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "xl", 2);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "xl2", 2);
Y([
  l({ reflect: !0 }),
  x(F1)
], F.prototype, "xl3", 2);
Y([
  l({ reflect: !0, attribute: "offset-xs" }),
  x(B1)
], F.prototype, "offsetXs", 2);
Y([
  l({ reflect: !0, attribute: "offset-sm" }),
  x(B1)
], F.prototype, "offsetSm", 2);
Y([
  l({ reflect: !0, attribute: "offset-md" }),
  x(B1)
], F.prototype, "offsetMd", 2);
Y([
  l({ reflect: !0, attribute: "offset-lg" }),
  x(B1)
], F.prototype, "offsetLg", 2);
Y([
  l({ reflect: !0, attribute: "offset-xl" }),
  x(B1)
], F.prototype, "offsetXl", 2);
Y([
  l({ reflect: !0, attribute: "offset-xl2" }),
  x(B1)
], F.prototype, "offsetXl2", 2);
Y([
  l({ reflect: !0, attribute: "offset-xl3" }),
  x(B1)
], F.prototype, "offsetXl3", 2);
F = Y([
  S("vds-grid-item")
], F);
const f3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}.overlay{position:fixed;display:flex;inset:0;justify-content:center;align-items:center;padding:var(--spacing-md);overflow-y:auto;background-color:var(--color-background-overlay);box-sizing:border-box;z-index:9999;opacity:0;visibility:hidden;transition:all .2s}.overlay--open{opacity:1;visibility:visible;transition:all 0s,opacity .2s}.modal{border-radius:var(--borderRadius-md)}.modal--sm{max-width:30rem}.modal--md{max-width:40rem}.modal--fullscreen{min-width:100%}.modal__contents{position:relative;display:block;background:var(--color-background-primary)}.modal__title{font-size:var(--text-headline-md);line-height:var(--text-headline-lineHeight);font-family:var(--text-headline-fontFamily);font-weight:var(--text-headline-fontWeight)}.modal__subtitle{font-size:var(--text-paragraph-md);line-height:var(--text-paragraph-lineHeight);font-family:var(--text-paragraph-fontFamily);font-weight:var(--text-paragraph-fontWeight)}.modal__close{outline:none;transition:box-shadow .15s;position:relative;display:inline-flex;float:right;height:2rem;width:2rem;align-items:center;justify-content:center;padding:0 var(--spacing-sm);color:var(--color-button-subtle-text);background-color:var(--color-button-subtle);border-radius:var(--borderRadius-round);border:1px solid transparent;cursor:pointer}.modal__close:focus{box-shadow:var(--focus-shadow)}.modal__close:hover{background-color:var(--color-button-subtle-hovered)}.modal__close:active{background-color:var(--color-button-subtle-pressed)}
`;
var b3 = Object.defineProperty, w3 = Object.getOwnPropertyDescriptor, H1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? w3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && b3(e, s, o), o;
};
const x3 = ["sm", "md", "fullscreen"], z0 = "overlay", A0 = "modal", A3 = "A title is REQUIRED!! Please provide a title attribute.", y3 = `needs a trigger, see the docs: https://staging.disqotech.com/design/vinyl-design-system/?path=/docs/getting-started-introduction--page
`;
let u1 = class extends B {
  constructor() {
    super(...arguments), this.focusTrapController = new b2(this, `.${z0}`), this.open = !1, this.size = "sm";
  }
  handleClick(t) {
    (t.target === this.closeButton || t.target === this.overlay) && (this.open = !1);
  }
  handleOverlayKeydown(t) {
    t.key === a1.Escape && (this.open = !1);
  }
  handleTriggerOnClose() {
    let t;
    if (this.trigger && typeof this.trigger == "string" ? t = this.getRootNode().getElementById(this.trigger) : t = this.trigger, !t) {
      console.warn(this, y3);
      return;
    }
    t.focus();
  }
  async handleOpen() {
    this.open && (await this.updateComplete, this.closeButton.focus()), this.open || (this.handleTriggerOnClose(), this.emit("vds-close"));
  }
  maybeSubtitle() {
    if (this.subtitle)
      return g`<p id="subtitle" class="modal__subtitle">${this.subtitle}</p>`;
  }
  getHeader() {
    return this.title ? g`<h2 id="title" class="modal__title">${this.title}</h2>` : g`<span id="no-title" style="color: var(--color-text-critical)">${A3}</span>`;
  }
  render() {
    return g`
      <div
        class=${E({
      [`${z0}`]: !0,
      [`${z0}--open`]: this.open
    })}
        @click=${this.handleClick}
        @keydown=${this.handleOverlayKeydown}
      >
        <vds-card
          class=${`${A0} ${A0}--${this.size}`}
          shadow="lg"
          part="base"
          aria-hidden=${!this.open}
          aria-modal=${!0}
          role="dialog"
          aria-labelledby="title"
          aria-describedby="subtitle"
        >
          <button
            class=${`${A0}__close`}
            @click=${this.handleClick}
          >
            <vds-icon name="close" size="sm"></vds-icon>
            <vds-visually-hidden>close</vds-visually-hidden>
          </button>
          <header id="header">
            ${this.getHeader()}
            ${this.maybeSubtitle()}
          </header>
          <slot class=${`${A0}__contents`}></slot>
        </vds-card>
      </div>
    `;
  }
};
u1.styles = $(f3);
H1([
  g1(`.${z0}`)
], u1.prototype, "overlay", 2);
H1([
  g1(`.${A0}__close`)
], u1.prototype, "closeButton", 2);
H1([
  l({ type: Boolean })
], u1.prototype, "open", 2);
H1([
  l()
], u1.prototype, "title", 2);
H1([
  l()
], u1.prototype, "subtitle", 2);
H1([
  l()
], u1.prototype, "trigger", 2);
H1([
  l(),
  x(x3, "sm")
], u1.prototype, "size", 2);
H1([
  V("open", { waitUntilFirstUpdate: !0 })
], u1.prototype, "handleOpen", 1);
u1 = H1([
  S("vds-modal")
], u1);
const k3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:block;user-select:none;color:var(--color-text-secondary)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}.label{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);padding:var(--spacing-sm) var(--spacing-sm) var(--spacing-4px) var(--spacing-4px);margin:0 0 var(--spacing-4px) 0}
`;
var _3 = Object.defineProperty, Z3 = Object.getOwnPropertyDescriptor, X0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? Z3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && _3(e, s, o), o;
};
let n0 = class extends B {
  connectedCallback() {
    super.connectedCallback(), this.setAttribute("role", "group"), this.setAttribute("tabindex", "-1");
  }
  getAllOptions() {
    return [...this.querySelectorAll("vds-option")];
  }
  handleDisabledChange() {
    this.getAllOptions().forEach((t) => {
      t.disabled = this.disabled;
    }), this.setAttribute("aria-disabled", `${this.disabled}`);
  }
  render() {
    return g`
      <div class="label">${this.label}</div>
      <slot></slot>
    `;
  }
};
n0.styles = $(k3);
X0([
  l()
], n0.prototype, "label", 2);
X0([
  l({ type: Boolean, reflect: !0 })
], n0.prototype, "disabled", 2);
X0([
  V("disabled")
], n0.prototype, "handleDisabledChange", 1);
n0 = X0([
  S("vds-optgroup")
], n0);
const C3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{position:fixed;top:0;left:0;right:0;z-index:var(--zIndex-notification);display:block;--color-button-auxiliary: var(--color-button-auxiliary-inverse);--color-button-auxiliary-text: var(--color-button-auxiliary-text-inverse);--color-button-auxiliary-hovered: var(--color-button-auxiliary-hovered-inverse);--color-button-auxiliary-pressed: var(--color-button-auxiliary-pressed-inverse);--color-button-auxiliary-focused: var(--color-button-auxiliary-focused-inverse);--color-button-auxiliary-disabled: var(--color-button-auxiliary-disabled-inverse);--color-button-auxiliary-disabled-text: var(--color-button-auxiliary-disabled-text-inverse)}:host([unpinned]){position:relative}.banner{width:100%;display:flex;justify-content:space-between;align-items:center;gap:var(--spacing-md);padding:var(--spacing-xs) var(--spacing-xs) var(--spacing-xs) var(--spacing-md);background-color:var(--color-background-primary-inverse);color:var(--color-text-normal-inverse)}.banner__content{display:flex;gap:var(--spacing-xs);align-items:center}.banner__message{min-height:2.5rem;display:flex;align-items:center;gap:var(--spacing-xs);flex-wrap:wrap;font-size:var(--text-label-sm);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}.banner__close-button{flex:0}.banner__close-button::part(base){color:var(--color-button-subtle-text-inverse);background-color:var(--color-button-subtle-inverse)}.banner__close-button::part(base):hover{background-color:var(--color-button-subtle-hovered-inverse)}.banner__close-button::part(base):active{background-color:var(--color-button-subtle-pressed-inverse)}@media screen and (min-width: 40em){.banner{display:grid;grid-template-columns:1fr auto 1fr;padding:var(--spacing-xs)}.banner__content{grid-column:2/3;align-items:center}.banner__close-button{grid-column:3/4;justify-self:end}.banner__message{flex-wrap:nowrap}}
`;
var $3 = Object.defineProperty, M3 = Object.getOwnPropertyDescriptor, Me = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? M3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && $3(e, s, o), o;
};
let V0 = class extends B {
  constructor() {
    super(...arguments), this.slotController = new I1(this, d1), this.showCloseButton = !1, this.unpinned = !1;
  }
  maybeGetClearButton() {
    return this.showCloseButton ? g`
      <vds-button class="banner__close-button" size="small" icon-description="close" @click=${() => {
      this.style.display = "none";
    }}>
        <vds-icon name="close"></vds-icon>
      </vds-button>
    ` : void 0;
  }
  render() {
    return g`
      <div class="banner" aria-labelledby="message-text" aria-live="polite" role="status">
        <div class="banner__content">
          <slot name="icon"></slot>
          <div class="banner__message">
            <slot id="message-text"></slot>
            <slot name="cta"></slot>
          </div>
        </div>
        ${this.maybeGetClearButton()}
      </div>
    `;
  }
};
V0.styles = $(C3);
Me([
  l({ type: Boolean, attribute: "show-close-button" })
], V0.prototype, "showCloseButton", 2);
Me([
  l({ type: Boolean, attribute: "unpinned" })
], V0.prototype, "unpinned", 2);
V0 = Me([
  S("vds-pinned-banner")
], V0);
const V3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}.tooltip{--color-arrow-color: transparent;width:fit-content}.tooltip__popup{font-size:var(--text-label-sm);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);display:none;padding:8px 16px;width:max-content;background:var(--color-background-primary-inverse);color:var(--color-text-normal-inverse);border-radius:var(--borderRadius-md)}.tooltip:hover .tooltip__popup,:host(.hover) .tooltip__popup,.tooltip:focus-within .tooltip__popup,.tooltip .popup__arrow{display:block}.tooltip:hover,.tooltip :host(.hover),.tooltip:focus-within{--color-arrow-color: var(--color-background-primary-inverse)}.tooltip__shortcut{color:var(--color-text-secondary-inverse);display:flex;justify-content:center}
`;
var H3 = Object.defineProperty, S3 = Object.getOwnPropertyDescriptor, J0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? S3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && H3(e, s, o), o;
};
let l0 = class extends B {
  constructor() {
    super(...arguments), this.title = "", this.shortcut = "", this.placement = "top";
  }
  render() {
    return g`
    <div class="tooltip" data-cy="tooltip">
      <vds-popup distance="4" style="--arrow-color: var(--color-arrow-color);" active arrow placement=${this.placement}>
        <div class="tooltip__popup">
          <span>${this.title}</span>
          <br/>
          <span class="tooltip__shortcut">${this.shortcut}</span>
        </div>
        <div slot="anchor"><slot></slot></div>
      </vds-popup>
    </div>
    `;
  }
};
l0.styles = $(V3);
J0([
  l()
], l0.prototype, "title", 2);
J0([
  l()
], l0.prototype, "shortcut", 2);
J0([
  l({ reflect: !0 }),
  x($2, "top")
], l0.prototype, "placement", 2);
l0 = J0([
  S("vds-tooltip")
], l0);
const D3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{position:relative;top:0;left:0;right:0;z-index:var(--zIndex-notification);display:block}::slotted(:first-child){margin-top:0}::slotted(:last-child){margin-bottom:0}.accordion{display:grid;grid-template-rows:0fr;overflow:hidden;transition:.3s ease}.accordion--open{grid-template-rows:1fr}.accordion__item{min-height:0}.banner{width:100%;display:flex;justify-content:space-between;align-items:center;gap:var(--spacing-md);padding:var(--spacing-xs) var(--spacing-xs) var(--spacing-xs) var(--spacing-md);color:var(--color-text-normal);border-radius:8px}.banner--critical{background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical-light);color:var(--color-background-critical)}.banner--info{background-color:var(--color-background-info-light);border:1px solid var(--color-border-info-light);color:var(--color-background-info)}.banner--success{background-color:var(--color-background-success-light);border:1px solid var(--color-border-success-light);color:var(--color-background-success)}.banner--warning{background-color:var(--color-background-warning-light);border:1px solid var(--color-border-warning-light);color:var(--color-background-warning)}.banner__content{display:flex;gap:var(--spacing-md);align-items:center}.banner__headline{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);margin:0}.banner__headline--bold{font-weight:var(--text-bold-fontWeight)}.banner__message{font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);margin:0}.banner__cta{flex:0 0 auto}.banner__body{color:var(--color-text-normal);margin:var(--spacing-xs) 0;flex:1}.banner__body--stacked{gap:var(--spacing-4px);display:flex;flex-direction:column}.banner__close-button{flex:0}.banner__close-button::part(base){color:var(--color-button-subtle-text);background-color:var(--color-button-subtle)}.banner__close-button::part(base):hover{background-color:var(--color-button-subtle-hovered-inverse)}.banner__close-button::part(base):active{background-color:var(--color-button-subtle-pressed-inverse)}.banner--inline :is(.banner__body,.banner__headline){display:inline}.banner--inline .banner__message::slotted(*){display:inline}
`;
var L3 = Object.defineProperty, O3 = Object.getOwnPropertyDescriptor, S0 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? O3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && L3(e, s, o), o;
};
const E3 = ["info", "critical", "warning", "success"], T3 = ["inline", "stacked"];
let W1 = class extends B {
  constructor() {
    super(...arguments), this.appearance = "info", this.layout = "stacked", this.showCloseButton = !1, this.open = !0, this.slotController = new I1(this, d1, "message"), this.iconName = {
      info: "info",
      critical: "critical",
      warning: "warning",
      success: "check-circle"
    };
  }
  get hasMessage() {
    return this.slotController.hasSlot("message");
  }
  getCloseButton() {
    if (this.showCloseButton)
      return g`
      <vds-button
        class="banner__close-button"
        size="small"
        icon-description="close"
        @click=${this.hide}
      >
        <vds-icon name="close"></vds-icon>
      </vds-button>
    `;
  }
  show() {
    this.open || (this.open = !0, this.emit("vds-show"));
  }
  hide() {
    this.open && (this.open = !1, this.emit("vds-hide"));
  }
  render() {
    const t = E({
      accordion: !0,
      "accordion--open": this.open
    }), e = E({
      banner: !0,
      [`banner--${this.appearance}`]: !0,
      [`banner--${this.layout}`]: !0
    }), s = E({
      banner__body: !0,
      [`banner__body--${this.layout}`]: !0
    }), r = E({
      banner__headline: !0,
      "banner__headline--bold": this.hasMessage
    });
    return g`
      <div class=${t}>
        <div class="accordion__item">
          <div
            class=${e}
            aria-hidden=${this.open ? "false" : "true"}
            aria-labelledby="message-text"
            aria-live="polite"
            role="status"
            part="base"
          >
            <div class="banner__content">
              <slot name="icon">
                <vds-icon name=${this.iconName[this.appearance]} size="lg"></vds-icon>
              </slot>
              <div class=${s}>
                <h4 class=${r}>
                  <slot id="message-text">
                  </slot>
                </h4>
                <slot name="message" class="banner__message"></slot>
              </div>
              <slot name="cta" class="banner__cta"></slot>
            </div>
            ${this.getCloseButton()}
          </div>
        </div>
      </div>
    `;
  }
};
W1.styles = $(D3);
S0([
  l(),
  x(E3, "info")
], W1.prototype, "appearance", 2);
S0([
  l(),
  x(T3, "stacked")
], W1.prototype, "layout", 2);
S0([
  l({ type: Boolean, attribute: "show-close-button" })
], W1.prototype, "showCloseButton", 2);
S0([
  K()
], W1.prototype, "open", 2);
W1 = S0([
  S("vds-inline-banner")
], W1);
const z3 = `:host{box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:grid;gap:var(--spacing-4px);color:var(--color-text-secondary);font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight)}:host([required]) .password__label-text:after{content:" *";color:var(--color-text-critical)}:host([disabled]){cursor:not-allowed;color:var(--color-text-disabled)}:host([disabled]) .password__wrapper{color:var(--color-text-disabled)}.password__label{display:flex;align-items:flex-end;justify-content:space-between}.password__label-text{cursor:inherit;color:var(--color-text-normal)}.password__wrapper{position:relative;color:var(--color-text-normal)}.password__help-text{font-size:var(--text-label-sm);color:var(--color-text-secondary)}.password__accordion{display:grid;grid-template-rows:0fr;overflow:hidden;animation:grow .25s ease-out both}@keyframes grow{0%{grid-template-rows:0fr}to{grid-template-rows:1fr}}.password__accordion-item{min-height:0}.password__error{color:var(--color-text-critical);display:flex;gap:var(--spacing-4px);align-items:flex-start;justify-content:flex-start}.password__error-icon{flex:0}.password__error-text{margin-top:2px;margin-bottom:var(--spacing-xs)}.password__control{appearance:none;background-color:var(--color-background-primary);border:1px solid var(--color-border-normal);border-radius:var(--borderRadius-sm);box-sizing:border-box;min-height:3rem;padding:var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-sm);width:100%;margin:0;font-size:var(--text-label-md);line-height:var(--text-label-lineHeight);font-family:var(--text-label-fontFamily);font-weight:var(--text-label-fontWeight);color:inherit;cursor:inherit;outline:none;transition:box-shadow .15s}.password__control:focus{box-shadow:var(--focus-shadow)}.password__control:is(.touched,:not([required])):is(:invalid,[aria-invalid=true]){background-color:var(--color-background-critical-light);border:1px solid var(--color-border-critical)}.password__control:disabled{background-color:var(--color-background-secondary-alpha);border:1px solid var(--color-border-light)}.password__control{padding:var(--spacing-sm) var(--spacing-2xl) var(--spacing-sm) var(--spacing-sm)}.password__toggle{position:absolute;top:50%;transform:translateY(-50%);right:var(--spacing-xs)}.password__toggle::part(base){color:var(--color-button-subtle-text);background-color:var(--color-button-subtle)}.password__toggle::part(base):hover{background-color:var(--color-button-subtle-hovered)}.password__toggle::part(base):active{background-color:var(--color-button-subtle-pressed)}
`;
var I3 = Object.defineProperty, P3 = Object.getOwnPropertyDescriptor, A1 = (t, e, s, r) => {
  for (var o = r > 1 ? void 0 : r ? P3(e, s) : e, i = t.length - 1, a; i >= 0; i--)
    (a = t[i]) && (o = (r ? a(e, s, o) : a(o)) || o);
  return r && o && I3(e, s, o), o;
};
let l1 = class extends j {
  constructor() {
    super(...arguments), this.value = "", this.autocomplete = "current-password", this.placeholder = "", this._showPassword = !1;
  }
  firstUpdated() {
    this.formSubmitController.updateValidity();
  }
  /**
   * Selects the password input's text.
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/select
   * */
  select() {
    this.input.select();
  }
  handleInput() {
    this.value = this.input.value, this.formSubmitController.updateValidity(), this.emit("vds-input"), this.input.validity.valid && this.updateValidityMessage();
  }
  handleInvalid() {
    this.formSubmitController.setValidity(!1);
  }
  handleKeyDown(t) {
    t.key === "Enter" && !t.defaultPrevented && this.formSubmitController.submit();
  }
  async handleValueChange() {
    await this.updateComplete, this.formSubmitController.updateValidity();
  }
  handleDisabledChange() {
    this.formSubmitController.setValidity(this.disabled);
  }
  toggleShowPassword() {
    this._showPassword = !this._showPassword, this.input.type = this._showPassword ? "text" : "password", this.input.focus();
  }
  render() {
    return g`
      ${this.getLabel(a2)}
      <div class="password__wrapper">
        <input
          id="control"
          class=${E({
      password__control: !0,
      touched: this.hasTouched
    })}
          part="base"
          type="password"
          name=${A(this.name ? this.name : void 0)}
          ?disabled=${this.disabled}
          ?required=${this.required}
          minlength=${A(this.minlength)}
          maxlength=${A(this.maxlength)}
          .value=${c0(A(this.value))}
          autocomplete=${A(this.autocomplete)}
          spellcheck=${A(this.spellcheck)}
          pattern=${A(this.pattern)}
          aria-labelledby="label help-text"
          @input=${this.handleInput}
          @invalid=${this.handleInvalid}
          @keydown=${this.handleKeyDown}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          placeholder=${this.placeholder}
        />
        <vds-button
          class="password__toggle"
          size="tiny"
          icon-description=${this._showPassword ? "Hide password" : "Show password"}
          @click=${this.toggleShowPassword}
        >
          <vds-icon
            name=${this._showPassword ? "visibility-on" : "visibility-off"}
          ></vds-icon>
        </vds-button>
      </div>
      ${this.getHelpText(a2)}
    `;
  }
};
l1.styles = $(z3);
A1([
  l()
], l1.prototype, "value", 2);
A1([
  l({ type: Number })
], l1.prototype, "minlength", 2);
A1([
  l({ type: Number })
], l1.prototype, "maxlength", 2);
A1([
  l()
], l1.prototype, "pattern", 2);
A1([
  l()
], l1.prototype, "autocomplete", 2);
A1([
  l()
], l1.prototype, "placeholder", 2);
A1([
  K()
], l1.prototype, "_showPassword", 2);
A1([
  V("value", { waitUntilFirstUpdate: !0 })
], l1.prototype, "handleValueChange", 1);
A1([
  V("disabled", { waitUntilFirstUpdate: !0 })
], l1.prototype, "handleDisabledChange", 1);
l1 = A1([
  S("vds-password")
], l1);
const a2 = "password";
export {
  Ui as CARD_BACKGROUND_COLOR_VARIANTS,
  R as VdsButtonWC,
  z1 as VdsCardWC,
  n1 as VdsCheckboxWC,
  t1 as VdsDatepickerWC,
  F as VdsGridItemWC,
  p1 as VdsGridWC,
  i0 as VdsIconWC,
  W1 as VdsInlineBannerWC,
  I as VdsInputWC,
  C1 as VdsLinkWC,
  u1 as VdsModalWC,
  n0 as VdsOptgroupWC,
  w1 as VdsOptionWC,
  l1 as VdsPasswordWC,
  V0 as VdsPinnedBannerWC,
  H as VdsPopupWC,
  E1 as VdsRadioGroupWC,
  $1 as VdsRadioWC,
  e1 as VdsSearchWC,
  P as VdsSelectWC,
  a0 as VdsSkeletonBarWC,
  j0 as VdsSkeletonWC,
  $0 as VdsTagWC,
  q as VdsTextareaWC,
  l0 as VdsTooltipWC,
  pe as VdsVisuallyHiddenWC,
  E3 as appearanceTypes,
  ai as arrowPlacement,
  ho as buttonColors,
  po as buttonSizes,
  uo as buttonTargets,
  co as buttonTypes,
  A3 as defaultTitleText,
  T3 as layoutTypes,
  x3 as modalSizes,
  go as needsDescriptionMessage,
  $2 as placement,
  zo as resizeOptions,
  i3 as skeletonBarHeights,
  a3 as skeletonBarWidths,
  ni as sync,
  Fo as tagColors,
  io as typeOptions
};
//# sourceMappingURL=index.mjs.map
